import { clsx } from 'clsx';
import { formatDistanceToNow, intervalToDuration, formatDuration as formatDuration$1, format } from 'date-fns';
import { AutomationProvider, useAutomation, useTrace, useError, useTestCase, useAutomations, useTraces, useErrors, useInfiniteActivity, useActivityStream, useUpdateErrorStatus, useUpdateErrorSeverity, useSyncErrors, useUpdateAutomationStatus, useDeleteAutomation, useTriggerAutomation, useAlertRules, useAlertHistory, useAlertMutes, useAlertStats, useCreateAlertRule, useUpdateAlertRule, useDeleteAlertRule, useAcknowledgeAlert, useResolveAlert, useCreateAlertMute, useDeleteAlertMute, useTickets, useCreateTicket, useUpdateTicket, useUpdateTicketStatus, useAssignTicket, useAddTicketComment, useSyncTicketToLinear, useTestSuites, useFlakyTests, useCoverageOverview, useCreateTestSuite, useUpdateTestSuite, useDeleteTestSuite, useRunTestSuite, useUpdateTestSuiteSchedule } from '@dain/automation-data';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cva } from 'class-variance-authority';
import React, { forwardRef, createContext, useRef, useEffect, useCallback, useState, useMemo, useContext } from 'react';
import { Search, X, ChevronDown, Tag, Check, ChevronRight, RefreshCw, ArrowDown, AlertCircle, WifiOff, Wifi, Copy, Trash2, AlertTriangle, Loader2, Activity as Activity$1, Workflow, Pause, FlaskConical, XCircle, CheckCircle2, Play, ChevronLeft, ExternalLink, PauseCircle, GitBranch, List, RotateCcw, Wrench, Zap, ArrowRight, ArrowLeft, Database, Eye, MessageSquare, EyeOff, FileText, Info, Clock, Ban, Circle, Code, Webhook } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { useNodesState, useEdgesState, ReactFlow, Background, BackgroundVariant, Controls, MiniMap, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card as Card$1, Flex, Text, Metric, BadgeDelta, SparkAreaChart, Title, AreaChart as AreaChart$1, BarChart as BarChart$1, DonutChart as DonutChart$1, Legend } from '@tremor/react';
import { useVirtualizer } from '@tanstack/react-virtual';

// src/lib/utils.ts
function cn(...inputs) {
  return clsx(inputs);
}
function formatRelativeTime(date) {
  if (!date) return "-";
  const d = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true });
}
function formatDuration(ms) {
  if (ms === null || ms === void 0) return "-";
  const duration = intervalToDuration({ start: 0, end: ms });
  if (ms < 1e3) return `${ms}ms`;
  return formatDuration$1(duration, {
    format: ms >= 36e5 ? ["hours", "minutes"] : ms >= 6e4 ? ["minutes", "seconds"] : ["seconds"],
    delimiter: " "
  });
}
function formatTimestamp(date, formatStr = "PPpp") {
  if (!date) return "-";
  const d = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  return format(d, formatStr);
}
function formatNumber(num) {
  return new Intl.NumberFormat().format(num);
}
function formatCompact(num) {
  return new Intl.NumberFormat("en", { notation: "compact" }).format(num);
}

// src/utils/formatting.ts
var rtfShort = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "short" });
var rtfLong = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "long" });
var TIME_THRESHOLDS = [
  { unit: "year", seconds: 31536e3 },
  { unit: "month", seconds: 2592e3 },
  { unit: "week", seconds: 604800 },
  { unit: "day", seconds: 86400 },
  { unit: "hour", seconds: 3600 },
  { unit: "minute", seconds: 60 },
  { unit: "second", seconds: 1 }
];
function formatTimeAgo(date, style = "short") {
  const d = typeof date === "string" ? new Date(date) : date;
  const diffSeconds = Math.round((d.getTime() - Date.now()) / 1e3);
  const rtf = style === "short" ? rtfShort : rtfLong;
  for (const { unit, seconds } of TIME_THRESHOLDS) {
    if (Math.abs(diffSeconds) >= seconds || unit === "second") {
      const value = Math.round(diffSeconds / seconds);
      return rtf.format(value, unit);
    }
  }
  return rtf.format(0, "second");
}
function formatDuration2(ms) {
  if (ms < 1e3) return `${ms}ms`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)}s`;
  const hours = Math.floor(ms / 36e5);
  const minutes = Math.floor(ms % 36e5 / 6e4);
  const seconds = Math.floor(ms % 6e4 / 1e3);
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
}
function formatCompact2(num) {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(num);
}
function formatPercentage(value, decimals = 1) {
  return new Intl.NumberFormat("en", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

// src/utils/statusMapping.ts
var runStatusColors = {
  pending: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700"
  },
  running: {
    variant: "warning",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    borderClass: "border-amber-200 dark:border-amber-800"
  },
  completed: {
    variant: "success",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
    borderClass: "border-emerald-200 dark:border-emerald-800"
  },
  failed: {
    variant: "danger",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-400",
    borderClass: "border-red-200 dark:border-red-800"
  },
  cancelled: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700"
  }
};
var errorStatusColors = {
  OPEN: {
    variant: "warning",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    borderClass: "border-amber-200 dark:border-amber-800"
  },
  INVESTIGATING: {
    variant: "info",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-400",
    borderClass: "border-blue-200 dark:border-blue-800"
  },
  RESOLVED: {
    variant: "success",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
    borderClass: "border-emerald-200 dark:border-emerald-800"
  },
  IGNORED: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700"
  }
};
var errorSeverityColors = {
  CRITICAL: {
    variant: "danger",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-400",
    borderClass: "border-red-200 dark:border-red-800"
  },
  HIGH: {
    variant: "danger",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-red-200 dark:border-red-800"
  },
  MEDIUM: {
    variant: "warning",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    borderClass: "border-amber-200 dark:border-amber-800"
  },
  LOW: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700"
  }
};
var automationStatusColors = {
  ACTIVE: {
    variant: "success",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
    borderClass: "border-emerald-200 dark:border-emerald-800"
  },
  PAUSED: {
    variant: "warning",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    borderClass: "border-amber-200 dark:border-amber-800"
  },
  INACTIVE: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700"
  },
  ERROR: {
    variant: "danger",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-400",
    borderClass: "border-red-200 dark:border-red-800"
  },
  DELETED: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-500 dark:text-slate-500",
    borderClass: "border-slate-200 dark:border-slate-700"
  }
};
var stepStatusColors = {
  pending: runStatusColors.pending,
  running: runStatusColors.running,
  completed: runStatusColors.completed,
  failed: runStatusColors.failed,
  skipped: {
    variant: "default",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-500 dark:text-slate-500",
    borderClass: "border-slate-200 dark:border-slate-700"
  }
};
function getRunStatusColor(status) {
  return runStatusColors[status] ?? runStatusColors.pending;
}
function getErrorStatusColor(status) {
  return errorStatusColors[status] ?? errorStatusColors.OPEN;
}
function getErrorSeverityColor(severity) {
  return errorSeverityColors[severity] ?? errorSeverityColors.MEDIUM;
}
function getAutomationStatusColor(status) {
  return automationStatusColors[status] ?? automationStatusColors.INACTIVE;
}
function getStepStatusColor(status) {
  return stepStatusColors[status] ?? stepStatusColors.pending;
}
function getRunStatusVariant(status) {
  return getRunStatusColor(status).variant;
}
function getErrorStatusVariant(status) {
  return getErrorStatusColor(status).variant;
}
function getErrorSeverityVariant(severity) {
  return getErrorSeverityColor(severity).variant;
}
function getAutomationStatusVariant(status) {
  return getAutomationStatusColor(status).variant;
}
function getStepStatusVariant(status) {
  return getStepStatusColor(status).variant;
}
function formatStatusLabel(status) {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}
function AutomationUIProvider({
  children,
  ...automationProviderProps
}) {
  return /* @__PURE__ */ jsx(AutomationProvider, { ...automationProviderProps, children });
}
var useAutomationUIStore = create()(
  devtools(
    (set) => ({
      // Initial selection state
      selectedAutomationId: null,
      selectedRunId: null,
      selectedErrorId: null,
      selectedTestCaseId: null,
      // Selection actions
      selectAutomation: (id) => set(
        {
          selectedAutomationId: id,
          // Clear child selections when automation changes
          selectedRunId: null,
          selectedErrorId: null,
          selectedTestCaseId: null
        },
        false,
        "selectAutomation"
      ),
      selectRun: (id) => set({ selectedRunId: id }, false, "selectRun"),
      selectError: (id) => set({ selectedErrorId: id }, false, "selectError"),
      selectTestCase: (id) => set({ selectedTestCaseId: id }, false, "selectTestCase"),
      clearSelections: () => set(
        {
          selectedAutomationId: null,
          selectedRunId: null,
          selectedErrorId: null,
          selectedTestCaseId: null
        },
        false,
        "clearSelections"
      ),
      // Global filters
      globalFilters: {},
      setGlobalFilters: (filters) => set({ globalFilters: filters }, false, "setGlobalFilters"),
      updateGlobalFilters: (updates) => set(
        (state) => ({
          globalFilters: { ...state.globalFilters, ...updates }
        }),
        false,
        "updateGlobalFilters"
      ),
      clearGlobalFilters: () => set({ globalFilters: {} }, false, "clearGlobalFilters"),
      // UI preferences
      density: "comfortable",
      setDensity: (density) => set({ density }, false, "setDensity")
    }),
    { name: "AutomationUI" }
  )
);
function useAutomationSelection() {
  const selectedAutomationId = useAutomationUIStore((state) => state.selectedAutomationId);
  const selectAutomation = useAutomationUIStore((state) => state.selectAutomation);
  const { data: automation, isLoading, error } = useAutomation(selectedAutomationId ?? "", {
    enabled: !!selectedAutomationId
  });
  return {
    /** The currently selected automation ID */
    selectedAutomationId,
    /** The full automation data (fetched when selected) */
    selectedAutomation: automation ?? null,
    /** Whether the automation data is loading */
    isLoading,
    /** Error from fetching automation data */
    error,
    /** Whether an automation is currently selected */
    isSelected: !!selectedAutomationId,
    /** Select an automation by ID (or null to deselect) */
    selectAutomation
  };
}
function useRunSelection() {
  const selectedRunId = useAutomationUIStore((state) => state.selectedRunId);
  const selectRun = useAutomationUIStore((state) => state.selectRun);
  const { data: trace, isLoading, error } = useTrace(selectedRunId ?? "", {
    enabled: !!selectedRunId
  });
  return {
    /** The currently selected run ID */
    selectedRunId,
    /** The full run/trace data with steps (fetched when selected) */
    selectedRun: trace ?? null,
    /** Whether the run data is loading */
    isLoading,
    /** Error from fetching run data */
    error,
    /** Whether a run is currently selected */
    isSelected: !!selectedRunId,
    /** Select a run by ID (or null to deselect) */
    selectRun
  };
}
function useErrorSelection() {
  const selectedErrorId = useAutomationUIStore((state) => state.selectedErrorId);
  const selectError = useAutomationUIStore((state) => state.selectError);
  const { data: errorGroup, isLoading, error } = useError(selectedErrorId ?? "", {
    enabled: !!selectedErrorId
  });
  return {
    /** The currently selected error ID */
    selectedErrorId,
    /** The full error group with occurrences (fetched when selected) */
    selectedError: errorGroup ?? null,
    /** Whether the error data is loading */
    isLoading,
    /** Error from fetching error data */
    error,
    /** Whether an error is currently selected */
    isSelected: !!selectedErrorId,
    /** Select an error by ID (or null to deselect) */
    selectError
  };
}
function useTestCaseSelection() {
  const selectedTestCaseId = useAutomationUIStore((state) => state.selectedTestCaseId);
  const selectTestCase = useAutomationUIStore((state) => state.selectTestCase);
  const { data: testCase, isLoading, error } = useTestCase(selectedTestCaseId ?? "", {
    enabled: !!selectedTestCaseId
  });
  return {
    /** The currently selected test case ID */
    selectedTestCaseId,
    /** The full test case with runs (fetched when selected) */
    selectedTestCase: testCase ?? null,
    /** Whether the test case data is loading */
    isLoading,
    /** Error from fetching test case data */
    error,
    /** Whether a test case is currently selected */
    isSelected: !!selectedTestCaseId,
    /** Select a test case by ID (or null to deselect) */
    selectTestCase
  };
}
var badgeVariants = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors",
  {
    variants: {
      variant: {
        pending: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
        running: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        paused: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
        failed: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
        cancelled: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        timeout: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
      },
      size: {
        sm: "text-sm px-2.5 py-0.5",
        md: "text-base px-3 py-1",
        lg: "text-lg px-4 py-1.5"
      }
    },
    defaultVariants: {
      variant: "pending",
      size: "md"
    }
  }
);
var statusConfig = {
  PENDING: { label: "Pending", icon: /* @__PURE__ */ jsx(PendingIcon, {}), variant: "pending" },
  RUNNING: { label: "Running", icon: /* @__PURE__ */ jsx(RunningIcon, {}), variant: "running" },
  PAUSED: { label: "Awaiting Approval", icon: /* @__PURE__ */ jsx(PausedIcon, {}), variant: "paused" },
  COMPLETED: { label: "Completed", icon: /* @__PURE__ */ jsx(CompletedIcon, {}), variant: "completed" },
  FAILED: { label: "Failed", icon: /* @__PURE__ */ jsx(FailedIcon, {}), variant: "failed" },
  CANCELLED: { label: "Cancelled", icon: /* @__PURE__ */ jsx(CancelledIcon, {}), variant: "cancelled" },
  TIMEOUT: { label: "Timeout", icon: /* @__PURE__ */ jsx(TimeoutIcon, {}), variant: "timeout" }
};
function RunStatusBadge({
  status,
  size,
  showIcon = true,
  showLabel = true,
  className
}) {
  const config = statusConfig[status] ?? statusConfig.PENDING;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        badgeVariants({ variant: config.variant, size }),
        className
      ),
      children: [
        showIcon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: config.icon }),
        showLabel && /* @__PURE__ */ jsx("span", { children: config.label })
      ]
    }
  );
}
function PendingIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", fill: "none", stroke: "currentColor", strokeWidth: "2" }) });
}
function RunningIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3 animate-spin", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", stroke: "currentColor", strokeWidth: "2", strokeDasharray: "24", strokeDashoffset: "8" }) });
}
function PausedIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "currentColor", children: [
    /* @__PURE__ */ jsx("rect", { x: "4", y: "3", width: "3", height: "10", rx: "1" }),
    /* @__PURE__ */ jsx("rect", { x: "9", y: "3", width: "3", height: "10", rx: "1" })
  ] });
}
function CompletedIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M3 8l4 4 6-8", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function FailedIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8", strokeLinecap: "round" }) });
}
function CancelledIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M4 8h8", strokeLinecap: "round" })
  ] });
}
function TimeoutIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 4v4l3 2", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
var badgeVariants2 = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors",
  {
    variants: {
      variant: {
        pending: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
        running: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
        failed: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
        skipped: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
        timeout: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
      },
      size: {
        sm: "text-sm px-2.5 py-0.5",
        md: "text-base px-3 py-1",
        lg: "text-lg px-4 py-1.5"
      }
    },
    defaultVariants: {
      variant: "pending",
      size: "md"
    }
  }
);
var statusConfig2 = {
  PENDING: { label: "Pending", dotColor: "bg-slate-400", variant: "pending" },
  RUNNING: { label: "Running", dotColor: "bg-blue-500", variant: "running" },
  COMPLETED: { label: "Completed", dotColor: "bg-emerald-500", variant: "completed" },
  FAILED: { label: "Failed", dotColor: "bg-red-500", variant: "failed" },
  SKIPPED: { label: "Skipped", dotColor: "bg-slate-300", variant: "skipped" },
  TIMEOUT: { label: "Timeout", dotColor: "bg-orange-500", variant: "timeout" }
};
function StepStatusBadge({
  status,
  size,
  showDot = true,
  showLabel = true,
  className
}) {
  const config = statusConfig2[status] ?? statusConfig2.PENDING;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        badgeVariants2({ variant: config.variant, size }),
        className
      ),
      children: [
        showDot && /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "w-2 h-2 rounded-full flex-shrink-0",
              config.dotColor,
              status === "RUNNING" && "animate-pulse"
            )
          }
        ),
        showLabel && /* @__PURE__ */ jsx("span", { children: config.label })
      ]
    }
  );
}
function getStepStatusColor2(status) {
  return statusConfig2[status]?.dotColor ?? statusConfig2.PENDING.dotColor;
}
var badgeVariants3 = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors",
  {
    variants: {
      variant: {
        active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
        paused: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        inactive: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        error: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
        deleted: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
      },
      size: {
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-2.5 py-0.5",
        lg: "text-base px-3 py-1"
      }
    },
    defaultVariants: {
      variant: "inactive",
      size: "md"
    }
  }
);
var statusConfig3 = {
  ACTIVE: { label: "Active", icon: /* @__PURE__ */ jsx(ActiveIcon, {}), variant: "active" },
  PAUSED: { label: "Paused", icon: /* @__PURE__ */ jsx(PausedIcon2, {}), variant: "paused" },
  INACTIVE: { label: "Inactive", icon: /* @__PURE__ */ jsx(InactiveIcon, {}), variant: "inactive" },
  ERROR: { label: "Error", icon: /* @__PURE__ */ jsx(ErrorIcon, {}), variant: "error" },
  DELETED: { label: "Deleted", icon: /* @__PURE__ */ jsx(DeletedIcon, {}), variant: "deleted" }
};
function AutomationStatusBadge({
  status,
  size,
  showIcon = true,
  showLabel = true,
  className
}) {
  const config = statusConfig3[status] ?? statusConfig3.INACTIVE;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        badgeVariants3({ variant: config.variant, size }),
        className
      ),
      children: [
        showIcon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: config.icon }),
        showLabel && /* @__PURE__ */ jsx("span", { children: config.label })
      ]
    }
  );
}
function ActiveIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M3 8l4 4 6-8", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function PausedIcon2() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "currentColor", children: [
    /* @__PURE__ */ jsx("rect", { x: "4", y: "3", width: "3", height: "10", rx: "1" }),
    /* @__PURE__ */ jsx("rect", { x: "9", y: "3", width: "3", height: "10", rx: "1" })
  ] });
}
function InactiveIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }) });
}
function ErrorIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("path", { d: "M8 4v5M8 11v1", strokeLinecap: "round" }),
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" })
  ] });
}
function DeletedIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8", strokeLinecap: "round" }) });
}
var badgeVariants4 = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors",
  {
    variants: {
      variant: {
        critical: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
        high: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
        medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        low: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
      },
      size: {
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-2.5 py-0.5",
        lg: "text-base px-3 py-1"
      }
    },
    defaultVariants: {
      variant: "medium",
      size: "md"
    }
  }
);
var severityConfig = {
  CRITICAL: { label: "Critical", icon: /* @__PURE__ */ jsx(CriticalIcon, {}), variant: "critical" },
  HIGH: { label: "High", icon: /* @__PURE__ */ jsx(HighIcon, {}), variant: "high" },
  MEDIUM: { label: "Medium", icon: /* @__PURE__ */ jsx(MediumIcon, {}), variant: "medium" },
  LOW: { label: "Low", icon: /* @__PURE__ */ jsx(LowIcon, {}), variant: "low" }
};
function ErrorSeverityBadge({
  severity,
  size,
  showIcon = true,
  showLabel = true,
  className
}) {
  const config = severityConfig[severity] ?? severityConfig.MEDIUM;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        badgeVariants4({ variant: config.variant, size }),
        className
      ),
      children: [
        showIcon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: config.icon }),
        showLabel && /* @__PURE__ */ jsx("span", { children: config.label })
      ]
    }
  );
}
function CriticalIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M8 1l7 14H1L8 1z" }) });
}
function HighIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M8 2l5 10H3L8 2z" }) });
}
function MediumIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "4" }) });
}
function LowIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "4" }) });
}
var badgeVariants5 = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors",
  {
    variants: {
      variant: {
        open: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        investigating: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        resolved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
        ignored: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
      },
      size: {
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-2.5 py-0.5",
        lg: "text-base px-3 py-1"
      }
    },
    defaultVariants: {
      variant: "open",
      size: "md"
    }
  }
);
var statusConfig4 = {
  OPEN: { label: "Open", icon: /* @__PURE__ */ jsx(OpenIcon, {}), variant: "open" },
  INVESTIGATING: { label: "Investigating", icon: /* @__PURE__ */ jsx(InvestigatingIcon, {}), variant: "investigating" },
  RESOLVED: { label: "Resolved", icon: /* @__PURE__ */ jsx(ResolvedIcon, {}), variant: "resolved" },
  IGNORED: { label: "Ignored", icon: /* @__PURE__ */ jsx(IgnoredIcon, {}), variant: "ignored" }
};
function ErrorStatusBadge({
  status,
  size,
  showIcon = true,
  showLabel = true,
  className
}) {
  const config = statusConfig4[status] ?? statusConfig4.OPEN;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        badgeVariants5({ variant: config.variant, size }),
        className
      ),
      children: [
        showIcon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: config.icon }),
        showLabel && /* @__PURE__ */ jsx("span", { children: config.label })
      ]
    }
  );
}
function OpenIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 5v3M8 10v1", strokeLinecap: "round" })
  ] });
}
function InvestigatingIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "7", cy: "7", r: "4" }),
    /* @__PURE__ */ jsx("path", { d: "M10 10l3 3", strokeLinecap: "round" })
  ] });
}
function ResolvedIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M3 8l4 4 6-8", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function IgnoredIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M5 8h6", strokeLinecap: "round" })
  ] });
}
var badgeVariants6 = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors",
  {
    variants: {
      variant: {
        webhook: "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200",
        schedule: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        code_ping: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200",
        manual: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        event: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
      },
      size: {
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-2.5 py-0.5",
        lg: "text-base px-3 py-1"
      }
    },
    defaultVariants: {
      variant: "manual",
      size: "md"
    }
  }
);
var triggerConfig = {
  WEBHOOK: { label: "Webhook", icon: /* @__PURE__ */ jsx(WebhookIcon, {}), variant: "webhook" },
  SCHEDULE: { label: "Schedule", icon: /* @__PURE__ */ jsx(ScheduleIcon, {}), variant: "schedule" },
  CODE_PING: { label: "Code Ping", icon: /* @__PURE__ */ jsx(CodePingIcon, {}), variant: "code_ping" },
  MANUAL: { label: "Manual", icon: /* @__PURE__ */ jsx(ManualIcon, {}), variant: "manual" },
  EVENT: { label: "Event", icon: /* @__PURE__ */ jsx(EventIcon, {}), variant: "event" }
};
function TriggerTypeBadge({
  type,
  size,
  showIcon = true,
  showLabel = true,
  className
}) {
  const config = triggerConfig[type] ?? triggerConfig.MANUAL;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        badgeVariants6({ variant: config.variant, size }),
        className
      ),
      children: [
        showIcon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: config.icon }),
        showLabel && /* @__PURE__ */ jsx("span", { children: config.label })
      ]
    }
  );
}
function WebhookIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("path", { d: "M4 8a4 4 0 108 0", strokeLinecap: "round" }),
    /* @__PURE__ */ jsx("path", { d: "M8 12v2M8 2v2", strokeLinecap: "round" })
  ] });
}
function ScheduleIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 4v4l3 2", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function CodePingIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M5 4l-3 4 3 4M11 4l3 4-3 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function ManualIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M8 3v10M4 7l4-4 4 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function EventIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M3 6l5 5 5-5", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
var indicatorVariants = cva(
  "inline-flex items-center gap-2 font-medium",
  {
    variants: {
      status: {
        healthy: "text-emerald-700 dark:text-emerald-400",
        warning: "text-amber-700 dark:text-amber-400",
        failing: "text-red-700 dark:text-red-400"
      },
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base"
      }
    },
    defaultVariants: {
      status: "healthy",
      size: "md"
    }
  }
);
var dotVariants = cva(
  "rounded-full",
  {
    variants: {
      status: {
        healthy: "bg-emerald-500",
        warning: "bg-amber-500",
        failing: "bg-red-500"
      },
      size: {
        sm: "w-2 h-2",
        md: "w-2.5 h-2.5",
        lg: "w-3 h-3"
      },
      pulse: {
        true: "animate-pulse",
        false: ""
      }
    },
    defaultVariants: {
      status: "healthy",
      size: "md",
      pulse: false
    }
  }
);
var statusLabels = {
  healthy: "Healthy",
  warning: "Warning",
  failing: "Failing"
};
function HealthIndicator({
  status,
  size,
  pulse = false,
  label,
  showLabel = true,
  className
}) {
  const displayLabel = label ?? statusLabels[status];
  return /* @__PURE__ */ jsxs("span", { className: cn(indicatorVariants({ status, size }), className), children: [
    /* @__PURE__ */ jsx("span", { className: cn(dotVariants({ status, size, pulse })) }),
    showLabel && /* @__PURE__ */ jsx("span", { children: displayLabel })
  ] });
}
var sizeClasses = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
  full: "max-w-[95vw] max-h-[95vh]"
};
function Modal({
  isOpen,
  onClose,
  title,
  size = "lg",
  children,
  footer,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className
}) {
  const modalRef = useRef(null);
  const handleKeyDown = useCallback(
    (event) => {
      if (closeOnEscape && event.key === "Escape") {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "fixed inset-0 z-50 flex items-center justify-center p-4",
      onClick: handleBackdropClick,
      children: [
        /* @__PURE__ */ jsx("div", { className: "fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            ref: modalRef,
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": title ? "modal-title" : void 0,
            tabIndex: -1,
            className: cn(
              "relative w-full bg-white dark:bg-slate-900 rounded-xl shadow-2xl",
              "border border-slate-200 dark:border-slate-700",
              "flex flex-col overflow-hidden",
              "animate-in zoom-in-95 fade-in duration-200",
              sizeClasses[size],
              size === "full" ? "h-[90vh]" : "max-h-[90vh]",
              className
            ),
            children: [
              title && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0", children: [
                /* @__PURE__ */ jsx(
                  "h2",
                  {
                    id: "modal-title",
                    className: "text-lg font-semibold text-slate-900 dark:text-slate-100",
                    children: title
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: onClose,
                    className: "p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors",
                    "aria-label": "Close modal",
                    children: /* @__PURE__ */ jsx(CloseIcon, { className: "w-5 h-5" })
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto", children }),
              footer && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex-shrink-0", children: footer })
            ]
          }
        )
      ]
    }
  );
}
function CloseIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8", strokeLinecap: "round" }) });
}
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600",
        secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200 focus-visible:ring-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700",
        ghost: "text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-500 dark:text-slate-300 dark:hover:bg-slate-800",
        danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 dark:bg-red-500 dark:hover:bg-red-600",
        success: "bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-600",
        outline: "border border-slate-300 text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-500 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      },
      size: {
        sm: "text-sm px-3 py-1.5 h-8",
        md: "text-sm px-4 py-2 h-9",
        lg: "text-base px-5 py-2.5 h-11",
        icon: "h-9 w-9 p-0"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var Button = forwardRef(
  ({
    className,
    variant,
    size,
    leftIcon,
    rightIcon,
    loading,
    disabled,
    children,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        disabled: disabled || loading,
        className: cn(buttonVariants({ variant, size }), className),
        ...props,
        children: [
          loading ? /* @__PURE__ */ jsx(LoadingSpinner, {}) : leftIcon ? /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: leftIcon }) : null,
          children,
          !loading && rightIcon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: rightIcon })
        ]
      }
    );
  }
);
Button.displayName = "Button";
function LoadingSpinner() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: "w-4 h-4 animate-spin",
      viewBox: "0 0 24 24",
      fill: "none",
      children: /* @__PURE__ */ jsx(
        "circle",
        {
          cx: "12",
          cy: "12",
          r: "10",
          stroke: "currentColor",
          strokeWidth: "2.5",
          strokeDasharray: "32",
          strokeDashoffset: "8",
          strokeLinecap: "round"
        }
      )
    }
  );
}
var cardVariants = cva(
  "rounded-lg border bg-white dark:bg-slate-950",
  {
    variants: {
      variant: {
        default: "border-slate-200 dark:border-slate-800",
        outline: "border-slate-300 dark:border-slate-700",
        ghost: "border-transparent bg-transparent"
      },
      padding: {
        none: "",
        sm: "p-3",
        md: "p-4",
        lg: "p-6"
      }
    },
    defaultVariants: {
      variant: "default",
      padding: "md"
    }
  }
);
function Card({
  className,
  variant,
  padding,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx("div", { className: cn(cardVariants({ variant, padding }), className), ...props, children });
}
function CardHeader({
  className,
  title,
  description,
  icon,
  action,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("flex items-start justify-between gap-4", className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
          icon && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 text-slate-500 dark:text-slate-400", children: icon }),
          /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
            title && /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-slate-100", children: title }),
            description && /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: description }),
            children
          ] })
        ] }),
        action && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: action })
      ]
    }
  );
}
function CardContent({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("mt-4", className), ...props });
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2",
        className
      ),
      ...props
    }
  );
}
var badgeVariants7 = cva(
  "inline-flex items-center gap-1 font-medium rounded-full transition-colors",
  {
    variants: {
      variant: {
        default: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
        primary: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        success: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
        warning: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        danger: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
        info: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200",
        outline: "border border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-300"
      },
      size: {
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-2.5 py-0.5",
        lg: "text-sm px-3 py-1"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md"
    }
  }
);
function Badge({
  className,
  variant,
  size,
  dot,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs("span", { className: cn(badgeVariants7({ variant, size }), className), ...props, children: [
    dot && /* @__PURE__ */ jsx(
      "span",
      {
        className: cn(
          "w-1.5 h-1.5 rounded-full",
          variant === "default" && "bg-slate-500",
          variant === "primary" && "bg-blue-500",
          variant === "success" && "bg-emerald-500",
          variant === "warning" && "bg-amber-500",
          variant === "danger" && "bg-red-500",
          variant === "info" && "bg-cyan-500",
          variant === "outline" && "bg-slate-500"
        )
      }
    ),
    children
  ] });
}
var SearchInput = forwardRef(
  ({ className, value, onClear, ...props }, ref) => {
    const hasValue = value !== void 0 && value !== "";
    return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" }),
      /* @__PURE__ */ jsx(
        "input",
        {
          ref,
          type: "search",
          value,
          className: cn(
            "w-full h-9 pl-9 pr-8 text-sm rounded-lg border border-slate-300 dark:border-slate-700",
            "bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100",
            "placeholder:text-slate-400 dark:placeholder:text-slate-500",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "[&::-webkit-search-cancel-button]:hidden",
            className
          ),
          ...props
        }
      ),
      hasValue && onClear && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: onClear,
          className: "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300",
          children: /* @__PURE__ */ jsx(X, { className: "w-3 h-3" })
        }
      )
    ] });
  }
);
SearchInput.displayName = "SearchInput";
var selectVariants = cva(
  [
    "appearance-none w-full rounded-lg border text-sm",
    "bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100",
    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "cursor-pointer"
  ],
  {
    variants: {
      size: {
        sm: "h-8 pl-3 pr-8",
        md: "h-9 pl-3 pr-8",
        lg: "h-11 pl-4 pr-10"
      },
      variant: {
        default: "border-slate-300 dark:border-slate-700",
        ghost: "border-transparent bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800"
      }
    },
    defaultVariants: {
      size: "md",
      variant: "default"
    }
  }
);
var Select = forwardRef(
  ({
    className,
    options,
    placeholder,
    size,
    variant,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs(
        "select",
        {
          ref,
          className: cn(selectVariants({ size, variant }), className),
          ...props,
          children: [
            placeholder && /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: placeholder }),
            options.map((option) => /* @__PURE__ */ jsx(
              "option",
              {
                value: option.value,
                disabled: option.disabled,
                children: option.label
              },
              option.value
            ))
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        ChevronDown,
        {
          className: cn(
            "absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none",
            size === "lg" && "right-3"
          )
        }
      )
    ] });
  }
);
Select.displayName = "Select";
function MultiSelect({
  className,
  options,
  value,
  onChange,
  placeholder = "Select items...",
  label,
  maxDisplay = 2,
  searchable = false,
  searchPlaceholder = "Search...",
  disabled = false,
  presets,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);
  const filteredOptions = search ? options.filter(
    (opt) => opt.label.toLowerCase().includes(search.toLowerCase())
  ) : options;
  const handleToggle = (optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };
  const handleSelectAll = () => {
    const allValues = options.filter((opt) => !opt.disabled).map((opt) => opt.value);
    onChange(allValues);
  };
  const handleClearAll = () => {
    onChange([]);
  };
  const handlePreset = (values) => {
    onChange(values);
  };
  const selectedLabels = value.map((v) => options.find((opt) => opt.value === v)?.label).filter(Boolean);
  const displayText = selectedLabels.length === 0 ? placeholder : selectedLabels.length <= maxDisplay ? selectedLabels.join(", ") : `${selectedLabels.slice(0, maxDisplay).join(", ")} +${selectedLabels.length - maxDisplay} more`;
  const groupedOptions = filteredOptions.reduce(
    (acc, opt) => {
      const group = opt.group || "";
      if (!acc[group]) acc[group] = [];
      acc[group].push(opt);
      return acc;
    },
    {}
  );
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative", className), ...props, children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => !disabled && setIsOpen(!isOpen),
        disabled,
        className: cn(
          "flex items-center justify-between gap-2 w-full h-9 px-3 text-sm rounded-lg border",
          "bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100",
          "border-slate-300 dark:border-slate-700",
          "hover:border-slate-400 dark:hover:border-slate-600",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          isOpen && "ring-2 ring-blue-500 border-transparent"
        ),
        children: [
          /* @__PURE__ */ jsxs("span", { className: cn("truncate", value.length === 0 && "text-slate-400"), children: [
            label && value.length > 0 ? `${label}: ` : "",
            displayText
          ] }),
          /* @__PURE__ */ jsx(
            ChevronDown,
            {
              className: cn(
                "w-4 h-4 flex-shrink-0 text-slate-400 transition-transform",
                isOpen && "rotate-180"
              )
            }
          )
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsxs("div", { className: "absolute z-50 w-full mt-1 py-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg", children: [
      searchable && /* @__PURE__ */ jsx("div", { className: "px-2 pb-1", children: /* @__PURE__ */ jsx(
        "input",
        {
          ref: inputRef,
          type: "text",
          value: search,
          onChange: (e) => setSearch(e.target.value),
          placeholder: searchPlaceholder,
          className: cn(
            "w-full h-8 px-2 text-sm rounded border",
            "bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100",
            "border-slate-200 dark:border-slate-700",
            "focus:outline-none focus:ring-1 focus:ring-blue-500"
          )
        }
      ) }),
      presets && presets.length > 0 && /* @__PURE__ */ jsx("div", { className: "px-2 pb-1 flex flex-wrap gap-1", children: presets.map((preset) => /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => handlePreset(preset.values),
          className: "px-2 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700",
          children: preset.label
        },
        preset.label
      )) }),
      /* @__PURE__ */ jsxs("div", { className: "px-2 pb-1 flex items-center justify-between border-b border-slate-100 dark:border-slate-800", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleSelectAll,
            className: "text-xs text-blue-600 dark:text-blue-400 hover:underline",
            children: "Select all"
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleClearAll,
            className: "text-xs text-slate-500 dark:text-slate-400 hover:underline",
            children: "Clear"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "max-h-60 overflow-y-auto", children: [
        Object.entries(groupedOptions).map(([group, groupOptions]) => /* @__PURE__ */ jsxs("div", { children: [
          group && /* @__PURE__ */ jsx("div", { className: "px-3 py-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide", children: group }),
          groupOptions.map((option) => {
            const isSelected = value.includes(option.value);
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                disabled: option.disabled,
                onClick: () => handleToggle(option.value),
                className: cn(
                  "flex items-center gap-2 w-full px-3 py-2 text-sm text-left",
                  "hover:bg-slate-50 dark:hover:bg-slate-800",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  isSelected && "bg-blue-50 dark:bg-blue-950/30"
                ),
                children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: cn(
                        "flex items-center justify-center w-4 h-4 rounded border",
                        isSelected ? "bg-blue-500 border-blue-500 text-white" : "border-slate-300 dark:border-slate-600"
                      ),
                      children: isSelected && /* @__PURE__ */ jsx(Check, { className: "w-3 h-3" })
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      className: cn(
                        "flex-1",
                        isSelected ? "text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-300"
                      ),
                      children: option.label
                    }
                  )
                ]
              },
              option.value
            );
          })
        ] }, group || "default")),
        filteredOptions.length === 0 && /* @__PURE__ */ jsx("div", { className: "px-3 py-4 text-sm text-slate-400 text-center", children: "No options found" })
      ] })
    ] })
  ] });
}
function MultiSelectChips({
  className,
  value,
  options,
  onRemove,
  onClearAll,
  ...props
}) {
  if (value.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-2", className), ...props, children: [
    value.map((v) => {
      const option = options.find((opt) => opt.value === v);
      return /* @__PURE__ */ jsxs(
        "span",
        {
          className: "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
          children: [
            option?.label || v,
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => onRemove(v),
                className: "p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700",
                children: /* @__PURE__ */ jsx(X, { className: "w-3 h-3" })
              }
            )
          ]
        },
        v
      );
    }),
    onClearAll && value.length > 1 && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onClearAll,
        className: "text-xs text-slate-500 dark:text-slate-400 hover:underline",
        children: "Clear all"
      }
    )
  ] });
}
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "animate-pulse rounded-md bg-slate-200 dark:bg-slate-800",
        className
      ),
      ...props
    }
  );
}
function SkeletonText({ className, lines = 1, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), ...props, children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx(
    Skeleton,
    {
      className: cn(
        "h-4",
        i === lines - 1 && lines > 1 ? "w-3/4" : "w-full"
      )
    },
    i
  )) });
}
function SkeletonCard({ className, ...props }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-10 w-10 rounded-full" }),
          /* @__PURE__ */ jsxs("div", { className: "space-y-2 flex-1", children: [
            /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-1/3" }),
            /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-1/2" })
          ] })
        ] }),
        /* @__PURE__ */ jsx(SkeletonText, { lines: 3 })
      ]
    }
  );
}
function SkeletonTable({
  className,
  rows = 5,
  columns = 4,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("div", { className: "bg-slate-50 dark:bg-slate-900 px-4 py-3 flex gap-4", children: Array.from({ length: columns }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-4 flex-1" }, i)) }),
        /* @__PURE__ */ jsx("div", { className: "divide-y divide-slate-200 dark:divide-slate-800", children: Array.from({ length: rows }).map((_, rowIndex) => /* @__PURE__ */ jsx("div", { className: "px-4 py-3 flex gap-4", children: Array.from({ length: columns }).map((_2, colIndex) => /* @__PURE__ */ jsx(Skeleton, { className: "h-4 flex-1" }, colIndex)) }, rowIndex)) })
      ]
    }
  );
}
function EmptyState({
  className,
  icon,
  title,
  description,
  action,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center py-12 px-4 text-center",
        className
      ),
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx("div", { className: "mb-4 text-slate-400 dark:text-slate-500", children: icon }),
        /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-slate-900 dark:text-slate-100", children: title }),
        description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-sm", children: description }),
        action && /* @__PURE__ */ jsx("div", { className: "mt-4", children: action })
      ]
    }
  );
}
function EmptyBoxIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn("w-12 h-12", className),
      viewBox: "0 0 48 48",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M24 6L42 16V32L24 42L6 32V16L24 6Z",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx("path", { d: "M24 22V42", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M6 16L24 26L42 16", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M6 16L24 6L42 16", strokeLinecap: "round", strokeLinejoin: "round" })
      ]
    }
  );
}
function EmptySearchIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn("w-12 h-12", className),
      viewBox: "0 0 48 48",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "20", cy: "20", r: "12", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M29 29L42 42", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M15 20H25", strokeLinecap: "round", strokeLinejoin: "round" })
      ]
    }
  );
}
function EmptyDocumentIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn("w-12 h-12", className),
      viewBox: "0 0 48 48",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M8 6H30L40 16V42H8V6Z",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx("path", { d: "M30 6V16H40", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M16 24H32", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M16 32H26", strokeLinecap: "round", strokeLinejoin: "round" })
      ]
    }
  );
}
function EmptyErrorIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn("w-12 h-12", className),
      viewBox: "0 0 48 48",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "24", cy: "24", r: "18", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M24 14V26", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("circle", { cx: "24", cy: "33", r: "1.5", fill: "currentColor" })
      ]
    }
  );
}
ModuleRegistry.registerModules([AllCommunityModule]);
var lightTheme = themeQuartz.withParams({
  accentColor: "#3b82f6",
  backgroundColor: "#ffffff",
  foregroundColor: "#0f172a",
  borderColor: "#e2e8f0",
  headerBackgroundColor: "#f8fafc",
  headerTextColor: "#64748b",
  oddRowBackgroundColor: "#ffffff",
  rowHoverColor: "#f8fafc",
  selectedRowBackgroundColor: "#eff6ff",
  fontFamily: "system-ui, sans-serif",
  fontSize: 14,
  headerFontSize: 13,
  headerFontWeight: 500,
  cellHorizontalPadding: 16,
  headerHeight: 44,
  rowHeight: 48,
  borderRadius: 8,
  wrapperBorderRadius: 8
});
var darkTheme = themeQuartz.withParams({
  accentColor: "#3b82f6",
  backgroundColor: "#0f172a",
  foregroundColor: "#f1f5f9",
  borderColor: "#334155",
  headerBackgroundColor: "#1e293b",
  headerTextColor: "#94a3b8",
  oddRowBackgroundColor: "#0f172a",
  rowHoverColor: "#1e293b",
  selectedRowBackgroundColor: "#1e3a5f",
  fontFamily: "system-ui, sans-serif",
  fontSize: 14,
  headerFontSize: 13,
  headerFontWeight: 500,
  cellHorizontalPadding: 16,
  headerHeight: 44,
  rowHeight: 48,
  borderRadius: 8,
  wrapperBorderRadius: 8
});
function useDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === "undefined") return false;
    if (document.documentElement.classList.contains("dark")) return true;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          if (document.documentElement.classList.contains("dark")) {
            setIsDark(true);
          } else {
            setIsDark(window.matchMedia("(prefers-color-scheme: dark)").matches);
          }
        }
      });
    });
    observer.observe(document.documentElement, { attributes: true });
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      if (!document.documentElement.classList.contains("dark")) {
        setIsDark(e.matches);
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);
  return isDark;
}
function DataGrid({
  data,
  columns,
  getRowId,
  loading,
  emptyState,
  emptyTitle = "No data",
  emptyDescription = "There is no data to display.",
  onRowClick,
  onRowDoubleClick,
  rowSelection,
  selectedRowKeys,
  onSelectionChange,
  className,
  height = "auto",
  pagination = false,
  pageSize = 20,
  suppressHorizontalScroll = false,
  autoSizeColumns = true,
  gridOptions
}) {
  const gridRef = useRef(null);
  const isDark = useDarkMode();
  const theme = isDark ? darkTheme : lightTheme;
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      suppressMovable: true
    }),
    []
  );
  const rowSelectionConfig = useMemo(() => {
    if (!rowSelection) return void 0;
    return {
      mode: rowSelection === "multiple" ? "multiRow" : "singleRow",
      checkboxes: rowSelection === "multiple",
      headerCheckbox: rowSelection === "multiple"
    };
  }, [rowSelection]);
  const handleGridReady = useCallback(
    (event) => {
      if (autoSizeColumns) {
        event.api.sizeColumnsToFit();
      }
      if (selectedRowKeys?.length && getRowId) {
        event.api.forEachNode((node) => {
          if (node.data && selectedRowKeys.includes(getRowId(node.data))) {
            node.setSelected(true);
          }
        });
      }
    },
    [autoSizeColumns, selectedRowKeys, getRowId]
  );
  const handleRowClicked = useCallback(
    (event) => {
      if (event.data) {
        onRowClick?.(event.data);
      }
    },
    [onRowClick]
  );
  const handleRowDoubleClicked = useCallback(
    (event) => {
      if (event.data) {
        onRowDoubleClick?.(event.data);
      }
    },
    [onRowDoubleClick]
  );
  const handleSelectionChanged = useCallback(() => {
    if (!onSelectionChange) return;
    const api = gridRef.current?.api;
    if (api) {
      const selectedRows = api.getSelectedRows();
      onSelectionChange(selectedRows);
    }
  }, [onSelectionChange]);
  const containerStyle = useMemo(() => {
    if (height === "auto") {
      return { width: "100%" };
    }
    return {
      width: "100%",
      height: typeof height === "number" ? `${height}px` : height
    };
  }, [height]);
  if (loading) {
    return /* @__PURE__ */ jsx(SkeletonTable, { rows: 5, columns: columns.length, className });
  }
  if (data.length === 0) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "rounded-lg border border-slate-200 dark:border-slate-800",
          className
        ),
        children: emptyState || /* @__PURE__ */ jsx(
          EmptyState,
          {
            icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
            title: emptyTitle,
            description: emptyDescription
          }
        )
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("rounded-lg overflow-hidden", className), style: containerStyle, children: /* @__PURE__ */ jsx(
    AgGridReact,
    {
      ref: gridRef,
      theme,
      rowData: data,
      columnDefs: columns,
      defaultColDef,
      getRowId: getRowId ? (params) => getRowId(params.data) : void 0,
      domLayout: height === "auto" ? "autoHeight" : "normal",
      rowSelection: rowSelectionConfig,
      onGridReady: handleGridReady,
      onRowClicked: handleRowClicked,
      onRowDoubleClicked: handleRowDoubleClicked,
      onSelectionChanged: handleSelectionChanged,
      pagination,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: [10, 20, 50, 100],
      suppressHorizontalScroll,
      suppressCellFocus: true,
      animateRows: true,
      ...gridOptions
    }
  ) });
}
var eventCardVariants = cva(
  "relative rounded-lg border transition-all",
  {
    variants: {
      variant: {
        default: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800",
        success: "bg-white dark:bg-slate-900 border-l-4 border-l-emerald-500 border-slate-200 dark:border-slate-800",
        warning: "bg-white dark:bg-slate-900 border-l-4 border-l-amber-500 border-slate-200 dark:border-slate-800",
        danger: "bg-white dark:bg-slate-900 border-l-4 border-l-red-500 border-slate-200 dark:border-slate-800",
        info: "bg-white dark:bg-slate-900 border-l-4 border-l-blue-500 border-slate-200 dark:border-slate-800"
      },
      interactive: {
        true: "cursor-pointer hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700",
        false: ""
      },
      highlighted: {
        true: "animate-highlight-fade ring-2 ring-blue-500/50",
        false: ""
      }
    },
    defaultVariants: {
      variant: "default",
      interactive: false,
      highlighted: false
    }
  }
);
function EventCard({
  className,
  variant,
  interactive,
  highlighted,
  icon,
  title,
  subtitle,
  timestamp,
  summary,
  details,
  expandable = false,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  actions,
  onClick,
  ...props
}) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== void 0;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;
  const handleToggle = () => {
    if (!expandable) return;
    const newExpanded = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };
  const handleClick = (e) => {
    onClick?.(e);
    if (expandable && !e.defaultPrevented) {
      handleToggle();
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        eventCardVariants({ variant, interactive: interactive ?? expandable, highlighted }),
        className
      ),
      onClick: handleClick,
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 p-4", children: [
          icon && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: icon }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium text-slate-900 dark:text-white truncate", children: title }),
              subtitle && /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-500 dark:text-slate-400 truncate", children: subtitle })
            ] }),
            summary && /* @__PURE__ */ jsx("div", { className: "mt-1 text-sm text-slate-600 dark:text-slate-300", children: summary })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-shrink-0", children: [
            timestamp && /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: timestamp }),
            actions && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-1", onClick: (e) => e.stopPropagation(), children: actions }),
            expandable && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  handleToggle();
                },
                className: "p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
                "aria-expanded": isExpanded,
                "aria-label": isExpanded ? "Collapse" : "Expand",
                children: isExpanded ? /* @__PURE__ */ jsx(ChevronDown, { className: "w-4 h-4" }) : /* @__PURE__ */ jsx(ChevronRight, { className: "w-4 h-4" })
              }
            )
          ] })
        ] }),
        expandable && isExpanded && details && /* @__PURE__ */ jsx("div", { className: "px-4 pb-4 pt-0 border-t border-slate-100 dark:border-slate-800 mt-0", children: /* @__PURE__ */ jsx("div", { className: "pt-3 text-sm text-slate-600 dark:text-slate-300", children: details }) })
      ]
    }
  );
}
function EventCardIcon({
  className,
  variant = "default",
  children,
  ...props
}) {
  const variantStyles3 = {
    default: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
    success: "bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400",
    warning: "bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400",
    danger: "bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400",
    info: "bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex h-9 w-9 items-center justify-center rounded-full",
        variantStyles3[variant],
        className
      ),
      ...props,
      children
    }
  );
}
function EventCardMeta({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("grid grid-cols-2 gap-x-4 gap-y-2 text-sm", className),
      ...props,
      children
    }
  );
}
function EventCardMetaItem({
  className,
  label,
  value,
  ...props
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col", className), ...props, children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: label }),
    /* @__PURE__ */ jsx("span", { className: "text-slate-700 dark:text-slate-300", children: value })
  ] });
}
var tabsVariants = cva("", {
  variants: {
    variant: {
      default: "",
      pills: "",
      underline: ""
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var TabsContext = createContext(null);
function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tab components must be used within a Tabs component");
  }
  return context;
}
function Tabs({
  value,
  onChange,
  variant = "default",
  children,
  className
}) {
  const resolvedVariant = variant ?? "default";
  return /* @__PURE__ */ jsx(TabsContext.Provider, { value: { value, onChange, variant: resolvedVariant }, children: /* @__PURE__ */ jsx("div", { className: cn(tabsVariants({ variant: resolvedVariant }), className), children }) });
}
function TabList({ children, className }) {
  const { variant } = useTabs();
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "tablist",
      className: cn(
        "flex",
        variant === "default" && "gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800",
        variant === "pills" && "gap-2",
        variant === "underline" && "gap-4 border-b border-slate-200 dark:border-slate-800",
        className
      ),
      children
    }
  );
}
function Tab({ value: tabValue, children, disabled, className }) {
  const { value, onChange, variant } = useTabs();
  const isActive = value === tabValue;
  return /* @__PURE__ */ jsx(
    "button",
    {
      role: "tab",
      type: "button",
      "aria-selected": isActive,
      disabled,
      onClick: () => onChange(tabValue),
      className: cn(
        "text-sm font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variant === "default" && [
          "px-3 py-1.5 rounded-md",
          isActive ? "bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
        ],
        variant === "pills" && [
          "px-4 py-2 rounded-full",
          isActive ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
        ],
        variant === "underline" && [
          "pb-3 -mb-px border-b-2",
          isActive ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400" : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
        ],
        className
      ),
      children
    }
  );
}
function TabPanel({ value: panelValue, children, className }) {
  const { value } = useTabs();
  if (value !== panelValue) return null;
  return /* @__PURE__ */ jsx("div", { role: "tabpanel", className: cn("mt-4", className), children });
}
function FilterBar({
  children,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  activeFilters = [],
  onRemoveFilter,
  onClearAll,
  onRefresh,
  refreshing,
  className,
  actions
}) {
  const hasActiveFilters = activeFilters.length > 0 || searchValue && searchValue.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [
      onSearchChange && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-64", children: /* @__PURE__ */ jsx(
        SearchInput,
        {
          value: searchValue,
          onChange: (e) => onSearchChange(e.target.value),
          onClear: () => onSearchChange(""),
          placeholder: searchPlaceholder
        }
      ) }),
      children,
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 ml-auto", children: [
        actions,
        onRefresh && /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "icon",
            onClick: onRefresh,
            disabled: refreshing,
            "aria-label": "Refresh",
            children: /* @__PURE__ */ jsx(
              RefreshCw,
              {
                className: cn("w-4 h-4", refreshing && "animate-spin")
              }
            )
          }
        )
      ] })
    ] }),
    hasActiveFilters && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      searchValue && searchValue.length > 0 && /* @__PURE__ */ jsx(
        FilterChipButton,
        {
          label: `Search: ${searchValue}`,
          onRemove: () => onSearchChange?.("")
        }
      ),
      activeFilters.map((filter) => /* @__PURE__ */ jsx(
        FilterChipButton,
        {
          label: `${filter.label}: ${filter.value}`,
          onRemove: () => onRemoveFilter?.(filter.id)
        },
        filter.id
      )),
      onClearAll && /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "sm",
          onClick: onClearAll,
          className: "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300",
          children: "Clear all"
        }
      )
    ] })
  ] });
}
function FilterChipButton({ label, onRemove }) {
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300", children: [
    label,
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onRemove,
        className: "p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700",
        children: /* @__PURE__ */ jsx(X, { className: "w-3 h-3" })
      }
    )
  ] });
}
function LiveBadge({
  className,
  count,
  label = "{count} new events",
  animated = true,
  visible = true,
  onClick,
  ...props
}) {
  if (!visible || count <= 0) return null;
  const displayLabel = label.replace("{count}", String(count));
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick,
      className: cn(
        "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full",
        "bg-blue-500 text-white shadow-lg",
        "hover:bg-blue-600 active:bg-blue-700",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        "transition-all duration-200",
        animated && "animate-bounce-subtle",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(ArrowDown, { className: "w-4 h-4" }),
        /* @__PURE__ */ jsx("span", { children: displayLabel })
      ]
    }
  );
}
function LiveBadgeContainer({
  className,
  position = "top",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "sticky z-10 flex justify-center",
        position === "top" ? "top-0 pt-2" : "bottom-0 pb-2",
        className
      ),
      ...props,
      children
    }
  );
}
var defaultLabels = {
  connected: "Connected",
  disconnected: "Disconnected",
  connecting: "Connecting...",
  error: "Connection error"
};
function ConnectionStatus({
  className,
  state,
  labels,
  showIcon = true,
  showLabel = true,
  size = "md",
  onRetry,
  ...props
}) {
  const mergedLabels = { ...defaultLabels, ...labels };
  const stateConfig = {
    connected: {
      dotClass: "bg-emerald-500",
      animated: true,
      icon: Wifi,
      textClass: "text-slate-500 dark:text-slate-400"
    },
    disconnected: {
      dotClass: "bg-slate-400",
      animated: false,
      icon: WifiOff,
      textClass: "text-slate-400 dark:text-slate-500"
    },
    connecting: {
      dotClass: "bg-amber-500",
      animated: true,
      icon: RefreshCw,
      textClass: "text-slate-500 dark:text-slate-400"
    },
    error: {
      dotClass: "bg-red-500",
      animated: false,
      icon: AlertCircle,
      textClass: "text-red-500 dark:text-red-400"
    }
  };
  const config = stateConfig[state];
  config.icon;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("flex items-center gap-2", className),
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx("div", { className: "relative flex items-center justify-center", children: /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "rounded-full",
              size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2",
              config.dotClass,
              config.animated && "animate-pulse"
            )
          }
        ) }),
        showLabel && /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              config.textClass,
              size === "sm" ? "text-xs" : "text-xs"
            ),
            children: mergedLabels[state]
          }
        ),
        state === "error" && onRetry && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onRetry,
            className: "text-xs text-blue-600 dark:text-blue-400 hover:underline",
            children: "Retry"
          }
        )
      ]
    }
  );
}
function ConnectionIndicator({
  className,
  connected,
  connectedLabel = "Connected",
  disconnectedLabel = "Disconnected",
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("flex items-center", className),
      title: connected ? connectedLabel : disconnectedLabel,
      "aria-label": connected ? connectedLabel : disconnectedLabel,
      role: "status",
      ...props,
      children: /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "h-2 w-2 rounded-full",
            connected ? "bg-emerald-500 animate-pulse" : "bg-slate-400"
          )
        }
      )
    }
  );
}
var jsonViewerVariants = cva(
  "font-mono text-sm rounded-lg border overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700",
        minimal: "bg-transparent border-transparent"
      },
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "sm"
    }
  }
);
function getValueType(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}
var TYPE_COLORS = {
  string: "text-green-600 dark:text-green-400",
  number: "text-blue-600 dark:text-blue-400",
  boolean: "text-purple-600 dark:text-purple-400",
  null: "text-slate-400 dark:text-slate-500",
  array: "text-slate-700 dark:text-slate-300",
  object: "text-slate-700 dark:text-slate-300"
};
function JsonNode({ keyName, value, depth, collapsedDepth, isLast }) {
  const [isCollapsed, setIsCollapsed] = useState(depth >= collapsedDepth);
  const valueType = getValueType(value);
  const isExpandable = valueType === "object" || valueType === "array";
  const indent = depth * 16;
  const renderValue = useCallback(() => {
    switch (valueType) {
      case "string":
        return /* @__PURE__ */ jsxs("span", { className: TYPE_COLORS.string, children: [
          '"',
          String(value),
          '"'
        ] });
      case "number":
        return /* @__PURE__ */ jsx("span", { className: TYPE_COLORS.number, children: String(value) });
      case "boolean":
        return /* @__PURE__ */ jsx("span", { className: TYPE_COLORS.boolean, children: String(value) });
      case "null":
        return /* @__PURE__ */ jsx("span", { className: TYPE_COLORS.null, children: "null" });
      default:
        return null;
    }
  }, [value, valueType]);
  if (!isExpandable) {
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center py-0.5", style: { paddingLeft: indent }, children: [
      /* @__PURE__ */ jsx("span", { className: "w-4" }),
      keyName !== void 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("span", { className: "text-slate-600 dark:text-slate-400", children: [
          '"',
          keyName,
          '"'
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-slate-500 dark:text-slate-500", children: ": " })
      ] }),
      renderValue(),
      !isLast && /* @__PURE__ */ jsx("span", { className: "text-slate-500", children: "," })
    ] });
  }
  const entries = valueType === "array" ? value.map((v, i) => [i, v]) : Object.entries(value);
  const isEmpty = entries.length === 0;
  const openBracket = valueType === "array" ? "[" : "{";
  const closeBracket = valueType === "array" ? "]" : "}";
  if (isEmpty) {
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center py-0.5", style: { paddingLeft: indent }, children: [
      /* @__PURE__ */ jsx("span", { className: "w-4" }),
      keyName !== void 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("span", { className: "text-slate-600 dark:text-slate-400", children: [
          '"',
          keyName,
          '"'
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-slate-500 dark:text-slate-500", children: ": " })
      ] }),
      /* @__PURE__ */ jsxs("span", { className: TYPE_COLORS[valueType], children: [
        openBracket,
        closeBracket
      ] }),
      !isLast && /* @__PURE__ */ jsx("span", { className: "text-slate-500", children: "," })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex items-center py-0.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800",
        style: { paddingLeft: indent },
        onClick: () => setIsCollapsed(!isCollapsed),
        children: [
          /* @__PURE__ */ jsx("span", { className: "w-4 flex-shrink-0 flex items-center justify-center text-slate-400", children: isCollapsed ? /* @__PURE__ */ jsx(ChevronRight, { className: "h-3 w-3" }) : /* @__PURE__ */ jsx(ChevronDown, { className: "h-3 w-3" }) }),
          keyName !== void 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs("span", { className: "text-slate-600 dark:text-slate-400", children: [
              '"',
              keyName,
              '"'
            ] }),
            /* @__PURE__ */ jsx("span", { className: "text-slate-500 dark:text-slate-500", children: ": " })
          ] }),
          /* @__PURE__ */ jsx("span", { className: TYPE_COLORS[valueType], children: openBracket }),
          isCollapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("span", { className: "text-slate-400 mx-1", children: valueType === "array" ? `${entries.length} items` : `${entries.length} keys` }),
            /* @__PURE__ */ jsx("span", { className: TYPE_COLORS[valueType], children: closeBracket }),
            !isLast && /* @__PURE__ */ jsx("span", { className: "text-slate-500", children: "," })
          ] })
        ]
      }
    ),
    !isCollapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
      entries.map(([k, v], i) => /* @__PURE__ */ jsx(
        JsonNode,
        {
          keyName: valueType === "object" ? String(k) : void 0,
          value: v,
          depth: depth + 1,
          collapsedDepth,
          isLast: i === entries.length - 1
        },
        String(k)
      )),
      /* @__PURE__ */ jsxs("div", { className: "py-0.5", style: { paddingLeft: indent }, children: [
        /* @__PURE__ */ jsx("span", { className: "w-4 inline-block" }),
        /* @__PURE__ */ jsx("span", { className: TYPE_COLORS[valueType], children: closeBracket }),
        !isLast && /* @__PURE__ */ jsx("span", { className: "text-slate-500", children: "," })
      ] })
    ] })
  ] });
}
function JsonViewer({
  className,
  variant,
  size,
  data,
  collapsed = false,
  collapsedDepth = collapsed ? 0 : 100,
  showCopyButton = true,
  maxHeight,
  lineNumbers = false,
  ...props
}) {
  const [copied, setCopied] = useState(false);
  const formattedJson = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(formattedJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  }, [formattedJson]);
  const style = useMemo(() => {
    if (maxHeight) {
      return {
        maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
        overflow: "auto"
      };
    }
    return void 0;
  }, [maxHeight]);
  return /* @__PURE__ */ jsxs("div", { className: cn(jsonViewerVariants({ variant, size }), className), ...props, children: [
    showCopyButton && /* @__PURE__ */ jsx("div", { className: "flex justify-end border-b border-slate-200 dark:border-slate-700 px-2 py-1 bg-slate-100 dark:bg-slate-800", children: /* @__PURE__ */ jsx(
      "button",
      {
        onClick: handleCopy,
        className: "flex items-center gap-1 px-2 py-0.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors",
        children: copied ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Check, { className: "h-3 w-3" }),
          "Copied"
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Copy, { className: "h-3 w-3" }),
          "Copy"
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "p-2", style, children: /* @__PURE__ */ jsx(
      JsonNode,
      {
        value: data,
        depth: 0,
        collapsedDepth,
        isLast: true
      }
    ) })
  ] });
}
var codeBlockVariants = cva(
  "font-mono text-sm rounded-lg border overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700",
        dark: "bg-slate-900 border-slate-700 text-slate-100",
        minimal: "bg-transparent border-transparent"
      },
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "sm"
    }
  }
);
function detectLanguage(code) {
  const trimmed = code.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      JSON.parse(trimmed);
      return "json";
    } catch {
    }
  }
  if (trimmed.startsWith("<?xml") || trimmed.startsWith("<")) {
    return "xml";
  }
  if (trimmed.includes("function") || trimmed.includes("const ") || trimmed.includes("let ")) {
    return "javascript";
  }
  if (trimmed.includes("interface ") || trimmed.includes("type ")) {
    return "typescript";
  }
  return "text";
}
function highlightCode(code, language) {
  if (language === "json") {
    return code.replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, '<span class="text-green-600 dark:text-green-400">"$1"</span>').replace(/\b(\d+\.?\d*)\b/g, '<span class="text-blue-600 dark:text-blue-400">$1</span>').replace(/\b(true|false|null)\b/g, '<span class="text-purple-600 dark:text-purple-400">$1</span>');
  }
  if (language === "xml") {
    return code.replace(/(&lt;\/?)(\w+)/g, '$1<span class="text-blue-600 dark:text-blue-400">$2</span>').replace(/<(\/?)(\w+)/g, '&lt;$1<span class="text-blue-600 dark:text-blue-400">$2</span>').replace(/(\s)(\w+)(=)/g, '$1<span class="text-purple-600 dark:text-purple-400">$2</span>$3').replace(/"([^"]*)"/g, '<span class="text-green-600 dark:text-green-400">"$1"</span>');
  }
  if (language === "javascript" || language === "typescript") {
    return code.replace(
      /\b(const|let|var|function|return|if|else|for|while|class|export|import|from|async|await|try|catch|throw|new|typeof|instanceof)\b/g,
      '<span class="text-purple-600 dark:text-purple-400">$1</span>'
    ).replace(/'([^'\\]*(\\.[^'\\]*)*)'/g, `<span class="text-green-600 dark:text-green-400">'$1'</span>`).replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, '<span class="text-green-600 dark:text-green-400">"$1"</span>').replace(/`([^`\\]*(\\.[^`\\]*)*)`/g, '<span class="text-green-600 dark:text-green-400">`$1`</span>').replace(/\b(\d+\.?\d*)\b/g, '<span class="text-blue-600 dark:text-blue-400">$1</span>').replace(/(\/\/.*$)/gm, '<span class="text-slate-400 dark:text-slate-500">$1</span>').replace(
      /\b(interface|type|string|number|boolean|void|any|unknown|never)\b/g,
      '<span class="text-cyan-600 dark:text-cyan-400">$1</span>'
    );
  }
  return code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function CodeBlock({
  className,
  variant,
  size,
  code,
  language: providedLanguage,
  showCopyButton = true,
  showLineNumbers = false,
  maxHeight,
  wrapLines = false,
  title,
  ...props
}) {
  const [copied, setCopied] = useState(false);
  const language = providedLanguage || detectLanguage(code);
  const lines = useMemo(() => code.split("\n"), [code]);
  const highlightedLines = useMemo(() => {
    return lines.map((line) => highlightCode(line, language));
  }, [lines, language]);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  }, [code]);
  const contentStyle = useMemo(() => {
    const style = {};
    if (maxHeight) {
      style.maxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
      style.overflow = "auto";
    }
    if (!wrapLines) {
      style.overflowX = "auto";
    }
    return style;
  }, [maxHeight, wrapLines]);
  return /* @__PURE__ */ jsxs("div", { className: cn(codeBlockVariants({ variant, size }), className), ...props, children: [
    (showCopyButton || title) && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1.5 bg-slate-100 dark:bg-slate-800", children: [
      title ? /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-slate-600 dark:text-slate-400", children: title }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-500 uppercase", children: language }),
      showCopyButton && /* @__PURE__ */ jsx(
        "button",
        {
          onClick: handleCopy,
          className: "flex items-center gap-1 px-2 py-0.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors",
          children: copied ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Check, { className: "h-3 w-3" }),
            "Copied"
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Copy, { className: "h-3 w-3" }),
            "Copy"
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "p-3", style: contentStyle, children: /* @__PURE__ */ jsx("pre", { className: cn("m-0", wrapLines && "whitespace-pre-wrap break-words"), children: showLineNumbers ? /* @__PURE__ */ jsx("table", { className: "border-collapse w-full", children: /* @__PURE__ */ jsx("tbody", { children: highlightedLines.map((line, i) => /* @__PURE__ */ jsxs("tr", { className: "hover:bg-slate-100 dark:hover:bg-slate-800", children: [
      /* @__PURE__ */ jsx("td", { className: "select-none pr-4 text-right text-slate-400 dark:text-slate-600 w-8", children: i + 1 }),
      /* @__PURE__ */ jsx("td", { className: "text-slate-700 dark:text-slate-300", children: /* @__PURE__ */ jsx("code", { dangerouslySetInnerHTML: { __html: line || "&nbsp;" } }) })
    ] }, i)) }) }) : /* @__PURE__ */ jsx(
      "code",
      {
        className: "text-slate-700 dark:text-slate-300",
        dangerouslySetInnerHTML: { __html: highlightedLines.join("\n") || "&nbsp;" }
      }
    ) }) })
  ] });
}
var timelineItemVariants = cva(
  "relative flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer",
  {
    variants: {
      selected: {
        true: "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800",
        false: "hover:bg-slate-50 dark:hover:bg-slate-800/50"
      }
    },
    defaultVariants: {
      selected: false
    }
  }
);
var nodeTypeBadgeVariants = cva(
  "text-sm px-2 py-0.5 rounded font-semibold",
  {
    variants: {
      type: {
        trigger: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        action: "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200",
        condition: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        wait: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200",
        ai: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200",
        agent: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200",
        http: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200",
        crypto: "bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200",
        subflow: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
      }
    },
    defaultVariants: {
      type: "action"
    }
  }
);
function getStepDisplayName(step) {
  const isGenericId = /^(trigger|action|condition|wait|ai|agent|http|crypto|subflow)-\d+$/.test(step.nodeName || "");
  if (step.nodeName && !isGenericId) {
    return step.nodeName;
  }
  if (step.metadata?.toolName) {
    return step.metadata.toolName;
  }
  return step.nodeId;
}
function TraceTimeline({
  trace,
  selectedStepId,
  onStepSelect,
  showMetadata = true,
  className
}) {
  const { steps } = trace;
  if (!steps || steps.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("p-6 text-center", className), children: /* @__PURE__ */ jsx("p", { className: "text-slate-700 dark:text-slate-300", children: "No steps recorded for this run." }) });
  }
  const stepsWithDepth = useMemo(() => {
    let currentDepth = 0;
    let inBranch = false;
    return steps.map((step, index) => {
      const prevStep = index > 0 ? steps[index - 1] : null;
      if (prevStep?.nodeType === "condition") {
        currentDepth = 1;
        inBranch = true;
      }
      if (inBranch && step.nodeType === "condition") {
        currentDepth = 0;
        inBranch = false;
      }
      const depth = currentDepth;
      return { step, depth };
    });
  }, [steps]);
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-1 max-w-2xl mx-auto", className), children: stepsWithDepth.map(({ step, depth }, index) => /* @__PURE__ */ jsx(
    TimelineItem,
    {
      step,
      isFirst: index === 0,
      isLast: index === steps.length - 1,
      isSelected: step.id === selectedStepId,
      showMetadata,
      onClick: () => onStepSelect?.(step),
      depth
    },
    step.id
  )) });
}
function TimelineItem({
  step,
  isFirst,
  isLast,
  isSelected,
  showMetadata,
  onClick,
  depth = 0
}) {
  const dotColor = getStepStatusColor2(step.status);
  const displayName = getStepDisplayName(step);
  const isConditionBranch = depth > 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        timelineItemVariants({ selected: isSelected }),
        // Add left margin for branch indentation
        isConditionBranch && "ml-6 border-l-2 border-amber-300 dark:border-amber-600"
      ),
      onClick,
      role: "button",
      tabIndex: 0,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center pt-1", children: [
          !isFirst && /* @__PURE__ */ jsx("div", { className: "w-0.5 h-3 bg-slate-200 dark:bg-slate-700 -mt-4 mb-1" }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "w-3 h-3 rounded-full flex-shrink-0",
                dotColor,
                step.status === "RUNNING" && "animate-pulse"
              )
            }
          ),
          !isLast && /* @__PURE__ */ jsx("div", { className: "w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 min-h-[20px] mt-1" })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
            /* @__PURE__ */ jsx("span", { className: "font-semibold text-base text-slate-900 dark:text-slate-100", children: displayName }),
            /* @__PURE__ */ jsx("span", { className: nodeTypeBadgeVariants({ type: step.nodeType }), children: step.nodeType }),
            /* @__PURE__ */ jsx(StepStatusBadge, { status: step.status, size: "sm" })
          ] }),
          showMetadata && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 mt-1.5 text-sm text-slate-700 dark:text-slate-300", children: [
            step.duration !== void 0 && step.duration !== null && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
              /* @__PURE__ */ jsx(ClockIcon, { className: "w-4 h-4" }),
              formatDuration(step.duration)
            ] }),
            step.metadata?.tokenUsage?.total && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
              /* @__PURE__ */ jsx(TokenIcon, { className: "w-4 h-4" }),
              formatCompact(step.metadata.tokenUsage.total),
              " tokens"
            ] }),
            step.metadata?.model && /* @__PURE__ */ jsx("span", { className: "font-mono text-slate-600 dark:text-slate-400", children: step.metadata.model }),
            step.startedAt && /* @__PURE__ */ jsx("span", { children: formatRelativeTime(step.startedAt) })
          ] }),
          step.error && /* @__PURE__ */ jsx("div", { className: "mt-2 text-sm text-red-700 dark:text-red-300 truncate font-semibold", children: step.error.message })
        ] })
      ]
    }
  );
}
function ClockIcon({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 4v4l2.5 1.5", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function TokenIcon({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("rect", { x: "3", y: "3", width: "10", height: "10", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M6 6h4M6 8h4M6 10h2", strokeLinecap: "round" })
  ] });
}
var sectionVariants = cva(
  "mt-2 p-3 border rounded-lg overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50",
        success: "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30",
        error: "border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/30",
        info: "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var nodeTypeBadgeVariants2 = cva(
  "text-sm px-2.5 py-0.5 rounded font-semibold",
  {
    variants: {
      type: {
        trigger: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
        action: "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200",
        condition: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
        wait: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200",
        ai: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200",
        agent: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200",
        http: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200",
        crypto: "bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200",
        subflow: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
      }
    },
    defaultVariants: {
      type: "action"
    }
  }
);
function StepDetails({ step, className }) {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    /* @__PURE__ */ jsx("div", { className: "flex items-start justify-between gap-4", children: /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-slate-900 dark:text-slate-100 truncate", children: step.nodeName || step.nodeId }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-1.5 flex-wrap", children: [
        /* @__PURE__ */ jsx("span", { className: nodeTypeBadgeVariants2({ type: step.nodeType }), children: step.nodeType }),
        /* @__PURE__ */ jsx(StepStatusBadge, { status: step.status, size: "sm" })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(MetadataGrid, { step }),
    step.resolvedInput && Object.keys(step.resolvedInput).length > 0 && /* @__PURE__ */ jsx(CollapsibleSection, { title: "Tool Input", icon: /* @__PURE__ */ jsx(ToolInputIcon, {}), defaultOpen: true, variant: "info", children: /* @__PURE__ */ jsx(JsonViewer2, { data: step.resolvedInput }) }),
    step.toolOutput && Object.keys(step.toolOutput).length > 0 && /* @__PURE__ */ jsx(
      CollapsibleSection,
      {
        title: "Tool Output",
        icon: /* @__PURE__ */ jsx(ToolOutputIcon, {}),
        defaultOpen: true,
        variant: step.status === "COMPLETED" ? "success" : "default",
        children: /* @__PURE__ */ jsx(JsonViewer2, { data: step.toolOutput })
      }
    ),
    step.input && Object.keys(step.input).length > 0 && /* @__PURE__ */ jsx(
      CollapsibleSection,
      {
        title: "Execution Context",
        icon: /* @__PURE__ */ jsx(InputIcon, {}),
        defaultOpen: !step.resolvedInput,
        children: /* @__PURE__ */ jsx(JsonViewer2, { data: step.input })
      }
    ),
    step.output && Object.keys(step.output).length > 0 && !step.toolOutput && /* @__PURE__ */ jsx(
      CollapsibleSection,
      {
        title: "Output",
        icon: /* @__PURE__ */ jsx(OutputIcon, {}),
        defaultOpen: true,
        variant: step.status === "COMPLETED" ? "success" : "default",
        children: /* @__PURE__ */ jsx(JsonViewer2, { data: step.output })
      }
    ),
    step.error && /* @__PURE__ */ jsx(
      CollapsibleSection,
      {
        title: "Error",
        icon: /* @__PURE__ */ jsx(ErrorIcon2, {}),
        defaultOpen: true,
        variant: "error",
        children: /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
          /* @__PURE__ */ jsx("p", { className: "text-base text-red-700 dark:text-red-300 font-semibold", children: step.error.message }),
          step.error.stack && /* @__PURE__ */ jsx("pre", { className: "text-sm text-slate-800 dark:text-slate-200 overflow-x-auto whitespace-pre-wrap font-mono bg-red-100/50 dark:bg-red-900/20 p-3 rounded leading-relaxed", children: step.error.stack }),
          step.error.details && Object.keys(step.error.details).length > 0 && /* @__PURE__ */ jsxs("div", { className: "pt-2 border-t border-red-200 dark:border-red-800", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-red-700 dark:text-red-300 mb-2", children: "Additional Details" }),
            /* @__PURE__ */ jsx(JsonViewer2, { data: step.error.details })
          ] })
        ] })
      }
    )
  ] });
}
function MetadataGrid({ step }) {
  const items = [
    { label: "Duration", value: formatDuration(step.duration) },
    { label: "Started", value: step.startedAt ? formatTimestamp(step.startedAt, "PPp") : null },
    { label: "Completed", value: step.completedAt ? formatTimestamp(step.completedAt, "PPp") : null }
  ];
  if (step.metadata?.tokenUsage) {
    items.push(
      { label: "Prompt Tokens", value: formatNumber(step.metadata.tokenUsage.prompt) },
      { label: "Completion Tokens", value: formatNumber(step.metadata.tokenUsage.completion) },
      { label: "Total Tokens", value: formatNumber(step.metadata.tokenUsage.total) }
    );
  }
  if (step.metadata?.model) {
    items.push({ label: "Model", value: step.metadata.model });
  }
  if (step.metadata?.attemptNumber) {
    const attemptText = step.metadata.retryCount ? `${step.metadata.attemptNumber} of ${step.metadata.retryCount + 1}` : String(step.metadata.attemptNumber);
    items.push({ label: "Attempt", value: attemptText });
  }
  const validItems = items.filter((item) => item.value !== null && item.value !== "-");
  if (validItems.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-800", children: validItems.map((item) => /* @__PURE__ */ jsx(MetadataItem, { label: item.label, value: item.value }, item.label)) });
}
function MetadataItem({ label, value }) {
  return /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
    /* @__PURE__ */ jsx("dt", { className: "text-sm font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide", children: label }),
    /* @__PURE__ */ jsx("dd", { className: "text-base font-medium text-slate-900 dark:text-slate-100 mt-0.5 truncate", children: value })
  ] });
}
function CollapsibleSection({
  title,
  icon,
  children,
  defaultOpen = false,
  variant
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => setIsOpen(!isOpen),
        className: "flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-100 transition-colors w-full text-left",
        children: [
          /* @__PURE__ */ jsx(ChevronIcon, { className: cn("w-5 h-5 transition-transform", isOpen && "rotate-90") }),
          icon && /* @__PURE__ */ jsx("span", { className: "w-5 h-5", children: icon }),
          title
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsx("div", { className: sectionVariants({ variant }), children })
  ] });
}
function JsonViewer2({ data }) {
  const jsonString = JSON.stringify(data, null, 2);
  const lines = jsonString.split("\n");
  const isLarge = lines.length > 20;
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsx("pre", { className: cn(
      "text-sm font-mono text-slate-900 dark:text-slate-100 overflow-x-auto leading-relaxed",
      isLarge && "max-h-80 overflow-y-auto"
    ), children: /* @__PURE__ */ jsx("code", { children: jsonString }) }),
    /* @__PURE__ */ jsx(CopyButton, { text: jsonString })
  ] });
}
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  };
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: handleCopy,
      className: "absolute top-2 right-2 p-1.5 rounded bg-slate-200/80 dark:bg-slate-700/80 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors",
      title: "Copy to clipboard",
      children: copied ? /* @__PURE__ */ jsx(CheckIcon, { className: "w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" }) : /* @__PURE__ */ jsx(CopyIcon, { className: "w-3.5 h-3.5 text-slate-600 dark:text-slate-400" })
    }
  );
}
function ChevronIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M6 4l4 4-4 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function InputIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-full h-full", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx("path", { d: "M2 8h12M10 4l4 4-4 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function OutputIcon() {
  return /* @__PURE__ */ jsx("svg", { className: "w-full h-full", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx("path", { d: "M14 8H2M6 4L2 8l4 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function ToolInputIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-full h-full", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("rect", { x: "2", y: "3", width: "12", height: "10", rx: "1" }),
    /* @__PURE__ */ jsx("path", { d: "M5 7h6M5 10h4", strokeLinecap: "round" })
  ] });
}
function ToolOutputIcon() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-full h-full", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("rect", { x: "2", y: "3", width: "12", height: "10", rx: "1" }),
    /* @__PURE__ */ jsx("path", { d: "M5 8l2 2 4-4", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function ErrorIcon2() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-full h-full", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 5v4M8 11v.5", strokeLinecap: "round" })
  ] });
}
function CopyIcon({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("rect", { x: "5", y: "5", width: "8", height: "8", rx: "1" }),
    /* @__PURE__ */ jsx("path", { d: "M3 11V3h8", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function CheckIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M3 8l4 4 6-8", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
var nodeTypeConfig = {
  trigger: {
    bg: "bg-blue-50",
    border: "border-blue-400",
    text: "text-blue-700",
    darkBg: "dark:bg-blue-950",
    darkBorder: "dark:border-blue-600",
    darkText: "dark:text-blue-300",
    icon: "\u26A1"
  },
  action: {
    bg: "bg-purple-50",
    border: "border-purple-400",
    text: "text-purple-700",
    darkBg: "dark:bg-purple-950",
    darkBorder: "dark:border-purple-600",
    darkText: "dark:text-purple-300",
    icon: "\u25B6"
  },
  condition: {
    bg: "bg-amber-50",
    border: "border-amber-400",
    text: "text-amber-700",
    darkBg: "dark:bg-amber-950",
    darkBorder: "dark:border-amber-600",
    darkText: "dark:text-amber-300",
    icon: "\u25C7"
  },
  wait: {
    bg: "bg-orange-50",
    border: "border-orange-400",
    text: "text-orange-700",
    darkBg: "dark:bg-orange-950",
    darkBorder: "dark:border-orange-600",
    darkText: "dark:text-orange-300",
    icon: "\u23F8"
  },
  ai: {
    bg: "bg-cyan-50",
    border: "border-cyan-400",
    text: "text-cyan-700",
    darkBg: "dark:bg-cyan-950",
    darkBorder: "dark:border-cyan-600",
    darkText: "dark:text-cyan-300",
    icon: "\u{1F916}"
  },
  agent: {
    bg: "bg-indigo-50",
    border: "border-indigo-400",
    text: "text-indigo-700",
    darkBg: "dark:bg-indigo-950",
    darkBorder: "dark:border-indigo-600",
    darkText: "dark:text-indigo-300",
    icon: "\u{1F9E0}"
  },
  http: {
    bg: "bg-green-50",
    border: "border-green-400",
    text: "text-green-700",
    darkBg: "dark:bg-green-950",
    darkBorder: "dark:border-green-600",
    darkText: "dark:text-green-300",
    icon: "\u{1F310}"
  },
  crypto: {
    bg: "bg-pink-50",
    border: "border-pink-400",
    text: "text-pink-700",
    darkBg: "dark:bg-pink-950",
    darkBorder: "dark:border-pink-600",
    darkText: "dark:text-pink-300",
    icon: "\u{1F510}"
  },
  subflow: {
    bg: "bg-slate-50",
    border: "border-slate-400",
    text: "text-slate-700",
    darkBg: "dark:bg-slate-900",
    darkBorder: "dark:border-slate-600",
    darkText: "dark:text-slate-300",
    icon: "\u{1F4E6}"
  }
};
var nodeVariants = cva(
  "px-4 py-3 rounded-lg border-2 min-w-[160px] shadow-sm transition-all",
  {
    variants: {
      selected: {
        true: "ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900",
        false: ""
      }
    },
    defaultVariants: {
      selected: false
    }
  }
);
function AutomationNode({ data, selected }) {
  const nodeType = data.nodeType ?? "action";
  const config = nodeTypeConfig[nodeType] ?? nodeTypeConfig.action;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        nodeVariants({ selected }),
        config.bg,
        config.border,
        config.darkBg,
        config.darkBorder
      ),
      children: [
        /* @__PURE__ */ jsx(
          Handle,
          {
            type: "target",
            position: Position.Top,
            className: "!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800"
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5", children: [
          /* @__PURE__ */ jsx("span", { className: "text-lg flex-shrink-0", role: "img", "aria-label": nodeType, children: config.icon }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
            /* @__PURE__ */ jsx("div", { className: cn("font-medium text-sm truncate", config.text, config.darkText), children: data.name }),
            /* @__PURE__ */ jsx("div", { className: "text-xs text-slate-500 dark:text-slate-400 capitalize", children: nodeType })
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          Handle,
          {
            type: "source",
            position: Position.Bottom,
            className: "!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800"
          }
        )
      ]
    }
  );
}
var nodeTypes = {
  automation: AutomationNode
};
function FlowView({
  flow,
  selectedNodeId,
  onNodeSelect,
  className,
  interactive = true,
  showMiniMap = true,
  showControls = true
}) {
  const initialNodes = useMemo(() => {
    return flow.nodes.map((node, index) => ({
      id: node.id,
      type: "automation",
      position: node.position ?? { x: 100, y: index * 120 },
      data: {
        nodeId: node.id,
        nodeType: node.type,
        name: node.name,
        config: node.config
      },
      selected: node.id === selectedNodeId
    }));
  }, [flow.nodes, selectedNodeId]);
  const initialEdges = useMemo(() => {
    return flow.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      animated: false,
      style: { stroke: "#94a3b8", strokeWidth: 2 },
      labelStyle: { fill: "#64748b", fontWeight: 500, fontSize: 12 },
      labelBgStyle: { fill: "#f8fafc", fillOpacity: 0.9 }
    }));
  }, [flow.edges]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);
  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);
  const onNodeClick = useCallback(
    (_, node) => {
      const flowNode = flow.nodes.find((n) => n.id === node.id);
      if (flowNode && onNodeSelect) {
        onNodeSelect(flowNode);
      }
    },
    [flow.nodes, onNodeSelect]
  );
  return /* @__PURE__ */ jsx("div", { className: cn(
    "w-full h-[500px] border rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900",
    "border-slate-200 dark:border-slate-700",
    className
  ), children: /* @__PURE__ */ jsxs(
    ReactFlow,
    {
      nodes,
      edges,
      onNodesChange: interactive ? onNodesChange : void 0,
      onEdgesChange: interactive ? onEdgesChange : void 0,
      onNodeClick,
      nodeTypes,
      fitView: true,
      fitViewOptions: { padding: 0.2 },
      proOptions: { hideAttribution: true },
      className: "bg-slate-50 dark:bg-slate-900",
      children: [
        /* @__PURE__ */ jsx(
          Background,
          {
            variant: BackgroundVariant.Dots,
            gap: 16,
            size: 1,
            color: "#cbd5e1",
            className: "dark:!bg-slate-900 [&>*]:dark:!stroke-slate-700"
          }
        ),
        showControls && /* @__PURE__ */ jsx(
          Controls,
          {
            className: "!bg-white dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700 !shadow-md [&>button]:!bg-white dark:[&>button]:!bg-slate-800 [&>button]:!border-slate-200 dark:[&>button]:!border-slate-700 [&>button]:hover:!bg-slate-100 dark:[&>button]:hover:!bg-slate-700 [&>button>svg]:!fill-slate-600 dark:[&>button>svg]:!fill-slate-300"
          }
        ),
        showMiniMap && /* @__PURE__ */ jsx(
          MiniMap,
          {
            nodeColor: (node) => {
              const nodeData = node.data;
              const nodeType = nodeData.nodeType ?? "action";
              const isDark = typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
              switch (nodeType) {
                case "trigger":
                  return isDark ? "#3b82f6" : "#60a5fa";
                case "action":
                  return isDark ? "#8b5cf6" : "#a78bfa";
                case "condition":
                  return isDark ? "#f59e0b" : "#fbbf24";
                case "wait":
                  return isDark ? "#f97316" : "#fb923c";
                case "ai":
                  return isDark ? "#06b6d4" : "#22d3ee";
                case "agent":
                  return isDark ? "#6366f1" : "#818cf8";
                case "http":
                  return isDark ? "#22c55e" : "#4ade80";
                case "crypto":
                  return isDark ? "#ec4899" : "#f472b6";
                case "subflow":
                  return isDark ? "#64748b" : "#94a3b8";
                default:
                  return isDark ? "#64748b" : "#94a3b8";
              }
            },
            maskColor: "rgba(0, 0, 0, 0.1)",
            className: "!bg-white dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700"
          }
        )
      ]
    }
  ) });
}
function parseAutomationToFlow(nodes, connections) {
  return {
    nodes: nodes.map((node, index) => ({
      id: node.id,
      type: node.type ?? "action",
      name: node.name ?? node.id,
      config: node.config,
      position: { x: 100, y: index * 120 }
    })),
    edges: connections.map((conn, index) => ({
      id: `edge-${index}`,
      source: conn.source,
      target: conn.target,
      condition: conn.condition,
      label: conn.condition
    }))
  };
}
var nodeTypeConfig2 = {
  trigger: {
    bg: "bg-blue-50",
    border: "border-blue-400",
    text: "text-blue-700",
    darkBg: "dark:bg-blue-950",
    darkBorder: "dark:border-blue-600",
    darkText: "dark:text-blue-300",
    icon: "\u26A1"
  },
  action: {
    bg: "bg-purple-50",
    border: "border-purple-400",
    text: "text-purple-700",
    darkBg: "dark:bg-purple-950",
    darkBorder: "dark:border-purple-600",
    darkText: "dark:text-purple-300",
    icon: "\u25B6"
  },
  condition: {
    bg: "bg-amber-50",
    border: "border-amber-400",
    text: "text-amber-700",
    darkBg: "dark:bg-amber-950",
    darkBorder: "dark:border-amber-600",
    darkText: "dark:text-amber-300",
    icon: "\u25C7"
  },
  wait: {
    bg: "bg-orange-50",
    border: "border-orange-400",
    text: "text-orange-700",
    darkBg: "dark:bg-orange-950",
    darkBorder: "dark:border-orange-600",
    darkText: "dark:text-orange-300",
    icon: "\u23F8"
  },
  ai: {
    bg: "bg-cyan-50",
    border: "border-cyan-400",
    text: "text-cyan-700",
    darkBg: "dark:bg-cyan-950",
    darkBorder: "dark:border-cyan-600",
    darkText: "dark:text-cyan-300",
    icon: "\u{1F916}"
  },
  agent: {
    bg: "bg-indigo-50",
    border: "border-indigo-400",
    text: "text-indigo-700",
    darkBg: "dark:bg-indigo-950",
    darkBorder: "dark:border-indigo-600",
    darkText: "dark:text-indigo-300",
    icon: "\u{1F9E0}"
  },
  http: {
    bg: "bg-green-50",
    border: "border-green-400",
    text: "text-green-700",
    darkBg: "dark:bg-green-950",
    darkBorder: "dark:border-green-600",
    darkText: "dark:text-green-300",
    icon: "\u{1F310}"
  },
  crypto: {
    bg: "bg-pink-50",
    border: "border-pink-400",
    text: "text-pink-700",
    darkBg: "dark:bg-pink-950",
    darkBorder: "dark:border-pink-600",
    darkText: "dark:text-pink-300",
    icon: "\u{1F510}"
  },
  subflow: {
    bg: "bg-slate-50",
    border: "border-slate-400",
    text: "text-slate-700",
    darkBg: "dark:bg-slate-900",
    darkBorder: "dark:border-slate-600",
    darkText: "dark:text-slate-300",
    icon: "\u{1F4E6}"
  }
};
var executionStatusConfig = {
  pending: {
    ring: "",
    glow: "",
    badge: "bg-slate-200 dark:bg-slate-700",
    badgeText: "text-slate-600 dark:text-slate-300"
  },
  running: {
    ring: "ring-4 ring-blue-400/50 dark:ring-blue-500/50",
    glow: "shadow-[0_0_20px_rgba(59,130,246,0.5)]",
    badge: "bg-blue-500",
    badgeText: "text-white"
  },
  completed: {
    ring: "ring-2 ring-emerald-400 dark:ring-emerald-500",
    glow: "",
    badge: "bg-emerald-500",
    badgeText: "text-white"
  },
  failed: {
    ring: "ring-2 ring-red-400 dark:ring-red-500",
    glow: "shadow-[0_0_15px_rgba(239,68,68,0.4)]",
    badge: "bg-red-500",
    badgeText: "text-white"
  },
  skipped: {
    ring: "",
    glow: "",
    badge: "bg-slate-300 dark:bg-slate-600",
    badgeText: "text-slate-500 dark:text-slate-400"
  }
};
var nodeVariants2 = cva(
  "px-4 py-3 rounded-lg border-2 min-w-[160px] shadow-sm transition-all relative",
  {
    variants: {
      selected: {
        true: "ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900",
        false: ""
      }
    },
    defaultVariants: {
      selected: false
    }
  }
);
function getNodeDisplayName(name, nodeId, config) {
  const isGenericId = /^(trigger|action|condition|wait|ai|agent|http|crypto|subflow)-\d+$/.test(name);
  if (!isGenericId) {
    return name;
  }
  const toolId = config?.tool;
  if (toolId) {
    const toolName = toolId.includes(":") ? toolId.split(":")[1] : toolId;
    return toolName || name;
  }
  return nodeId;
}
function ExecutionNode({ data, selected }) {
  const nodeType = data.nodeType ?? "action";
  const config = nodeTypeConfig2[nodeType] ?? nodeTypeConfig2.action;
  const execStatus = data.executionStatus ?? "pending";
  const execConfig = executionStatusConfig[execStatus];
  const isRunning = execStatus === "running";
  const displayName = getNodeDisplayName(data.name, data.nodeId, data.config);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        nodeVariants2({ selected }),
        config.bg,
        config.border,
        config.darkBg,
        config.darkBorder,
        execConfig.ring,
        execConfig.glow,
        isRunning && "animate-pulse"
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: "absolute -top-2 -right-2 flex items-center gap-1", children: /* @__PURE__ */ jsxs(
          "span",
          {
            className: cn(
              "w-6 h-6 rounded-full flex items-center justify-center shadow-md",
              execConfig.badge,
              execConfig.badgeText
            ),
            children: [
              execStatus === "completed" && /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2.5", children: /* @__PURE__ */ jsx("path", { d: "M3 8l4 4 6-8", strokeLinecap: "round", strokeLinejoin: "round" }) }),
              execStatus === "failed" && /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2.5", children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8", strokeLinecap: "round", strokeLinejoin: "round" }) }),
              execStatus === "running" && /* @__PURE__ */ jsxs("svg", { className: "w-3.5 h-3.5 animate-spin", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
                /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", strokeOpacity: "0.3" }),
                /* @__PURE__ */ jsx("path", { d: "M8 2a6 6 0 0 1 6 6", strokeLinecap: "round" })
              ] }),
              execStatus === "skipped" && /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2.5", children: /* @__PURE__ */ jsx("path", { d: "M4 8h8", strokeLinecap: "round" }) }),
              execStatus === "pending" && /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "4" }) })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(
          Handle,
          {
            type: "target",
            position: Position.Top,
            className: "!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800"
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5", children: [
          /* @__PURE__ */ jsx("span", { className: "text-lg flex-shrink-0", role: "img", "aria-label": nodeType, children: config.icon }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("div", { className: cn("font-medium text-sm truncate", config.text, config.darkText), children: displayName }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400 capitalize", children: nodeType }),
              data.durationMs !== void 0 && execStatus !== "pending" && execStatus !== "running" && /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500 font-mono", children: formatDuration(data.durationMs) })
            ] })
          ] })
        ] }),
        data.error && /* @__PURE__ */ jsx("div", { className: "mt-2 text-xs text-red-600 dark:text-red-400 truncate font-medium", children: data.error.message }),
        /* @__PURE__ */ jsx(
          Handle,
          {
            type: "source",
            position: Position.Bottom,
            className: "!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800"
          }
        )
      ]
    }
  );
}
var nodeTypes2 = {
  execution: ExecutionNode
};
function FlowViewWithExecution({
  flow,
  executionSteps = [],
  selectedNodeId,
  onNodeClick,
  className,
  interactive = false,
  showMiniMap = false,
  showControls = true
}) {
  const executionMap = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    executionSteps.forEach((step) => map.set(step.nodeId, step));
    return map;
  }, [executionSteps]);
  const initialNodes = useMemo(() => {
    return flow.nodes.map((node, index) => {
      const execStep = executionMap.get(node.id);
      return {
        id: node.id,
        type: "execution",
        position: node.position ?? { x: 100, y: index * 120 },
        data: {
          nodeId: node.id,
          nodeType: node.type,
          name: node.name,
          config: node.config,
          executionStatus: execStep?.status,
          durationMs: execStep?.durationMs,
          error: execStep?.error
        },
        selected: node.id === selectedNodeId
      };
    });
  }, [flow.nodes, executionMap, selectedNodeId]);
  const completedNodeIds = useMemo(() => {
    const completed = /* @__PURE__ */ new Set();
    executionSteps.forEach((step) => {
      if (step.status === "completed" || step.status === "failed" || step.status === "skipped") {
        completed.add(step.nodeId);
      }
    });
    return completed;
  }, [executionSteps]);
  useMemo(() => {
    const running = executionSteps.find((s) => s.status === "running");
    return running?.nodeId;
  }, [executionSteps]);
  const initialEdges = useMemo(() => {
    return flow.edges.map((edge) => {
      const sourceCompleted = completedNodeIds.has(edge.source);
      const isActive = sourceCompleted && !completedNodeIds.has(edge.target);
      const isCompleted = sourceCompleted && completedNodeIds.has(edge.target);
      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        animated: isActive,
        style: {
          stroke: isCompleted ? "#10b981" : isActive ? "#3b82f6" : "#94a3b8",
          // slate-400
          strokeWidth: isActive || isCompleted ? 3 : 2
        },
        labelStyle: { fill: "#64748b", fontWeight: 500, fontSize: 12 },
        labelBgStyle: { fill: "#f8fafc", fillOpacity: 0.9 }
      };
    });
  }, [flow.edges, completedNodeIds]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);
  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);
  const handleNodeClick = useCallback(
    (_, node) => {
      const flowNode = flow.nodes.find((n) => n.id === node.id);
      if (flowNode && onNodeClick) {
        onNodeClick(flowNode);
      }
    },
    [flow.nodes, onNodeClick]
  );
  return /* @__PURE__ */ jsx("div", { className: cn(
    "w-full h-full min-h-[400px] border rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900",
    "border-slate-200 dark:border-slate-700",
    className
  ), children: /* @__PURE__ */ jsxs(
    ReactFlow,
    {
      nodes,
      edges,
      onNodesChange: interactive ? onNodesChange : void 0,
      onEdgesChange: interactive ? onEdgesChange : void 0,
      onNodeClick: handleNodeClick,
      nodeTypes: nodeTypes2,
      fitView: true,
      fitViewOptions: { padding: 0.3 },
      proOptions: { hideAttribution: true },
      className: "bg-slate-50 dark:bg-slate-900",
      nodesDraggable: interactive,
      nodesConnectable: false,
      elementsSelectable: true,
      children: [
        /* @__PURE__ */ jsx(
          Background,
          {
            variant: BackgroundVariant.Dots,
            gap: 16,
            size: 1,
            color: "#cbd5e1",
            className: "dark:!bg-slate-900 [&>*]:dark:!stroke-slate-700"
          }
        ),
        showControls && /* @__PURE__ */ jsx(
          Controls,
          {
            className: "!bg-white dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700 !shadow-md [&>button]:!bg-white dark:[&>button]:!bg-slate-800 [&>button]:!border-slate-200 dark:[&>button]:!border-slate-700 [&>button]:hover:!bg-slate-100 dark:[&>button]:hover:!bg-slate-700 [&>button>svg]:!fill-slate-600 dark:[&>button>svg]:!fill-slate-300"
          }
        ),
        showMiniMap && /* @__PURE__ */ jsx(
          MiniMap,
          {
            nodeColor: (node) => {
              const nodeData = node.data;
              const execStatus = nodeData.executionStatus;
              if (execStatus === "completed") return "#10b981";
              if (execStatus === "failed") return "#ef4444";
              if (execStatus === "running") return "#3b82f6";
              if (execStatus === "skipped") return "#94a3b8";
              return "#e2e8f0";
            },
            maskColor: "rgba(0, 0, 0, 0.1)",
            className: "!bg-white dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700"
          }
        )
      ]
    }
  ) });
}
var editorVariants = cva(
  "font-mono text-sm border rounded-lg transition-colors",
  {
    variants: {
      hasError: {
        true: "border-red-300 dark:border-red-700 focus-within:border-red-500 dark:focus-within:border-red-500",
        false: "border-slate-200 dark:border-slate-700 focus-within:border-blue-500 dark:focus-within:border-blue-500"
      },
      readOnly: {
        true: "bg-slate-50 dark:bg-slate-900/50",
        false: "bg-white dark:bg-slate-900"
      }
    },
    defaultVariants: {
      hasError: false,
      readOnly: false
    }
  }
);
function PayloadEditor({
  value,
  onChange,
  schema,
  validationErrors = [],
  readOnly = false,
  placeholder = '{\n  "key": "value"\n}',
  className
}) {
  const [rawText, setRawText] = useState(() => JSON.stringify(value, null, 2));
  const [parseError, setParseError] = useState(null);
  const handleChange = useCallback(
    (text) => {
      setRawText(text);
      try {
        const parsed = JSON.parse(text);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          setParseError(null);
          onChange(parsed);
        } else {
          setParseError("Value must be a JSON object");
        }
      } catch (error) {
        setParseError(error instanceof Error ? error.message : "Invalid JSON");
      }
    },
    [onChange]
  );
  const formatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(rawText);
      setRawText(JSON.stringify(parsed, null, 2));
      setParseError(null);
    } catch {
    }
  }, [rawText]);
  const hasError = parseError !== null || validationErrors.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("label", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300", children: "Payload" }),
      !readOnly && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: formatJson,
          className: "text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium",
          children: "Format JSON"
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: editorVariants({ hasError, readOnly }), children: /* @__PURE__ */ jsx(
      "textarea",
      {
        value: rawText,
        onChange: (e) => handleChange(e.target.value),
        placeholder,
        readOnly,
        rows: 12,
        className: cn(
          "w-full p-4 bg-transparent resize-y outline-none",
          "text-slate-900 dark:text-slate-100",
          "placeholder:text-slate-400 dark:placeholder:text-slate-500",
          readOnly && "cursor-not-allowed"
        ),
        spellCheck: false
      }
    ) }),
    parseError && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-sm text-red-600 dark:text-red-400", children: [
      /* @__PURE__ */ jsx(ErrorIcon3, { className: "w-4 h-4 flex-shrink-0" }),
      /* @__PURE__ */ jsx("span", { className: "font-medium", children: parseError })
    ] }),
    validationErrors.length > 0 && /* @__PURE__ */ jsx("div", { className: "space-y-1", children: validationErrors.map((error, index) => /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex items-start gap-2 text-sm text-red-600 dark:text-red-400",
        children: [
          /* @__PURE__ */ jsx(ErrorIcon3, { className: "w-4 h-4 flex-shrink-0 mt-0.5" }),
          /* @__PURE__ */ jsxs("span", { children: [
            /* @__PURE__ */ jsx("span", { className: "font-mono font-medium", children: error.path }),
            ": ",
            error.message
          ] })
        ]
      },
      index
    )) }),
    schema && /* @__PURE__ */ jsx("div", { className: "text-sm text-slate-500 dark:text-slate-400", children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "underline hover:text-slate-700 dark:hover:text-slate-300",
        onClick: () => {
          console.log("Schema:", schema);
        },
        children: "View schema"
      }
    ) })
  ] });
}
function ErrorIcon3({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 5v4M8 11v.5", strokeLinecap: "round" })
  ] });
}
var resultBannerVariants = cva(
  "p-4 rounded-lg border font-semibold",
  {
    variants: {
      status: {
        passed: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200",
        failed: "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200",
        running: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
      }
    }
  }
);
var stepStatusVariants = cva(
  "w-3 h-3 rounded-full flex-shrink-0",
  {
    variants: {
      status: {
        pending: "bg-slate-300 dark:bg-slate-600",
        running: "bg-blue-500 animate-pulse",
        completed: "bg-emerald-500",
        failed: "bg-red-500",
        skipped: "bg-slate-400 dark:bg-slate-500"
      }
    }
  }
);
function getStepDisplayName2(name) {
  const isGenericId = /^(trigger|action|condition|wait|ai|agent|http|crypto|subflow)-\d+$/.test(name);
  if (isGenericId) {
    const match = name.match(/^(\w+)-\d+$/);
    if (match && match[1]) {
      const type = match[1];
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
  }
  return name;
}
function ExecutionResultView({
  status,
  durationMs,
  steps,
  onStepClick,
  className
}) {
  const statusLabel = {
    passed: "Test Passed",
    failed: "Test Failed",
    running: "Running..."
  }[status];
  const statusIcon = {
    passed: /* @__PURE__ */ jsx(CheckIcon2, { className: "w-5 h-5" }),
    failed: /* @__PURE__ */ jsx(XIcon, { className: "w-5 h-5" }),
    running: /* @__PURE__ */ jsx(SpinnerIcon, { className: "w-5 h-5 animate-spin" })
  }[status];
  const completedSteps = steps.filter((s) => s.status === "completed").length;
  const failedSteps = steps.filter((s) => s.status === "failed").length;
  const totalSteps = steps.length;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4 max-w-2xl mx-auto", className), children: [
    /* @__PURE__ */ jsx("div", { className: resultBannerVariants({ status }), children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        statusIcon,
        /* @__PURE__ */ jsx("span", { className: "text-lg", children: statusLabel })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4 text-sm font-medium", children: [
        durationMs !== void 0 && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(ClockIcon2, { className: "w-4 h-4" }),
          formatDuration(durationMs)
        ] }),
        /* @__PURE__ */ jsxs("span", { children: [
          completedSteps,
          "/",
          totalSteps,
          " steps",
          failedSteps > 0 && /* @__PURE__ */ jsxs("span", { className: "text-red-600 dark:text-red-400 ml-1", children: [
            "(",
            failedSteps,
            " failed)"
          ] })
        ] })
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs("div", { className: "border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden", children: [
      /* @__PURE__ */ jsx("div", { className: "bg-slate-50 dark:bg-slate-800 px-4 py-2 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300", children: "Execution Steps" }) }),
      /* @__PURE__ */ jsx("div", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: steps.map((step, index) => /* @__PURE__ */ jsx(
        StepRow,
        {
          step,
          index,
          onClick: onStepClick ? () => onStepClick(step.id) : void 0
        },
        step.id
      )) })
    ] })
  ] });
}
function StepRow({ step, index, onClick }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center gap-3 px-4 py-3",
        onClick && "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors",
        step.status === "failed" && "bg-red-50/50 dark:bg-red-950/20"
      ),
      onClick,
      role: onClick ? "button" : void 0,
      tabIndex: onClick ? 0 : void 0,
      onKeyDown: (e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      },
      children: [
        /* @__PURE__ */ jsxs("span", { className: "text-sm font-mono text-slate-400 dark:text-slate-500 w-6", children: [
          index + 1,
          "."
        ] }),
        /* @__PURE__ */ jsx("div", { className: stepStatusVariants({ status: step.status }) }),
        /* @__PURE__ */ jsx("span", { className: "flex-1 text-base font-medium text-slate-900 dark:text-slate-100", children: getStepDisplayName2(step.name) }),
        step.durationMs !== void 0 && /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-500 dark:text-slate-400 font-mono", children: formatDuration(step.durationMs) }),
        /* @__PURE__ */ jsx(StatusBadge, { status: step.status })
      ]
    }
  );
}
function StatusBadge({ status }) {
  const config = {
    pending: { label: "Pending", className: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
    running: { label: "Running", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" },
    completed: { label: "Completed", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" },
    failed: { label: "Failed", className: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300" },
    skipped: { label: "Skipped", className: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" }
  }[status];
  return /* @__PURE__ */ jsx("span", { className: cn("px-2 py-0.5 text-sm font-semibold rounded", config.className), children: config.label });
}
function CheckIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M3 8l4 4 6-8", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function XIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8", strokeLinecap: "round" }) });
}
function SpinnerIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", strokeDasharray: "24", strokeDashoffset: "8" }) });
}
function ClockIcon2({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 4v4l2.5 1.5", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
var statusIndicatorVariants = cva(
  "w-2.5 h-2.5 rounded-full flex-shrink-0",
  {
    variants: {
      status: {
        passed: "bg-emerald-500",
        failed: "bg-red-500",
        running: "bg-blue-500 animate-pulse",
        never_run: "bg-slate-300 dark:bg-slate-600"
      }
    },
    defaultVariants: {
      status: "never_run"
    }
  }
);
var statusBadgeVariants = cva(
  "px-2 py-0.5 text-sm font-semibold rounded",
  {
    variants: {
      status: {
        passed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
        failed: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
        running: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
        never_run: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
      }
    },
    defaultVariants: {
      status: "never_run"
    }
  }
);
function TestCaseCard({
  id,
  name,
  description,
  lastRunStatus = "never_run",
  lastRunAt,
  onRun,
  onEdit,
  onDelete,
  isRunning = false,
  className
}) {
  const statusLabels2 = {
    passed: "Passed",
    failed: "Failed",
    running: "Running",
    never_run: "Not run yet"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "border border-slate-200 dark:border-slate-700 rounded-lg p-4",
        "bg-white dark:bg-slate-900",
        "hover:border-slate-300 dark:hover:border-slate-600 transition-colors",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 min-w-0", children: [
            /* @__PURE__ */ jsx("div", { className: statusIndicatorVariants({ status: lastRunStatus }) }),
            /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-slate-100 truncate", children: name })
          ] }),
          /* @__PURE__ */ jsx("span", { className: statusBadgeVariants({ status: lastRunStatus }), children: statusLabels2[lastRunStatus] })
        ] }),
        description && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2", children: description }),
        /* @__PURE__ */ jsxs("div", { className: "mt-3 flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400", children: [
          lastRunAt && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
            /* @__PURE__ */ jsx(ClockIcon3, { className: "w-4 h-4" }),
            "Last run ",
            formatRelativeTime(lastRunAt)
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "font-mono text-slate-400 dark:text-slate-500", children: [
            "#",
            id.slice(0, 8)
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "mt-4 flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: onRun,
              disabled: isRunning,
              className: cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors",
                "bg-blue-600 text-white hover:bg-blue-700",
                "disabled:bg-blue-400 disabled:cursor-not-allowed"
              ),
              children: isRunning ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(SpinnerIcon2, { className: "w-4 h-4 animate-spin" }),
                "Running..."
              ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(PlayIcon, { className: "w-4 h-4" }),
                "Run"
              ] })
            }
          ),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: onEdit,
              disabled: isRunning,
              className: cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors",
                "border border-slate-200 dark:border-slate-700",
                "text-slate-700 dark:text-slate-300",
                "hover:bg-slate-50 dark:hover:bg-slate-800",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              ),
              children: [
                /* @__PURE__ */ jsx(EditIcon, { className: "w-4 h-4" }),
                "Edit"
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: onDelete,
              disabled: isRunning,
              className: cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ml-auto",
                "text-red-600 dark:text-red-400",
                "hover:bg-red-50 dark:hover:bg-red-950/30",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              ),
              children: [
                /* @__PURE__ */ jsx(TrashIcon, { className: "w-4 h-4" }),
                "Delete"
              ]
            }
          )
        ] })
      ]
    }
  );
}
function PlayIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M4 3l9 5-9 5V3z" }) });
}
function SpinnerIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", strokeDasharray: "24", strokeDashoffset: "8" }) });
}
function EditIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx("path", { d: "M10.5 3.5l2 2M4 10l-1 3 3-1 7-7-2-2-7 7z", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function TrashIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx("path", { d: "M3 4h10M6 4V3h4v1M5 4v9h6V4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function ClockIcon3({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 4v4l2.5 1.5", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function TestRunnerPanel({
  triggers,
  selectedTriggerId,
  onTriggerSelect,
  onRunTest,
  executionState,
  executionSteps = [],
  executionError,
  executionDurationMs,
  validationErrors = [],
  onStepClick,
  className
}) {
  const [payload, setPayload] = useState({});
  const selectedTrigger = triggers.find((t) => t.id === selectedTriggerId);
  const isRunning = executionState === "running";
  const hasResults = executionState === "completed" || executionState === "failed";
  const handleRunTest = () => {
    onRunTest(payload);
  };
  const resultStatus = executionState === "completed" ? "passed" : executionState === "failed" ? "failed" : "running";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col h-full", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex-shrink-0 border-b border-slate-200 dark:border-slate-700 p-4", children: [
      /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold text-slate-900 dark:text-slate-100", children: "Test Runner" }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-600 dark:text-slate-400 mt-1", children: "Run a test execution to validate your automation" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 overflow-y-auto p-4 space-y-6", children: [
      /* @__PURE__ */ jsxs("section", { children: [
        /* @__PURE__ */ jsx("label", { className: "block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2", children: "Select Trigger" }),
        /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2", children: triggers.map((trigger) => /* @__PURE__ */ jsx(
          TriggerCard,
          {
            trigger,
            isSelected: trigger.id === selectedTriggerId,
            onSelect: () => onTriggerSelect(trigger.id),
            disabled: isRunning
          },
          trigger.id
        )) }),
        triggers.length === 0 && /* @__PURE__ */ jsx("div", { className: "text-sm text-slate-500 dark:text-slate-400 p-4 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-center", children: "No triggers available for this automation" })
      ] }),
      selectedTrigger && /* @__PURE__ */ jsx("section", { children: /* @__PURE__ */ jsx(
        PayloadEditor,
        {
          value: payload,
          onChange: setPayload,
          validationErrors,
          readOnly: isRunning
        }
      ) }),
      selectedTrigger && /* @__PURE__ */ jsxs("section", { className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleRunTest,
            disabled: isRunning || !selectedTriggerId,
            className: cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-base font-semibold transition-colors",
              "bg-blue-600 text-white hover:bg-blue-700",
              "disabled:bg-blue-400 disabled:cursor-not-allowed"
            ),
            children: isRunning ? /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(SpinnerIcon3, { className: "w-5 h-5 animate-spin" }),
              "Running Test..."
            ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(PlayIcon2, { className: "w-5 h-5" }),
              "Run Test"
            ] })
          }
        ),
        hasResults && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => setPayload({}),
            className: "text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100",
            children: "Clear & Reset"
          }
        )
      ] }),
      executionError && /* @__PURE__ */ jsx("section", { className: "p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
        /* @__PURE__ */ jsx(ErrorIcon4, { className: "w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { className: "font-semibold text-red-800 dark:text-red-200", children: executionError.message }),
          executionError.details && /* @__PURE__ */ jsx("pre", { className: "mt-2 text-sm text-red-700 dark:text-red-300 overflow-x-auto", children: JSON.stringify(executionError.details, null, 2) })
        ] })
      ] }) }),
      (hasResults || isRunning) && executionSteps.length > 0 && /* @__PURE__ */ jsx("section", { children: /* @__PURE__ */ jsx(
        ExecutionResultView,
        {
          status: resultStatus,
          durationMs: executionDurationMs,
          steps: executionSteps,
          onStepClick
        }
      ) })
    ] })
  ] });
}
function TriggerCard({ trigger, isSelected, onSelect, disabled }) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: onSelect,
      disabled,
      className: cn(
        "flex items-center gap-3 p-3 rounded-lg border transition-colors text-left",
        isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600",
        disabled && "opacity-50 cursor-not-allowed"
      ),
      children: [
        /* @__PURE__ */ jsx(TriggerIcon, { type: trigger.type, className: "w-8 h-8" }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-slate-900 dark:text-slate-100 truncate", children: trigger.name }),
          /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400 capitalize", children: trigger.type })
        ] }),
        isSelected && /* @__PURE__ */ jsx(CheckCircleIcon, { className: "w-5 h-5 text-blue-600 dark:text-blue-400 ml-auto flex-shrink-0" })
      ]
    }
  );
}
function TriggerIcon({ type, className }) {
  const iconPath = {
    webhook: "M8 2v12M2 8h12",
    schedule: "M8 4v4l2.5 1.5",
    event: "M8 2l6 10H2l6-10z",
    manual: "M8 5v6M5 8h6"
  }[type] || "M8 2v12M2 8h12";
  return /* @__PURE__ */ jsx("div", { className: cn(
    "flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800",
    className
  ), children: /* @__PURE__ */ jsx("svg", { className: "w-4 h-4 text-slate-600 dark:text-slate-400", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: type === "schedule" ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: iconPath, strokeLinecap: "round", strokeLinejoin: "round" })
  ] }) : type === "event" ? /* @__PURE__ */ jsx("path", { d: iconPath, strokeLinecap: "round", strokeLinejoin: "round" }) : /* @__PURE__ */ jsx("path", { d: iconPath, strokeLinecap: "round" }) }) });
}
function PlayIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M4 3l9 5-9 5V3z" }) });
}
function SpinnerIcon3({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", strokeDasharray: "24", strokeDashoffset: "8" }) });
}
function ErrorIcon4({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M8 5v4M8 11v.5", strokeLinecap: "round" })
  ] });
}
function CheckCircleIcon({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
    /* @__PURE__ */ jsx("path", { d: "M5 8l2 2 4-4", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function TestExecutionModal({
  isOpen,
  onClose,
  automationName,
  flow,
  executionState,
  executionSteps,
  executionDurationMs,
  executionError,
  onStepClick,
  onRetry
}) {
  const [selectedNodeId, setSelectedNodeId] = useState();
  const nodeExecutionSteps = executionSteps.map((step) => ({
    nodeId: step.id,
    // Assuming step.id matches nodeId
    status: step.status === "completed" ? "completed" : step.status === "failed" ? "failed" : step.status === "running" ? "running" : step.status === "skipped" ? "skipped" : "pending",
    durationMs: step.durationMs,
    error: step.error
  }));
  const handleNodeClick = (node) => {
    setSelectedNodeId(node.id);
    const step = executionSteps.find((s) => s.id === node.id || s.name === node.name);
    if (step && onStepClick) {
      onStepClick(step.id);
    }
  };
  const handleStepClick = (step) => {
    setSelectedNodeId(step.id);
    onStepClick?.(step.id);
  };
  const isRunning = executionState === "running";
  const isCompleted = executionState === "completed";
  const isFailed = executionState === "failed";
  const statusText = isRunning ? "Running..." : isCompleted ? "Test Passed" : isFailed ? "Test Failed" : "Ready";
  const statusColor = isRunning ? "text-blue-600 dark:text-blue-400" : isCompleted ? "text-emerald-600 dark:text-emerald-400" : isFailed ? "text-red-600 dark:text-red-400" : "text-slate-600 dark:text-slate-400";
  const completedCount = executionSteps.filter((s) => s.status === "completed").length;
  const failedCount = executionSteps.filter((s) => s.status === "failed").length;
  const totalCount = executionSteps.length;
  return /* @__PURE__ */ jsx(
    Modal,
    {
      isOpen,
      onClose,
      title: `Test: ${automationName}`,
      size: "full",
      closeOnBackdropClick: !isRunning,
      closeOnEscape: !isRunning,
      footer: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        (isCompleted || isFailed) && onRetry && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onRetry,
            className: "px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors",
            children: "Run Again"
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onClose,
            disabled: isRunning,
            className: cn(
              "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
              isRunning ? "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed" : "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200"
            ),
            children: isRunning ? "Running..." : "Close"
          }
        )
      ] }),
      children: /* @__PURE__ */ jsxs("div", { className: "flex h-full", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex-1 flex flex-col", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                isRunning && /* @__PURE__ */ jsx(SpinnerIcon4, { className: "w-5 h-5 text-blue-500 animate-spin" }),
                isCompleted && /* @__PURE__ */ jsx(CheckCircleIcon2, { className: "w-5 h-5 text-emerald-500" }),
                isFailed && /* @__PURE__ */ jsx(XCircleIcon, { className: "w-5 h-5 text-red-500" }),
                /* @__PURE__ */ jsx("span", { className: cn("font-semibold", statusColor), children: statusText })
              ] }),
              executionDurationMs !== void 0 && /* @__PURE__ */ jsxs("span", { className: "text-sm text-slate-500 dark:text-slate-400", children: [
                "Duration: ",
                formatDuration(executionDurationMs)
              ] })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "text-sm text-slate-600 dark:text-slate-400", children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium", children: completedCount }),
              "/",
              totalCount,
              " steps",
              failedCount > 0 && /* @__PURE__ */ jsxs("span", { className: "text-red-500 ml-2", children: [
                "(",
                failedCount,
                " failed)"
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 p-4", children: /* @__PURE__ */ jsx(
            FlowViewWithExecution,
            {
              flow,
              executionSteps: nodeExecutionSteps,
              selectedNodeId,
              onNodeClick: handleNodeClick,
              className: "h-full",
              showControls: true
            }
          ) }),
          executionError && /* @__PURE__ */ jsx("div", { className: "mx-4 mb-4 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx(XCircleIcon, { className: "w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" }),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("p", { className: "font-semibold text-red-800 dark:text-red-200", children: executionError.message }),
              executionError.details && /* @__PURE__ */ jsx("pre", { className: "mt-2 text-sm text-red-700 dark:text-red-300 overflow-x-auto", children: JSON.stringify(executionError.details, null, 2) })
            ] })
          ] }) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "w-80 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col", children: [
          /* @__PURE__ */ jsx("div", { className: "px-4 py-3 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx("h3", { className: "font-semibold text-slate-900 dark:text-slate-100", children: "Execution Steps" }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 overflow-y-auto", children: [
            /* @__PURE__ */ jsx("div", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: executionSteps.map((step, index) => /* @__PURE__ */ jsx(
              StepListItem,
              {
                step,
                index,
                isSelected: selectedNodeId === step.id,
                onClick: () => handleStepClick(step)
              },
              step.id
            )) }),
            executionSteps.length === 0 && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-sm text-slate-500 dark:text-slate-400", children: "Waiting for execution to start..." })
          ] })
        ] })
      ] })
    }
  );
}
function StepListItem({ step, index, isSelected, onClick }) {
  const isRunning = step.status === "running";
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick,
      className: cn(
        "w-full text-left px-4 py-3 transition-colors",
        isSelected ? "bg-blue-50 dark:bg-blue-950/30" : "hover:bg-slate-50 dark:hover:bg-slate-800/50",
        isRunning && "bg-blue-50/50 dark:bg-blue-950/20"
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx("span", { className: cn(
            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
            step.status === "completed" && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
            step.status === "failed" && "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
            step.status === "running" && "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
            step.status === "pending" && "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
            step.status === "skipped" && "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
          ), children: step.status === "running" ? /* @__PURE__ */ jsx("span", { className: "animate-spin", children: "\u25CC" }) : index + 1 }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx("p", { className: cn(
              "text-sm font-medium truncate",
              step.status === "skipped" ? "text-slate-400 dark:text-slate-500" : "text-slate-900 dark:text-slate-100"
            ), children: step.name }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-0.5", children: [
              /* @__PURE__ */ jsx(
                StepStatusBadge,
                {
                  status: step.status === "completed" ? "COMPLETED" : step.status === "failed" ? "FAILED" : step.status === "running" ? "RUNNING" : step.status === "skipped" ? "SKIPPED" : "PENDING",
                  size: "sm"
                }
              ),
              step.durationMs !== void 0 && step.status !== "pending" && step.status !== "running" && /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500 font-mono", children: formatDuration(step.durationMs) })
            ] })
          ] }),
          /* @__PURE__ */ jsx(ChevronRightIcon, { className: "w-4 h-4 text-slate-400 flex-shrink-0" })
        ] }),
        step.error && /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-red-600 dark:text-red-400 truncate pl-9", children: step.error.message })
      ]
    }
  );
}
function SpinnerIcon4({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6", strokeDasharray: "24", strokeDashoffset: "8" }) });
}
function CheckCircleIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M8 15A7 7 0 108 1a7 7 0 000 14zm3.354-8.354a.5.5 0 00-.708-.708L7 9.586 5.354 7.94a.5.5 0 00-.708.708l2 2a.5.5 0 00.708 0l4-4z", clipRule: "evenodd" }) });
}
function XCircleIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M8 15A7 7 0 108 1a7 7 0 000 14zm-2.354-9.354a.5.5 0 01.708 0L8 7.293l1.646-1.647a.5.5 0 01.708.708L8.707 8l1.647 1.646a.5.5 0 01-.708.708L8 8.707l-1.646 1.647a.5.5 0 01-.708-.708L7.293 8 5.646 6.354a.5.5 0 010-.708z", clipRule: "evenodd" }) });
}
function ChevronRightIcon({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M6 4l4 4-4 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
var nodeTypeColors = {
  trigger: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  action: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
  condition: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  wait: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300",
  ai: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300",
  agent: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
  http: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
  crypto: "bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300",
  subflow: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
};
var fieldTypeIcons = {
  string: "T",
  number: "#",
  boolean: "\u25C9",
  object: "{ }",
  array: "[ ]",
  unknown: "?"
};
function InputMappingEditor({
  field,
  value,
  onChange,
  availableSteps,
  disabled = false,
  error,
  className
}) {
  const [isSelectingStep, setIsSelectingStep] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState(
    value?.type === "reference" ? value.stepId : null
  );
  const isReference = value?.type === "reference";
  availableSteps.find((s) => s.stepId === selectedStepId);
  const handleSelectStep = (stepId) => {
    setSelectedStepId(stepId);
  };
  const handleSelectField = (stepId, path) => {
    onChange({
      type: "reference",
      stepId,
      path
    });
    setIsSelectingStep(false);
  };
  const handleCustomValue = (customValue) => {
    onChange({
      type: "custom",
      value: customValue
    });
  };
  const handleSwitchToCustom = () => {
    setSelectedStepId(null);
    setIsSelectingStep(false);
    onChange({
      type: "custom",
      value: field.defaultValue ?? ""
    });
  };
  const handleSwitchToReference = () => {
    setIsSelectingStep(true);
  };
  useMemo(() => {
    if (!value) return null;
    if (value.type === "custom") {
      if (typeof value.value === "string") return value.value;
      if (typeof value.value === "number") return String(value.value);
      if (typeof value.value === "boolean") return value.value ? "true" : "false";
      return JSON.stringify(value.value);
    }
    const step = availableSteps.find((s) => s.stepId === value.stepId);
    return `${step?.stepName ?? value.stepId}.${value.path}`;
  }, [value, availableSteps]);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("label", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300", children: [
        field.label,
        field.required && /* @__PURE__ */ jsx("span", { className: "text-red-500 ml-1", children: "*" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleSwitchToReference,
            disabled,
            className: cn(
              "text-xs px-2 py-1 rounded font-medium transition-colors",
              isReference ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            ),
            children: "From Step"
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleSwitchToCustom,
            disabled,
            className: cn(
              "text-xs px-2 py-1 rounded font-medium transition-colors",
              !isReference ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            ),
            children: "Custom"
          }
        )
      ] })
    ] }),
    field.description && /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: field.description }),
    isSelectingStep || isReference && !value ? /* @__PURE__ */ jsx(
      StepOutputPicker,
      {
        steps: availableSteps,
        selectedStepId,
        onSelectStep: handleSelectStep,
        onSelectField: handleSelectField,
        onCancel: () => setIsSelectingStep(false)
      }
    ) : isReference && value?.type === "reference" ? /* @__PURE__ */ jsx(
      ReferenceDisplay,
      {
        value,
        step: availableSteps.find((s) => s.stepId === value.stepId),
        onEdit: () => setIsSelectingStep(true),
        onClear: handleSwitchToCustom,
        disabled
      }
    ) : /* @__PURE__ */ jsx(
      CustomValueInput,
      {
        field,
        value: value?.type === "custom" ? value.value : field.defaultValue,
        onChange: handleCustomValue,
        disabled,
        error
      }
    ),
    error && /* @__PURE__ */ jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error })
  ] });
}
function StepOutputPicker({
  steps,
  selectedStepId,
  onSelectStep,
  onSelectField,
  onCancel
}) {
  const selectedStep = steps.find((s) => s.stepId === selectedStepId);
  return /* @__PURE__ */ jsxs("div", { className: "border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex", children: [
      /* @__PURE__ */ jsxs("div", { className: "w-1/2 border-r border-slate-200 dark:border-slate-700", children: [
        /* @__PURE__ */ jsx("div", { className: "px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase", children: "Select Step" }) }),
        /* @__PURE__ */ jsx("div", { className: "max-h-60 overflow-y-auto", children: steps.length === 0 ? /* @__PURE__ */ jsx("div", { className: "p-4 text-sm text-slate-500 dark:text-slate-400 text-center", children: "No previous steps available" }) : steps.map((step) => /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: () => onSelectStep(step.stepId),
            className: cn(
              "w-full px-3 py-2 text-left transition-colors flex items-center gap-2",
              selectedStepId === step.stepId ? "bg-blue-50 dark:bg-blue-950/30" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
            ),
            children: [
              /* @__PURE__ */ jsx("span", { className: cn(
                "text-xs px-1.5 py-0.5 rounded font-medium",
                nodeTypeColors[step.nodeType]
              ), children: step.nodeType }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-slate-900 dark:text-slate-100 truncate", children: step.stepName })
            ]
          },
          step.stepId
        )) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "w-1/2", children: [
        /* @__PURE__ */ jsx("div", { className: "px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase", children: selectedStep ? "Select Field" : "Choose a step first" }) }),
        /* @__PURE__ */ jsx("div", { className: "max-h-60 overflow-y-auto", children: selectedStep ? /* @__PURE__ */ jsx(
          FieldTree,
          {
            fields: selectedStep.fields,
            stepId: selectedStep.stepId,
            onSelectField
          }
        ) : /* @__PURE__ */ jsx("div", { className: "p-4 text-sm text-slate-400 dark:text-slate-500 text-center", children: "Select a step to see its outputs" }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "px-3 py-2 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end", children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onCancel,
        className: "text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200",
        children: "Cancel"
      }
    ) })
  ] });
}
function FieldTree({ fields, stepId, onSelectField, depth = 0 }) {
  const [expanded, setExpanded] = useState(/* @__PURE__ */ new Set());
  const toggleExpand = (path) => {
    const next = new Set(expanded);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
    }
    setExpanded(next);
  };
  return /* @__PURE__ */ jsx("div", { className: cn(depth > 0 && "ml-4 border-l border-slate-200 dark:border-slate-700"), children: fields.map((field) => {
    const hasChildren = field.children && field.children.length > 0;
    const isExpanded = expanded.has(field.path);
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => {
            if (hasChildren) {
              toggleExpand(field.path);
            } else {
              onSelectField(stepId, field.path);
            }
          },
          className: "w-full px-3 py-1.5 text-left flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors",
          children: [
            hasChildren && /* @__PURE__ */ jsx(
              ChevronIcon2,
              {
                className: cn(
                  "w-3 h-3 text-slate-400 transition-transform",
                  isExpanded && "rotate-90"
                )
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-xs font-mono text-slate-400 dark:text-slate-500 w-5", children: fieldTypeIcons[field.type] }),
            /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-700 dark:text-slate-300", children: field.label }),
            field.sampleValue !== void 0 && /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500 truncate ml-auto", children: typeof field.sampleValue === "string" ? `"${field.sampleValue.slice(0, 20)}${field.sampleValue.length > 20 ? "..." : ""}"` : String(field.sampleValue) })
          ]
        }
      ),
      hasChildren && isExpanded && /* @__PURE__ */ jsx(
        FieldTree,
        {
          fields: field.children,
          stepId,
          onSelectField,
          depth: depth + 1
        }
      )
    ] }, field.path);
  }) });
}
function ReferenceDisplay({ value, step, onEdit, onClear, disabled }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg", children: [
    /* @__PURE__ */ jsx(LinkIcon, { className: "w-4 h-4 text-blue-500 flex-shrink-0" }),
    /* @__PURE__ */ jsx("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      step && /* @__PURE__ */ jsx("span", { className: cn(
        "text-xs px-1.5 py-0.5 rounded font-medium",
        nodeTypeColors[step.nodeType]
      ), children: step.stepName }),
      /* @__PURE__ */ jsxs("span", { className: "text-sm font-mono text-blue-700 dark:text-blue-300 truncate", children: [
        ".",
        value.path
      ] })
    ] }) }),
    !disabled && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: onEdit,
          className: "p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded",
          children: /* @__PURE__ */ jsx(EditIcon2, { className: "w-4 h-4" })
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: onClear,
          className: "p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded",
          children: /* @__PURE__ */ jsx(XIcon2, { className: "w-4 h-4" })
        }
      )
    ] })
  ] });
}
function CustomValueInput({ field, value, onChange, disabled, error }) {
  if (field.type === "boolean") {
    return /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-2 cursor-pointer", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          checked: Boolean(value),
          onChange: (e) => onChange(e.target.checked),
          disabled,
          className: "w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-700 dark:text-slate-300", children: value ? "Yes" : "No" })
    ] });
  }
  if (field.type === "number") {
    return /* @__PURE__ */ jsx(
      "input",
      {
        type: "number",
        value: typeof value === "number" ? value : "",
        onChange: (e) => onChange(e.target.value ? Number(e.target.value) : void 0),
        placeholder: field.placeholder,
        disabled,
        className: cn(
          "w-full px-3 py-2 rounded-lg border transition-colors font-mono",
          "bg-white dark:bg-slate-900",
          "text-slate-900 dark:text-slate-100",
          error ? "border-red-300 dark:border-red-700 focus:border-red-500" : "border-slate-200 dark:border-slate-700 focus:border-blue-500",
          "focus:outline-none focus:ring-1 focus:ring-blue-500/20",
          disabled && "bg-slate-50 dark:bg-slate-800 cursor-not-allowed"
        )
      }
    );
  }
  if (field.type === "object" || field.type === "array") {
    return /* @__PURE__ */ jsx(
      "textarea",
      {
        value: typeof value === "string" ? value : JSON.stringify(value ?? (field.type === "array" ? [] : {}), null, 2),
        onChange: (e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
          }
        },
        placeholder: field.placeholder ?? (field.type === "array" ? "[]" : "{}"),
        disabled,
        rows: 4,
        className: cn(
          "w-full px-3 py-2 rounded-lg border transition-colors font-mono text-sm",
          "bg-white dark:bg-slate-900",
          "text-slate-900 dark:text-slate-100",
          error ? "border-red-300 dark:border-red-700 focus:border-red-500" : "border-slate-200 dark:border-slate-700 focus:border-blue-500",
          "focus:outline-none focus:ring-1 focus:ring-blue-500/20",
          disabled && "bg-slate-50 dark:bg-slate-800 cursor-not-allowed"
        )
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "input",
    {
      type: "text",
      value: typeof value === "string" ? value : "",
      onChange: (e) => onChange(e.target.value),
      placeholder: field.placeholder,
      disabled,
      className: cn(
        "w-full px-3 py-2 rounded-lg border transition-colors",
        "bg-white dark:bg-slate-900",
        "text-slate-900 dark:text-slate-100",
        error ? "border-red-300 dark:border-red-700 focus:border-red-500" : "border-slate-200 dark:border-slate-700 focus:border-blue-500",
        "focus:outline-none focus:ring-1 focus:ring-blue-500/20",
        disabled && "bg-slate-50 dark:bg-slate-800 cursor-not-allowed"
      )
    }
  );
}
function ChevronIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M6 4l4 4-4 4", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function LinkIcon({ className }) {
  return /* @__PURE__ */ jsxs("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [
    /* @__PURE__ */ jsx("path", { d: "M6 10l4-4", strokeLinecap: "round" }),
    /* @__PURE__ */ jsx("path", { d: "M9 4h3v3M7 12H4V9", strokeLinecap: "round", strokeLinejoin: "round" })
  ] });
}
function EditIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx("path", { d: "M10.5 3.5l2 2M4 10l-1 3 3-1 7-7-2-2-7 7z", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function XIcon2({ className }) {
  return /* @__PURE__ */ jsx("svg", { className, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8", strokeLinecap: "round" }) });
}
var nodeTypeColors2 = {
  trigger: { bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200 dark:border-blue-800", text: "text-blue-700 dark:text-blue-300" },
  action: { bg: "bg-purple-50 dark:bg-purple-950/30", border: "border-purple-200 dark:border-purple-800", text: "text-purple-700 dark:text-purple-300" },
  condition: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", text: "text-amber-700 dark:text-amber-300" },
  wait: { bg: "bg-orange-50 dark:bg-orange-950/30", border: "border-orange-200 dark:border-orange-800", text: "text-orange-700 dark:text-orange-300" },
  ai: { bg: "bg-cyan-50 dark:bg-cyan-950/30", border: "border-cyan-200 dark:border-cyan-800", text: "text-cyan-700 dark:text-cyan-300" },
  agent: { bg: "bg-indigo-50 dark:bg-indigo-950/30", border: "border-indigo-200 dark:border-indigo-800", text: "text-indigo-700 dark:text-indigo-300" },
  http: { bg: "bg-green-50 dark:bg-green-950/30", border: "border-green-200 dark:border-green-800", text: "text-green-700 dark:text-green-300" },
  crypto: { bg: "bg-pink-50 dark:bg-pink-950/30", border: "border-pink-200 dark:border-pink-800", text: "text-pink-700 dark:text-pink-300" },
  subflow: { bg: "bg-slate-50 dark:bg-slate-900/30", border: "border-slate-200 dark:border-slate-800", text: "text-slate-700 dark:text-slate-300" }
};
function NodeInputForm({
  nodeId,
  nodeName,
  nodeType,
  inputFields,
  inputValues,
  onInputChange,
  availableSteps,
  errors = {},
  disabled = false,
  className
}) {
  const colors = nodeTypeColors2[nodeType];
  const groupedFields = inputFields.reduce((acc, field) => {
    const category = field.category || "default";
    if (!acc[category]) acc[category] = [];
    acc[category].push(field);
    return acc;
  }, {});
  const categories = Object.keys(groupedFields);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-6", className), children: [
    /* @__PURE__ */ jsx("div", { className: cn(
      "p-4 rounded-lg border",
      colors.bg,
      colors.border
    ), children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx("div", { className: cn(
        "w-10 h-10 rounded-lg flex items-center justify-center",
        "bg-white/50 dark:bg-black/20"
      ), children: /* @__PURE__ */ jsx(NodeTypeIcon, { type: nodeType, className: "w-5 h-5" }) }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: cn("font-semibold", colors.text), children: nodeName }),
        /* @__PURE__ */ jsxs("p", { className: "text-sm text-slate-500 dark:text-slate-400 capitalize", children: [
          nodeType,
          " node"
        ] })
      ] })
    ] }) }),
    categories.length === 1 && categories[0] === "default" ? (
      // No categories - render flat list
      /* @__PURE__ */ jsx("div", { className: "space-y-4", children: inputFields.map((field) => /* @__PURE__ */ jsx(
        InputMappingEditor,
        {
          field,
          value: inputValues[field.name],
          onChange: (value) => onInputChange(field.name, value),
          availableSteps,
          error: errors[field.name],
          disabled
        },
        field.name
      )) })
    ) : (
      // Render with categories
      categories.map((category) => /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
        category !== "default" && /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide", children: category }),
        groupedFields[category]?.map((field) => /* @__PURE__ */ jsx(
          InputMappingEditor,
          {
            field,
            value: inputValues[field.name],
            onChange: (value) => onInputChange(field.name, value),
            availableSteps,
            error: errors[field.name],
            disabled
          },
          field.name
        ))
      ] }, category))
    ),
    inputFields.length === 0 && /* @__PURE__ */ jsx("div", { className: "text-center py-8 text-slate-500 dark:text-slate-400", children: "This node has no configurable inputs." })
  ] });
}
function NodeTypeIcon({ type, className }) {
  const icons = {
    trigger: "\u26A1",
    action: "\u25B6",
    condition: "\u25C7",
    wait: "\u23F8",
    ai: "\u{1F916}",
    agent: "\u{1F9E0}",
    http: "\u{1F310}",
    crypto: "\u{1F510}",
    subflow: "\u{1F4E6}"
  };
  return /* @__PURE__ */ jsx("span", { className, children: icons[type] });
}
function MetricCard({
  title,
  value,
  subtitle,
  delta,
  deltaType = "unchanged",
  icon,
  sparklineData,
  sparklineColor = "blue",
  loading,
  className
}) {
  if (loading) {
    return /* @__PURE__ */ jsxs(Card$1, { className: cn("animate-pulse", className), children: [
      /* @__PURE__ */ jsx("div", { className: "h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-3" }),
      /* @__PURE__ */ jsx("div", { className: "h-8 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-2" }),
      /* @__PURE__ */ jsx("div", { className: "h-3 w-32 bg-slate-200 dark:bg-slate-700 rounded" })
    ] });
  }
  return /* @__PURE__ */ jsx(Card$1, { className, children: /* @__PURE__ */ jsxs(Flex, { alignItems: "start", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
      /* @__PURE__ */ jsxs(Flex, { alignItems: "center", className: "gap-2", children: [
        icon && /* @__PURE__ */ jsx("span", { className: "text-slate-600 dark:text-slate-300", children: icon }),
        /* @__PURE__ */ jsx(Text, { className: "text-slate-700 dark:text-slate-200", children: title })
      ] }),
      /* @__PURE__ */ jsx(Metric, { className: "mt-2 text-slate-900 dark:text-white", children: value }),
      (subtitle || delta) && /* @__PURE__ */ jsxs(Flex, { className: "mt-2 gap-2", alignItems: "center", children: [
        delta && /* @__PURE__ */ jsx(BadgeDelta, { deltaType, children: delta }),
        subtitle && /* @__PURE__ */ jsx(Text, { className: "text-slate-600 dark:text-slate-300", children: subtitle })
      ] })
    ] }),
    sparklineData && sparklineData.length > 0 && /* @__PURE__ */ jsx(
      SparkAreaChart,
      {
        data: sparklineData,
        categories: ["value"],
        index: "index",
        colors: [sparklineColor],
        className: "h-10 w-20"
      }
    )
  ] }) });
}
function AreaChart({
  data,
  index,
  categories,
  colors = ["blue"],
  title,
  subtitle,
  height = "h-72",
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  showYAxis = true,
  stack = false,
  curveType = "natural",
  valueFormatter,
  loading,
  className,
  withCard = true
}) {
  if (loading) {
    const LoadingSkeleton = /* @__PURE__ */ jsxs("div", { className: cn("animate-pulse", height, className), children: [
      title && /* @__PURE__ */ jsx("div", { className: "h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-2" }),
      subtitle && /* @__PURE__ */ jsx("div", { className: "h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-4" }),
      /* @__PURE__ */ jsx("div", { className: "h-full w-full bg-slate-200 dark:bg-slate-700 rounded" })
    ] });
    if (withCard) {
      return /* @__PURE__ */ jsx(Card$1, { className, children: LoadingSkeleton });
    }
    return LoadingSkeleton;
  }
  const ChartContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    title && /* @__PURE__ */ jsx(Title, { children: title }),
    subtitle && /* @__PURE__ */ jsx(Text, { className: "mt-1", children: subtitle }),
    /* @__PURE__ */ jsx(
      AreaChart$1,
      {
        data,
        index,
        categories,
        colors,
        className: cn("mt-4", height),
        showLegend,
        showGridLines,
        showXAxis,
        showYAxis,
        stack,
        curveType,
        valueFormatter
      }
    )
  ] });
  if (withCard) {
    return /* @__PURE__ */ jsx(Card$1, { className, children: ChartContent });
  }
  return /* @__PURE__ */ jsx("div", { className, children: ChartContent });
}
function BarChart({
  data,
  index,
  categories,
  colors = ["blue"],
  title,
  subtitle,
  height = "h-72",
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  showYAxis = true,
  stack = false,
  layout = "vertical",
  valueFormatter,
  loading,
  className,
  withCard = true
}) {
  if (loading) {
    const LoadingSkeleton = /* @__PURE__ */ jsxs("div", { className: cn("animate-pulse", height, className), children: [
      title && /* @__PURE__ */ jsx("div", { className: "h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-2" }),
      subtitle && /* @__PURE__ */ jsx("div", { className: "h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-4" }),
      /* @__PURE__ */ jsx("div", { className: "h-full w-full bg-slate-200 dark:bg-slate-700 rounded" })
    ] });
    if (withCard) {
      return /* @__PURE__ */ jsx(Card$1, { className, children: LoadingSkeleton });
    }
    return LoadingSkeleton;
  }
  const ChartContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    title && /* @__PURE__ */ jsx(Title, { children: title }),
    subtitle && /* @__PURE__ */ jsx(Text, { className: "mt-1", children: subtitle }),
    /* @__PURE__ */ jsx(
      BarChart$1,
      {
        data,
        index,
        categories,
        colors,
        className: cn("mt-4", height),
        showLegend,
        showGridLines,
        showXAxis,
        showYAxis,
        stack,
        layout,
        valueFormatter
      }
    )
  ] });
  if (withCard) {
    return /* @__PURE__ */ jsx(Card$1, { className, children: ChartContent });
  }
  return /* @__PURE__ */ jsx("div", { className, children: ChartContent });
}
function DonutChart({
  data,
  colors = ["blue", "emerald", "amber", "red", "slate"],
  title,
  subtitle,
  height = "h-60",
  showLegend = true,
  legendPosition = "right",
  showLabel = false,
  showAnimation = true,
  showTooltip = true,
  variant = "donut",
  valueFormatter,
  loading,
  className,
  withCard = true
}) {
  if (loading) {
    const LoadingSkeleton = /* @__PURE__ */ jsxs("div", { className: cn("animate-pulse", className), children: [
      title && /* @__PURE__ */ jsx("div", { className: "h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-2" }),
      subtitle && /* @__PURE__ */ jsx("div", { className: "h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-4" }),
      /* @__PURE__ */ jsxs("div", { className: cn("flex gap-4", legendPosition === "bottom" && "flex-col"), children: [
        /* @__PURE__ */ jsx("div", { className: cn("rounded-full bg-slate-200 dark:bg-slate-700", height, "aspect-square") }),
        showLegend && /* @__PURE__ */ jsx("div", { className: "space-y-2", children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx("div", { className: "h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded" }, i)) })
      ] })
    ] });
    if (withCard) {
      return /* @__PURE__ */ jsx(Card$1, { className, children: LoadingSkeleton });
    }
    return LoadingSkeleton;
  }
  const ChartContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    title && /* @__PURE__ */ jsx(Title, { children: title }),
    subtitle && /* @__PURE__ */ jsx(Text, { className: "mt-1", children: subtitle }),
    /* @__PURE__ */ jsxs("div", { className: cn(
      "flex mt-4 gap-4",
      legendPosition === "bottom" && "flex-col",
      legendPosition === "left" && "flex-row-reverse"
    ), children: [
      /* @__PURE__ */ jsx(
        DonutChart$1,
        {
          data,
          index: "name",
          category: "value",
          colors,
          className: height,
          showLabel,
          showAnimation,
          showTooltip,
          variant,
          valueFormatter
        }
      ),
      showLegend && /* @__PURE__ */ jsx(
        Legend,
        {
          categories: data.map((d) => d.name),
          colors,
          className: "max-w-xs"
        }
      )
    ] })
  ] });
  if (withCard) {
    return /* @__PURE__ */ jsx(Card$1, { className, children: ChartContent });
  }
  return /* @__PURE__ */ jsx("div", { className, children: ChartContent });
}
function AutomationList({
  view = "list",
  onSelect,
  params,
  showStats = true,
  showHealth = true,
  className
}) {
  const selectedAutomationId = useAutomationUIStore((state) => state.selectedAutomationId);
  const selectAutomation = useAutomationUIStore((state) => state.selectAutomation);
  const { data, isLoading, error } = useAutomations(params);
  const handleSelect = (automation) => {
    selectAutomation(automation.id);
    onSelect?.(automation);
  };
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-3", className), children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-20 w-full" }, i)) });
  }
  if (error) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Failed to load automations",
        description: error.message,
        className
      }
    );
  }
  if (!data?.automations?.length) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No automations found",
        description: "Create your first automation to get started.",
        className
      }
    );
  }
  const automations = data.automations;
  if (view === "grid") {
    return /* @__PURE__ */ jsx("div", { className: cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", className), children: automations.map((automation) => /* @__PURE__ */ jsx(
      AutomationCard,
      {
        automation,
        isSelected: selectedAutomationId === automation.id,
        onClick: () => handleSelect(automation),
        showStats,
        showHealth
      },
      automation.id
    )) });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: automations.map((automation) => /* @__PURE__ */ jsx(
    AutomationRow,
    {
      automation,
      isSelected: selectedAutomationId === automation.id,
      onClick: () => handleSelect(automation),
      showStats,
      showHealth
    },
    automation.id
  )) });
}
function AutomationCard({
  automation,
  isSelected,
  onClick,
  showStats,
  showHealth
}) {
  return /* @__PURE__ */ jsxs(
    Card,
    {
      className: cn(
        "p-4 cursor-pointer transition-all hover:shadow-md",
        isSelected && "ring-2 ring-blue-500 dark:ring-blue-400"
      ),
      onClick,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2 mb-3", children: [
          /* @__PURE__ */ jsx("h3", { className: "font-medium text-sm truncate", children: automation.name }),
          /* @__PURE__ */ jsx(AutomationStatusBadge, { status: automation.status, size: "sm", showLabel: false })
        ] }),
        automation.description && /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400 mb-3 line-clamp-2", children: automation.description }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
          /* @__PURE__ */ jsx(TriggerTypeBadge, { type: automation.triggerType, size: "sm" }),
          showHealth && /* @__PURE__ */ jsx(
            HealthIndicator,
            {
              status: getHealthStatus(automation),
              size: "sm",
              showLabel: false
            }
          )
        ] }),
        showStats && automation.stats && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400", children: [
          /* @__PURE__ */ jsxs("span", { children: [
            automation.stats.totalRuns,
            " runs"
          ] }),
          /* @__PURE__ */ jsxs("span", { children: [
            automation.stats.successRate,
            "% success"
          ] })
        ] })
      ]
    }
  );
}
function AutomationRow({
  automation,
  isSelected,
  onClick,
  showStats,
  showHealth
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-colors",
        "hover:bg-slate-50 dark:hover:bg-slate-800/50",
        isSelected && "bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200 dark:ring-blue-800"
      ),
      onClick,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mb-1", children: [
            /* @__PURE__ */ jsx("h3", { className: "font-medium text-sm truncate", children: automation.name }),
            /* @__PURE__ */ jsx(AutomationStatusBadge, { status: automation.status, size: "sm" })
          ] }),
          automation.description && /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400 truncate", children: automation.description })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx(TriggerTypeBadge, { type: automation.triggerType, size: "sm" }),
          showHealth && /* @__PURE__ */ jsx(
            HealthIndicator,
            {
              status: getHealthStatus(automation),
              size: "sm",
              showLabel: false
            }
          ),
          showStats && automation.stats && /* @__PURE__ */ jsxs("div", { className: "text-xs text-slate-500 dark:text-slate-400 text-right", children: [
            /* @__PURE__ */ jsxs("div", { children: [
              automation.stats.totalRuns,
              " runs"
            ] }),
            /* @__PURE__ */ jsxs("div", { children: [
              automation.stats.successRate,
              "%"
            ] })
          ] }),
          automation.updatedAt && /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap", children: formatTimeAgo(automation.updatedAt) })
        ] })
      ]
    }
  );
}
function getHealthStatus(automation) {
  if (automation.status === "ERROR") return "failing";
  if (automation.status === "PAUSED") return "warning";
  if (automation.stats) {
    if (automation.stats.successRate < 50) return "failing";
    if (automation.stats.successRate < 80) return "warning";
  }
  return "healthy";
}
function toUIStatus(status) {
  return status.toUpperCase();
}
function RunsTable({
  automationId,
  onSelect,
  params,
  pageSize = 20,
  showAutomation = false,
  className
}) {
  const selectedAutomationId = useAutomationUIStore((state) => state.selectedAutomationId);
  const selectedRunId = useAutomationUIStore((state) => state.selectedRunId);
  const selectRun = useAutomationUIStore((state) => state.selectRun);
  const effectiveAutomationId = automationId ?? selectedAutomationId;
  const { data, isLoading, error } = useTraces({
    ...params,
    automationId: effectiveAutomationId ?? void 0,
    limit: pageSize
  });
  const handleSelect = (run) => {
    selectRun(run.id);
    onSelect?.(run);
  };
  if (isLoading) {
    return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-10 w-full" }),
      [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-12 w-full" }, i))
    ] });
  }
  if (error) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Failed to load runs",
        description: error.message,
        className
      }
    );
  }
  if (!data?.runs?.length) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No runs found",
        description: effectiveAutomationId ? "This automation hasn't been executed yet." : "No automation runs have been recorded.",
        className
      }
    );
  }
  const runs = data.runs;
  return /* @__PURE__ */ jsx("div", { className: cn("overflow-x-auto", className), children: /* @__PURE__ */ jsxs("table", { className: "w-full text-sm", children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "border-b border-slate-200 dark:border-slate-700", children: [
      /* @__PURE__ */ jsx("th", { className: "text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400", children: "Status" }),
      showAutomation && /* @__PURE__ */ jsx("th", { className: "text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400", children: "Automation" }),
      /* @__PURE__ */ jsx("th", { className: "text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400", children: "Run ID" }),
      /* @__PURE__ */ jsx("th", { className: "text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400", children: "Duration" }),
      /* @__PURE__ */ jsx("th", { className: "text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400", children: "Steps" }),
      /* @__PURE__ */ jsx("th", { className: "text-right py-3 px-4 font-medium text-slate-600 dark:text-slate-400", children: "Started" })
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { children: runs.map((run) => /* @__PURE__ */ jsxs(
      "tr",
      {
        className: cn(
          "border-b border-slate-100 dark:border-slate-800 cursor-pointer transition-colors",
          "hover:bg-slate-50 dark:hover:bg-slate-800/50",
          selectedRunId === run.id && "bg-blue-50 dark:bg-blue-900/20"
        ),
        onClick: () => handleSelect(run),
        children: [
          /* @__PURE__ */ jsx("td", { className: "py-3 px-4", children: /* @__PURE__ */ jsx(RunStatusBadge, { status: toUIStatus(run.status), size: "sm" }) }),
          showAutomation && /* @__PURE__ */ jsx("td", { className: "py-3 px-4 text-slate-700 dark:text-slate-300", children: run.automation?.name ?? run.automationId ?? "-" }),
          /* @__PURE__ */ jsxs("td", { className: "py-3 px-4 font-mono text-xs text-slate-600 dark:text-slate-400", children: [
            run.id.slice(0, 8),
            "..."
          ] }),
          /* @__PURE__ */ jsx("td", { className: "py-3 px-4 text-slate-600 dark:text-slate-400", children: run.completedAt && run.createdAt ? formatDuration2(new Date(run.completedAt).getTime() - new Date(run.createdAt).getTime()) : "-" }),
          /* @__PURE__ */ jsx("td", { className: "py-3 px-4 text-slate-600 dark:text-slate-400", children: "-" }),
          /* @__PURE__ */ jsx("td", { className: "py-3 px-4 text-right text-slate-500 dark:text-slate-500", children: run.createdAt ? formatTimeAgo(run.createdAt) : "-" })
        ]
      },
      run.id
    )) })
  ] }) });
}
function ErrorsPanel({
  automationId,
  onSelect,
  params,
  showSeverityFilter = false,
  showStatusFilter = false,
  className
}) {
  const selectedAutomationId = useAutomationUIStore((state) => state.selectedAutomationId);
  const selectedErrorId = useAutomationUIStore((state) => state.selectedErrorId);
  const selectError = useAutomationUIStore((state) => state.selectError);
  const effectiveAutomationId = automationId ?? selectedAutomationId;
  const { data, isLoading, error } = useErrors({
    ...params,
    automationId: effectiveAutomationId ?? void 0
  });
  const handleSelect = (errorGroup) => {
    selectError(errorGroup.id);
    onSelect?.(errorGroup);
  };
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-3", className), children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-24 w-full" }, i)) });
  }
  if (error) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Failed to load errors",
        description: error.message,
        className
      }
    );
  }
  if (!data?.groups?.length) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No errors found",
        description: "Great news! No errors have been recorded.",
        className
      }
    );
  }
  const errors = data.groups;
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-3", className), children: errors.map((errorGroup) => /* @__PURE__ */ jsx(
    ErrorCard,
    {
      error: errorGroup,
      isSelected: selectedErrorId === errorGroup.id,
      onClick: () => handleSelect(errorGroup)
    },
    errorGroup.id
  )) });
}
function ErrorCard({ error, isSelected, onClick }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "p-4 rounded-lg border cursor-pointer transition-all",
        "border-slate-200 dark:border-slate-700",
        "hover:border-slate-300 dark:hover:border-slate-600",
        isSelected && "ring-2 ring-blue-500 dark:ring-blue-400 border-transparent"
      ),
      onClick,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2 mb-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
            /* @__PURE__ */ jsx(ErrorSeverityBadge, { severity: error.severity, size: "sm" }),
            /* @__PURE__ */ jsx(ErrorStatusBadge, { status: error.status, size: "sm" })
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "text-xs text-slate-500 dark:text-slate-500 whitespace-nowrap", children: [
            error.occurrenceCount,
            " occurrence",
            error.occurrenceCount !== 1 ? "s" : ""
          ] })
        ] }),
        /* @__PURE__ */ jsx("h3", { className: "font-medium text-sm mb-1 text-slate-800 dark:text-slate-200", children: error.errorType }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400 line-clamp-2 font-mono", children: error.errorMessage }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-800", children: [
          /* @__PURE__ */ jsxs("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: [
            "First seen: ",
            formatTimeAgo(error.firstSeenAt)
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: [
            "Last seen: ",
            formatTimeAgo(error.lastSeenAt)
          ] })
        ] }),
        error.linearIssueUrl && /* @__PURE__ */ jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsx(
          "a",
          {
            href: error.linearIssueUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-xs text-blue-600 dark:text-blue-400 hover:underline",
            onClick: (e) => e.stopPropagation(),
            children: "View Linear Issue"
          }
        ) })
      ]
    }
  );
}
function ActivityFeed({
  automationId,
  types,
  live = false,
  maxEvents = 50,
  className
}) {
  const selectedAutomationId = useAutomationUIStore((state) => state.selectedAutomationId);
  const effectiveAutomationId = automationId ?? selectedAutomationId;
  const params = {
    automationId: effectiveAutomationId ?? void 0,
    types,
    limit: 20
  };
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteActivity(params);
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-3", className), children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-16 w-full" }, i)) });
  }
  if (error) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Failed to load activity",
        description: error.message,
        className
      }
    );
  }
  const allEvents = data?.pages.flatMap((page) => page.activities) ?? [];
  const events = allEvents.slice(0, maxEvents);
  if (!events.length) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No activity",
        description: "No activity has been recorded yet.",
        className
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    live && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end mb-2", children: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full", children: [
      /* @__PURE__ */ jsx("span", { className: "w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" }),
      "Live"
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: "space-y-2", children: events.map((event) => /* @__PURE__ */ jsx(ActivityEventCard, { event }, event.id)) }),
    hasNextPage && events.length < maxEvents && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: () => fetchNextPage(),
        disabled: isFetchingNextPage,
        className: cn(
          "w-full py-2 text-sm text-center rounded-lg transition-colors",
          "text-slate-600 dark:text-slate-400",
          "hover:bg-slate-100 dark:hover:bg-slate-800",
          "disabled:opacity-50 disabled:cursor-not-allowed"
        ),
        children: isFetchingNextPage ? "Loading..." : "Load more"
      }
    )
  ] });
}
function ActivityEventCard({ event }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50", children: [
    /* @__PURE__ */ jsx("div", { className: cn(
      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium",
      getEventColor(event.type)
    ), children: getEventIcon(event.type) }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mb-0.5", children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium text-sm text-slate-800 dark:text-slate-200", children: event.title || formatEventType(event.type) }),
        /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: formatTimeAgo(event.timestamp) })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-600 dark:text-slate-400 line-clamp-2", children: event.description ?? getEventDescription(event) }),
      event.metadata && Object.keys(event.metadata).length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-2 flex flex-wrap gap-1", children: Object.entries(event.metadata).slice(0, 3).map(([key, value]) => /* @__PURE__ */ jsxs(
        "span",
        {
          className: "inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400",
          children: [
            key,
            ": ",
            String(value).slice(0, 20)
          ]
        },
        key
      )) })
    ] })
  ] });
}
function getEventColor(type) {
  const colors = {
    "run.started": "bg-blue-500",
    "run.completed": "bg-emerald-500",
    "run.failed": "bg-red-500",
    "automation.created": "bg-purple-500",
    "automation.updated": "bg-amber-500",
    "automation.deleted": "bg-slate-500",
    "error.created": "bg-red-500",
    "error.resolved": "bg-emerald-500"
  };
  return colors[type] ?? "bg-slate-500";
}
function getEventIcon(type) {
  const icons = {
    "run.started": "\u25B6",
    "run.completed": "\u2713",
    "run.failed": "\u2717",
    "automation.created": "+",
    "automation.updated": "\u270E",
    "automation.deleted": "-",
    "error.created": "!",
    "error.resolved": "\u2713"
  };
  return icons[type] ?? "\u2022";
}
function formatEventType(type) {
  return type.split(".").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
function getEventDescription(event) {
  const descriptions = {
    "run.started": "A new run has started",
    "run.completed": "Run completed successfully",
    "run.failed": "Run failed with an error",
    "automation.created": "New automation created",
    "automation.updated": "Automation configuration updated",
    "automation.deleted": "Automation was deleted",
    "error.created": "New error detected",
    "error.resolved": "Error was resolved"
  };
  return descriptions[event.type] ?? `Event: ${event.type}`;
}
var statusOptions = {
  run: [
    { value: "PENDING", label: "Pending" },
    { value: "RUNNING", label: "Running" },
    { value: "COMPLETED", label: "Completed" },
    { value: "FAILED", label: "Failed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "TIMEOUT", label: "Timeout" }
  ],
  error: [
    { value: "OPEN", label: "Open" },
    { value: "INVESTIGATING", label: "Investigating" },
    { value: "RESOLVED", label: "Resolved" },
    { value: "IGNORED", label: "Ignored" }
  ],
  automation: [
    { value: "ACTIVE", label: "Active" },
    { value: "PAUSED", label: "Paused" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "ERROR", label: "Error" }
  ],
  errorSeverity: [
    { value: "CRITICAL", label: "Critical" },
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" }
  ]
};
function StatusFilter({
  type,
  value = [],
  onChange,
  multiple = true,
  className
}) {
  const options = statusOptions[type] ?? [];
  const handleToggle = (optionValue) => {
    if (!onChange) return;
    if (multiple) {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    } else {
      onChange(value.includes(optionValue) ? [] : [optionValue]);
    }
  };
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-2", className), children: options.map((option) => {
    const isSelected = value.includes(option.value);
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => handleToggle(option.value),
        className: cn(
          "px-3 py-1.5 text-sm rounded-full border transition-colors",
          isSelected ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-600"
        ),
        children: option.label
      },
      option.value
    );
  }) });
}
var presetConfigs = {
  today: {
    label: "Today",
    getRange: () => {
      const now = /* @__PURE__ */ new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { start, end: now };
    }
  },
  yesterday: {
    label: "Yesterday",
    getRange: () => {
      const now = /* @__PURE__ */ new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { start, end };
    }
  },
  "7d": {
    label: "Last 7 days",
    getRange: () => {
      const now = /* @__PURE__ */ new Date();
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1e3);
      return { start, end: now };
    }
  },
  "30d": {
    label: "Last 30 days",
    getRange: () => {
      const now = /* @__PURE__ */ new Date();
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1e3);
      return { start, end: now };
    }
  },
  "90d": {
    label: "Last 90 days",
    getRange: () => {
      const now = /* @__PURE__ */ new Date();
      const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1e3);
      return { start, end: now };
    }
  }
};
function DateRangeFilter({
  value,
  onChange,
  presets = ["today", "7d", "30d", "90d"],
  selectedPreset,
  onPresetChange,
  className
}) {
  const handlePresetClick = (preset) => {
    const config = presetConfigs[preset];
    const range = config.getRange();
    onChange?.(range);
    onPresetChange?.(preset);
  };
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-2", className), children: presets.filter((p) => p !== "custom").map((preset) => {
    const config = presetConfigs[preset];
    const isSelected = selectedPreset === preset;
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => handlePresetClick(preset),
        className: cn(
          "px-3 py-1.5 text-sm rounded-full border transition-colors",
          isSelected ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-600"
        ),
        children: config.label
      },
      preset
    );
  }) });
}
function ActiveFilters({
  filters,
  onRemove,
  onClearAll,
  className
}) {
  if (!filters.length) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-2", className), children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-500 dark:text-slate-400", children: "Filters:" }),
    filters.map((filter) => /* @__PURE__ */ jsxs(
      "span",
      {
        className: "inline-flex items-center gap-1 px-2 py-1 text-sm bg-slate-100 dark:bg-slate-800 rounded-full",
        children: [
          filter.category && /* @__PURE__ */ jsxs("span", { className: "text-slate-400 dark:text-slate-500", children: [
            filter.category,
            ":"
          ] }),
          /* @__PURE__ */ jsx("span", { className: "text-slate-700 dark:text-slate-300", children: filter.label }),
          onRemove && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => onRemove(filter.id),
              className: "ml-0.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors",
              "aria-label": `Remove ${filter.label} filter`,
              children: /* @__PURE__ */ jsx(XIcon3, { className: "w-3.5 h-3.5" })
            }
          )
        ]
      },
      filter.id
    )),
    onClearAll && filters.length > 1 && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onClearAll,
        className: "text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors",
        children: "Clear all"
      }
    )
  ] });
}
function XIcon3({ className }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className,
      viewBox: "0 0 16 16",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8" })
    }
  );
}
var ActivityInternalContext = createContext(null);
function useDebouncedValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}
function ActivityProvider({
  children,
  defaultLiveMode = true,
  debounceMs = 300,
  pageSize = 30,
  maxPendingEvents = 50,
  maxMergedEvents = 100,
  automationId,
  defaultTypes = []
}) {
  const [filters, setFiltersState] = useState({
    types: defaultTypes,
    automationIds: automationId ? [automationId] : [],
    search: ""
  });
  const debouncedFilters = useDebouncedValue(filters, debounceMs);
  const [isLiveMode, setIsLiveMode] = useState(defaultLiveMode);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [mergedEvents, setMergedEvents] = useState([]);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const parentRef = useRef(null);
  const { isConnected, error: streamError } = useActivityStream({
    enabled: isLiveMode,
    onEvent: (event) => {
      setPendingEvents((prev) => [event, ...prev].slice(0, maxPendingEvents));
    },
    onError: (error2) => {
      console.error("Activity stream error:", error2);
    }
  });
  const queryParams = {
    limit: pageSize,
    types: debouncedFilters.types.length > 0 ? debouncedFilters.types : void 0,
    automationIds: debouncedFilters.automationIds.length > 0 ? debouncedFilters.automationIds : void 0,
    search: debouncedFilters.search || void 0
  };
  const {
    data,
    isLoading,
    error: fetchError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteActivity(queryParams);
  const historicalEvents = useMemo(() => {
    return data?.pages.flatMap((page) => page.activities) ?? [];
  }, [data]);
  const automationOptions = useMemo(() => {
    const seen = /* @__PURE__ */ new Map();
    for (const event of historicalEvents) {
      if (event.automationId && event.automationName && !seen.has(event.automationId)) {
        seen.set(event.automationId, event.automationName);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ value: id, label: name })).sort((a, b) => a.label.localeCompare(b.label));
  }, [historicalEvents]);
  const allEvents = useMemo(() => {
    if (isLiveMode && mergedEvents.length > 0) {
      const historicalIds = new Set(historicalEvents.map((e) => e.id));
      const uniqueMergedEvents = mergedEvents.filter((e) => !historicalIds.has(e.id));
      return [...uniqueMergedEvents, ...historicalEvents];
    }
    return historicalEvents;
  }, [historicalEvents, isLiveMode, mergedEvents]);
  const error = streamError || fetchError;
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      types: [],
      automationIds: automationId ? [automationId] : [],
      search: ""
    });
  }, [automationId]);
  const hasActiveFilters = filters.types.length > 0 || filters.automationIds.length > 0 && !automationId || filters.search.length > 0;
  const toggleLiveMode = useCallback(() => {
    if (isLiveMode) {
      refetch();
    } else {
      setPendingEvents([]);
      setMergedEvents([]);
    }
    setIsLiveMode(!isLiveMode);
  }, [isLiveMode, refetch]);
  const mergePendingEvents = useCallback(() => {
    setMergedEvents((prev) => [...pendingEvents, ...prev].slice(0, maxMergedEvents));
    setPendingEvents([]);
  }, [pendingEvents, maxMergedEvents]);
  const clearMergedEvents = useCallback(() => {
    setMergedEvents([]);
  }, []);
  const value = {
    events: allEvents,
    pendingEvents,
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLiveMode,
    isConnected,
    toggleLiveMode,
    mergePendingEvents,
    clearMergedEvents,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    fetchNextPage,
    refetch,
    expandedEventId,
    setExpandedEventId,
    parentRef,
    automationOptions
  };
  return /* @__PURE__ */ jsx(ActivityInternalContext.Provider, { value, children });
}
function useActivityInternal() {
  const context = useContext(ActivityInternalContext);
  if (!context) {
    throw new Error("Activity hooks must be used within an Activity.Root component");
  }
  return context;
}
function useActivityList() {
  const ctx = useActivityInternal();
  return {
    events: ctx.events,
    isLoading: ctx.isLoading,
    error: ctx.error,
    hasNextPage: ctx.hasNextPage,
    isFetchingNextPage: ctx.isFetchingNextPage,
    fetchNextPage: ctx.fetchNextPage,
    refetch: ctx.refetch,
    parentRef: ctx.parentRef
  };
}
function useActivityFilters() {
  const ctx = useActivityInternal();
  return {
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters,
    automationOptions: ctx.automationOptions
  };
}
function useActivityLive() {
  const ctx = useActivityInternal();
  return {
    isLiveMode: ctx.isLiveMode,
    isConnected: ctx.isConnected,
    toggleLiveMode: ctx.toggleLiveMode,
    pendingCount: ctx.pendingEvents.length,
    pendingEvents: ctx.pendingEvents,
    mergePendingEvents: ctx.mergePendingEvents,
    clearMergedEvents: ctx.clearMergedEvents
  };
}
function useActivityExpanded() {
  const ctx = useActivityInternal();
  return {
    expandedEventId: ctx.expandedEventId,
    setExpandedEventId: ctx.setExpandedEventId,
    isExpanded: (eventId) => ctx.expandedEventId === eventId,
    toggleExpanded: (eventId) => ctx.setExpandedEventId(ctx.expandedEventId === eventId ? null : eventId)
  };
}
function useActivity() {
  const ctx = useActivityInternal();
  return {
    // List
    events: ctx.events,
    isLoading: ctx.isLoading,
    error: ctx.error,
    // Filters
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters,
    // Live mode
    isLiveMode: ctx.isLiveMode,
    isConnected: ctx.isConnected,
    toggleLiveMode: ctx.toggleLiveMode,
    pendingCount: ctx.pendingEvents.length,
    mergePendingEvents: ctx.mergePendingEvents,
    // Pagination
    hasNextPage: ctx.hasNextPage,
    isFetchingNextPage: ctx.isFetchingNextPage,
    fetchNextPage: ctx.fetchNextPage,
    refetch: ctx.refetch
  };
}

// src/components/activity/activityUtils.ts
var EVENT_TYPE_OPTIONS = [
  { value: "run_started", label: "Run Started", group: "Runs" },
  { value: "run_completed", label: "Run Completed", group: "Runs" },
  { value: "run_failed", label: "Run Failed", group: "Runs" },
  { value: "run_cancelled", label: "Run Cancelled", group: "Runs" },
  { value: "error_occurred", label: "Error Occurred", group: "Errors" },
  { value: "error_resolved", label: "Error Resolved", group: "Errors" },
  { value: "test_passed", label: "Test Passed", group: "Tests" },
  { value: "test_failed", label: "Test Failed", group: "Tests" },
  { value: "automation_paused", label: "Automation Paused", group: "Lifecycle" },
  { value: "automation_resumed", label: "Automation Resumed", group: "Lifecycle" },
  { value: "automation_created", label: "Automation Created", group: "Lifecycle" },
  { value: "automation_deleted", label: "Automation Deleted", group: "Lifecycle" }
];
var EVENT_TYPE_PRESETS = [
  { label: "All Failures", values: ["run_failed", "error_occurred", "test_failed"] },
  { label: "All Runs", values: ["run_started", "run_completed", "run_failed", "run_cancelled"] },
  { label: "All Tests", values: ["test_passed", "test_failed"] }
];
var EVENT_CONFIGS = {
  run_started: {
    label: "Run started",
    iconName: "play",
    variant: "info",
    iconVariant: "info"
  },
  run_completed: {
    label: "Run completed",
    iconName: "check",
    variant: "success",
    iconVariant: "success"
  },
  run_failed: {
    label: "Run failed",
    iconName: "x",
    variant: "danger",
    iconVariant: "danger"
  },
  run_cancelled: {
    label: "Run cancelled",
    iconName: "x",
    variant: "default",
    iconVariant: "default"
  },
  error_occurred: {
    label: "Error occurred",
    iconName: "alert",
    variant: "warning",
    iconVariant: "warning"
  },
  error_resolved: {
    label: "Error resolved",
    iconName: "check",
    variant: "success",
    iconVariant: "success"
  },
  test_passed: {
    label: "Test passed",
    iconName: "flask",
    variant: "success",
    iconVariant: "success"
  },
  test_failed: {
    label: "Test failed",
    iconName: "flask",
    variant: "danger",
    iconVariant: "danger"
  },
  automation_paused: {
    label: "Automation paused",
    iconName: "pause",
    variant: "warning",
    iconVariant: "warning"
  },
  automation_resumed: {
    label: "Automation resumed",
    iconName: "play",
    variant: "success",
    iconVariant: "success"
  },
  automation_created: {
    label: "Automation created",
    iconName: "workflow",
    variant: "info",
    iconVariant: "info"
  },
  automation_deleted: {
    label: "Automation deleted",
    iconName: "trash",
    variant: "default",
    iconVariant: "default"
  }
};
function getEventConfig(type) {
  return EVENT_CONFIGS[type] || {
    label: type,
    iconName: "activity",
    variant: "default",
    iconVariant: "default"
  };
}
function formatDuration3(ms) {
  if (ms < 1e3) return `${ms}ms`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)}s`;
  return `${Math.floor(ms / 6e4)}m ${Math.round(ms % 6e4 / 1e3)}s`;
}
function formatTimeAgo2(dateString) {
  const date = new Date(dateString);
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
function formatTimestamp3(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
}
function getEventDescription2(event) {
  const metadata = event.metadata;
  const parts = [];
  switch (event.type) {
    case "run_started": {
      if (metadata?.trigger) parts.push(String(metadata.trigger));
      if (metadata?.status) parts.push(String(metadata.status));
      return parts.length > 0 ? parts.join(" \u2022 ") : "Starting execution";
    }
    case "run_completed": {
      if (metadata?.duration) parts.push(formatDuration3(Number(metadata.duration)));
      if (metadata?.stepCount) parts.push(`${metadata.stepCount} steps`);
      if (metadata?.status) parts.push(String(metadata.status));
      return parts.length > 0 ? parts.join(" \u2022 ") : "Completed successfully";
    }
    case "run_failed": {
      if (metadata?.duration) parts.push(formatDuration3(Number(metadata.duration)));
      const errMsg = metadata?.errorMessage ? String(metadata.errorMessage) : "Unknown error";
      const truncatedError = errMsg.length > 60 ? errMsg.slice(0, 60) + "..." : errMsg;
      parts.push(truncatedError);
      return parts.join(" \u2022 ");
    }
    case "run_cancelled": {
      if (metadata?.duration) parts.push(formatDuration3(Number(metadata.duration)));
      parts.push("Cancelled by user");
      return parts.join(" \u2022 ");
    }
    case "error_occurred": {
      if (metadata?.severity) parts.push(String(metadata.severity));
      if (metadata?.occurrenceCount) parts.push(`${metadata.occurrenceCount} occurrences`);
      if (metadata?.errorType) parts.push(String(metadata.errorType));
      const errorMsg = metadata?.errorMessage ? String(metadata.errorMessage) : "";
      if (errorMsg && parts.length < 2) {
        const truncated = errorMsg.length > 50 ? errorMsg.slice(0, 50) + "..." : errorMsg;
        parts.push(truncated);
      }
      return parts.length > 0 ? parts.join(" \u2022 ") : "Error detected";
    }
    case "error_resolved": {
      if (metadata?.errorType) parts.push(String(metadata.errorType));
      parts.push("Marked as resolved");
      return parts.join(" \u2022 ");
    }
    case "test_passed": {
      const testName = metadata?.testCaseName || metadata?.testName;
      if (testName) parts.push(String(testName));
      if (metadata?.durationMs) parts.push(formatDuration3(Number(metadata.durationMs)));
      parts.push("passed");
      return parts.join(" \u2022 ");
    }
    case "test_failed": {
      const testNameFailed = metadata?.testCaseName || metadata?.testName;
      if (testNameFailed) parts.push(String(testNameFailed));
      if (metadata?.durationMs) parts.push(formatDuration3(Number(metadata.durationMs)));
      parts.push("failed");
      return parts.join(" \u2022 ");
    }
    case "automation_paused":
      return "Automation paused by user";
    case "automation_resumed":
      return "Automation resumed";
    case "automation_created":
      return "New automation created";
    case "automation_deleted":
      return "Automation deleted";
    default:
      return event.description || event.type;
  }
}
function getInlineChips(event) {
  const metadata = event.metadata;
  const chips = [];
  switch (event.type) {
    case "run_started":
    case "run_completed":
    case "run_failed":
    case "run_cancelled": {
      if (metadata?.status) {
        const status = String(metadata.status);
        const statusColors = {
          completed: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
          failed: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
          running: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
          cancelled: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
          pending: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
        };
        chips.push({
          label: "Status",
          value: status,
          className: statusColors[status] || "bg-slate-100 dark:bg-slate-800"
        });
      }
      if (metadata?.trigger) {
        chips.push({
          label: "Trigger",
          value: String(metadata.trigger),
          className: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
        });
      }
      if (metadata?.duration) {
        chips.push({
          label: "Duration",
          value: formatDuration3(Number(metadata.duration)),
          className: "bg-slate-100 dark:bg-slate-800"
        });
      }
      break;
    }
    case "error_occurred":
    case "error_resolved": {
      if (metadata?.severity) {
        const severity = String(metadata.severity);
        const severityColors = {
          CRITICAL: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
          HIGH: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
          MEDIUM: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400",
          LOW: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
        };
        chips.push({
          label: "Severity",
          value: severity,
          className: severityColors[severity] || "bg-slate-100 dark:bg-slate-800"
        });
      }
      if (metadata?.occurrenceCount) {
        chips.push({
          label: "Count",
          value: String(metadata.occurrenceCount),
          className: "bg-slate-100 dark:bg-slate-800"
        });
      }
      break;
    }
    case "test_passed":
    case "test_failed": {
      chips.push({
        label: "Result",
        value: event.type === "test_passed" ? "passed" : "failed",
        className: event.type === "test_passed" ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
      });
      if (metadata?.durationMs) {
        chips.push({
          label: "Duration",
          value: formatDuration3(Number(metadata.durationMs)),
          className: "bg-slate-100 dark:bg-slate-800"
        });
      }
      break;
    }
  }
  return chips;
}
var ICONS = {
  play: Play,
  check: CheckCircle2,
  x: XCircle,
  alert: AlertTriangle,
  flask: FlaskConical,
  pause: Pause,
  workflow: Workflow,
  trash: Trash2,
  activity: Activity$1
};
function ActivityHeader({
  title = "Activity Log",
  subtitle = "Real-time feed of automation events",
  children,
  className
}) {
  const { isLiveMode, isConnected, toggleLiveMode } = useActivityLive();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-shrink-0", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white", children: title }),
      /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx(
        ConnectionStatus,
        {
          state: isLiveMode ? isConnected ? "connected" : "connecting" : "disconnected",
          labels: {
            connected: "Live",
            disconnected: "Paused",
            connecting: "Connecting..."
          }
        }
      ),
      /* @__PURE__ */ jsx(Button, { variant: isLiveMode ? "primary" : "secondary", onClick: toggleLiveMode, children: isLiveMode ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Wifi, { className: "mr-2 h-4 w-4" }),
        "Live"
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(WifiOff, { className: "mr-2 h-4 w-4" }),
        "Paused"
      ] }) }),
      children
    ] })
  ] });
}
function ActivityFilters({
  showTypeFilter = true,
  showAutomationFilter = true,
  showSearch = true,
  showRefresh = true,
  showClearMerged = true,
  children,
  className
}) {
  const { filters, setFilters, clearFilters, hasActiveFilters, automationOptions } = useActivityFilters();
  const { isLiveMode, clearMergedEvents } = useActivityLive();
  const { events, isLoading, refetch } = useActivityList();
  const hasMergedEvents = isLiveMode && events.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3 flex-shrink-0", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 sm:flex-row sm:items-center", children: [
      showTypeFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-56", children: /* @__PURE__ */ jsx(
        MultiSelect,
        {
          options: EVENT_TYPE_OPTIONS,
          value: filters.types,
          onChange: (types) => setFilters({ types }),
          placeholder: "Event types...",
          searchable: true,
          presets: EVENT_TYPE_PRESETS
        }
      ) }),
      showAutomationFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-56", children: /* @__PURE__ */ jsx(
        MultiSelect,
        {
          options: automationOptions,
          value: filters.automationIds,
          onChange: (automationIds) => setFilters({ automationIds }),
          placeholder: "Automations...",
          searchable: true
        }
      ) }),
      showSearch && /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx(
        SearchInput,
        {
          value: filters.search,
          onChange: (e) => setFilters({ search: e.target.value }),
          onClear: () => setFilters({ search: "" }),
          placeholder: "Search events..."
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        showClearMerged && isLiveMode && hasMergedEvents && /* @__PURE__ */ jsxs(Button, { variant: "ghost", onClick: clearMergedEvents, children: [
          /* @__PURE__ */ jsx(Trash2, { className: "mr-2 h-4 w-4" }),
          "Clear"
        ] }),
        showRefresh && !isLiveMode && /* @__PURE__ */ jsxs(Button, { variant: "secondary", onClick: () => refetch(), disabled: isLoading, children: [
          /* @__PURE__ */ jsx(RefreshCw, { className: `mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}` }),
          "Refresh"
        ] }),
        children
      ] })
    ] }),
    hasActiveFilters && /* @__PURE__ */ jsx(
      MultiSelectChips,
      {
        value: [...filters.types, ...filters.automationIds],
        options: [...EVENT_TYPE_OPTIONS, ...automationOptions],
        onRemove: (v) => {
          setFilters({
            types: filters.types.filter((t) => t !== v),
            automationIds: filters.automationIds.filter((a) => a !== v)
          });
        },
        onClearAll: clearFilters
      }
    )
  ] });
}
function ActivityPendingBadge({ className }) {
  const { pendingCount, mergePendingEvents } = useActivityLive();
  if (pendingCount === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(LiveBadgeContainer, { className, children: /* @__PURE__ */ jsx(LiveBadge, { count: pendingCount, onClick: mergePendingEvents }) });
}
function ActivityList({
  renderEvent,
  estimatedRowHeight = 100,
  overscan = 5,
  emptyLoadingTitle = "Waiting for activity...",
  emptyTitle = "No activity found",
  emptyDescription,
  className
}) {
  const { events, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage, refetch, parentRef } = useActivityList();
  const { isLiveMode } = useActivityLive();
  const { hasActiveFilters } = useActivityFilters();
  const { expandedEventId, setExpandedEventId } = useActivityExpanded();
  const virtualizer = useVirtualizer({
    count: events.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan
  });
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    };
    scrollElement.addEventListener("scroll", handleScroll);
    return () => scrollElement.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, parentRef]);
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load activity" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500 dark:text-slate-400", children: error.message }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading && !isLiveMode) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsx("div", { className: "p-4 space-y-3", children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(ActivityItemSkeleton, {}, i)) }) });
  }
  if (events.length === 0) {
    const description = emptyDescription || (isLiveMode ? "Events will appear here as they happen" : hasActiveFilters ? "Try adjusting your filters" : "Try adjusting your time range");
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
        title: isLiveMode ? emptyLoadingTitle : emptyTitle,
        description,
        className
      }
    );
  }
  return /* @__PURE__ */ jsx(Card, { className: cn("flex-1 flex flex-col min-h-0 overflow-hidden", className), children: /* @__PURE__ */ jsxs("div", { ref: parentRef, className: "flex-1 overflow-auto", children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative"
        },
        children: virtualizer.getVirtualItems().map((virtualItem) => {
          const event = events[virtualItem.index];
          if (!event) return null;
          return /* @__PURE__ */ jsx(
            "div",
            {
              "data-index": virtualItem.index,
              ref: virtualizer.measureElement,
              style: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`
              },
              children: /* @__PURE__ */ jsx("div", { className: "pb-2", children: renderEvent ? renderEvent(event, virtualItem.index) : /* @__PURE__ */ jsx(
                DefaultActivityEventCard,
                {
                  event,
                  expanded: expandedEventId === event.id,
                  onExpandedChange: (expanded) => setExpandedEventId(expanded ? event.id : null)
                }
              ) })
            },
            virtualItem.key
          );
        })
      }
    ),
    isFetchingNextPage && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center py-4", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "h-6 w-6 animate-spin text-slate-400" }),
      /* @__PURE__ */ jsx("span", { className: "ml-2 text-sm text-slate-500", children: "Loading more..." })
    ] }),
    !hasNextPage && events.length > 0 && /* @__PURE__ */ jsx("div", { className: "text-center py-4 text-sm text-slate-400", children: "No more events to load" })
  ] }) });
}
function ActivityItemSkeleton() {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4 p-3", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-8 rounded-full flex-shrink-0" }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-3/4" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-1/2" })
    ] }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-16 flex-shrink-0" })
  ] });
}
function DefaultActivityEventCard({ event, expanded, onExpandedChange }) {
  const config = getEventConfig(event.type);
  const IconComponent = ICONS[config.iconName];
  const metadata = event.metadata;
  const runId = metadata?.runId ? String(metadata.runId) : null;
  const inlineChips = getInlineChips(event);
  return /* @__PURE__ */ jsx(
    EventCard,
    {
      variant: config.variant,
      icon: /* @__PURE__ */ jsx(EventCardIcon, { variant: config.iconVariant, children: /* @__PURE__ */ jsx(IconComponent, { className: "w-4 h-4" }) }),
      title: config.label,
      subtitle: event.automationName,
      timestamp: formatTimeAgo2(event.timestamp),
      summary: /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx("div", { children: getEventDescription2(event) }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs", children: [
          runId && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded font-mono", children: [
            /* @__PURE__ */ jsx("span", { className: "text-slate-500 dark:text-slate-400", children: "ID:" }),
            /* @__PURE__ */ jsx(CopyableId, { id: runId })
          ] }),
          inlineChips.map((chip, i) => /* @__PURE__ */ jsxs("span", { className: `inline-flex items-center gap-1 px-2 py-0.5 rounded ${chip.className}`, children: [
            /* @__PURE__ */ jsxs("span", { className: "text-slate-500 dark:text-slate-400", children: [
              chip.label,
              ":"
            ] }),
            /* @__PURE__ */ jsx("span", { className: "font-medium", children: chip.value })
          ] }, i)),
          /* @__PURE__ */ jsx("span", { className: "text-slate-400 dark:text-slate-500", children: formatTimestamp3(event.timestamp) })
        ] })
      ] }),
      expandable: true,
      expanded,
      onExpandedChange,
      details: /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsxs(EventCardMeta, { children: [
          Boolean(metadata?.runId) && /* @__PURE__ */ jsx(EventCardMetaItem, { label: "Run ID", value: /* @__PURE__ */ jsx(CopyableId, { id: String(metadata?.runId) }) }),
          Boolean(metadata?.duration) && /* @__PURE__ */ jsx(EventCardMetaItem, { label: "Duration", value: `${String(metadata?.duration)}ms` }),
          Boolean(metadata?.trigger) && /* @__PURE__ */ jsx(EventCardMetaItem, { label: "Trigger", value: String(metadata?.trigger) }),
          Boolean(metadata?.severity) && /* @__PURE__ */ jsx(EventCardMetaItem, { label: "Severity", value: String(metadata?.severity) }),
          Boolean(metadata?.occurrenceCount) && /* @__PURE__ */ jsx(EventCardMetaItem, { label: "Occurrences", value: String(metadata?.occurrenceCount) }),
          Boolean(metadata?.testCaseName) && /* @__PURE__ */ jsx(EventCardMetaItem, { label: "Test Case", value: String(metadata?.testCaseName) })
        ] }),
        Boolean(metadata?.errorMessage) && /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: "Error" }),
          /* @__PURE__ */ jsx("p", { className: "text-red-600 dark:text-red-400 text-sm font-mono mt-1", children: String(metadata?.errorMessage) })
        ] })
      ] })
    }
  );
}
function CopyableId({ id }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2e3);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    },
    [id]
  );
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-blue-600 dark:text-blue-400", children: id }),
    /* @__PURE__ */ jsx(
      "button",
      {
        onClick: handleCopy,
        className: "p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors",
        title: copied ? "Copied!" : "Copy",
        children: copied ? /* @__PURE__ */ jsx(Check, { className: "w-3.5 h-3.5 text-green-500" }) : /* @__PURE__ */ jsx(Copy, { className: "w-3.5 h-3.5" })
      }
    )
  ] });
}
function ActivityRoot({ children, className, ...providerProps }) {
  return /* @__PURE__ */ jsx(ActivityProvider, { ...providerProps, children: /* @__PURE__ */ jsx("div", { className: cn("flex flex-col h-full space-y-6", className), children }) });
}
var Activity = {
  /**
   * Root container - wraps children with ActivityProvider
   * @example
   * <Activity.Root automationId="abc">
   *   <Activity.Header />
   *   <Activity.List />
   * </Activity.Root>
   */
  Root: ActivityRoot,
  /**
   * Header with title, subtitle, and live mode toggle
   * @example
   * <Activity.Header title="Events" subtitle="Real-time updates" />
   */
  Header: ActivityHeader,
  /**
   * Filter controls for event types, automations, and search
   * @example
   * <Activity.Filters showTypeFilter showAutomationFilter showSearch />
   */
  Filters: ActivityFilters,
  /**
   * Badge showing pending events count with merge action
   * @example
   * <Activity.PendingBadge />
   */
  PendingBadge: ActivityPendingBadge,
  /**
   * Virtualized list of events with infinite scroll
   * @example
   * <Activity.List renderEvent={(event) => <CustomCard event={event} />} />
   */
  List: ActivityList,
  /**
   * Loading skeleton for event cards
   * @example
   * {isLoading && <Activity.ItemSkeleton />}
   */
  ItemSkeleton: ActivityItemSkeleton
};
var ErrorsInternalContext = createContext(null);
function ErrorsProvider({
  children,
  automationId,
  defaultStatus = [],
  defaultSeverity = [],
  pageSize: initialPageSize = 20
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    status: defaultStatus,
    severity: defaultSeverity,
    automationIds: automationId ? [automationId] : [],
    search: ""
  });
  const [selectedErrorId, setSelectedErrorId] = useState(null);
  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: filters.status.length > 0 ? filters.status.join(",") : void 0,
      severity: filters.severity.length > 0 ? filters.severity.join(",") : void 0,
      automationId: filters.automationIds[0] || automationId
    }),
    [page, pageSize, filters, automationId]
  );
  const { data, isLoading, error: fetchError, refetch } = useErrors(queryParams);
  const updateStatusMutation = useUpdateErrorStatus();
  const updateSeverityMutation = useUpdateErrorSeverity();
  const syncMutation = useSyncErrors();
  const errors = data?.groups ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const selectedError = useMemo(() => {
    if (!selectedErrorId) return null;
    return errors.find((e) => e.id === selectedErrorId) ?? null;
  }, [selectedErrorId, errors]);
  const openCount = useMemo(() => errors.filter((e) => e.status === "OPEN").length, [errors]);
  const criticalCount = useMemo(() => errors.filter((e) => e.severity === "CRITICAL").length, [errors]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      status: [],
      severity: [],
      automationIds: automationId ? [automationId] : [],
      search: ""
    });
    setPage(1);
  }, [automationId]);
  const hasActiveFilters = filters.status.length > 0 || filters.severity.length > 0 || filters.automationIds.length > 0 && !automationId || filters.search.length > 0;
  const updateStatus = useCallback(
    async (errorId, status) => {
      await updateStatusMutation.mutateAsync({ id: errorId, status });
    },
    [updateStatusMutation]
  );
  const updateSeverity = useCallback(
    async (errorId, severity) => {
      await updateSeverityMutation.mutateAsync({ id: errorId, severity });
    },
    [updateSeverityMutation]
  );
  const syncErrors = useCallback(async () => {
    await syncMutation.mutateAsync();
  }, [syncMutation]);
  const value = {
    errors,
    total,
    isLoading,
    error: fetchError instanceof Error ? fetchError : fetchError ? new Error(String(fetchError)) : null,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedErrorId,
    setSelectedErrorId,
    selectedError,
    refetch,
    updateStatus,
    updateSeverity,
    syncErrors,
    isSyncing: syncMutation.isPending,
    openCount,
    criticalCount
  };
  return /* @__PURE__ */ jsx(ErrorsInternalContext.Provider, { value, children });
}
function useErrorsInternal() {
  const context = useContext(ErrorsInternalContext);
  if (!context) {
    throw new Error("Errors hooks must be used within an Errors.Root component");
  }
  return context;
}
function useErrorsList() {
  const ctx = useErrorsInternal();
  return {
    errors: ctx.errors,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    setPageSize: ctx.setPageSize,
    refetch: ctx.refetch
  };
}
function useErrorsFilters() {
  const ctx = useErrorsInternal();
  return {
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters
  };
}
function useErrorsSelection() {
  const ctx = useErrorsInternal();
  const isSelected = useCallback((errorId) => ctx.selectedErrorId === errorId, [ctx.selectedErrorId]);
  const toggleSelected = useCallback(
    (errorId) => {
      ctx.setSelectedErrorId(ctx.selectedErrorId === errorId ? null : errorId);
    },
    [ctx.selectedErrorId, ctx.setSelectedErrorId]
  );
  return {
    selectedErrorId: ctx.selectedErrorId,
    selectedError: ctx.selectedError,
    setSelectedErrorId: ctx.setSelectedErrorId,
    isSelected,
    toggleSelected
  };
}
function useErrorsActions() {
  const ctx = useErrorsInternal();
  return {
    updateStatus: ctx.updateStatus,
    updateSeverity: ctx.updateSeverity,
    syncErrors: ctx.syncErrors,
    isSyncing: ctx.isSyncing,
    refetch: ctx.refetch
  };
}
function useErrorsStats() {
  const ctx = useErrorsInternal();
  return {
    total: ctx.total,
    openCount: ctx.openCount,
    criticalCount: ctx.criticalCount
  };
}
function useErrors3() {
  const ctx = useErrorsInternal();
  return {
    // List
    errors: ctx.errors,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    // Pagination
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    // Filters
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters,
    // Selection
    selectedErrorId: ctx.selectedErrorId,
    selectedError: ctx.selectedError,
    setSelectedErrorId: ctx.setSelectedErrorId,
    // Actions
    updateStatus: ctx.updateStatus,
    syncErrors: ctx.syncErrors,
    isSyncing: ctx.isSyncing,
    refetch: ctx.refetch,
    // Stats
    openCount: ctx.openCount,
    criticalCount: ctx.criticalCount
  };
}

// src/components/errors/errorsUtils.ts
var SEVERITY_CONFIG = {
  CRITICAL: {
    label: "Critical",
    variant: "danger",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/30",
    iconName: "alert"
  },
  HIGH: {
    label: "High",
    variant: "danger",
    color: "text-red-600 dark:text-red-400",
    bgColor: "bg-red-50 dark:bg-red-900/20",
    iconName: "alert"
  },
  MEDIUM: {
    label: "Medium",
    variant: "warning",
    color: "text-amber-700 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    iconName: "warning"
  },
  LOW: {
    label: "Low",
    variant: "default",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "info"
  }
};
var STATUS_CONFIG = {
  OPEN: {
    label: "Open",
    variant: "danger",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/30"
  },
  INVESTIGATING: {
    label: "Investigating",
    variant: "warning",
    color: "text-amber-700 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30"
  },
  RESOLVED: {
    label: "Resolved",
    variant: "success",
    color: "text-green-700 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900/30"
  },
  IGNORED: {
    label: "Ignored",
    variant: "default",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800"
  }
};
var SEVERITY_OPTIONS = [
  { value: "CRITICAL", label: "Critical" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" }
];
var STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "INVESTIGATING", label: "Investigating" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "IGNORED", label: "Ignored" }
];
var STATUS_PRESETS = [
  { label: "Active", values: ["OPEN", "INVESTIGATING"] },
  { label: "Closed", values: ["RESOLVED", "IGNORED"] }
];
var SEVERITY_PRESETS = [
  { label: "Critical & High", values: ["CRITICAL", "HIGH"] },
  { label: "All", values: ["CRITICAL", "HIGH", "MEDIUM", "LOW"] }
];
function getSeverityConfig(severity) {
  return SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.LOW;
}
function getStatusConfig(status) {
  return STATUS_CONFIG[status] ?? STATUS_CONFIG.OPEN;
}
function formatTimeAgo3(date) {
  const now = /* @__PURE__ */ new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}
function formatTimestamp4(date) {
  const d = new Date(date);
  return d.toLocaleString();
}
function truncateMessage(message, maxLength = 100) {
  if (message.length <= maxLength) return message;
  return message.slice(0, maxLength) + "...";
}
function getErrorTypeLabel(errorType) {
  return errorType.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim();
}
function getSeverityBorderColor(severity) {
  switch (severity) {
    case "CRITICAL":
      return "border-l-red-600 dark:border-l-red-500";
    case "HIGH":
      return "border-l-red-400 dark:border-l-red-400";
    case "MEDIUM":
      return "border-l-amber-500 dark:border-l-amber-400";
    case "LOW":
      return "border-l-slate-400 dark:border-l-slate-500";
    default:
      return "border-l-slate-300 dark:border-l-slate-600";
  }
}
function SeverityIcon({ severity, className }) {
  const config = getSeverityConfig(severity);
  switch (config.iconName) {
    case "alert":
      return /* @__PURE__ */ jsx(AlertTriangle, { className });
    case "warning":
      return /* @__PURE__ */ jsx(AlertCircle, { className });
    case "info":
    default:
      return /* @__PURE__ */ jsx(Info, { className });
  }
}
function ErrorsHeader({
  title = "Error Groups",
  subtitle = "Monitor and manage automation errors",
  children,
  className
}) {
  const { total, openCount, criticalCount } = useErrorsStats();
  const { syncErrors, isSyncing, refetch } = useErrorsActions();
  const { isLoading } = useErrorsList();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-shrink-0", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white", children: title }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs(Badge, { variant: "default", children: [
            total,
            " total"
          ] }),
          openCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "warning", children: [
            openCount,
            " open"
          ] }),
          criticalCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "danger", children: [
            criticalCount,
            " critical"
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "secondary", onClick: () => refetch(), disabled: isLoading, children: [
        /* @__PURE__ */ jsx(RefreshCw, { className: cn("mr-2 h-4 w-4", isLoading && "animate-spin") }),
        "Refresh"
      ] }),
      /* @__PURE__ */ jsx(Button, { variant: "secondary", onClick: () => syncErrors(), disabled: isSyncing, children: isSyncing ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }),
        "Syncing..."
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(RefreshCw, { className: "mr-2 h-4 w-4" }),
        "Sync Errors"
      ] }) }),
      children
    ] })
  ] });
}
function ErrorsFilters({
  showStatusFilter = true,
  showSeverityFilter = true,
  showSearch = true,
  children,
  className
}) {
  const { filters, setFilters, clearFilters, hasActiveFilters } = useErrorsFilters();
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-3 flex-shrink-0", className), children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 sm:flex-row sm:items-center", children: [
    showStatusFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-48", children: /* @__PURE__ */ jsx(
      MultiSelect,
      {
        options: STATUS_OPTIONS,
        value: filters.status,
        onChange: (status) => setFilters({ status }),
        placeholder: "Status...",
        presets: STATUS_PRESETS
      }
    ) }),
    showSeverityFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-48", children: /* @__PURE__ */ jsx(
      MultiSelect,
      {
        options: SEVERITY_OPTIONS,
        value: filters.severity,
        onChange: (severity) => setFilters({ severity }),
        placeholder: "Severity...",
        presets: SEVERITY_PRESETS
      }
    ) }),
    showSearch && /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx(
      SearchInput,
      {
        value: filters.search,
        onChange: (e) => setFilters({ search: e.target.value }),
        onClear: () => setFilters({ search: "" }),
        placeholder: "Search errors..."
      }
    ) }),
    hasActiveFilters && /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: clearFilters, children: "Clear filters" }),
    children
  ] }) });
}
function ErrorsList({
  renderError,
  emptyTitle = "No errors found",
  emptyDescription = "Great news! No errors match your filters.",
  className
}) {
  const { errors, isLoading, error, refetch } = useErrorsList();
  const { hasActiveFilters } = useErrorsFilters();
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load errors" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500 dark:text-slate-400", children: error.message }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-3", className), children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(ErrorCardSkeleton, {}, i)) });
  }
  if (errors.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
        title: emptyTitle,
        description: hasActiveFilters ? "Try adjusting your filters" : emptyDescription,
        className
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-3", className), children: errors.map(
    (err, index) => renderError ? renderError(err, index) : /* @__PURE__ */ jsx(DefaultErrorCard, { error: err }, err.id)
  ) });
}
function ErrorsPagination({ className }) {
  const { page, pageSize, totalPages, total, setPage } = useErrorsList();
  if (totalPages <= 1) return null;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "text-sm text-slate-500 dark:text-slate-400", children: [
      "Showing ",
      startItem,
      "-",
      endItem,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page - 1), disabled: page <= 1, children: [
        /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" }),
        "Previous"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: [
        "Page ",
        page,
        " of ",
        totalPages
      ] }),
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page + 1), disabled: page >= totalPages, children: [
        "Next",
        /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
      ] })
    ] })
  ] });
}
function ErrorCardSkeleton() {
  return /* @__PURE__ */ jsx(Card, { className: "p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-10 w-10 rounded-full flex-shrink-0" }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-20" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-16" })
      ] }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-3/4" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-1/2" })
    ] }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-16 flex-shrink-0" })
  ] }) });
}
function DefaultErrorCard({ error }) {
  const { isSelected, toggleSelected } = useErrorsSelection();
  const selected = isSelected(error.id);
  const severityConfig2 = getSeverityConfig(error.severity);
  const statusConfig5 = getStatusConfig(error.status);
  return /* @__PURE__ */ jsx(
    Card,
    {
      className: cn(
        "p-4 cursor-pointer transition-all border-l-4",
        getSeverityBorderColor(error.severity),
        selected && "ring-2 ring-blue-500 dark:ring-blue-400"
      ),
      onClick: () => toggleSelected(error.id),
      children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4", children: [
        /* @__PURE__ */ jsx("div", { className: cn("p-2 rounded-full", severityConfig2.bgColor), children: /* @__PURE__ */ jsx(SeverityIcon, { severity: error.severity, className: cn("h-5 w-5", severityConfig2.color) }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
            /* @__PURE__ */ jsx(Badge, { className: cn(severityConfig2.bgColor, severityConfig2.color), children: severityConfig2.label }),
            /* @__PURE__ */ jsx(Badge, { className: cn(statusConfig5.bgColor, statusConfig5.color), children: statusConfig5.label }),
            /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-slate-900 dark:text-white", children: error.errorType })
          ] }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-600 dark:text-slate-300 truncate", children: truncateMessage(error.errorMessage) }),
          /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400", children: [
            error.automationName && /* @__PURE__ */ jsx("span", { children: error.automationName }),
            error.nodeName && /* @__PURE__ */ jsxs("span", { children: [
              "Node: ",
              error.nodeName,
              " (",
              error.nodeType,
              ")"
            ] }),
            /* @__PURE__ */ jsxs("span", { children: [
              error.occurrenceCount,
              " occurrences"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-2 flex-shrink-0", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: formatTimeAgo3(error.lastSeenAt) }),
          error.linearIssueUrl && /* @__PURE__ */ jsxs(
            "a",
            {
              href: error.linearIssueUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              className: "text-blue-600 dark:text-blue-400 hover:underline text-xs flex items-center gap-1",
              onClick: (e) => e.stopPropagation(),
              children: [
                /* @__PURE__ */ jsx(ExternalLink, { className: "h-3 w-3" }),
                error.linearIssueKey || "Linear"
              ]
            }
          )
        ] })
      ] })
    }
  );
}
function ErrorsStatusActions({ errorId, currentStatus, className }) {
  const { updateStatus } = useErrorsActions();
  const statuses = ["OPEN", "INVESTIGATING", "RESOLVED", "IGNORED"];
  return /* @__PURE__ */ jsx("div", { className: cn("flex items-center gap-2", className), children: statuses.map((status) => {
    const config = getStatusConfig(status);
    const isActive = status === currentStatus;
    return /* @__PURE__ */ jsx(
      Button,
      {
        variant: isActive ? "primary" : "ghost",
        size: "sm",
        onClick: () => updateStatus(errorId, status),
        className: cn(isActive && config.bgColor, isActive && config.color),
        children: config.label
      },
      status
    );
  }) });
}
function ErrorsSeverityBadge({ severity, showIcon = true, className }) {
  const config = getSeverityConfig(severity);
  return /* @__PURE__ */ jsxs(Badge, { className: cn(config.bgColor, config.color, className), children: [
    showIcon && /* @__PURE__ */ jsx(SeverityIcon, { severity, className: "h-3 w-3 mr-1" }),
    config.label
  ] });
}
function ErrorsStatusBadge({ status, className }) {
  const config = getStatusConfig(status);
  return /* @__PURE__ */ jsx(Badge, { className: cn(config.bgColor, config.color, className), children: config.label });
}
function ErrorsRoot({ children, ...providerProps }) {
  return /* @__PURE__ */ jsx(ErrorsProvider, { ...providerProps, children });
}
var Errors = {
  /** Root provider component - wraps all other components */
  Root: ErrorsRoot,
  /** Header with title, stats, and sync button */
  Header: ErrorsHeader,
  /** Filter controls for status, severity, and search */
  Filters: ErrorsFilters,
  /** List of error cards */
  List: ErrorsList,
  /** Pagination controls */
  Pagination: ErrorsPagination,
  /** Quick status update buttons */
  StatusActions: ErrorsStatusActions,
  /** Severity badge component */
  SeverityBadge: ErrorsSeverityBadge,
  /** Status badge component */
  StatusBadge: ErrorsStatusBadge,
  /** Loading skeleton for error cards */
  ItemSkeleton: ErrorCardSkeleton
};
var RunsInternalContext = createContext(null);
function RunsProvider({
  children,
  automationId,
  defaultStatus = [],
  pageSize: initialPageSize = 25
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    status: defaultStatus,
    automationIds: automationId ? [automationId] : []
  });
  const [selectedRunId, setSelectedRunId] = useState(null);
  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: filters.status.length > 0 ? filters.status.join(",") : void 0,
      automationId: filters.automationIds[0] || automationId
    }),
    [page, pageSize, filters, automationId]
  );
  const { data, isLoading, error: fetchError, refetch } = useTraces(queryParams);
  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const selectedRun = useMemo(() => {
    if (!selectedRunId) return null;
    return runs.find((r) => r.id === selectedRunId) ?? null;
  }, [selectedRunId, runs]);
  const runningCount = useMemo(() => runs.filter((r) => r.status === "running").length, [runs]);
  const failedCount = useMemo(() => runs.filter((r) => r.status === "failed").length, [runs]);
  const completedCount = useMemo(() => runs.filter((r) => r.status === "completed").length, [runs]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      status: [],
      automationIds: automationId ? [automationId] : []
    });
    setPage(1);
  }, [automationId]);
  const hasActiveFilters = filters.status.length > 0 || filters.automationIds.length > 0 && !automationId;
  const value = {
    runs,
    total,
    isLoading,
    error: fetchError instanceof Error ? fetchError : fetchError ? new Error(String(fetchError)) : null,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedRunId,
    setSelectedRunId,
    selectedRun,
    refetch,
    runningCount,
    failedCount,
    completedCount
  };
  return /* @__PURE__ */ jsx(RunsInternalContext.Provider, { value, children });
}
function useRunsInternal() {
  const context = useContext(RunsInternalContext);
  if (!context) {
    throw new Error("Runs hooks must be used within a Runs.Root component");
  }
  return context;
}
function useRunsList() {
  const ctx = useRunsInternal();
  return {
    runs: ctx.runs,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    setPageSize: ctx.setPageSize,
    refetch: ctx.refetch
  };
}
function useRunsFilters() {
  const ctx = useRunsInternal();
  return {
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters
  };
}
function useRunsSelection() {
  const ctx = useRunsInternal();
  const isSelected = useCallback((runId) => ctx.selectedRunId === runId, [ctx.selectedRunId]);
  const toggleSelected = useCallback(
    (runId) => {
      ctx.setSelectedRunId(ctx.selectedRunId === runId ? null : runId);
    },
    [ctx.selectedRunId, ctx.setSelectedRunId]
  );
  return {
    selectedRunId: ctx.selectedRunId,
    selectedRun: ctx.selectedRun,
    setSelectedRunId: ctx.setSelectedRunId,
    isSelected,
    toggleSelected
  };
}
function useRunsStats() {
  const ctx = useRunsInternal();
  return {
    total: ctx.total,
    runningCount: ctx.runningCount,
    failedCount: ctx.failedCount,
    completedCount: ctx.completedCount
  };
}
function useRuns() {
  const ctx = useRunsInternal();
  return {
    // List
    runs: ctx.runs,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    // Pagination
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    // Filters
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters,
    // Selection
    selectedRunId: ctx.selectedRunId,
    selectedRun: ctx.selectedRun,
    setSelectedRunId: ctx.setSelectedRunId,
    // Actions
    refetch: ctx.refetch,
    // Stats
    runningCount: ctx.runningCount,
    failedCount: ctx.failedCount,
    completedCount: ctx.completedCount
  };
}

// src/components/runs/runsUtils.ts
var RUN_STATUS_CONFIG = {
  pending: {
    label: "Pending",
    variant: "default",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "clock"
  },
  running: {
    label: "Running",
    variant: "warning",
    color: "text-amber-700 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    iconName: "loader"
  },
  completed: {
    label: "Completed",
    variant: "success",
    color: "text-green-700 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900/30",
    iconName: "check"
  },
  failed: {
    label: "Failed",
    variant: "danger",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/30",
    iconName: "x"
  },
  cancelled: {
    label: "Cancelled",
    variant: "default",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "ban"
  }
};
var RUN_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" }
];
var RUN_STATUS_PRESETS = [
  { label: "Active", values: ["pending", "running"] },
  { label: "Finished", values: ["completed", "failed", "cancelled"] },
  { label: "Problems", values: ["failed", "cancelled"] }
];
function getRunStatusConfig(status) {
  return RUN_STATUS_CONFIG[status] ?? RUN_STATUS_CONFIG.pending;
}
function formatDuration4(startDate, endDate) {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : /* @__PURE__ */ new Date();
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return "0s";
  if (diffMs < 1e3) return `${diffMs}ms`;
  const diffSecs = Math.floor(diffMs / 1e3);
  if (diffSecs < 60) return `${diffSecs}s`;
  const diffMins = Math.floor(diffSecs / 60);
  const remainingSecs = diffSecs % 60;
  if (diffMins < 60) return `${diffMins}m ${remainingSecs}s`;
  const diffHours = Math.floor(diffMins / 60);
  const remainingMins = diffMins % 60;
  return `${diffHours}h ${remainingMins}m`;
}
function formatDurationMs(ms) {
  if (ms < 1e3) return `${ms}ms`;
  const secs = Math.floor(ms / 1e3);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainingSecs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
}
function formatTimeAgo4(date) {
  const now = /* @__PURE__ */ new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}
function formatTimestamp5(date) {
  const d = new Date(date);
  return d.toLocaleString();
}
function truncateRunId(runId, length = 8) {
  if (runId.length <= length) return runId;
  return runId.slice(0, length) + "...";
}
function isRunActive(status) {
  return status === "pending" || status === "running";
}
function isRunFinished(status) {
  return status === "completed" || status === "failed" || status === "cancelled";
}
function RunStatusIcon({ status, className }) {
  const config = getRunStatusConfig(status);
  switch (config.iconName) {
    case "check":
      return /* @__PURE__ */ jsx(CheckCircle2, { className });
    case "x":
      return /* @__PURE__ */ jsx(XCircle, { className });
    case "loader":
      return /* @__PURE__ */ jsx(Loader2, { className: cn(className, "animate-spin") });
    case "ban":
      return /* @__PURE__ */ jsx(Ban, { className });
    case "clock":
    default:
      return /* @__PURE__ */ jsx(Clock, { className });
  }
}
function RunsHeader({
  title = "Runs",
  subtitle = "View automation execution history",
  children,
  className
}) {
  const { total, runningCount, failedCount, completedCount } = useRunsStats();
  const { refetch, isLoading } = useRunsList();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-shrink-0", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white", children: title }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs(Badge, { variant: "default", children: [
            total,
            " total"
          ] }),
          runningCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "warning", children: [
            runningCount,
            " running"
          ] }),
          failedCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "danger", children: [
            failedCount,
            " failed"
          ] }),
          completedCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "success", children: [
            completedCount,
            " completed"
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "secondary", onClick: () => refetch(), disabled: isLoading, children: [
        /* @__PURE__ */ jsx(RefreshCw, { className: cn("mr-2 h-4 w-4", isLoading && "animate-spin") }),
        "Refresh"
      ] }),
      children
    ] })
  ] });
}
function RunsFilters({ showStatusFilter = true, children, className }) {
  const { filters, setFilters, clearFilters, hasActiveFilters } = useRunsFilters();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3 sm:flex-row sm:items-center flex-shrink-0", className), children: [
    showStatusFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-48", children: /* @__PURE__ */ jsx(
      MultiSelect,
      {
        options: RUN_STATUS_OPTIONS,
        value: filters.status,
        onChange: (status) => setFilters({ status }),
        placeholder: "Status...",
        presets: RUN_STATUS_PRESETS
      }
    ) }),
    hasActiveFilters && /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: clearFilters, children: "Clear filters" }),
    children
  ] });
}
function RunsList({
  renderRun,
  emptyTitle = "No runs found",
  emptyDescription = "No automation runs match your filters.",
  className
}) {
  const { runs, isLoading, error, refetch } = useRunsList();
  const { hasActiveFilters } = useRunsFilters();
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load runs" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500 dark:text-slate-400", children: error.message }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(RunCardSkeleton, {}, i)) });
  }
  if (runs.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
        title: emptyTitle,
        description: hasActiveFilters ? "Try adjusting your filters" : emptyDescription,
        className
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: runs.map((run, index) => renderRun ? renderRun(run, index) : /* @__PURE__ */ jsx(DefaultRunCard, { run }, run.id)) });
}
function RunsTable2({ onRowClick, className }) {
  const { runs, isLoading, error, refetch } = useRunsList();
  const { isSelected, setSelectedRunId } = useRunsSelection();
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load runs" }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsx("div", { className: "p-4 space-y-2", children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-12 w-full" }, i)) }) });
  }
  if (runs.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}), title: "No runs found", className });
  }
  return /* @__PURE__ */ jsx(Card, { className: cn("overflow-hidden", className), children: /* @__PURE__ */ jsxs("table", { className: "w-full", children: [
    /* @__PURE__ */ jsx("thead", { className: "bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsxs("tr", { children: [
      /* @__PURE__ */ jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider", children: "Run ID" }),
      /* @__PURE__ */ jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider", children: "Status" }),
      /* @__PURE__ */ jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider", children: "Started" }),
      /* @__PURE__ */ jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider", children: "Duration" })
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { className: "divide-y divide-slate-200 dark:divide-slate-700", children: runs.map((run) => {
      const statusConfig5 = getRunStatusConfig(run.status);
      const selected = isSelected(run.id);
      return /* @__PURE__ */ jsxs(
        "tr",
        {
          className: cn(
            "hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors",
            selected && "bg-blue-50 dark:bg-blue-900/20"
          ),
          onClick: () => {
            setSelectedRunId(run.id);
            onRowClick?.(run);
          },
          children: [
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("span", { className: "font-mono text-sm text-slate-900 dark:text-white", children: truncateRunId(run.id) }) }),
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsxs(Badge, { className: cn(statusConfig5.bgColor, statusConfig5.color), children: [
              /* @__PURE__ */ jsx(RunStatusIcon, { status: run.status, className: "h-3 w-3 mr-1" }),
              statusConfig5.label
            ] }) }),
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: formatTimeAgo4(run.createdAt) }) }),
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: formatDuration4(run.createdAt, run.completedAt) }) })
          ]
        },
        run.id
      );
    }) })
  ] }) });
}
function RunsPagination({ className }) {
  const { page, pageSize, totalPages, total, setPage } = useRunsList();
  if (totalPages <= 1) return null;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "text-sm text-slate-500 dark:text-slate-400", children: [
      "Showing ",
      startItem,
      "-",
      endItem,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page - 1), disabled: page <= 1, children: [
        /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" }),
        "Previous"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: [
        "Page ",
        page,
        " of ",
        totalPages
      ] }),
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page + 1), disabled: page >= totalPages, children: [
        "Next",
        /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
      ] })
    ] })
  ] });
}
function RunCardSkeleton() {
  return /* @__PURE__ */ jsx(Card, { className: "p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-8 rounded-full flex-shrink-0" }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-32" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-24" })
    ] }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-16 flex-shrink-0" })
  ] }) });
}
function DefaultRunCard({ run }) {
  const { isSelected, toggleSelected } = useRunsSelection();
  const selected = isSelected(run.id);
  const statusConfig5 = getRunStatusConfig(run.status);
  return /* @__PURE__ */ jsxs(
    Card,
    {
      className: cn(
        "p-4 cursor-pointer transition-all",
        selected && "ring-2 ring-blue-500 dark:ring-blue-400"
      ),
      onClick: () => toggleSelected(run.id),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
          /* @__PURE__ */ jsx("div", { className: cn("p-2 rounded-full", statusConfig5.bgColor), children: /* @__PURE__ */ jsx(RunStatusIcon, { status: run.status, className: cn("h-4 w-4", statusConfig5.color) }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "font-mono text-sm font-medium text-slate-900 dark:text-white", children: truncateRunId(run.id, 12) }),
              /* @__PURE__ */ jsx(Badge, { className: cn(statusConfig5.bgColor, statusConfig5.color), children: statusConfig5.label })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "mt-1 text-xs text-slate-500 dark:text-slate-400", children: run.automationId && /* @__PURE__ */ jsxs("span", { children: [
              "Automation: ",
              run.automationId
            ] }) })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-1 flex-shrink-0", children: [
            /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: formatTimeAgo4(run.createdAt) }),
            /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 dark:text-slate-500", children: formatDuration4(run.createdAt, run.completedAt) })
          ] })
        ] }),
        run.status === "failed" && run.error && /* @__PURE__ */ jsx("div", { className: "mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300", children: typeof run.error === "string" ? run.error : run.error.message })
      ]
    }
  );
}
function RunsStatusBadge({ status, showIcon = true, className }) {
  const config = getRunStatusConfig(status);
  return /* @__PURE__ */ jsxs(Badge, { className: cn(config.bgColor, config.color, className), children: [
    showIcon && /* @__PURE__ */ jsx(RunStatusIcon, { status, className: "h-3 w-3 mr-1" }),
    config.label
  ] });
}
function RunsRoot(props) {
  return /* @__PURE__ */ jsx(RunsProvider, { ...props });
}
var Runs = {
  /** Root provider component - wraps all other Runs components */
  Root: RunsRoot,
  /** Page header with title, stats, and refresh button */
  Header: RunsHeader,
  /** Filter controls for status */
  Filters: RunsFilters,
  /** List view of runs with card layout */
  List: RunsList,
  /** Table view of runs */
  Table: RunsTable2,
  /** Pagination controls */
  Pagination: RunsPagination,
  /** Status badge component */
  StatusBadge: RunsStatusBadge,
  /** Loading skeleton for run cards */
  ItemSkeleton: RunCardSkeleton
};
var AutomationsInternalContext = createContext(null);
function AutomationsProvider({
  children,
  defaultStatus = [],
  defaultTriggerType = [],
  pageSize: initialPageSize = 25
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    status: defaultStatus,
    triggerType: defaultTriggerType,
    search: ""
  });
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: filters.status.length > 0 ? filters.status.join(",") : void 0,
      triggerType: filters.triggerType.length > 0 ? filters.triggerType.join(",") : void 0,
      search: filters.search || void 0
    }),
    [page, pageSize, filters]
  );
  const { data, isLoading, error: fetchError, refetch } = useAutomations(queryParams);
  const updateStatusMutation = useUpdateAutomationStatus();
  const deleteMutation = useDeleteAutomation();
  const triggerMutation = useTriggerAutomation();
  const automations = data?.automations ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const selectedAutomation = useMemo(() => {
    if (!selectedAutomationId) return null;
    return automations.find((a) => a.id === selectedAutomationId) ?? null;
  }, [selectedAutomationId, automations]);
  const activeCount = useMemo(() => automations.filter((a) => a.status === "ACTIVE").length, [automations]);
  const pausedCount = useMemo(() => automations.filter((a) => a.status === "PAUSED").length, [automations]);
  const errorCount = useMemo(() => automations.filter((a) => a.status === "ERROR").length, [automations]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      status: [],
      triggerType: [],
      search: ""
    });
    setPage(1);
  }, []);
  const hasActiveFilters = filters.status.length > 0 || filters.triggerType.length > 0 || filters.search.length > 0;
  const updateStatus = useCallback(
    async (id, status) => {
      await updateStatusMutation.mutateAsync({ id, status });
    },
    [updateStatusMutation]
  );
  const deleteAutomationAction = useCallback(
    async (id) => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );
  const triggerAutomationAction = useCallback(
    async (id, payload) => {
      const result = await triggerMutation.mutateAsync({ id, payload });
      return result;
    },
    [triggerMutation]
  );
  const value = {
    automations,
    total,
    isLoading,
    error: fetchError instanceof Error ? fetchError : fetchError ? new Error(String(fetchError)) : null,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedAutomationId,
    setSelectedAutomationId,
    selectedAutomation,
    refetch,
    updateStatus,
    deleteAutomation: deleteAutomationAction,
    triggerAutomation: triggerAutomationAction,
    isUpdating: updateStatusMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isTriggering: triggerMutation.isPending,
    activeCount,
    pausedCount,
    errorCount
  };
  return /* @__PURE__ */ jsx(AutomationsInternalContext.Provider, { value, children });
}
function useAutomationsInternal() {
  const context = useContext(AutomationsInternalContext);
  if (!context) {
    throw new Error("Automations hooks must be used within an Automations.Root component");
  }
  return context;
}
function useAutomationsList() {
  const ctx = useAutomationsInternal();
  return {
    automations: ctx.automations,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    setPageSize: ctx.setPageSize,
    refetch: ctx.refetch
  };
}
function useAutomationsFilters() {
  const ctx = useAutomationsInternal();
  return {
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters
  };
}
function useAutomationsSelection() {
  const ctx = useAutomationsInternal();
  const isSelected = useCallback(
    (automationId) => ctx.selectedAutomationId === automationId,
    [ctx.selectedAutomationId]
  );
  const toggleSelected = useCallback(
    (automationId) => {
      ctx.setSelectedAutomationId(ctx.selectedAutomationId === automationId ? null : automationId);
    },
    [ctx.selectedAutomationId, ctx.setSelectedAutomationId]
  );
  return {
    selectedAutomationId: ctx.selectedAutomationId,
    selectedAutomation: ctx.selectedAutomation,
    setSelectedAutomationId: ctx.setSelectedAutomationId,
    isSelected,
    toggleSelected
  };
}
function useAutomationsActions() {
  const ctx = useAutomationsInternal();
  return {
    updateStatus: ctx.updateStatus,
    deleteAutomation: ctx.deleteAutomation,
    triggerAutomation: ctx.triggerAutomation,
    isUpdating: ctx.isUpdating,
    isDeleting: ctx.isDeleting,
    isTriggering: ctx.isTriggering
  };
}
function useAutomationsStats() {
  const ctx = useAutomationsInternal();
  return {
    total: ctx.total,
    activeCount: ctx.activeCount,
    pausedCount: ctx.pausedCount,
    errorCount: ctx.errorCount
  };
}
function useAutomationsHook() {
  const ctx = useAutomationsInternal();
  return {
    // List
    automations: ctx.automations,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    // Pagination
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    // Filters
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters,
    // Selection
    selectedAutomationId: ctx.selectedAutomationId,
    selectedAutomation: ctx.selectedAutomation,
    setSelectedAutomationId: ctx.setSelectedAutomationId,
    // Actions
    refetch: ctx.refetch,
    updateStatus: ctx.updateStatus,
    deleteAutomation: ctx.deleteAutomation,
    triggerAutomation: ctx.triggerAutomation,
    // Stats
    activeCount: ctx.activeCount,
    pausedCount: ctx.pausedCount,
    errorCount: ctx.errorCount
  };
}

// src/components/automations/automationsUtils.ts
var AUTOMATION_STATUS_CONFIG = {
  ACTIVE: {
    label: "Active",
    variant: "success",
    color: "text-green-700 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900/30",
    iconName: "check"
  },
  PAUSED: {
    label: "Paused",
    variant: "warning",
    color: "text-amber-700 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    iconName: "pause"
  },
  INACTIVE: {
    label: "Inactive",
    variant: "default",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "circle"
  },
  ERROR: {
    label: "Error",
    variant: "danger",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/30",
    iconName: "x"
  },
  DELETED: {
    label: "Deleted",
    variant: "default",
    color: "text-slate-500 dark:text-slate-500",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "trash"
  }
};
var TRIGGER_TYPE_CONFIG = {
  WEBHOOK: {
    label: "Webhook",
    variant: "primary",
    color: "text-blue-700 dark:text-blue-400",
    bgColor: "bg-blue-100 dark:bg-blue-900/30",
    iconName: "webhook"
  },
  SCHEDULE: {
    label: "Schedule",
    variant: "secondary",
    color: "text-purple-700 dark:text-purple-400",
    bgColor: "bg-purple-100 dark:bg-purple-900/30",
    iconName: "clock"
  },
  CODE_PING: {
    label: "Code Ping",
    variant: "secondary",
    color: "text-cyan-700 dark:text-cyan-400",
    bgColor: "bg-cyan-100 dark:bg-cyan-900/30",
    iconName: "code"
  },
  MANUAL: {
    label: "Manual",
    variant: "default",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "play"
  },
  EVENT: {
    label: "Event",
    variant: "primary",
    color: "text-orange-700 dark:text-orange-400",
    bgColor: "bg-orange-100 dark:bg-orange-900/30",
    iconName: "zap"
  }
};
var AUTOMATION_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "ERROR", label: "Error" },
  { value: "DELETED", label: "Deleted" }
];
var TRIGGER_TYPE_OPTIONS = [
  { value: "WEBHOOK", label: "Webhook" },
  { value: "SCHEDULE", label: "Schedule" },
  { value: "CODE_PING", label: "Code Ping" },
  { value: "MANUAL", label: "Manual" },
  { value: "EVENT", label: "Event" }
];
var AUTOMATION_STATUS_PRESETS = [
  { label: "Running", values: ["ACTIVE"] },
  { label: "Stopped", values: ["PAUSED", "INACTIVE"] },
  { label: "Problems", values: ["ERROR"] }
];
var TRIGGER_TYPE_PRESETS = [
  { label: "Automated", values: ["WEBHOOK", "SCHEDULE", "EVENT"] },
  { label: "Manual", values: ["MANUAL", "CODE_PING"] }
];
function getAutomationStatusConfig(status) {
  return AUTOMATION_STATUS_CONFIG[status] ?? AUTOMATION_STATUS_CONFIG.INACTIVE;
}
function getTriggerTypeConfig(triggerType) {
  return TRIGGER_TYPE_CONFIG[triggerType] ?? TRIGGER_TYPE_CONFIG.MANUAL;
}
function formatTimeAgo5(date) {
  if (!date) return "Never";
  const now = /* @__PURE__ */ new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}
function formatTimestamp6(date) {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleString();
}
function formatSuccessRate(rate) {
  return `${Math.round(rate * 100)}%`;
}
function formatDuration5(ms) {
  if (ms < 1e3) return `${ms}ms`;
  const secs = Math.floor(ms / 1e3);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainingSecs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
}
function truncateName(name, length = 30) {
  if (name.length <= length) return name;
  return name.slice(0, length) + "...";
}
function isAutomationActive(status) {
  return status === "ACTIVE";
}
function isAutomationPaused(status) {
  return status === "PAUSED";
}
function isAutomationRunnable(status) {
  return status === "ACTIVE" || status === "PAUSED";
}
function getHealthStatus2(successRate, errorCount) {
  if (errorCount > 0 || successRate < 0.5) return "failing";
  if (successRate < 0.9) return "warning";
  return "healthy";
}
function AutomationStatusIcon({ status, className }) {
  const config = getAutomationStatusConfig(status);
  switch (config.iconName) {
    case "check":
      return /* @__PURE__ */ jsx(CheckCircle2, { className });
    case "pause":
      return /* @__PURE__ */ jsx(PauseCircle, { className });
    case "x":
      return /* @__PURE__ */ jsx(XCircle, { className });
    case "trash":
      return /* @__PURE__ */ jsx(Trash2, { className });
    case "circle":
    default:
      return /* @__PURE__ */ jsx(Circle, { className });
  }
}
function TriggerTypeIcon({ triggerType, className }) {
  const config = getTriggerTypeConfig(triggerType);
  switch (config.iconName) {
    case "webhook":
      return /* @__PURE__ */ jsx(Webhook, { className });
    case "clock":
      return /* @__PURE__ */ jsx(Clock, { className });
    case "code":
      return /* @__PURE__ */ jsx(Code, { className });
    case "zap":
      return /* @__PURE__ */ jsx(Zap, { className });
    case "play":
    default:
      return /* @__PURE__ */ jsx(Play, { className });
  }
}
function AutomationsHeader({
  title = "Automations",
  subtitle = "Manage your automation workflows",
  children,
  className
}) {
  const { total, activeCount, pausedCount, errorCount } = useAutomationsStats();
  const { refetch, isLoading } = useAutomationsList();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-shrink-0", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white", children: title }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs(Badge, { variant: "default", children: [
            total,
            " total"
          ] }),
          activeCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "success", children: [
            activeCount,
            " active"
          ] }),
          pausedCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "warning", children: [
            pausedCount,
            " paused"
          ] }),
          errorCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "danger", children: [
            errorCount,
            " errors"
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "secondary", onClick: () => refetch(), disabled: isLoading, children: [
        /* @__PURE__ */ jsx(RefreshCw, { className: cn("mr-2 h-4 w-4", isLoading && "animate-spin") }),
        "Refresh"
      ] }),
      children
    ] })
  ] });
}
function AutomationsFilters({
  showStatusFilter = true,
  showTriggerTypeFilter = true,
  showSearch = true,
  children,
  className
}) {
  const { filters, setFilters, clearFilters, hasActiveFilters } = useAutomationsFilters();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3 sm:flex-row sm:items-center flex-shrink-0", className), children: [
    showSearch && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-64", children: /* @__PURE__ */ jsx(
      SearchInput,
      {
        value: filters.search,
        onChange: (e) => setFilters({ search: e.target.value }),
        onClear: () => setFilters({ search: "" }),
        placeholder: "Search automations..."
      }
    ) }),
    showStatusFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-48", children: /* @__PURE__ */ jsx(
      MultiSelect,
      {
        options: AUTOMATION_STATUS_OPTIONS,
        value: filters.status,
        onChange: (status) => setFilters({ status }),
        placeholder: "Status...",
        presets: AUTOMATION_STATUS_PRESETS
      }
    ) }),
    showTriggerTypeFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-48", children: /* @__PURE__ */ jsx(
      MultiSelect,
      {
        options: TRIGGER_TYPE_OPTIONS,
        value: filters.triggerType,
        onChange: (triggerType) => setFilters({ triggerType }),
        placeholder: "Trigger...",
        presets: TRIGGER_TYPE_PRESETS
      }
    ) }),
    hasActiveFilters && /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: clearFilters, children: "Clear filters" }),
    children
  ] });
}
function AutomationsList({
  renderAutomation,
  emptyTitle = "No automations found",
  emptyDescription = "No automations match your filters.",
  className
}) {
  const { automations, isLoading, error, refetch } = useAutomationsList();
  const { hasActiveFilters } = useAutomationsFilters();
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load automations" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500 dark:text-slate-400", children: error.message }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(AutomationCardSkeleton, {}, i)) });
  }
  if (automations.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
        title: emptyTitle,
        description: hasActiveFilters ? "Try adjusting your filters" : emptyDescription,
        className
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: automations.map(
    (automation, index) => renderAutomation ? renderAutomation(automation, index) : /* @__PURE__ */ jsx(DefaultAutomationCard, { automation }, automation.id)
  ) });
}
function AutomationsGrid({
  renderAutomation,
  emptyTitle = "No automations found",
  emptyDescription = "No automations match your filters.",
  className
}) {
  const { automations, isLoading, error, refetch } = useAutomationsList();
  const { hasActiveFilters } = useAutomationsFilters();
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load automations" }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className), children: [1, 2, 3, 4, 5, 6].map((i) => /* @__PURE__ */ jsx(AutomationCardSkeleton, {}, i)) });
  }
  if (automations.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
        title: emptyTitle,
        description: hasActiveFilters ? "Try adjusting your filters" : emptyDescription,
        className
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className), children: automations.map(
    (automation, index) => renderAutomation ? renderAutomation(automation, index) : /* @__PURE__ */ jsx(DefaultAutomationCard, { automation }, automation.id)
  ) });
}
function AutomationsPagination({ className }) {
  const { page, pageSize, totalPages, total, setPage } = useAutomationsList();
  if (totalPages <= 1) return null;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "text-sm text-slate-500 dark:text-slate-400", children: [
      "Showing ",
      startItem,
      "-",
      endItem,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page - 1), disabled: page <= 1, children: [
        /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" }),
        "Previous"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: [
        "Page ",
        page,
        " of ",
        totalPages
      ] }),
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page + 1), disabled: page >= totalPages, children: [
        "Next",
        /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
      ] })
    ] })
  ] });
}
function AutomationCardSkeleton() {
  return /* @__PURE__ */ jsx(Card, { className: "p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-10 w-10 rounded-lg flex-shrink-0" }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-40" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-24" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-1", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-16" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-20" })
    ] })
  ] }) });
}
function DefaultAutomationCard({ automation }) {
  const { isSelected, toggleSelected } = useAutomationsSelection();
  const selected = isSelected(automation.id);
  const statusConfig5 = getAutomationStatusConfig(automation.status);
  const triggerConfig2 = getTriggerTypeConfig(automation.triggerType);
  const healthStatus = getHealthStatus2(automation.stats.successRate, automation.errorCount);
  return /* @__PURE__ */ jsxs(
    Card,
    {
      className: cn(
        "p-4 cursor-pointer transition-all hover:shadow-md",
        selected && "ring-2 ring-blue-500 dark:ring-blue-400"
      ),
      onClick: () => toggleSelected(automation.id),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx("div", { className: cn("p-2 rounded-lg", statusConfig5.bgColor), children: /* @__PURE__ */ jsx(AutomationStatusIcon, { status: automation.status, className: cn("h-5 w-5", statusConfig5.color) }) }),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-slate-900",
                  healthStatus === "healthy" && "bg-green-500",
                  healthStatus === "warning" && "bg-amber-500",
                  healthStatus === "failing" && "bg-red-500"
                )
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium text-slate-900 dark:text-white truncate", children: automation.name }),
              /* @__PURE__ */ jsx(Badge, { className: cn(statusConfig5.bgColor, statusConfig5.color), children: statusConfig5.label })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "mt-1 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400", children: [
              /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
                /* @__PURE__ */ jsx(TriggerTypeIcon, { triggerType: automation.triggerType, className: "h-3 w-3" }),
                triggerConfig2.label
              ] }),
              /* @__PURE__ */ jsxs("span", { children: [
                automation.stats.totalRuns,
                " runs"
              ] }),
              /* @__PURE__ */ jsxs("span", { children: [
                formatSuccessRate(automation.stats.successRate),
                " success"
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-1 flex-shrink-0", children: [
            /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: automation.lastRunAt ? `Last run ${formatTimeAgo5(automation.lastRunAt)}` : "Never run" }),
            automation.errorCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "danger", className: "text-xs", children: [
              automation.errorCount,
              " errors"
            ] })
          ] })
        ] }),
        automation.description && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2", children: automation.description })
      ]
    }
  );
}
function AutomationsStatusBadge({ status, showIcon = true, className }) {
  const config = getAutomationStatusConfig(status);
  return /* @__PURE__ */ jsxs(Badge, { className: cn(config.bgColor, config.color, className), children: [
    showIcon && /* @__PURE__ */ jsx(AutomationStatusIcon, { status, className: "h-3 w-3 mr-1" }),
    config.label
  ] });
}
function AutomationsTriggerTypeBadge({
  triggerType,
  showIcon = true,
  className
}) {
  const config = getTriggerTypeConfig(triggerType);
  return /* @__PURE__ */ jsxs(Badge, { className: cn(config.bgColor, config.color, className), children: [
    showIcon && /* @__PURE__ */ jsx(TriggerTypeIcon, { triggerType, className: "h-3 w-3 mr-1" }),
    config.label
  ] });
}
function AutomationsQuickActions({ automationId, className }) {
  const { automations } = useAutomationsList();
  const { updateStatus, triggerAutomation, isUpdating, isTriggering } = useAutomationsActions();
  const automation = automations.find((a) => a.id === automationId);
  if (!automation) return null;
  const canPause = automation.status === "ACTIVE";
  const canResume = automation.status === "PAUSED";
  const canTrigger = automation.status === "ACTIVE" || automation.status === "PAUSED";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    canPause && /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "secondary",
        size: "sm",
        onClick: (e) => {
          e.stopPropagation();
          updateStatus(automationId, "PAUSED");
        },
        disabled: isUpdating,
        children: [
          /* @__PURE__ */ jsx(PauseCircle, { className: "h-4 w-4 mr-1" }),
          "Pause"
        ]
      }
    ),
    canResume && /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "secondary",
        size: "sm",
        onClick: (e) => {
          e.stopPropagation();
          updateStatus(automationId, "ACTIVE");
        },
        disabled: isUpdating,
        children: [
          /* @__PURE__ */ jsx(CheckCircle2, { className: "h-4 w-4 mr-1" }),
          "Resume"
        ]
      }
    ),
    canTrigger && /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "primary",
        size: "sm",
        onClick: (e) => {
          e.stopPropagation();
          triggerAutomation(automationId);
        },
        disabled: isTriggering,
        children: [
          /* @__PURE__ */ jsx(Play, { className: "h-4 w-4 mr-1" }),
          "Run Now"
        ]
      }
    )
  ] });
}
function AutomationsRoot(props) {
  return /* @__PURE__ */ jsx(AutomationsProvider, { ...props });
}
var Automations = {
  /** Root provider component - wraps all other Automations components */
  Root: AutomationsRoot,
  /** Page header with title, stats, and refresh button */
  Header: AutomationsHeader,
  /** Filter controls for status, trigger type, and search */
  Filters: AutomationsFilters,
  /** List view of automations with card layout */
  List: AutomationsList,
  /** Grid view of automations */
  Grid: AutomationsGrid,
  /** Pagination controls */
  Pagination: AutomationsPagination,
  /** Status badge component */
  StatusBadge: AutomationsStatusBadge,
  /** Trigger type badge component */
  TriggerTypeBadge: AutomationsTriggerTypeBadge,
  /** Quick actions (pause/resume/trigger) */
  QuickActions: AutomationsQuickActions,
  /** Loading skeleton for automation cards */
  ItemSkeleton: AutomationCardSkeleton
};

// src/components/traces/tracesUtils.ts
function mapRunStatus(status) {
  const normalizedStatus = typeof status === "string" ? status.toLowerCase() : status;
  const statusMap = {
    pending: "PENDING",
    running: "RUNNING",
    completed: "COMPLETED",
    failed: "FAILED",
    cancelled: "CANCELLED"
  };
  return statusMap[normalizedStatus] || "PENDING";
}
function mapStepStatus(status) {
  const normalizedStatus = status.toLowerCase();
  const statusMap = {
    pending: "PENDING",
    running: "RUNNING",
    completed: "COMPLETED",
    failed: "FAILED",
    skipped: "SKIPPED"
  };
  return statusMap[normalizedStatus] || "PENDING";
}
function mapNodeType(type) {
  const normalizedType = type.toLowerCase();
  const validTypes = ["trigger", "action", "condition", "wait", "ai", "agent", "http", "crypto", "subflow"];
  return validTypes.includes(normalizedType) ? normalizedType : "action";
}
function mapToExecutionStatus(status) {
  const statusMap = {
    COMPLETED: "completed",
    FAILED: "failed",
    SKIPPED: "skipped",
    RUNNING: "running",
    PENDING: "pending",
    TIMEOUT: "failed"
  };
  return statusMap[status] || "pending";
}
function deduplicateSteps(steps) {
  const nodeMap = /* @__PURE__ */ new Map();
  const orderMap = /* @__PURE__ */ new Map();
  steps.forEach((step, index) => {
    if (!orderMap.has(step.nodeId)) {
      orderMap.set(step.nodeId, index);
    }
    nodeMap.set(step.nodeId, step);
  });
  return Array.from(nodeMap.values()).sort((a, b) => {
    const orderA = orderMap.get(a.nodeId) ?? 0;
    const orderB = orderMap.get(b.nodeId) ?? 0;
    return orderA - orderB;
  });
}
function groupStepsByAttempt(steps) {
  if (steps.length === 0) return [];
  const attempts = [];
  let currentAttempt = [];
  const seenNodes = /* @__PURE__ */ new Set();
  for (const step of steps) {
    if (seenNodes.has(step.nodeId)) {
      if (currentAttempt.length > 0) {
        attempts.push(currentAttempt);
      }
      currentAttempt = [];
      seenNodes.clear();
    }
    currentAttempt.push(step);
    seenNodes.add(step.nodeId);
  }
  if (currentAttempt.length > 0) {
    attempts.push(currentAttempt);
  }
  return attempts;
}
function parseFlowFromXmlContent(xmlContent) {
  if (!xmlContent) return null;
  try {
    const parsed = JSON.parse(xmlContent);
    if (parsed && Array.isArray(parsed.nodes) && Array.isArray(parsed.edges)) {
      return {
        nodes: parsed.nodes.map(
          (node, index) => ({
            id: node.id,
            type: mapNodeType(node.type),
            name: node.name || node.id,
            config: node.config,
            position: node.position || { x: 200, y: index * 150 }
          })
        ),
        edges: parsed.edges.map(
          (edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            condition: edge.condition,
            label: edge.label || edge.condition
          })
        )
      };
    }
    return null;
  } catch (error) {
    console.error("Failed to parse flow definition from xmlContent:", error);
    return null;
  }
}
function stepsToFlowDefinition(steps) {
  const nodes = steps.map((step, index) => ({
    id: step.nodeId,
    type: step.nodeType,
    name: step.nodeName,
    position: { x: 200, y: index * 150 }
  }));
  const edges = [];
  for (let i = 0; i < steps.length - 1; i++) {
    const currentStep = steps[i];
    const nextStep = steps[i + 1];
    if (currentStep && nextStep) {
      edges.push({
        id: `edge-${currentStep.nodeId}-${nextStep.nodeId}`,
        source: currentStep.nodeId,
        target: nextStep.nodeId
      });
    }
  }
  return { nodes, edges };
}
var TRACE_STATUS_CONFIG = {
  completed: {
    label: "Completed",
    color: "text-emerald-700 dark:text-emerald-400",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    iconName: "check"
  },
  failed: {
    label: "Failed",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/30",
    iconName: "x"
  },
  running: {
    label: "Running",
    color: "text-amber-700 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    iconName: "loader"
  },
  pending: {
    label: "Pending",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "clock"
  },
  cancelled: {
    label: "Cancelled",
    color: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-100 dark:bg-slate-800",
    iconName: "ban"
  }
};
function getTraceStatusConfig(status) {
  const normalizedStatus = typeof status === "string" ? status.toLowerCase() : status;
  return TRACE_STATUS_CONFIG[normalizedStatus] || TRACE_STATUS_CONFIG.pending;
}
var TRACE_STATUS_OPTIONS = [
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
  { value: "pending", label: "Pending" },
  { value: "cancelled", label: "Cancelled" }
];
var TRACE_STATUS_PRESETS = [
  { label: "Active", values: ["running", "pending"] },
  { label: "Finished", values: ["completed", "failed", "cancelled"] },
  { label: "Problems", values: ["failed"] }
];
function formatTraceDuration(startedAt, completedAt) {
  if (!startedAt || !completedAt) return "-";
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  if (durationMs < 1e3) return `${durationMs}ms`;
  if (durationMs < 6e4) return `${(durationMs / 1e3).toFixed(1)}s`;
  const minutes = Math.floor(durationMs / 6e4);
  const seconds = Math.round(durationMs % 6e4 / 1e3);
  return `${minutes}m ${seconds}s`;
}
function formatTraceTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return "just now";
}
function formatTraceTimestamp(dateString) {
  return new Date(dateString).toLocaleString();
}
function truncateTraceId(id, length = 8) {
  if (id.length <= length) return id;
  return `${id.slice(0, length)}...`;
}
function isTraceActive(status) {
  return status === "running" || status === "pending";
}
function isTraceFinished(status) {
  return status === "completed" || status === "failed" || status === "cancelled";
}
function isTraceFailed(status) {
  return status === "failed";
}
var TracesInternalContext = createContext(null);
function TracesProvider({
  children,
  automationId,
  defaultStatus = [],
  pageSize: initialPageSize = 25,
  selectedTraceId: initialSelectedTraceId,
  defaultViewMode = "flow",
  onTraceClick
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    status: defaultStatus,
    automationIds: automationId ? [automationId] : [],
    dateRange: null
  });
  const [selectedTraceId, setSelectedTraceId] = useState(initialSelectedTraceId ?? null);
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedAttemptIndex, setSelectedAttemptIndexState] = useState(null);
  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: filters.status.length > 0 ? filters.status.join(",") : void 0,
      automationId: filters.automationIds[0] || automationId,
      startDate: filters.dateRange?.startDate,
      endDate: filters.dateRange?.endDate
    }),
    [page, pageSize, filters, automationId]
  );
  const { data: listData, isLoading: isLoadingList, error: listError, refetch } = useTraces(queryParams);
  const {
    data: selectedTraceDetail,
    isLoading: isLoadingDetailQuery,
    isFetching: isFetchingDetail,
    error: detailError
  } = useTrace(selectedTraceId ?? "", { enabled: !!selectedTraceId });
  const isLoadingDetail = selectedTraceId ? isLoadingDetailQuery || isFetchingDetail && !selectedTraceDetail : false;
  const traces = listData?.runs ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const selectedTrace = useMemo(() => {
    if (!selectedTraceId) return null;
    return traces.find((t) => t.id === selectedTraceId) ?? null;
  }, [selectedTraceId, traces]);
  const runningCount = useMemo(() => traces.filter((t) => t.status === "running").length, [traces]);
  const failedCount = useMemo(() => traces.filter((t) => t.status === "failed").length, [traces]);
  const completedCount = useMemo(() => traces.filter((t) => t.status === "completed").length, [traces]);
  const allSteps = useMemo(() => {
    if (!selectedTraceDetail) return [];
    return selectedTraceDetail.steps.map((step) => ({
      id: step.id,
      runId: step.runId,
      nodeId: step.nodeId,
      nodeName: step.nodeName,
      nodeType: mapNodeType(step.nodeType),
      status: mapStepStatus(step.status),
      startedAt: step.startedAt ? new Date(step.startedAt) : void 0,
      completedAt: step.completedAt ? new Date(step.completedAt) : void 0,
      duration: step.durationMs,
      input: step.input,
      output: step.output,
      error: step.error
    }));
  }, [selectedTraceDetail]);
  const attempts = useMemo(() => groupStepsByAttempt(allSteps), [allSteps]);
  const hasMultipleAttempts = attempts.length > 1;
  const activeAttemptIndex = selectedAttemptIndex ?? attempts.length - 1;
  const currentAttemptSteps = attempts[activeAttemptIndex] || [];
  const uniqueSteps = useMemo(() => deduplicateSteps(currentAttemptSteps), [currentAttemptSteps]);
  const traceData = useMemo(() => {
    if (!selectedTraceDetail) return null;
    return {
      run: {
        id: selectedTraceDetail.run.id,
        automationId: selectedTraceDetail.run.automationId,
        status: mapRunStatus(selectedTraceDetail.run.status),
        startedAt: selectedTraceDetail.run.createdAt ? new Date(selectedTraceDetail.run.createdAt) : void 0,
        completedAt: selectedTraceDetail.run.completedAt ? new Date(selectedTraceDetail.run.completedAt) : void 0,
        error: selectedTraceDetail.run.error
      },
      steps: uniqueSteps
    };
  }, [selectedTraceDetail, uniqueSteps]);
  const duration = useMemo(() => {
    if (!selectedTraceDetail?.run.createdAt || !selectedTraceDetail?.run.completedAt) return null;
    return new Date(selectedTraceDetail.run.completedAt).getTime() - new Date(selectedTraceDetail.run.createdAt).getTime();
  }, [selectedTraceDetail?.run.createdAt, selectedTraceDetail?.run.completedAt]);
  const flowDefinition = useMemo(() => {
    if (!selectedTraceDetail) return null;
    const flowDef = selectedTraceDetail.flow?.definition;
    if (flowDef && Array.isArray(flowDef.nodes) && Array.isArray(flowDef.edges)) {
      return {
        nodes: flowDef.nodes.map((node, index) => ({
          id: node.id,
          type: mapNodeType(node.type),
          name: node.name || node.id,
          config: node.config,
          position: node.position || { x: 200, y: index * 150 }
        })),
        edges: flowDef.edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          condition: edge.condition,
          label: edge.label || edge.condition
        }))
      };
    }
    const parsedFlow = parseFlowFromXmlContent(selectedTraceDetail.automation?.xmlContent);
    if (parsedFlow) return parsedFlow;
    return stepsToFlowDefinition(uniqueSteps);
  }, [selectedTraceDetail, uniqueSteps]);
  const nodeNameToFlowId = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    if (flowDefinition) {
      flowDefinition.nodes.forEach((node) => {
        map.set(node.name, node.id);
      });
    }
    return map;
  }, [flowDefinition]);
  const executionSteps = useMemo(() => {
    if (!flowDefinition || !traceData) return [];
    return traceData.steps.map((step) => {
      const directMatch = flowDefinition.nodes.find((n) => n.id === step.nodeId);
      let effectiveNodeId = step.nodeId;
      if (!directMatch) {
        const idFromName = nodeNameToFlowId.get(step.nodeName);
        if (idFromName) effectiveNodeId = idFromName;
      }
      return {
        nodeId: effectiveNodeId,
        status: mapToExecutionStatus(step.status),
        durationMs: step.duration,
        error: step.error ? { message: step.error.message } : void 0
      };
    });
  }, [flowDefinition, traceData, nodeNameToFlowId]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      status: [],
      automationIds: automationId ? [automationId] : [],
      dateRange: null
    });
    setPage(1);
  }, [automationId]);
  const hasActiveFilters = filters.status.length > 0 || filters.automationIds.length > 0 && !automationId || filters.dateRange !== null;
  const setActiveAttemptIndex = useCallback((index) => {
    setSelectedAttemptIndexState(index);
  }, []);
  const value = {
    // List
    traces,
    total,
    isLoading: isLoadingList,
    error: listError instanceof Error ? listError : listError ? new Error(String(listError)) : null,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedTraceId,
    setSelectedTraceId,
    selectedTrace,
    refetch,
    runningCount,
    failedCount,
    completedCount,
    // Detail
    selectedTraceDetail: selectedTraceDetail ?? null,
    isLoadingDetail,
    detailError: detailError instanceof Error ? detailError : detailError ? new Error(String(detailError)) : null,
    traceData,
    allSteps,
    attempts,
    hasMultipleAttempts,
    activeAttemptIndex,
    setActiveAttemptIndex,
    currentAttemptSteps,
    uniqueSteps,
    duration,
    flowDefinition,
    executionSteps,
    viewMode,
    setViewMode,
    selectedStep,
    setSelectedStep,
    onTraceClick
  };
  return /* @__PURE__ */ jsx(TracesInternalContext.Provider, { value, children });
}
function useTracesContext() {
  const context = useContext(TracesInternalContext);
  if (!context) {
    throw new Error("Traces hooks must be used within a <Traces.Root> component");
  }
  return context;
}
function useTracesList() {
  const ctx = useTracesContext();
  return {
    traces: ctx.traces,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    refetch: ctx.refetch,
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    setPageSize: ctx.setPageSize
  };
}
function useTracesFilters() {
  const ctx = useTracesContext();
  return {
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters
  };
}
function useTracesSelection() {
  const ctx = useTracesContext();
  return {
    selectedTraceId: ctx.selectedTraceId,
    selectedTrace: ctx.selectedTrace,
    setSelectedTraceId: ctx.setSelectedTraceId,
    isSelected: (id) => ctx.selectedTraceId === id
  };
}
function useTracesStats() {
  const ctx = useTracesContext();
  return {
    total: ctx.total,
    runningCount: ctx.runningCount,
    failedCount: ctx.failedCount,
    completedCount: ctx.completedCount
  };
}
function useTraceDetail() {
  const ctx = useTracesContext();
  return {
    selectedTraceDetail: ctx.selectedTraceDetail,
    isLoadingDetail: ctx.isLoadingDetail,
    detailError: ctx.detailError,
    traceData: ctx.traceData,
    allSteps: ctx.allSteps,
    uniqueSteps: ctx.uniqueSteps,
    attempts: ctx.attempts,
    hasMultipleAttempts: ctx.hasMultipleAttempts,
    activeAttemptIndex: ctx.activeAttemptIndex,
    setActiveAttemptIndex: ctx.setActiveAttemptIndex,
    currentAttemptSteps: ctx.currentAttemptSteps,
    duration: ctx.duration
  };
}
function useTraceFlow() {
  const ctx = useTracesContext();
  return {
    flowDefinition: ctx.flowDefinition,
    executionSteps: ctx.executionSteps,
    viewMode: ctx.viewMode,
    setViewMode: ctx.setViewMode
  };
}
function useTraceStep() {
  const ctx = useTracesContext();
  return {
    selectedStep: ctx.selectedStep,
    setSelectedStep: ctx.setSelectedStep
  };
}
function useTracesNavigation() {
  const ctx = useTracesContext();
  return {
    onTraceClick: ctx.onTraceClick
  };
}
function useTraces4() {
  const ctx = useTracesContext();
  return {
    // List
    traces: ctx.traces,
    total: ctx.total,
    isLoading: ctx.isLoading,
    error: ctx.error,
    refetch: ctx.refetch,
    page: ctx.page,
    pageSize: ctx.pageSize,
    totalPages: ctx.totalPages,
    setPage: ctx.setPage,
    setPageSize: ctx.setPageSize,
    // Filters
    filters: ctx.filters,
    setFilters: ctx.setFilters,
    clearFilters: ctx.clearFilters,
    hasActiveFilters: ctx.hasActiveFilters,
    // Stats
    runningCount: ctx.runningCount,
    failedCount: ctx.failedCount,
    completedCount: ctx.completedCount,
    // Selection
    selectedTraceId: ctx.selectedTraceId,
    selectedTrace: ctx.selectedTrace,
    setSelectedTraceId: ctx.setSelectedTraceId,
    isSelected: (id) => ctx.selectedTraceId === id,
    // Detail
    selectedTraceDetail: ctx.selectedTraceDetail,
    isLoadingDetail: ctx.isLoadingDetail,
    detailError: ctx.detailError,
    traceData: ctx.traceData,
    // Flow
    flowDefinition: ctx.flowDefinition,
    executionSteps: ctx.executionSteps,
    viewMode: ctx.viewMode,
    setViewMode: ctx.setViewMode,
    // Steps
    allSteps: ctx.allSteps,
    uniqueSteps: ctx.uniqueSteps,
    selectedStep: ctx.selectedStep,
    setSelectedStep: ctx.setSelectedStep,
    // Attempts
    attempts: ctx.attempts,
    hasMultipleAttempts: ctx.hasMultipleAttempts,
    activeAttemptIndex: ctx.activeAttemptIndex,
    setActiveAttemptIndex: ctx.setActiveAttemptIndex,
    currentAttemptSteps: ctx.currentAttemptSteps,
    duration: ctx.duration
  };
}
function TraceStatusIcon({ status, className }) {
  const config = getTraceStatusConfig(status);
  switch (config.iconName) {
    case "check":
      return /* @__PURE__ */ jsx(CheckCircle2, { className });
    case "x":
      return /* @__PURE__ */ jsx(XCircle, { className });
    case "loader":
      return /* @__PURE__ */ jsx(Loader2, { className: cn(className, "animate-spin") });
    case "ban":
      return /* @__PURE__ */ jsx(Ban, { className });
    case "clock":
    default:
      return /* @__PURE__ */ jsx(Clock, { className });
  }
}
function TracesHeader({
  title = "Traces",
  subtitle = "View automation execution traces",
  children,
  className
}) {
  const { total, runningCount, failedCount, completedCount } = useTracesStats();
  const { refetch, isLoading } = useTracesList();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-shrink-0", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white", children: title }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs(Badge, { variant: "default", children: [
            total,
            " total"
          ] }),
          runningCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "warning", children: [
            runningCount,
            " running"
          ] }),
          failedCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "danger", children: [
            failedCount,
            " failed"
          ] }),
          completedCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "success", children: [
            completedCount,
            " completed"
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "secondary", onClick: () => refetch(), disabled: isLoading, children: [
        /* @__PURE__ */ jsx(RefreshCw, { className: cn("mr-2 h-4 w-4", isLoading && "animate-spin") }),
        "Refresh"
      ] }),
      children
    ] })
  ] });
}
var DATE_RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All time" }
];
function getDateRangeFromPreset(preset) {
  const now = /* @__PURE__ */ new Date();
  const endDate = now.toISOString();
  switch (preset) {
    case "today": {
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { startDate: startOfDay.toISOString(), endDate };
    }
    case "yesterday": {
      const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const endOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { startDate: startOfYesterday.toISOString(), endDate: endOfYesterday.toISOString() };
    }
    case "7d": {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1e3);
      return { startDate: sevenDaysAgo.toISOString(), endDate };
    }
    case "30d": {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1e3);
      return { startDate: thirtyDaysAgo.toISOString(), endDate };
    }
    case "all":
    default:
      return null;
  }
}
function getPresetFromDateRange(dateRange) {
  if (!dateRange || !dateRange.startDate && !dateRange.endDate) return "all";
  const now = /* @__PURE__ */ new Date();
  const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
  if (!startDate) return "all";
  const diffMs = now.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1e3));
  if (diffDays <= 1) {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (startDate.getTime() >= startOfToday.getTime()) return "today";
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (startDate.getTime() >= startOfYesterday.getTime()) return "yesterday";
  }
  if (diffDays <= 7) return "7d";
  if (diffDays <= 30) return "30d";
  return "all";
}
function AutomationPicker({ selectedAutomationId, onSelect, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: automationsData, isLoading } = useAutomations({ limit: 100 });
  const automations = automationsData?.automations || [];
  const selectedAutomation = automations.find((a) => a.id === selectedAutomationId);
  const filteredAutomations = useMemo(() => {
    if (!searchQuery.trim()) return automations;
    const query = searchQuery.toLowerCase();
    return automations.filter((automation) => {
      const nameMatch = automation.name.toLowerCase().includes(query);
      const descMatch = automation.description?.toLowerCase().includes(query) || false;
      const triggerMatch = automation.triggerType?.toLowerCase().includes(query) || false;
      const idMatch = automation.id.toLowerCase().includes(query);
      const userIdMatch = automation.userId?.toLowerCase().includes(query) || false;
      return nameMatch || descMatch || triggerMatch || idMatch || userIdMatch;
    });
  }, [automations, searchQuery]);
  const handleSelect = (automation) => {
    onSelect(automation?.id || null);
    setIsOpen(false);
    setSearchQuery("");
  };
  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => setIsOpen(true),
        className: cn(
          "flex items-center justify-between w-full h-9 px-3 text-sm rounded-lg border",
          "bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700",
          "text-slate-900 dark:text-slate-100",
          "hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          className
        ),
        children: [
          /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2 truncate", children: [
            /* @__PURE__ */ jsx(Zap, { className: "h-4 w-4 text-slate-400 flex-shrink-0" }),
            /* @__PURE__ */ jsx("span", { className: cn(selectedAutomation ? "text-slate-900 dark:text-slate-100" : "text-slate-500"), children: isLoading ? "Loading..." : selectedAutomation?.name || "All automations" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
            selectedAutomation && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: handleClear,
                className: "p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors",
                children: /* @__PURE__ */ jsx(X, { className: "h-3.5 w-3.5 text-slate-400" })
              }
            ),
            /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 text-slate-400" })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        isOpen,
        onClose: () => {
          setIsOpen(false);
          setSearchQuery("");
        },
        title: "Select Automation",
        size: "md",
        children: /* @__PURE__ */ jsxs("div", { className: "p-4 space-y-4", children: [
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "text",
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
                placeholder: "Search by name, ID, userId, trigger...",
                className: cn(
                  "w-full h-10 pl-10 pr-10 text-sm rounded-lg border",
                  "bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700",
                  "text-slate-900 dark:text-slate-100",
                  "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ),
                autoFocus: true
              }
            ),
            searchQuery && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setSearchQuery(""),
                className: "absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4 text-slate-400" })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => handleSelect(null),
              className: cn(
                "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                !selectedAutomationId ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" : "hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent"
              ),
              children: [
                /* @__PURE__ */ jsx("div", { className: cn(
                  "flex items-center justify-center w-10 h-10 rounded-lg",
                  !selectedAutomationId ? "bg-blue-100 dark:bg-blue-900/40" : "bg-slate-100 dark:bg-slate-800"
                ), children: /* @__PURE__ */ jsx(Zap, { className: cn(
                  "h-5 w-5",
                  !selectedAutomationId ? "text-blue-600 dark:text-blue-400" : "text-slate-500"
                ) }) }),
                /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ jsx("p", { className: cn(
                    "text-sm font-medium",
                    !selectedAutomationId ? "text-blue-900 dark:text-blue-100" : "text-slate-900 dark:text-slate-100"
                  ), children: "All automations" }),
                  /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: "Show runs from all automations" })
                ] }),
                !selectedAutomationId && /* @__PURE__ */ jsx(CheckCircle2, { className: "h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" })
              ]
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "max-h-80 overflow-y-auto space-y-1", children: isLoading ? /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-8", children: /* @__PURE__ */ jsx(Loader2, { className: "h-6 w-6 animate-spin text-slate-400" }) }) : filteredAutomations.length === 0 ? /* @__PURE__ */ jsxs("div", { className: "py-8 text-center", children: [
            /* @__PURE__ */ jsx(Search, { className: "h-8 w-8 text-slate-300 mx-auto" }),
            /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: searchQuery ? "No automations match your search" : "No automations found" })
          ] }) : filteredAutomations.map((automation) => {
            const isSelected = automation.id === selectedAutomationId;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => handleSelect(automation),
                className: cn(
                  "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                  isSelected ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" : "hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent"
                ),
                children: [
                  /* @__PURE__ */ jsx("div", { className: cn(
                    "flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0",
                    isSelected ? "bg-blue-100 dark:bg-blue-900/40" : "bg-slate-100 dark:bg-slate-800"
                  ), children: /* @__PURE__ */ jsx(Zap, { className: cn(
                    "h-5 w-5",
                    isSelected ? "text-blue-600 dark:text-blue-400" : "text-slate-500"
                  ) }) }),
                  /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsx("p", { className: cn(
                      "text-sm font-medium truncate",
                      isSelected ? "text-blue-900 dark:text-blue-100" : "text-slate-900 dark:text-slate-100"
                    ), children: automation.name }) }),
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-0.5 flex-wrap", children: [
                      /* @__PURE__ */ jsxs(
                        "span",
                        {
                          className: "font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
                          title: `Full ID: ${automation.id}`,
                          children: [
                            "#",
                            truncateTraceId(automation.id, 8)
                          ]
                        }
                      ),
                      automation.userId && /* @__PURE__ */ jsxs(
                        "span",
                        {
                          className: "font-mono text-xs px-1.5 py-0.5 rounded bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400",
                          title: `User ID: ${automation.userId}`,
                          children: [
                            "user:",
                            truncateTraceId(automation.userId, 6)
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400 capitalize", children: automation.triggerType?.toLowerCase().replace("_", " ") || "Unknown" }),
                      automation.stats && /* @__PURE__ */ jsxs(Fragment, { children: [
                        /* @__PURE__ */ jsx("span", { className: "text-slate-300 dark:text-slate-600", children: "\u2022" }),
                        /* @__PURE__ */ jsxs("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: [
                          automation.stats.totalRuns,
                          " runs"
                        ] }),
                        automation.stats.successRate !== void 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
                          /* @__PURE__ */ jsx("span", { className: "text-slate-300 dark:text-slate-600", children: "\u2022" }),
                          /* @__PURE__ */ jsxs("span", { className: cn(
                            "text-xs",
                            automation.stats.successRate >= 90 ? "text-emerald-600 dark:text-emerald-400" : automation.stats.successRate >= 70 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"
                          ), children: [
                            automation.stats.successRate.toFixed(0),
                            "% success"
                          ] })
                        ] })
                      ] })
                    ] })
                  ] }),
                  isSelected && /* @__PURE__ */ jsx(CheckCircle2, { className: "h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" })
                ]
              },
              automation.id
            );
          }) })
        ] })
      }
    )
  ] });
}
function TracesFilters({
  showStatusFilter = true,
  showAutomationFilter = true,
  showDateFilter = true,
  children,
  className
}) {
  const { filters, setFilters, clearFilters, hasActiveFilters } = useTracesFilters();
  const selectedAutomationId = filters.automationIds[0] || null;
  const handleAutomationSelect = (automationId) => {
    setFilters({ automationIds: automationId ? [automationId] : [] });
  };
  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    const dateRange = getDateRangeFromPreset(value);
    setFilters({ dateRange });
  };
  const currentDatePreset = getPresetFromDateRange(filters.dateRange);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap flex-shrink-0", className), children: [
    showStatusFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-48", children: /* @__PURE__ */ jsx(
      MultiSelect,
      {
        options: TRACE_STATUS_OPTIONS,
        value: filters.status,
        onChange: (status) => setFilters({ status }),
        placeholder: "Status...",
        presets: TRACE_STATUS_PRESETS
      }
    ) }),
    showAutomationFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-64", children: /* @__PURE__ */ jsx(
      AutomationPicker,
      {
        selectedAutomationId,
        onSelect: handleAutomationSelect
      }
    ) }),
    showDateFilter && /* @__PURE__ */ jsx("div", { className: "w-full sm:w-44", children: /* @__PURE__ */ jsx("div", { className: "relative", children: /* @__PURE__ */ jsx(
      Select,
      {
        options: DATE_RANGE_PRESETS,
        value: currentDatePreset,
        onChange: handleDateRangeChange
      }
    ) }) }),
    hasActiveFilters && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: clearFilters, children: "Clear filters" }),
    children
  ] });
}
function TracesList({
  renderTrace,
  emptyTitle = "No traces found",
  emptyDescription = "No traces match your filters.",
  className
}) {
  const { traces, isLoading, error, refetch } = useTracesList();
  const { hasActiveFilters } = useTracesFilters();
  const { onTraceClick } = useTracesNavigation();
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load traces" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500 dark:text-slate-400", children: error.message }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: [1, 2, 3, 4, 5].map((i) => /* @__PURE__ */ jsx(TraceCardSkeleton, {}, i)) });
  }
  if (traces.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        icon: /* @__PURE__ */ jsx(EmptyBoxIcon, {}),
        title: emptyTitle,
        description: hasActiveFilters ? "Try adjusting your filters" : emptyDescription,
        className
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: traces.map(
    (trace, index) => renderTrace ? renderTrace(trace, index) : /* @__PURE__ */ jsx(DefaultTraceCard, { trace, onTraceClick }, trace.id)
  ) });
}
function TracesTable({ onRowClick, onRowDoubleClick, columns: customColumns, className }) {
  const { traces, isLoading, error, refetch } = useTracesList();
  const { isSelected, setSelectedTraceId } = useTracesSelection();
  const defaultColumns = [
    {
      field: "id",
      headerName: "Run ID",
      flex: 1,
      minWidth: 150,
      cellRenderer: (params) => /* @__PURE__ */ jsx("span", { className: "font-mono text-blue-600 dark:text-blue-400", children: truncateTraceId(params.value) })
    },
    {
      field: "automation.name",
      headerName: "Automation",
      flex: 2,
      minWidth: 200,
      valueGetter: (params) => params.data?.automation?.name || params.data?.automationId || "-"
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      cellRenderer: (params) => /* @__PURE__ */ jsx(TracesStatusBadge, { status: params.value })
    },
    {
      headerName: "Started",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.data?.startedAt || params.data?.createdAt || null,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : "-"
    },
    {
      field: "completedAt",
      headerName: "Completed",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : "-"
    },
    {
      headerName: "Duration",
      width: 100,
      valueGetter: (params) => {
        if (params.data?.duration != null) return params.data.duration;
        const startTime = params.data?.startedAt || params.data?.createdAt;
        if (!startTime || !params.data?.completedAt) return null;
        const start = new Date(startTime).getTime();
        const end = new Date(params.data.completedAt).getTime();
        return end - start;
      },
      valueFormatter: (params) => params.value ? `${params.value}ms` : "-"
    }
  ];
  if (error) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load traces" }),
      /* @__PURE__ */ jsx(Button, { onClick: () => refetch(), className: "mt-4", children: "Retry" })
    ] }) });
  }
  return /* @__PURE__ */ jsx(Card, { className: cn("p-0", className), children: /* @__PURE__ */ jsx(
    DataGrid,
    {
      data: traces,
      columns: customColumns || defaultColumns,
      loading: isLoading,
      onRowClick: (row) => {
        setSelectedTraceId(row.id);
        onRowClick?.(row);
      },
      onRowDoubleClick,
      pagination: true,
      pageSize: 25,
      rowSelection: "single",
      getRowId: (row) => row.id,
      emptyTitle: "No traces yet",
      emptyDescription: "Run an automation to see traces here"
    }
  ) });
}
function TracesPagination({ className }) {
  const { page, pageSize, totalPages, total, setPage } = useTracesList();
  if (totalPages <= 1) return null;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "text-sm text-slate-500 dark:text-slate-400", children: [
      "Showing ",
      startItem,
      "-",
      endItem,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page - 1), disabled: page <= 1, children: [
        /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" }),
        "Previous"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: [
        "Page ",
        page,
        " of ",
        totalPages
      ] }),
      /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setPage(page + 1), disabled: page >= totalPages, children: [
        "Next",
        /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
      ] })
    ] })
  ] });
}
function TracesDetail({ className, children }) {
  const { isLoadingDetail, detailError, traceData, duration, selectedTraceDetail } = useTraceDetail();
  if (isLoadingDetail) {
    return /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center h-64", children: /* @__PURE__ */ jsx(Loader2, { className: "h-8 w-8 animate-spin text-blue-600" }) });
  }
  if (detailError) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: "mx-auto h-12 w-12 text-red-500" }),
      /* @__PURE__ */ jsx("h3", { className: "mt-4 text-lg font-semibold text-slate-900 dark:text-white", children: "Failed to load trace" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: detailError.message })
    ] }) });
  }
  if (!traceData || !selectedTraceDetail) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(Play, { className: "mx-auto h-12 w-12 text-slate-300" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: "Select a trace to view details" })
    ] }) });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-6", className), children: [
    /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs("div", { className: "p-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-4 sm:grid-cols-4", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-medium uppercase tracking-wider text-slate-500", children: "Run ID" }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 font-mono text-sm text-slate-900 dark:text-white", children: truncateTraceId(traceData.run.id, 12) })
        ] }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-medium uppercase tracking-wider text-slate-500", children: "Automation" }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-900 dark:text-white", children: selectedTraceDetail.automation?.name || selectedTraceDetail.run.automationId })
        ] }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-medium uppercase tracking-wider text-slate-500", children: "Started" }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-900 dark:text-white", children: selectedTraceDetail.run.createdAt ? new Date(selectedTraceDetail.run.createdAt).toLocaleString() : "-" })
        ] }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-medium uppercase tracking-wider text-slate-500", children: "Duration" }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-900 dark:text-white", children: duration ? `${duration}ms` : "-" })
        ] })
      ] }),
      traceData.run.error && /* @__PURE__ */ jsxs("div", { className: "mt-4 rounded-lg bg-red-50 p-3 dark:bg-red-950/30", children: [
        /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-red-700 dark:text-red-400", children: "Error" }),
        /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-red-600 dark:text-red-300", children: traceData.run.error.message })
      ] })
    ] }) }),
    children
  ] });
}
function TracesViewToggle({ className }) {
  const { viewMode, setViewMode } = useTraceFlow();
  const { traceData } = useTraceDetail();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-1 bg-slate-50 dark:bg-slate-800", children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          onClick: () => setViewMode("flow"),
          className: cn(
            "flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
            viewMode === "flow" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          ),
          children: [
            /* @__PURE__ */ jsx(GitBranch, { className: "h-4 w-4" }),
            "Flow"
          ]
        }
      ),
      /* @__PURE__ */ jsxs(
        "button",
        {
          onClick: () => setViewMode("timeline"),
          className: cn(
            "flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
            viewMode === "timeline" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          ),
          children: [
            /* @__PURE__ */ jsx(List, { className: "h-4 w-4" }),
            "Timeline"
          ]
        }
      )
    ] }),
    traceData && /* @__PURE__ */ jsxs("span", { className: "text-sm text-slate-500", children: [
      traceData.steps.length,
      " steps"
    ] })
  ] });
}
function TracesAttemptNavigation({ className }) {
  const { attempts, hasMultipleAttempts, activeAttemptIndex, setActiveAttemptIndex } = useTraceDetail();
  if (!hasMultipleAttempts) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-sm text-slate-500", children: [
      /* @__PURE__ */ jsx(RotateCcw, { className: "h-4 w-4" }),
      /* @__PURE__ */ jsxs("span", { children: [
        attempts.length,
        " attempts"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setActiveAttemptIndex(Math.max(0, activeAttemptIndex - 1)),
          disabled: activeAttemptIndex === 0,
          className: "flex items-center justify-center h-8 w-8 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors",
          children: /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" })
        }
      ),
      /* @__PURE__ */ jsxs("span", { className: "px-2 text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[60px] text-center", children: [
        activeAttemptIndex + 1,
        " / ",
        attempts.length
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setActiveAttemptIndex(Math.min(attempts.length - 1, activeAttemptIndex + 1)),
          disabled: activeAttemptIndex === attempts.length - 1,
          className: "flex items-center justify-center h-8 w-8 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors",
          children: /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
        }
      )
    ] }),
    activeAttemptIndex !== attempts.length - 1 && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: () => setActiveAttemptIndex(null),
        className: "text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300",
        children: "Go to latest"
      }
    )
  ] });
}
function TracesFlowView({ className, height = 500 }) {
  const { flowDefinition, executionSteps } = useTraceFlow();
  const { selectedStep, setSelectedStep } = useTraceStep();
  const { traceData } = useTraceDetail();
  if (!flowDefinition || !traceData) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(Play, { className: "mx-auto h-8 w-8 text-slate-300" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: "No flow data available" })
    ] }) });
  }
  return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-4", children: [
    /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-white mb-4", children: "Execution Flow" }),
    /* @__PURE__ */ jsx("div", { style: { height }, children: /* @__PURE__ */ jsx(
      FlowViewWithExecution,
      {
        flow: flowDefinition,
        executionSteps,
        selectedNodeId: selectedStep?.nodeId,
        onNodeClick: (node) => {
          const step = traceData.steps.find((s) => s.nodeId === node.id);
          if (step) setSelectedStep(step);
        },
        showControls: true,
        showMiniMap: false
      }
    ) })
  ] }) });
}
function TracesTimelineView({ className }) {
  const { traceData } = useTraceDetail();
  const { selectedStep, setSelectedStep } = useTraceStep();
  if (!traceData) {
    return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(Play, { className: "mx-auto h-8 w-8 text-slate-300" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: "No timeline data available" })
    ] }) });
  }
  return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-4", children: [
    /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-white", children: "Execution Timeline" }),
    /* @__PURE__ */ jsx("div", { className: "mt-4", children: /* @__PURE__ */ jsx(TraceTimeline, { trace: traceData, selectedStepId: selectedStep?.id, onStepSelect: setSelectedStep }) })
  ] }) });
}
function TracesStepDetails({ className }) {
  const { selectedStep } = useTraceStep();
  return /* @__PURE__ */ jsx(Card, { className, children: /* @__PURE__ */ jsxs("div", { className: "p-4", children: [
    /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-white", children: "Step Details" }),
    /* @__PURE__ */ jsx("div", { className: "mt-4", children: selectedStep ? /* @__PURE__ */ jsx(StepDetails, { step: selectedStep }) : /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center py-12 text-center", children: [
      /* @__PURE__ */ jsx(Play, { className: "h-8 w-8 text-slate-300" }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: "Select a step from the timeline to view details" })
    ] }) })
  ] }) });
}
function TraceCardSkeleton() {
  return /* @__PURE__ */ jsx(Card, { className: "p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-8 rounded-full flex-shrink-0" }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-32" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-24" })
    ] }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-16 flex-shrink-0" })
  ] }) });
}
function DefaultTraceCard({ trace, onTraceClick }) {
  const { isSelected, setSelectedTraceId } = useTracesSelection();
  const selected = isSelected(trace.id);
  const statusConfig5 = getTraceStatusConfig(trace.status);
  const [copied, setCopied] = React.useState(false);
  const handleCopyId = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(trace.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  };
  const handleClick = () => {
    setSelectedTraceId(trace.id);
    if (onTraceClick) {
      onTraceClick(trace);
    }
  };
  const normalizedStatus = trace.status?.toLowerCase();
  return /* @__PURE__ */ jsxs(
    Card,
    {
      className: cn("p-4 cursor-pointer transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50", selected && "ring-2 ring-blue-500 dark:ring-blue-400"),
      onClick: handleClick,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
          /* @__PURE__ */ jsx("div", { className: cn("p-2 rounded-full flex-shrink-0", statusConfig5.bgColor), children: /* @__PURE__ */ jsx(TraceStatusIcon, { status: trace.status, className: cn("h-4 w-4", statusConfig5.color) }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx("div", { className: "font-medium text-sm text-slate-900 dark:text-white truncate", children: trace.automation?.name || "Unknown automation" }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-1", children: [
              /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: handleCopyId,
                  className: "inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors",
                  title: copied ? "Copied!" : "Click to copy run ID",
                  children: [
                    /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-slate-600 dark:text-slate-400", children: truncateTraceId(trace.id, 8) }),
                    copied ? /* @__PURE__ */ jsx(CheckCircle2, { className: "h-3 w-3 text-green-500" }) : /* @__PURE__ */ jsx("span", { className: "text-slate-400 text-xs", children: "\u{1F4CB}" })
                  ]
                }
              ),
              /* @__PURE__ */ jsx(TracesStatusBadge, { status: trace.status })
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-1 flex-shrink-0 text-right", children: [
            /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: trace.startedAt || trace.createdAt ? formatTraceTimeAgo(trace.startedAt || trace.createdAt) : "-" }),
            /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-slate-600 dark:text-slate-300", children: trace.duration != null ? `${trace.duration}ms` : formatTraceDuration(trace.startedAt || trace.createdAt, trace.completedAt) })
          ] })
        ] }),
        normalizedStatus === "failed" && trace.error && /* @__PURE__ */ jsx("div", { className: "mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300 truncate", children: typeof trace.error === "string" ? trace.error : trace.error.message })
      ]
    }
  );
}
function TracesStatusBadge({ status, showIcon = true, className }) {
  const config = getTraceStatusConfig(status);
  return /* @__PURE__ */ jsxs(Badge, { className: cn(config.bgColor, config.color, className), children: [
    showIcon && /* @__PURE__ */ jsx(TraceStatusIcon, { status, className: "h-3 w-3 mr-1" }),
    config.label
  ] });
}
function TracesRoot({ children, ...providerProps }) {
  return /* @__PURE__ */ jsx(TracesProvider, { ...providerProps, children });
}
var Traces = {
  /** Root provider component - wraps all other components */
  Root: TracesRoot,
  /** Header with title, stats, and refresh button */
  Header: TracesHeader,
  /** Filter controls for status */
  Filters: TracesFilters,
  /** List of trace cards */
  List: TracesList,
  /** Table view of traces with DataGrid */
  Table: TracesTable,
  /** Pagination controls */
  Pagination: TracesPagination,
  /** Trace detail wrapper with summary card */
  Detail: TracesDetail,
  /** Flow/Timeline view toggle */
  ViewToggle: TracesViewToggle,
  /** Navigation between retry attempts */
  AttemptNavigation: TracesAttemptNavigation,
  /** Flow visualization with execution overlay */
  FlowView: TracesFlowView,
  /** Timeline visualization */
  TimelineView: TracesTimelineView,
  /** Step details panel */
  StepDetails: TracesStepDetails,
  /** Status badge component */
  StatusBadge: TracesStatusBadge,
  /** Loading skeleton for trace cards */
  ItemSkeleton: TraceCardSkeleton
};
var AlertsInternalContext = createContext(null);
function AlertsProvider({
  children,
  defaultStatus = [],
  defaultSeverity = [],
  pageSize: initialPageSize = 20
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    status: defaultStatus,
    severity: defaultSeverity,
    ruleId: null
  });
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const historyParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: filters.status.length === 1 ? filters.status[0] : void 0,
      severity: filters.severity.length === 1 ? filters.severity[0] : void 0,
      ruleId: filters.ruleId || void 0
    }),
    [page, pageSize, filters]
  );
  const { data: rulesData, isLoading: rulesLoading, error: rulesError } = useAlertRules();
  const { data: historyData, isLoading: alertsLoading, error: alertsError } = useAlertHistory(historyParams);
  const { data: mutesData, isLoading: mutesLoading } = useAlertMutes();
  const { data: statsData, isLoading: statsLoading } = useAlertStats();
  const createRuleMutation = useCreateAlertRule();
  const updateRuleMutation = useUpdateAlertRule();
  const deleteRuleMutation = useDeleteAlertRule();
  const acknowledgeMutation = useAcknowledgeAlert();
  const resolveMutation = useResolveAlert();
  const createMuteMutation = useCreateAlertMute();
  const deleteMuteMutation = useDeleteAlertMute();
  const rules = rulesData?.rules ?? [];
  const alerts = historyData?.alerts ?? [];
  const alertsTotal = historyData?.total ?? 0;
  const mutes = mutesData?.mutes ?? [];
  const totalPages = Math.ceil(alertsTotal / pageSize);
  const selectedRule = useMemo(() => {
    if (!selectedRuleId) return null;
    return rules.find((r) => r.id === selectedRuleId) ?? null;
  }, [selectedRuleId, rules]);
  const selectedAlert = useMemo(() => {
    if (!selectedAlertId) return null;
    return alerts.find((a) => a.id === selectedAlertId) ?? null;
  }, [selectedAlertId, alerts]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      status: [],
      severity: [],
      ruleId: null
    });
    setPage(1);
  }, []);
  const hasActiveFilters = filters.status.length > 0 || filters.severity.length > 0 || filters.ruleId !== null;
  const createRule = useCallback(
    async (data) => {
      return createRuleMutation.mutateAsync(data);
    },
    [createRuleMutation]
  );
  const updateRule = useCallback(
    async (id, data) => {
      return updateRuleMutation.mutateAsync({ id, data });
    },
    [updateRuleMutation]
  );
  const deleteRule = useCallback(
    async (id) => {
      await deleteRuleMutation.mutateAsync(id);
    },
    [deleteRuleMutation]
  );
  const acknowledgeAlert = useCallback(
    async (id, userId) => {
      return acknowledgeMutation.mutateAsync({ id, userId });
    },
    [acknowledgeMutation]
  );
  const resolveAlert = useCallback(
    async (id) => {
      return resolveMutation.mutateAsync(id);
    },
    [resolveMutation]
  );
  const createMute = useCallback(
    async (data) => {
      return createMuteMutation.mutateAsync(data);
    },
    [createMuteMutation]
  );
  const deleteMute = useCallback(
    async (id) => {
      await deleteMuteMutation.mutateAsync(id);
    },
    [deleteMuteMutation]
  );
  const value = {
    rules,
    rulesLoading,
    rulesError: rulesError instanceof Error ? rulesError : rulesError ? new Error(String(rulesError)) : null,
    alerts,
    alertsTotal,
    alertsLoading,
    alertsError: alertsError instanceof Error ? alertsError : alertsError ? new Error(String(alertsError)) : null,
    mutes,
    mutesLoading,
    stats: statsData ?? null,
    statsLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedRuleId,
    setSelectedRuleId,
    selectedRule,
    selectedAlertId,
    setSelectedAlertId,
    selectedAlert,
    createRule,
    updateRule,
    deleteRule,
    acknowledgeAlert,
    resolveAlert,
    createMute,
    deleteMute,
    isCreatingRule: createRuleMutation.isPending,
    isUpdatingRule: updateRuleMutation.isPending,
    isDeletingRule: deleteRuleMutation.isPending
  };
  return /* @__PURE__ */ jsx(AlertsInternalContext.Provider, { value, children });
}
function useAlertsList() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsList must be used within AlertsProvider");
  }
  return {
    rules: context.rules,
    alerts: context.alerts,
    total: context.alertsTotal,
    isLoading: context.rulesLoading || context.alertsLoading,
    error: context.rulesError || context.alertsError,
    mutes: context.mutes,
    stats: context.stats
  };
}
function useAlertsFilters() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsFilters must be used within AlertsProvider");
  }
  return {
    filters: context.filters,
    setFilters: context.setFilters,
    clearFilters: context.clearFilters,
    hasActiveFilters: context.hasActiveFilters
  };
}
function useAlertsPagination() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsPagination must be used within AlertsProvider");
  }
  return {
    page: context.page,
    pageSize: context.pageSize,
    setPage: context.setPage,
    setPageSize: context.setPageSize,
    totalPages: context.totalPages,
    total: context.alertsTotal
  };
}
function useAlertsSelection() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsSelection must be used within AlertsProvider");
  }
  return {
    selectedRuleId: context.selectedRuleId,
    setSelectedRuleId: context.setSelectedRuleId,
    selectedRule: context.selectedRule,
    selectedAlertId: context.selectedAlertId,
    setSelectedAlertId: context.setSelectedAlertId,
    selectedAlert: context.selectedAlert
  };
}
function useAlertsActions() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsActions must be used within AlertsProvider");
  }
  return {
    createRule: context.createRule,
    updateRule: context.updateRule,
    deleteRule: context.deleteRule,
    acknowledgeAlert: context.acknowledgeAlert,
    resolveAlert: context.resolveAlert,
    createMute: context.createMute,
    deleteMute: context.deleteMute,
    isCreatingRule: context.isCreatingRule,
    isUpdatingRule: context.isUpdatingRule,
    isDeletingRule: context.isDeletingRule
  };
}
function useAlertsStatsHook() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsStatsHook must be used within AlertsProvider");
  }
  return {
    stats: context.stats,
    isLoading: context.statsLoading
  };
}
function useAlertsContext() {
  const context = useContext(AlertsInternalContext);
  if (!context) {
    throw new Error("useAlertsContext must be used within AlertsProvider");
  }
  return context;
}
var severityBadgeVariants = cva(
  "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
  {
    variants: {
      severity: {
        INFO: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        WARNING: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
        CRITICAL: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
      }
    },
    defaultVariants: {
      severity: "INFO"
    }
  }
);
function AlertsSeverityBadge({ severity, className }) {
  return /* @__PURE__ */ jsx("span", { className: cn(severityBadgeVariants({ severity }), className), children: severity });
}
var statusBadgeVariants2 = cva(
  "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
  {
    variants: {
      status: {
        FIRING: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
        ACKNOWLEDGED: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
        RESOLVED: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
      }
    },
    defaultVariants: {
      status: "FIRING"
    }
  }
);
function AlertsStatusBadge({ status, className }) {
  return /* @__PURE__ */ jsx("span", { className: cn(statusBadgeVariants2({ status }), className), children: status });
}
var CONDITION_TYPE_LABELS = {
  ERROR_RATE_THRESHOLD: "Error Rate",
  SUCCESS_RATE_DROP: "Success Rate Drop",
  NEW_ERROR_TYPE: "New Error Type",
  ERROR_RECURRENCE: "Error Recurrence",
  ERROR_SPIKE: "Error Spike",
  AUTOMATION_FAILURE_STREAK: "Failure Streak",
  TEST_SUITE_FAILURE: "Test Suite Failure"
};
function AlertsConditionTypeBadge({ conditionType, className }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
        className
      ),
      children: CONDITION_TYPE_LABELS[conditionType] || conditionType
    }
  );
}
function AlertsHeader({
  title = "Alerts",
  description = "Configure alert rules and view alert history",
  children,
  className
}) {
  const { stats, isLoading } = useAlertsStatsHook();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 md:flex-row md:items-center md:justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold text-gray-900 dark:text-white", children: title }),
      description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400", children: description })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
      !isLoading && stats && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 text-sm", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-red-600 dark:text-red-400", children: [
          stats.alerts.firing,
          " firing"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "text-yellow-600 dark:text-yellow-400", children: [
          stats.alerts.acknowledged,
          " ack"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "text-gray-500", children: [
          stats.rules.active,
          "/",
          stats.rules.total,
          " rules"
        ] })
      ] }),
      children
    ] })
  ] });
}
function AlertsRulesList({ className, onRuleClick, renderRule }) {
  const { rules, isLoading } = useAlertsList();
  const { selectedRuleId, setSelectedRuleId } = useAlertsSelection();
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: 3 }).map((_, i) => /* @__PURE__ */ jsx(AlertRuleSkeleton, {}, i)) });
  }
  if (rules.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("text-center py-8 text-gray-500 dark:text-gray-400", className), children: "No alert rules configured" });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: rules.map(
    (rule) => renderRule ? renderRule(rule) : /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "p-4 rounded-lg border cursor-pointer transition-colors",
          selectedRuleId === rule.id ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        ),
        onClick: () => {
          setSelectedRuleId(rule.id);
          onRuleClick?.(rule);
        },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: cn("w-2 h-2 rounded-full", rule.enabled ? "bg-green-500" : "bg-gray-400") }),
              /* @__PURE__ */ jsx("span", { className: "font-medium text-gray-900 dark:text-white", children: rule.name })
            ] }),
            /* @__PURE__ */ jsx(AlertsConditionTypeBadge, { conditionType: rule.conditionType })
          ] }),
          rule.description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-1", children: rule.description }),
          /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center gap-3 text-xs text-gray-500", children: [
            /* @__PURE__ */ jsxs("span", { children: [
              "Cooldown: ",
              rule.cooldownMinutes,
              "m"
            ] }),
            rule._count && /* @__PURE__ */ jsxs("span", { children: [
              rule._count.alerts,
              " alerts"
            ] })
          ] })
        ]
      },
      rule.id
    )
  ) });
}
function AlertsHistoryList({ className, onAlertClick, renderAlert }) {
  const { alerts, isLoading } = useAlertsList();
  const { selectedAlertId, setSelectedAlertId } = useAlertsSelection();
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx(AlertHistorySkeleton, {}, i)) });
  }
  if (alerts.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("text-center py-8 text-gray-500 dark:text-gray-400", className), children: "No alerts found" });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: alerts.map(
    (alert) => renderAlert ? renderAlert(alert) : /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "p-4 rounded-lg border cursor-pointer transition-colors",
          selectedAlertId === alert.id ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        ),
        onClick: () => {
          setSelectedAlertId(alert.id);
          onAlertClick?.(alert);
        },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx("span", { className: "font-medium text-gray-900 dark:text-white", children: alert.title }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(AlertsSeverityBadge, { severity: alert.severity }),
              /* @__PURE__ */ jsx(AlertsStatusBadge, { status: alert.status })
            ] })
          ] }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2", children: alert.message }),
          /* @__PURE__ */ jsx("div", { className: "mt-2 text-xs text-gray-500", children: new Date(alert.firedAt).toLocaleString() })
        ]
      },
      alert.id
    )
  ) });
}
function AlertsPagination({ className }) {
  const { page, pageSize, setPage, totalPages, total } = useAlertsPagination();
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("span", { className: "text-sm text-gray-500 dark:text-gray-400", children: [
      "Showing ",
      start,
      "-",
      end,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setPage(page - 1),
          disabled: page === 1,
          className: "px-3 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800",
          children: "Previous"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setPage(page + 1),
          disabled: page === totalPages,
          className: "px-3 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800",
          children: "Next"
        }
      )
    ] })
  ] });
}
function AlertsQuickActions({
  alertId,
  currentStatus,
  className,
  onSuccess
}) {
  const { acknowledgeAlert, resolveAlert } = useAlertsActions();
  const handleAcknowledge = async () => {
    await acknowledgeAlert(alertId);
    onSuccess?.();
  };
  const handleResolve = async () => {
    await resolveAlert(alertId);
    onSuccess?.();
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex gap-2", className), children: [
    currentStatus === "FIRING" && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: handleAcknowledge,
        className: "px-3 py-1 text-sm rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300",
        children: "Acknowledge"
      }
    ),
    currentStatus !== "RESOLVED" && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: handleResolve,
        className: "px-3 py-1 text-sm rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300",
        children: "Resolve"
      }
    )
  ] });
}
function AlertRuleSkeleton() {
  return /* @__PURE__ */ jsxs("div", { className: "p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("div", { className: "w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" }),
        /* @__PURE__ */ jsx("div", { className: "h-4 w-32 bg-gray-300 dark:bg-gray-600 rounded" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "h-5 w-20 bg-gray-300 dark:bg-gray-600 rounded" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "mt-2 h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded" })
  ] });
}
function AlertHistorySkeleton() {
  return /* @__PURE__ */ jsxs("div", { className: "p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("div", { className: "h-4 w-40 bg-gray-300 dark:bg-gray-600 rounded" }),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx("div", { className: "h-5 w-16 bg-gray-300 dark:bg-gray-600 rounded-full" }),
        /* @__PURE__ */ jsx("div", { className: "h-5 w-16 bg-gray-300 dark:bg-gray-600 rounded-full" })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "mt-2 h-3 w-64 bg-gray-200 dark:bg-gray-700 rounded" }),
    /* @__PURE__ */ jsx("div", { className: "mt-2 h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" })
  ] });
}
function AlertsRoot({ children, ...providerProps }) {
  return /* @__PURE__ */ jsx(AlertsProvider, { ...providerProps, children });
}
var Alerts = {
  /** Root provider component - wraps all other components */
  Root: AlertsRoot,
  /** Header with title, stats */
  Header: AlertsHeader,
  /** List of alert rules */
  RulesList: AlertsRulesList,
  /** List of alert history */
  HistoryList: AlertsHistoryList,
  /** Pagination controls */
  Pagination: AlertsPagination,
  /** Quick actions for alerts */
  QuickActions: AlertsQuickActions,
  /** Severity badge component */
  SeverityBadge: AlertsSeverityBadge,
  /** Status badge component */
  StatusBadge: AlertsStatusBadge,
  /** Condition type badge component */
  ConditionTypeBadge: AlertsConditionTypeBadge,
  /** Loading skeleton for rules */
  RuleSkeleton: AlertRuleSkeleton,
  /** Loading skeleton for history */
  HistorySkeleton: AlertHistorySkeleton
};
var TicketsInternalContext = createContext(null);
function TicketsProvider({
  children,
  defaultStatus = [],
  defaultPriority = [],
  assigneeId,
  pageSize: initialPageSize = 20
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    status: defaultStatus,
    priority: defaultPriority,
    assigneeId: assigneeId || null
  });
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: filters.status.length === 1 ? filters.status[0] : void 0,
      priority: filters.priority.length === 1 ? filters.priority[0] : void 0,
      assigneeId: filters.assigneeId || void 0
    }),
    [page, pageSize, filters]
  );
  const { data, isLoading, error: fetchError, refetch } = useTickets(queryParams);
  const createMutation = useCreateTicket();
  const updateMutation = useUpdateTicket();
  const statusMutation = useUpdateTicketStatus();
  const assignMutation = useAssignTicket();
  const commentMutation = useAddTicketComment();
  const linearMutation = useSyncTicketToLinear();
  const tickets = data?.tickets ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const selectedTicket = useMemo(() => {
    if (!selectedTicketId) return null;
    return tickets.find((t) => t.id === selectedTicketId) ?? null;
  }, [selectedTicketId, tickets]);
  const openCount = useMemo(() => tickets.filter((t) => t.status === "OPEN").length, [tickets]);
  const inProgressCount = useMemo(() => tickets.filter((t) => t.status === "IN_PROGRESS").length, [tickets]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({
      status: [],
      priority: [],
      assigneeId: assigneeId || null
    });
    setPage(1);
  }, [assigneeId]);
  const hasActiveFilters = filters.status.length > 0 || filters.priority.length > 0 || filters.assigneeId !== null && filters.assigneeId !== assigneeId;
  const createTicket = useCallback(
    async (data2) => {
      return createMutation.mutateAsync(data2);
    },
    [createMutation]
  );
  const updateTicket = useCallback(
    async (id, data2) => {
      return updateMutation.mutateAsync({ id, data: data2 });
    },
    [updateMutation]
  );
  const updateStatus = useCallback(
    async (id, status) => {
      return statusMutation.mutateAsync({ id, status });
    },
    [statusMutation]
  );
  const assignTicket = useCallback(
    async (id, assigneeId2) => {
      return assignMutation.mutateAsync({ id, assigneeId: assigneeId2 });
    },
    [assignMutation]
  );
  const addComment = useCallback(
    async (id, data2) => {
      return commentMutation.mutateAsync({ id, data: data2 });
    },
    [commentMutation]
  );
  const syncToLinear = useCallback(
    async (id) => {
      return linearMutation.mutateAsync(id);
    },
    [linearMutation]
  );
  const value = {
    tickets,
    total,
    isLoading,
    error: fetchError instanceof Error ? fetchError : fetchError ? new Error(String(fetchError)) : null,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedTicketId,
    setSelectedTicketId,
    selectedTicket,
    createTicket,
    updateTicket,
    updateStatus,
    assignTicket,
    addComment,
    syncToLinear,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending || statusMutation.isPending || assignMutation.isPending,
    openCount,
    inProgressCount
  };
  return /* @__PURE__ */ jsx(TicketsInternalContext.Provider, { value, children });
}
function useTicketsList() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsList must be used within TicketsProvider");
  }
  return {
    tickets: context.tickets,
    total: context.total,
    isLoading: context.isLoading,
    error: context.error
  };
}
function useTicketsFilters() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsFilters must be used within TicketsProvider");
  }
  return {
    filters: context.filters,
    setFilters: context.setFilters,
    clearFilters: context.clearFilters,
    hasActiveFilters: context.hasActiveFilters
  };
}
function useTicketsPagination() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsPagination must be used within TicketsProvider");
  }
  return {
    page: context.page,
    pageSize: context.pageSize,
    setPage: context.setPage,
    setPageSize: context.setPageSize,
    totalPages: context.totalPages,
    total: context.total
  };
}
function useTicketsSelection() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsSelection must be used within TicketsProvider");
  }
  return {
    selectedTicketId: context.selectedTicketId,
    setSelectedTicketId: context.setSelectedTicketId,
    selectedTicket: context.selectedTicket
  };
}
function useTicketsActions() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsActions must be used within TicketsProvider");
  }
  return {
    createTicket: context.createTicket,
    updateTicket: context.updateTicket,
    updateStatus: context.updateStatus,
    assignTicket: context.assignTicket,
    addComment: context.addComment,
    syncToLinear: context.syncToLinear,
    isCreating: context.isCreating,
    isUpdating: context.isUpdating
  };
}
function useTicketsStats() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsStats must be used within TicketsProvider");
  }
  return {
    total: context.total,
    openCount: context.openCount,
    inProgressCount: context.inProgressCount
  };
}
function useTicketsContext() {
  const context = useContext(TicketsInternalContext);
  if (!context) {
    throw new Error("useTicketsContext must be used within TicketsProvider");
  }
  return context;
}
var statusBadgeVariants3 = cva(
  "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
  {
    variants: {
      status: {
        OPEN: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        IN_PROGRESS: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
        REVIEW: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
        DONE: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
        CLOSED: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
      }
    },
    defaultVariants: {
      status: "OPEN"
    }
  }
);
function TicketsStatusBadge({ status, className }) {
  const labels = {
    OPEN: "Open",
    IN_PROGRESS: "In Progress",
    REVIEW: "Review",
    DONE: "Done",
    CLOSED: "Closed"
  };
  return /* @__PURE__ */ jsx("span", { className: cn(statusBadgeVariants3({ status }), className), children: labels[status] });
}
var priorityBadgeVariants = cva(
  "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold",
  {
    variants: {
      priority: {
        P0: "bg-red-600 text-white",
        P1: "bg-orange-500 text-white",
        P2: "bg-yellow-500 text-black",
        P3: "bg-blue-500 text-white",
        P4: "bg-gray-400 text-white"
      }
    },
    defaultVariants: {
      priority: "P2"
    }
  }
);
function TicketsPriorityBadge({ priority, className }) {
  return /* @__PURE__ */ jsx("span", { className: cn(priorityBadgeVariants({ priority }), className), children: priority });
}
function TicketsHeader({
  title = "Tickets",
  description = "Track and manage internal tickets",
  children,
  className
}) {
  const { total, openCount, inProgressCount } = useTicketsStats();
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 md:flex-row md:items-center md:justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold text-gray-900 dark:text-white", children: title }),
      description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400", children: description })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 text-sm", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-blue-600 dark:text-blue-400", children: [
          openCount,
          " open"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "text-yellow-600 dark:text-yellow-400", children: [
          inProgressCount,
          " in progress"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "text-gray-500", children: [
          total,
          " total"
        ] })
      ] }),
      children
    ] })
  ] });
}
function TicketsList({ className, onTicketClick, renderTicket }) {
  const { tickets, isLoading } = useTicketsList();
  const { selectedTicketId, setSelectedTicketId } = useTicketsSelection();
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx(TicketCardSkeleton, {}, i)) });
  }
  if (tickets.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("text-center py-8 text-gray-500 dark:text-gray-400", className), children: "No tickets found" });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: tickets.map(
    (ticket) => renderTicket ? renderTicket(ticket) : /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "p-4 rounded-lg border cursor-pointer transition-colors",
          selectedTicketId === ticket.id ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        ),
        onClick: () => {
          setSelectedTicketId(ticket.id);
          onTicketClick?.(ticket);
        },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx(TicketsPriorityBadge, { priority: ticket.priority }),
                /* @__PURE__ */ jsx("span", { className: "font-medium text-gray-900 dark:text-white truncate", children: ticket.title })
              ] }),
              ticket.description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2", children: ticket.description })
            ] }),
            /* @__PURE__ */ jsx(TicketsStatusBadge, { status: ticket.status })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-3 flex items-center gap-4 text-xs text-gray-500", children: [
            ticket.assignee && /* @__PURE__ */ jsxs("span", { children: [
              "Assigned to ",
              ticket.assignee.name || ticket.assignee.email
            ] }),
            ticket.labels.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex gap-1", children: [
              ticket.labels.slice(0, 3).map((label) => /* @__PURE__ */ jsx(
                "span",
                {
                  className: "px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs",
                  children: label
                },
                label
              )),
              ticket.labels.length > 3 && /* @__PURE__ */ jsxs("span", { className: "text-gray-400", children: [
                "+",
                ticket.labels.length - 3
              ] })
            ] }),
            ticket.linearIssueKey && /* @__PURE__ */ jsx("span", { className: "text-blue-500", children: ticket.linearIssueKey }),
            /* @__PURE__ */ jsx("span", { className: "ml-auto", children: new Date(ticket.createdAt).toLocaleDateString() })
          ] })
        ]
      },
      ticket.id
    )
  ) });
}
function TicketsPagination({ className }) {
  const { page, pageSize, setPage, totalPages, total } = useTicketsPagination();
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("span", { className: "text-sm text-gray-500 dark:text-gray-400", children: [
      "Showing ",
      start,
      "-",
      end,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setPage(page - 1),
          disabled: page === 1,
          className: "px-3 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800",
          children: "Previous"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setPage(page + 1),
          disabled: page === totalPages,
          className: "px-3 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800",
          children: "Next"
        }
      )
    ] })
  ] });
}
function TicketCardSkeleton() {
  return /* @__PURE__ */ jsxs("div", { className: "p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("div", { className: "h-5 w-8 bg-gray-300 dark:bg-gray-600 rounded" }),
          /* @__PURE__ */ jsx("div", { className: "h-5 w-48 bg-gray-300 dark:bg-gray-600 rounded" })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "mt-2 h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "h-6 w-20 bg-gray-300 dark:bg-gray-600 rounded-full" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-3 flex items-center gap-4", children: [
      /* @__PURE__ */ jsx("div", { className: "h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" }),
      /* @__PURE__ */ jsx("div", { className: "h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" })
    ] })
  ] });
}
function TicketsRoot({ children, ...providerProps }) {
  return /* @__PURE__ */ jsx(TicketsProvider, { ...providerProps, children });
}
var Tickets = {
  /** Root provider component - wraps all other components */
  Root: TicketsRoot,
  /** Header with title and stats */
  Header: TicketsHeader,
  /** List of tickets */
  List: TicketsList,
  /** Pagination controls */
  Pagination: TicketsPagination,
  /** Status badge component */
  StatusBadge: TicketsStatusBadge,
  /** Priority badge component */
  PriorityBadge: TicketsPriorityBadge,
  /** Loading skeleton */
  ItemSkeleton: TicketCardSkeleton
};
var TestSuitesInternalContext = createContext(null);
function TestSuitesProvider({
  children,
  pageSize: initialPageSize = 20
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFiltersState] = useState({
    scheduleEnabled: null
  });
  const [selectedSuiteId, setSelectedSuiteId] = useState(null);
  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      scheduleEnabled: filters.scheduleEnabled ?? void 0
    }),
    [page, pageSize, filters]
  );
  const { data, isLoading, error: fetchError } = useTestSuites(queryParams);
  const { data: flakyData, isLoading: flakyLoading } = useFlakyTests();
  const { data: coverageData, isLoading: coverageLoading } = useCoverageOverview();
  const createMutation = useCreateTestSuite();
  const updateMutation = useUpdateTestSuite();
  const deleteMutation = useDeleteTestSuite();
  const runMutation = useRunTestSuite();
  const scheduleMutation = useUpdateTestSuiteSchedule();
  const suites = data?.suites ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const flakyTests = flakyData?.tests ?? [];
  const coverage = coverageData ?? null;
  const selectedSuite = useMemo(() => {
    if (!selectedSuiteId) return null;
    return suites.find((s) => s.id === selectedSuiteId) ?? null;
  }, [selectedSuiteId, suites]);
  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFiltersState({ scheduleEnabled: null });
    setPage(1);
  }, []);
  const hasActiveFilters = filters.scheduleEnabled !== null;
  const createSuite = useCallback(
    async (data2) => {
      return createMutation.mutateAsync(data2);
    },
    [createMutation]
  );
  const updateSuite = useCallback(
    async (id, data2) => {
      return updateMutation.mutateAsync({ id, data: data2 });
    },
    [updateMutation]
  );
  const deleteSuite = useCallback(
    async (id) => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );
  const runSuite = useCallback(
    async (id) => {
      return runMutation.mutateAsync(id);
    },
    [runMutation]
  );
  const updateSchedule = useCallback(
    async (id, data2) => {
      return scheduleMutation.mutateAsync({ id, data: data2 });
    },
    [scheduleMutation]
  );
  const value = {
    suites,
    total,
    isLoading,
    error: fetchError instanceof Error ? fetchError : fetchError ? new Error(String(fetchError)) : null,
    flakyTests,
    flakyLoading,
    coverage,
    coverageLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    selectedSuiteId,
    setSelectedSuiteId,
    selectedSuite,
    createSuite,
    updateSuite,
    deleteSuite,
    runSuite,
    updateSchedule,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending || scheduleMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRunning: runMutation.isPending
  };
  return /* @__PURE__ */ jsx(TestSuitesInternalContext.Provider, { value, children });
}
function useTestSuitesList() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useTestSuitesList must be used within TestSuitesProvider");
  }
  return {
    suites: context.suites,
    total: context.total,
    isLoading: context.isLoading,
    error: context.error
  };
}
function useFlakyTestsData() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useFlakyTestsData must be used within TestSuitesProvider");
  }
  return {
    flakyTests: context.flakyTests,
    isLoading: context.flakyLoading
  };
}
function useCoverageData() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useCoverageData must be used within TestSuitesProvider");
  }
  return {
    coverage: context.coverage,
    isLoading: context.coverageLoading
  };
}
function useTestSuitesFilters() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useTestSuitesFilters must be used within TestSuitesProvider");
  }
  return {
    filters: context.filters,
    setFilters: context.setFilters,
    clearFilters: context.clearFilters,
    hasActiveFilters: context.hasActiveFilters
  };
}
function useTestSuitesPagination() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useTestSuitesPagination must be used within TestSuitesProvider");
  }
  return {
    page: context.page,
    pageSize: context.pageSize,
    setPage: context.setPage,
    setPageSize: context.setPageSize,
    totalPages: context.totalPages,
    total: context.total
  };
}
function useTestSuitesSelection() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useTestSuitesSelection must be used within TestSuitesProvider");
  }
  return {
    selectedSuiteId: context.selectedSuiteId,
    setSelectedSuiteId: context.setSelectedSuiteId,
    selectedSuite: context.selectedSuite
  };
}
function useTestSuitesActions() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useTestSuitesActions must be used within TestSuitesProvider");
  }
  return {
    createSuite: context.createSuite,
    updateSuite: context.updateSuite,
    deleteSuite: context.deleteSuite,
    runSuite: context.runSuite,
    updateSchedule: context.updateSchedule,
    isCreating: context.isCreating,
    isUpdating: context.isUpdating,
    isDeleting: context.isDeleting,
    isRunning: context.isRunning
  };
}
function useTestSuitesContext() {
  const context = useContext(TestSuitesInternalContext);
  if (!context) {
    throw new Error("useTestSuitesContext must be used within TestSuitesProvider");
  }
  return context;
}
var runStatusBadgeVariants = cva(
  "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
  {
    variants: {
      status: {
        PENDING: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
        RUNNING: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        PASSED: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
        FAILED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
        PARTIAL: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
        TIMEOUT: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
        CANCELLED: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
      }
    },
    defaultVariants: {
      status: "PENDING"
    }
  }
);
function TestSuitesRunStatusBadge({ status, className }) {
  return /* @__PURE__ */ jsx("span", { className: cn(runStatusBadgeVariants({ status }), className), children: status });
}
function FlakinessIndicator({ score, className }) {
  const getColor = () => {
    if (score < 10) return "bg-green-500";
    if (score < 30) return "bg-yellow-500";
    return "bg-red-500";
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    /* @__PURE__ */ jsx("div", { className: "w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden", children: /* @__PURE__ */ jsx("div", { className: cn("h-full rounded-full", getColor()), style: { width: `${Math.min(score, 100)}%` } }) }),
    /* @__PURE__ */ jsxs("span", { className: "text-xs text-gray-500", children: [
      score.toFixed(1),
      "%"
    ] })
  ] });
}
function TestSuitesHeader({
  title = "Test Suites",
  description = "Manage test suites and scheduled runs",
  children,
  className
}) {
  const { suites } = useTestSuitesList();
  const { coverage, isLoading: coverageLoading } = useCoverageData();
  const { flakyTests, isLoading: flakyLoading } = useFlakyTestsData();
  const scheduledCount = suites.filter((s) => s.scheduleEnabled).length;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4 md:flex-row md:items-center md:justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold text-gray-900 dark:text-white", children: title }),
      description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400", children: description })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 text-sm", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-gray-500", children: [
          suites.length,
          " suites"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "text-blue-600 dark:text-blue-400", children: [
          scheduledCount,
          " scheduled"
        ] }),
        !flakyLoading && /* @__PURE__ */ jsxs("span", { className: "text-yellow-600 dark:text-yellow-400", children: [
          flakyTests.length,
          " flaky"
        ] }),
        !coverageLoading && coverage && /* @__PURE__ */ jsxs("span", { className: "text-green-600 dark:text-green-400", children: [
          coverage.averageCoverageScore.toFixed(0),
          "% coverage"
        ] })
      ] }),
      children
    ] })
  ] });
}
function TestSuitesList({ className, onSuiteClick, renderSuite }) {
  const { suites, isLoading } = useTestSuitesList();
  const { selectedSuiteId, setSelectedSuiteId } = useTestSuitesSelection();
  const { runSuite, isRunning } = useTestSuitesActions();
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx(TestSuiteCardSkeleton, {}, i)) });
  }
  if (suites.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("text-center py-8 text-gray-500 dark:text-gray-400", className), children: "No test suites found" });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: suites.map(
    (suite) => renderSuite ? renderSuite(suite) : /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "p-4 rounded-lg border cursor-pointer transition-colors",
          selectedSuiteId === suite.id ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        ),
        onClick: () => {
          setSelectedSuiteId(suite.id);
          onSuiteClick?.(suite);
        },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx("span", { className: "font-medium text-gray-900 dark:text-white", children: suite.name }),
                suite.scheduleEnabled && /* @__PURE__ */ jsx("span", { className: "px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded", children: "Scheduled" })
              ] }),
              suite.description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-1", children: suite.description })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: (e) => {
                  e.stopPropagation();
                  runSuite(suite.id);
                },
                disabled: isRunning,
                className: "px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
                children: isRunning ? "Running..." : "Run"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-3 flex items-center gap-4 text-xs text-gray-500", children: [
            /* @__PURE__ */ jsxs("span", { children: [
              suite._count?.testCases ?? 0,
              " tests"
            ] }),
            suite.scheduleCron && /* @__PURE__ */ jsxs("span", { children: [
              "Cron: ",
              suite.scheduleCron
            ] }),
            suite.lastScheduledRunAt && /* @__PURE__ */ jsxs("span", { children: [
              "Last run: ",
              new Date(suite.lastScheduledRunAt).toLocaleString()
            ] })
          ] })
        ]
      },
      suite.id
    )
  ) });
}
function TestSuitesPagination({ className }) {
  const { page, pageSize, setPage, totalPages, total } = useTestSuitesPagination();
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between", className), children: [
    /* @__PURE__ */ jsxs("span", { className: "text-sm text-gray-500 dark:text-gray-400", children: [
      "Showing ",
      start,
      "-",
      end,
      " of ",
      total
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setPage(page - 1),
          disabled: page === 1,
          className: "px-3 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800",
          children: "Previous"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => setPage(page + 1),
          disabled: page === totalPages,
          className: "px-3 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800",
          children: "Next"
        }
      )
    ] })
  ] });
}
function CoverageOverviewCard({ className }) {
  const { coverage, isLoading } = useCoverageData();
  if (isLoading) {
    return /* @__PURE__ */ jsxs("div", { className: cn("p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse", className), children: [
      /* @__PURE__ */ jsx("div", { className: "h-5 w-24 bg-gray-300 dark:bg-gray-600 rounded mb-4" }),
      /* @__PURE__ */ jsx("div", { className: "h-12 w-full bg-gray-200 dark:bg-gray-700 rounded" })
    ] });
  }
  if (!coverage) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("p-4 rounded-lg border border-gray-200 dark:border-gray-700", className), children: [
    /* @__PURE__ */ jsx("h3", { className: "font-medium text-gray-900 dark:text-white mb-4", children: "Test Coverage" }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-4", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs("div", { className: "text-2xl font-bold text-gray-900 dark:text-white", children: [
          coverage.averageCoverageScore.toFixed(1),
          "%"
        ] }),
        /* @__PURE__ */ jsx("div", { className: "text-sm text-gray-500", children: "Average Coverage" })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs("div", { className: "text-2xl font-bold text-gray-900 dark:text-white", children: [
          coverage.coveredAutomations,
          "/",
          coverage.totalAutomations
        ] }),
        /* @__PURE__ */ jsx("div", { className: "text-sm text-gray-500", children: "Automations Covered" })
      ] })
    ] })
  ] });
}
function FlakyTestsCard({ className, limit = 5 }) {
  const { flakyTests, isLoading } = useFlakyTestsData();
  if (isLoading) {
    return /* @__PURE__ */ jsxs("div", { className: cn("p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse", className), children: [
      /* @__PURE__ */ jsx("div", { className: "h-5 w-24 bg-gray-300 dark:bg-gray-600 rounded mb-4" }),
      /* @__PURE__ */ jsx("div", { className: "space-y-2", children: Array.from({ length: 3 }).map((_, i) => /* @__PURE__ */ jsx("div", { className: "h-8 bg-gray-200 dark:bg-gray-700 rounded" }, i)) })
    ] });
  }
  const displayTests = flakyTests.slice(0, limit);
  return /* @__PURE__ */ jsxs("div", { className: cn("p-4 rounded-lg border border-gray-200 dark:border-gray-700", className), children: [
    /* @__PURE__ */ jsxs("h3", { className: "font-medium text-gray-900 dark:text-white mb-4", children: [
      "Flaky Tests (",
      flakyTests.length,
      ")"
    ] }),
    displayTests.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-500", children: "No flaky tests detected" }) : /* @__PURE__ */ jsx("div", { className: "space-y-3", children: displayTests.map((test) => /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm text-gray-700 dark:text-gray-300 truncate", children: test.testCase?.name || test.testCaseId }),
      /* @__PURE__ */ jsx(FlakinessIndicator, { score: test.flakinessScore })
    ] }, test.id)) })
  ] });
}
function TestSuiteCardSkeleton() {
  return /* @__PURE__ */ jsxs("div", { className: "p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
        /* @__PURE__ */ jsx("div", { className: "h-5 w-32 bg-gray-300 dark:bg-gray-600 rounded" }),
        /* @__PURE__ */ jsx("div", { className: "mt-2 h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "h-8 w-16 bg-gray-300 dark:bg-gray-600 rounded" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-3 flex items-center gap-4", children: [
      /* @__PURE__ */ jsx("div", { className: "h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" }),
      /* @__PURE__ */ jsx("div", { className: "h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" })
    ] })
  ] });
}
function TestSuitesRoot({ children, ...providerProps }) {
  return /* @__PURE__ */ jsx(TestSuitesProvider, { ...providerProps, children });
}
var TestSuites = {
  /** Root provider component - wraps all other components */
  Root: TestSuitesRoot,
  /** Header with title and stats */
  Header: TestSuitesHeader,
  /** List of test suites */
  List: TestSuitesList,
  /** Pagination controls */
  Pagination: TestSuitesPagination,
  /** Run status badge */
  RunStatusBadge: TestSuitesRunStatusBadge,
  /** Flakiness indicator */
  FlakinessIndicator,
  /** Coverage overview card */
  CoverageCard: CoverageOverviewCard,
  /** Flaky tests card */
  FlakyTestsCard,
  /** Loading skeleton */
  ItemSkeleton: TestSuiteCardSkeleton
};
var CreateModalContext = createContext(null);
function useCreateModal() {
  const context = useContext(CreateModalContext);
  if (!context) {
    throw new Error("useCreateModal must be used within a CreateModal.Root");
  }
  return context;
}
function useCreateModalOptional() {
  return useContext(CreateModalContext);
}
var EMPTY_OBJECT = {};
var EMPTY_ARRAY = [];
function CreateModalProvider({
  children,
  isOpen,
  onClose,
  onSubmit,
  defaultValues,
  fieldOrder
}) {
  const stableDefaultValues = defaultValues ?? EMPTY_OBJECT;
  const stableFieldOrder = fieldOrder ?? EMPTY_ARRAY;
  const fieldsRef = useRef(/* @__PURE__ */ new Map());
  const errorsRef = useRef(/* @__PURE__ */ new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errorsVersion, setErrorsVersion] = useState(0);
  const [focusedFieldId, setFocusedFieldId] = useState(null);
  const fieldRefsMap = useRef(/* @__PURE__ */ new Map());
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const initialValuesRef = useRef(stableDefaultValues);
  useEffect(() => {
    initialValuesRef.current = stableDefaultValues;
  }, [stableDefaultValues]);
  useEffect(() => {
    if (isOpen) {
      setIsDirty(false);
      errorsRef.current = /* @__PURE__ */ new Map();
      setErrorsVersion((v) => v + 1);
      setIsSubmitting(false);
      setIsCommandPaletteOpen(false);
    }
  }, [isOpen]);
  const registerField = useCallback((field) => {
    fieldsRef.current.set(field.name, field);
  }, []);
  const unregisterField = useCallback((name) => {
    fieldsRef.current.delete(name);
    if (errorsRef.current.has(name)) {
      errorsRef.current.delete(name);
      setErrorsVersion((v) => v + 1);
    }
  }, []);
  const setFieldValue = useCallback((name, value2) => {
    const field = fieldsRef.current.get(name);
    if (field) {
      fieldsRef.current.set(name, { ...field, value: value2 });
    }
    setIsDirty(true);
    if (errorsRef.current.has(name)) {
      errorsRef.current.delete(name);
      setErrorsVersion((v) => v + 1);
    }
  }, []);
  const getFieldValue = useCallback((name) => {
    return fieldsRef.current.get(name)?.value;
  }, []);
  const setFieldError = useCallback((name, error) => {
    if (error === null) {
      if (errorsRef.current.has(name)) {
        errorsRef.current.delete(name);
        setErrorsVersion((v) => v + 1);
      }
    } else {
      errorsRef.current.set(name, error);
      setErrorsVersion((v) => v + 1);
    }
  }, []);
  const getFieldError = useCallback((name) => {
    return errorsRef.current.get(name) ?? null;
  }, []);
  const validateForm = useCallback(() => {
    const newErrors = /* @__PURE__ */ new Map();
    let isValid2 = true;
    fieldsRef.current.forEach((field) => {
      if (field.required && (field.value === void 0 || field.value === null || field.value === "")) {
        newErrors.set(field.name, `${field.name} is required`);
        isValid2 = false;
      } else if (field.validate) {
        const error = field.validate(field.value);
        if (error) {
          newErrors.set(field.name, error);
          isValid2 = false;
        }
      }
    });
    errorsRef.current = newErrors;
    setErrorsVersion((v) => v + 1);
    return isValid2;
  }, []);
  const isValid = errorsRef.current.size === 0;
  const submit = useCallback(async () => {
    if (!validateForm()) {
      const firstErrorField = Array.from(errorsRef.current.keys())[0];
      if (firstErrorField) {
        const element = fieldRefsMap.current.get(firstErrorField);
        element?.focus();
      }
      return;
    }
    setIsSubmitting(true);
    try {
      const data = {};
      fieldsRef.current.forEach((field) => {
        data[field.name] = field.value;
      });
      await onSubmit(data);
    } catch (err) {
      console.error("Form submission error:", err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, onSubmit]);
  const reset = useCallback(() => {
    fieldsRef.current.forEach((field, name) => {
      fieldsRef.current.set(name, {
        ...field,
        value: initialValuesRef.current[name] ?? void 0
      });
    });
    errorsRef.current = /* @__PURE__ */ new Map();
    setErrorsVersion((v) => v + 1);
    setIsDirty(false);
  }, []);
  const close = useCallback(() => {
    onClose();
  }, [onClose]);
  const registerFieldRef = useCallback((name, element) => {
    if (element) {
      fieldRefsMap.current.set(name, element);
    } else {
      fieldRefsMap.current.delete(name);
    }
  }, []);
  const focusField = useCallback((name) => {
    const element = fieldRefsMap.current.get(name);
    element?.focus();
  }, []);
  const getFieldNames = useCallback(() => {
    if (stableFieldOrder.length > 0) {
      return stableFieldOrder.filter((name) => fieldsRef.current.has(name));
    }
    return Array.from(fieldsRef.current.keys());
  }, [stableFieldOrder]);
  const focusNextField = useCallback(() => {
    const names = getFieldNames();
    const currentIndex = focusedFieldId ? names.indexOf(focusedFieldId) : -1;
    const nextIndex = (currentIndex + 1) % names.length;
    const nextName = names[nextIndex];
    if (nextName) {
      focusField(nextName);
    }
  }, [focusedFieldId, getFieldNames, focusField]);
  const focusPreviousField = useCallback(() => {
    const names = getFieldNames();
    const currentIndex = focusedFieldId ? names.indexOf(focusedFieldId) : 0;
    const prevIndex = (currentIndex - 1 + names.length) % names.length;
    const prevName = names[prevIndex];
    if (prevName) {
      focusField(prevName);
    }
  }, [focusedFieldId, getFieldNames, focusField]);
  const openCommandPalette = useCallback(() => setIsCommandPaletteOpen(true), []);
  const closeCommandPalette = useCallback(() => setIsCommandPaletteOpen(false), []);
  const toggleCommandPalette = useCallback(() => setIsCommandPaletteOpen((prev) => !prev), []);
  const value = useMemo(() => ({
    isOpen,
    isSubmitting,
    fields: fieldsRef.current,
    errors: errorsRef.current,
    isDirty,
    isValid,
    registerField,
    unregisterField,
    setFieldValue,
    getFieldValue,
    setFieldError,
    getFieldError,
    submit,
    reset,
    close,
    onClose: close,
    focusedFieldId,
    setFocusedFieldId,
    fieldRefs: fieldRefsMap.current,
    registerFieldRef,
    focusField,
    focusNextField,
    focusPreviousField,
    isCommandPaletteOpen,
    openCommandPalette,
    closeCommandPalette,
    toggleCommandPalette
  }), [
    isOpen,
    isSubmitting,
    isDirty,
    isValid,
    errorsVersion,
    // Include to trigger re-render when errors change
    registerField,
    unregisterField,
    setFieldValue,
    getFieldValue,
    setFieldError,
    getFieldError,
    submit,
    reset,
    close,
    focusedFieldId,
    registerFieldRef,
    focusField,
    focusNextField,
    focusPreviousField,
    isCommandPaletteOpen,
    openCommandPalette,
    closeCommandPalette,
    toggleCommandPalette
  ]);
  return /* @__PURE__ */ jsx(CreateModalContext.Provider, { value, children });
}
var variantStyles = {
  title: cn(
    "text-xl font-semibold",
    "placeholder:text-slate-400 placeholder:font-normal",
    "border-0 bg-transparent",
    "focus:outline-none focus:ring-0",
    "resize-none"
  ),
  description: cn(
    "text-sm",
    "placeholder:text-slate-400",
    "border-0 bg-transparent",
    "focus:outline-none focus:ring-0",
    "resize-none"
  ),
  default: cn(
    "text-sm",
    "px-3 py-2",
    "border border-slate-200 dark:border-slate-700",
    "rounded-lg",
    "bg-white dark:bg-slate-800",
    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
    "resize-none"
  )
};
var TextInput = forwardRef(function TextInput2({
  name,
  variant = "default",
  autoExpand = false,
  maxRows = 10,
  minRows = 1,
  value: controlledValue,
  onChange,
  error,
  required,
  className,
  placeholder,
  disabled,
  onFocus,
  onBlur,
  ...props
}, forwardedRef) {
  const internalRef = useRef(null);
  const ref = forwardedRef || internalRef;
  const context = useCreateModalOptional();
  useEffect(() => {
    if (context && name) {
      context.registerField({
        id: name,
        name,
        value: controlledValue ?? "",
        required
      });
      return () => {
        context.unregisterField(name);
      };
    }
  }, [name, required]);
  useEffect(() => {
    if (context && name && ref.current) {
      context.registerFieldRef(name, ref.current);
      return () => {
        context.registerFieldRef(name, null);
      };
    }
  }, [name]);
  const value = controlledValue ?? (context?.getFieldValue(name) ?? "");
  const fieldError = error ?? context?.getFieldError(name);
  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      onChange?.(newValue);
      context?.setFieldValue(name, newValue);
    },
    [onChange, context, name]
  );
  const handleFocus = useCallback(
    (e) => {
      context?.setFocusedFieldId(name);
      onFocus?.(e);
    },
    [context, name, onFocus]
  );
  const handleBlur = useCallback(
    (e) => {
      context?.setFocusedFieldId(null);
      onBlur?.(e);
    },
    [context, onBlur]
  );
  useEffect(() => {
    if (!autoExpand || !ref.current) return;
    const textarea = ref.current;
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight) || 24;
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;
    textarea.style.height = "auto";
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [value, autoExpand, minRows, maxRows, ref]);
  const rows = autoExpand ? minRows : props.rows ?? minRows;
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsx(
      "textarea",
      {
        ref,
        name,
        value,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        placeholder,
        disabled: disabled || context?.isSubmitting,
        rows,
        "aria-invalid": !!fieldError,
        "aria-describedby": fieldError ? `${name}-error` : void 0,
        className: cn(
          "w-full text-slate-900 dark:text-slate-100",
          "transition-colors duration-150",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          variantStyles[variant],
          fieldError && variant === "default" && "border-red-500 focus:ring-red-500",
          className
        ),
        ...props
      }
    ),
    fieldError && /* @__PURE__ */ jsx(
      "p",
      {
        id: `${name}-error`,
        className: "mt-1 text-xs text-red-500",
        role: "alert",
        children: fieldError
      }
    )
  ] });
});
var TextInputWithHint = forwardRef(
  function TextInputWithHint2({ hint, className, ...props }, ref) {
    return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(TextInput, { ref, className: cn(hint && "pr-20", className), ...props }),
      hint && /* @__PURE__ */ jsx("span", { className: "absolute right-2 top-2 text-xs text-slate-400 pointer-events-none", children: hint })
    ] });
  }
);
var baseStyles = cn(
  "inline-flex items-center gap-1.5",
  "rounded-md",
  "font-medium",
  "transition-all duration-150",
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
  "disabled:opacity-50 disabled:cursor-not-allowed"
);
var variantStyles2 = {
  default: cn(
    "bg-slate-100 dark:bg-slate-800",
    "border border-slate-200 dark:border-slate-700",
    "text-slate-700 dark:text-slate-300",
    "hover:bg-slate-200 dark:hover:bg-slate-700",
    "hover:border-slate-300 dark:hover:border-slate-600"
  ),
  subtle: cn(
    "bg-transparent",
    "border border-transparent",
    "text-slate-600 dark:text-slate-400",
    "hover:bg-slate-100 dark:hover:bg-slate-800",
    "hover:text-slate-900 dark:hover:text-slate-200"
  ),
  ghost: cn(
    "bg-transparent",
    "border border-dashed border-slate-300 dark:border-slate-600",
    "text-slate-500 dark:text-slate-400",
    "hover:bg-slate-50 dark:hover:bg-slate-800/50",
    "hover:border-slate-400 dark:hover:border-slate-500"
  )
};
var sizeStyles = {
  sm: "px-2 py-1 text-xs",
  md: "px-2.5 py-1.5 text-sm"
};
var activeStyles = cn(
  "ring-2 ring-blue-500 ring-offset-1",
  "bg-blue-50 dark:bg-blue-900/30",
  "border-blue-300 dark:border-blue-700"
);
function LoadingSpinner2({ size }) {
  const spinnerSize = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn(spinnerSize, "animate-spin text-current"),
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      children: [
        /* @__PURE__ */ jsx(
          "circle",
          {
            className: "opacity-25",
            cx: "12",
            cy: "12",
            r: "10",
            stroke: "currentColor",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            className: "opacity-75",
            fill: "currentColor",
            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          }
        )
      ]
    }
  );
}
var PropertyPill = forwardRef(function PropertyPill2({
  label,
  icon,
  variant = "default",
  size = "md",
  showChevron = true,
  isActive = false,
  isLoading = false,
  color,
  hasValue = false,
  className,
  disabled,
  ...props
}, ref) {
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const chevronSize = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  const colorStyle = color ? {
    backgroundColor: color.startsWith("#") ? `${color}20` : void 0,
    borderColor: color.startsWith("#") ? `${color}60` : void 0,
    color: color.startsWith("#") ? color : void 0
  } : void 0;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type: "button",
      disabled: disabled || isLoading,
      className: cn(
        baseStyles,
        variantStyles2[variant],
        sizeStyles[size],
        isActive && activeStyles,
        hasValue && "font-semibold",
        className
      ),
      style: colorStyle,
      ...props,
      children: [
        isLoading ? /* @__PURE__ */ jsx(LoadingSpinner2, { size }) : icon ? /* @__PURE__ */ jsx("span", { className: cn(iconSize, "flex-shrink-0"), children: icon }) : null,
        label && /* @__PURE__ */ jsx("span", { className: "truncate max-w-[120px]", children: label }),
        showChevron && !isLoading && /* @__PURE__ */ jsx(
          ChevronDown,
          {
            className: cn(
              chevronSize,
              "flex-shrink-0 opacity-60",
              isActive && "rotate-180 transition-transform"
            )
          }
        )
      ]
    }
  );
});
var PropertyPillWithDot = forwardRef(
  function PropertyPillWithDot2({ dotColor, icon, ...props }, ref) {
    const dotIcon = dotColor ? /* @__PURE__ */ jsx(
      "span",
      {
        className: "w-2 h-2 rounded-full flex-shrink-0",
        style: { backgroundColor: dotColor }
      }
    ) : null;
    return /* @__PURE__ */ jsx(PropertyPill, { ref, icon: dotIcon || icon, ...props });
  }
);
var PropertyPillWithAvatar = forwardRef(
  function PropertyPillWithAvatar2({ avatarUrl, initials, icon, label, size = "md", ...props }, ref) {
    const avatarSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
    const textSize = size === "sm" ? "text-[8px]" : "text-[10px]";
    const avatarIcon = avatarUrl ? /* @__PURE__ */ jsx(
      "img",
      {
        src: avatarUrl,
        alt: "",
        className: cn(avatarSize, "rounded-full object-cover flex-shrink-0")
      }
    ) : initials ? /* @__PURE__ */ jsx(
      "span",
      {
        className: cn(
          avatarSize,
          textSize,
          "rounded-full bg-slate-300 dark:bg-slate-600",
          "flex items-center justify-center",
          "font-medium text-slate-600 dark:text-slate-300",
          "flex-shrink-0"
        ),
        children: initials
      }
    ) : null;
    return /* @__PURE__ */ jsx(PropertyPill, { ref, icon: avatarIcon || icon, label, size, ...props });
  }
);
var AddPropertyButton = forwardRef(
  function AddPropertyButton2({ placeholder = "Add...", icon, ...props }, ref) {
    const plusIcon = /* @__PURE__ */ jsx(
      "svg",
      {
        className: "w-3.5 h-3.5",
        fill: "none",
        stroke: "currentColor",
        viewBox: "0 0 24 24",
        children: /* @__PURE__ */ jsx(
          "path",
          {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: 2,
            d: "M12 4v16m8-8H4"
          }
        )
      }
    );
    return /* @__PURE__ */ jsx(
      PropertyPill,
      {
        ref,
        variant: "ghost",
        icon: icon || plusIcon,
        label: placeholder,
        showChevron: false,
        ...props
      }
    );
  }
);
var DEFAULT_PRIORITY_OPTIONS = [
  { value: "P0", label: "Urgent", color: "#ef4444", description: "Drop everything" },
  { value: "P1", label: "High", color: "#f97316", description: "Critical issues" },
  { value: "P2", label: "Medium", color: "#eab308", description: "Important work" },
  { value: "P3", label: "Low", color: "#22c55e", description: "When time permits" },
  { value: "P4", label: "None", color: "#94a3b8", description: "No priority" }
];
function getPriorityOption(value) {
  if (!value) return void 0;
  return DEFAULT_PRIORITY_OPTIONS.find((opt) => opt.value === value);
}
function getPriorityColor(value) {
  const option = getPriorityOption(value);
  return option?.color ?? "#94a3b8";
}
function getPriorityLabel(value) {
  const option = getPriorityOption(value);
  return option?.label ?? "Priority";
}
var PriorityPicker = forwardRef(
  function PriorityPicker2({
    name,
    value: controlledValue,
    onChange,
    defaultValue = null,
    required,
    disabled,
    options = DEFAULT_PRIORITY_OPTIONS,
    placeholder = "Priority",
    size = "md",
    className
  }, forwardedRef) {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const buttonRef = useRef(null);
    const setButtonRef = useCallback(
      (node) => {
        buttonRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );
    const context = useCreateModalOptional();
    useEffect(() => {
      if (context && name) {
        context.registerField({
          id: name,
          name,
          value: controlledValue ?? defaultValue ?? "",
          required
        });
        return () => {
          context.unregisterField(name);
        };
      }
    }, [name, required, defaultValue]);
    useEffect(() => {
      if (context && name && controlledValue !== void 0) {
        const currentValue = context.getFieldValue(name);
        if (currentValue !== controlledValue) {
          context.setFieldValue(name, controlledValue);
        }
      }
    }, [name, controlledValue]);
    const value = controlledValue ?? (context?.getFieldValue(name) ?? defaultValue);
    const handleSelect = useCallback(
      (priority) => {
        onChange?.(priority);
        context?.setFieldValue(name, priority);
        setIsOpen(false);
        buttonRef.current?.focus();
      },
      [onChange, context, name]
    );
    const handleClear = useCallback(() => {
      onChange?.(null);
      context?.setFieldValue(name, null);
      setIsOpen(false);
      buttonRef.current?.focus();
    }, [onChange, context, name]);
    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);
    const handleKeyDown = useCallback(
      (event) => {
        if (disabled) return;
        switch (event.key) {
          case "Enter":
          case " ":
            event.preventDefault();
            if (isOpen && options[highlightedIndex]) {
              handleSelect(options[highlightedIndex].value);
            } else if (!isOpen) {
              setIsOpen(true);
            }
            break;
          case "Escape":
            event.preventDefault();
            setIsOpen(false);
            buttonRef.current?.focus();
            break;
          case "ArrowDown":
            event.preventDefault();
            if (isOpen) {
              setHighlightedIndex((prev) => (prev + 1) % options.length);
            } else {
              setIsOpen(true);
            }
            break;
          case "ArrowUp":
            event.preventDefault();
            if (isOpen) {
              setHighlightedIndex((prev) => (prev - 1 + options.length) % options.length);
            } else {
              setIsOpen(true);
            }
            break;
          case "Tab":
            if (isOpen) {
              setIsOpen(false);
            }
            break;
        }
      },
      [disabled, isOpen, highlightedIndex, options, handleSelect]
    );
    useEffect(() => {
      if (isOpen && listRef.current) {
        const highlightedElement = listRef.current.children[highlightedIndex];
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ block: "nearest" });
        }
      }
    }, [isOpen, highlightedIndex]);
    useEffect(() => {
      if (isOpen) {
        const currentIndex = options.findIndex((opt) => opt.value === value);
        setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
      }
    }, [isOpen, value, options]);
    const selectedOption = options.find((opt) => opt.value === value);
    const isDisabled = disabled || context?.isSubmitting;
    return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative inline-block", className), children: [
      /* @__PURE__ */ jsx(
        PropertyPillWithDot,
        {
          ref: setButtonRef,
          label: selectedOption?.label ?? placeholder,
          dotColor: selectedOption?.color ?? "#94a3b8",
          hasValue: !!selectedOption,
          isActive: isOpen,
          disabled: isDisabled,
          size,
          onClick: () => !isDisabled && setIsOpen(!isOpen),
          onKeyDown: handleKeyDown,
          "aria-haspopup": "listbox",
          "aria-expanded": isOpen,
          "aria-labelledby": `${name}-label`
        }
      ),
      isOpen && /* @__PURE__ */ jsxs(
        "div",
        {
          ref: listRef,
          role: "listbox",
          "aria-labelledby": `${name}-label`,
          className: cn(
            "absolute z-50 mt-1 min-w-[180px]",
            "bg-white dark:bg-slate-800",
            "border border-slate-200 dark:border-slate-700",
            "rounded-lg shadow-lg",
            "py-1",
            "animate-in fade-in-0 zoom-in-95 duration-100"
          ),
          children: [
            options.map((option, index) => /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                role: "option",
                "aria-selected": option.value === value,
                className: cn(
                  "w-full px-3 py-2 text-left",
                  "flex items-center gap-2",
                  "text-sm text-slate-700 dark:text-slate-200",
                  "hover:bg-slate-100 dark:hover:bg-slate-700",
                  "focus:outline-none",
                  index === highlightedIndex && "bg-slate-100 dark:bg-slate-700",
                  option.value === value && "font-medium"
                ),
                onClick: () => handleSelect(option.value),
                onMouseEnter: () => setHighlightedIndex(index),
                children: [
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      className: "w-2 h-2 rounded-full flex-shrink-0",
                      style: { backgroundColor: option.color }
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { className: "flex-1", children: option.label }),
                  option.description && /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400", children: option.description }),
                  option.value === value && /* @__PURE__ */ jsx(
                    "svg",
                    {
                      className: "w-4 h-4 text-blue-500",
                      fill: "none",
                      stroke: "currentColor",
                      viewBox: "0 0 24 24",
                      children: /* @__PURE__ */ jsx(
                        "path",
                        {
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          strokeWidth: 2,
                          d: "M5 13l4 4L19 7"
                        }
                      )
                    }
                  )
                ]
              },
              option.value
            )),
            value && !required && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("div", { className: "my-1 border-t border-slate-200 dark:border-slate-700" }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: cn(
                    "w-full px-3 py-2 text-left",
                    "text-sm text-slate-500",
                    "hover:bg-slate-100 dark:hover:bg-slate-700",
                    "focus:outline-none"
                  ),
                  onClick: handleClear,
                  children: "Clear priority"
                }
              )
            ] })
          ]
        }
      )
    ] });
  }
);
function PriorityBadge({ value, size = "md", className }) {
  const option = getPriorityOption(value);
  if (!option) return null;
  const sizeStyles2 = {
    sm: "px-1.5 py-0.5 text-xs",
    md: "px-2 py-1 text-sm"
  };
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1.5 rounded-md font-medium",
        sizeStyles2[size],
        className
      ),
      style: {
        backgroundColor: `${option.color}20`,
        color: option.color
      },
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            className: "w-1.5 h-1.5 rounded-full",
            style: { backgroundColor: option.color }
          }
        ),
        option.label
      ]
    }
  );
}
function getUserInitials(user) {
  if (!user) return "";
  const name = user.name || user.email;
  const parts = name.split(/[\s@]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return ((parts[0][0] || "") + (parts[1][0] || "")).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
function getUserDisplayName(user) {
  if (!user) return "";
  return user.name || user.email.split("@")[0] || user.email;
}
var UserPicker = forwardRef(
  function UserPicker2({
    name,
    value: controlledValue,
    onChange,
    defaultValue = null,
    users = [],
    isLoading = false,
    required,
    disabled,
    placeholder = "Assignee",
    size = "md",
    searchable = true,
    className
  }, forwardedRef) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const searchInputRef = useRef(null);
    const buttonRef = useRef(null);
    const setButtonRef = useCallback(
      (node) => {
        buttonRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );
    const context = useCreateModalOptional();
    useEffect(() => {
      if (context && name) {
        context.registerField({
          id: name,
          name,
          value: controlledValue ?? defaultValue ?? "",
          required
        });
        return () => {
          context.unregisterField(name);
        };
      }
    }, [name, required, defaultValue]);
    useEffect(() => {
      if (context && name && controlledValue !== void 0) {
        const currentValue = context.getFieldValue(name);
        if (currentValue !== controlledValue) {
          context.setFieldValue(name, controlledValue);
        }
      }
    }, [name, controlledValue]);
    const value = controlledValue ?? (context?.getFieldValue(name) ?? defaultValue);
    const filteredUsers = useMemo(() => {
      if (!searchQuery.trim()) return users;
      const query = searchQuery.toLowerCase();
      return users.filter(
        (user) => user.name?.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)
      );
    }, [users, searchQuery]);
    const selectedUser = useMemo(() => {
      if (!value) return null;
      return users.find((u) => u.id === value) ?? null;
    }, [value, users]);
    const handleSelect = useCallback(
      (userId) => {
        onChange?.(userId);
        context?.setFieldValue(name, userId);
        setIsOpen(false);
        setSearchQuery("");
        buttonRef.current?.focus();
      },
      [onChange, context, name]
    );
    const handleClear = useCallback(() => {
      onChange?.(null);
      context?.setFieldValue(name, null);
      setIsOpen(false);
      setSearchQuery("");
      buttonRef.current?.focus();
    }, [onChange, context, name]);
    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
          setSearchQuery("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);
    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);
    const handleKeyDown = useCallback(
      (event) => {
        if (disabled) return;
        switch (event.key) {
          case "Enter":
            event.preventDefault();
            if (isOpen && filteredUsers[highlightedIndex]) {
              handleSelect(filteredUsers[highlightedIndex].id);
            } else if (!isOpen) {
              setIsOpen(true);
            }
            break;
          case " ":
            if (!isOpen) {
              event.preventDefault();
              setIsOpen(true);
            }
            break;
          case "Escape":
            event.preventDefault();
            setIsOpen(false);
            setSearchQuery("");
            buttonRef.current?.focus();
            break;
          case "ArrowDown":
            event.preventDefault();
            if (isOpen) {
              setHighlightedIndex(
                (prev) => Math.min(prev + 1, filteredUsers.length - 1)
              );
            } else {
              setIsOpen(true);
            }
            break;
          case "ArrowUp":
            event.preventDefault();
            if (isOpen) {
              setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            } else {
              setIsOpen(true);
            }
            break;
          case "Tab":
            if (isOpen) {
              setIsOpen(false);
              setSearchQuery("");
            }
            break;
        }
      },
      [disabled, isOpen, highlightedIndex, filteredUsers, handleSelect]
    );
    useEffect(() => {
      if (isOpen && listRef.current) {
        const highlightedElement = listRef.current.children[highlightedIndex];
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ block: "nearest" });
        }
      }
    }, [isOpen, highlightedIndex]);
    useEffect(() => {
      setHighlightedIndex(0);
    }, [searchQuery]);
    useEffect(() => {
      if (isOpen) {
        const currentIndex = users.findIndex((u) => u.id === value);
        setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
      }
    }, [isOpen, value, users]);
    const isDisabled = disabled || context?.isSubmitting;
    return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative inline-block", className), children: [
      /* @__PURE__ */ jsx(
        PropertyPillWithAvatar,
        {
          ref: setButtonRef,
          label: selectedUser ? getUserDisplayName(selectedUser) : placeholder,
          avatarUrl: selectedUser?.avatarUrl,
          initials: selectedUser ? getUserInitials(selectedUser) : void 0,
          hasValue: !!selectedUser,
          isActive: isOpen,
          isLoading,
          disabled: isDisabled,
          size,
          variant: selectedUser ? "default" : "subtle",
          onClick: () => !isDisabled && setIsOpen(!isOpen),
          onKeyDown: handleKeyDown,
          "aria-haspopup": "listbox",
          "aria-expanded": isOpen,
          "aria-labelledby": `${name}-label`
        }
      ),
      isOpen && /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "absolute z-50 mt-1 min-w-[220px]",
            "bg-white dark:bg-slate-800",
            "border border-slate-200 dark:border-slate-700",
            "rounded-lg shadow-lg",
            "animate-in fade-in-0 zoom-in-95 duration-100",
            "overflow-hidden"
          ),
          children: [
            searchable && /* @__PURE__ */ jsx("div", { className: "p-2 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx(
              "input",
              {
                ref: searchInputRef,
                type: "text",
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
                onKeyDown: handleKeyDown,
                placeholder: "Search users...",
                className: cn(
                  "w-full px-2 py-1.5 text-sm",
                  "bg-slate-50 dark:bg-slate-900",
                  "border border-slate-200 dark:border-slate-600",
                  "rounded-md",
                  "focus:outline-none focus:ring-1 focus:ring-blue-500",
                  "placeholder:text-slate-400"
                )
              }
            ) }),
            /* @__PURE__ */ jsx(
              "div",
              {
                ref: listRef,
                role: "listbox",
                "aria-labelledby": `${name}-label`,
                className: "max-h-[240px] overflow-y-auto py-1",
                children: isLoading ? /* @__PURE__ */ jsxs("div", { className: "px-3 py-4 text-center text-sm text-slate-500", children: [
                  /* @__PURE__ */ jsxs(
                    "svg",
                    {
                      className: "w-5 h-5 mx-auto mb-2 animate-spin",
                      fill: "none",
                      viewBox: "0 0 24 24",
                      children: [
                        /* @__PURE__ */ jsx(
                          "circle",
                          {
                            className: "opacity-25",
                            cx: "12",
                            cy: "12",
                            r: "10",
                            stroke: "currentColor",
                            strokeWidth: "4"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "path",
                          {
                            className: "opacity-75",
                            fill: "currentColor",
                            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          }
                        )
                      ]
                    }
                  ),
                  "Loading users..."
                ] }) : filteredUsers.length === 0 ? /* @__PURE__ */ jsx("div", { className: "px-3 py-4 text-center text-sm text-slate-500", children: searchQuery ? "No users found" : "No users available" }) : filteredUsers.map((user, index) => /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    role: "option",
                    "aria-selected": user.id === value,
                    className: cn(
                      "w-full px-3 py-2 text-left",
                      "flex items-center gap-3",
                      "text-sm text-slate-700 dark:text-slate-200",
                      "hover:bg-slate-100 dark:hover:bg-slate-700",
                      "focus:outline-none",
                      index === highlightedIndex && "bg-slate-100 dark:bg-slate-700",
                      user.id === value && "font-medium"
                    ),
                    onClick: () => handleSelect(user.id),
                    onMouseEnter: () => setHighlightedIndex(index),
                    children: [
                      user.avatarUrl ? /* @__PURE__ */ jsx(
                        "img",
                        {
                          src: user.avatarUrl,
                          alt: "",
                          className: "w-6 h-6 rounded-full object-cover flex-shrink-0"
                        }
                      ) : /* @__PURE__ */ jsx("span", { className: "w-6 h-6 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-300 flex-shrink-0", children: getUserInitials(user) }),
                      /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                        /* @__PURE__ */ jsx("div", { className: "truncate", children: user.name || user.email.split("@")[0] }),
                        user.name && /* @__PURE__ */ jsx("div", { className: "text-xs text-slate-400 truncate", children: user.email })
                      ] }),
                      user.id === value && /* @__PURE__ */ jsx(
                        "svg",
                        {
                          className: "w-4 h-4 text-blue-500 flex-shrink-0",
                          fill: "none",
                          stroke: "currentColor",
                          viewBox: "0 0 24 24",
                          children: /* @__PURE__ */ jsx(
                            "path",
                            {
                              strokeLinecap: "round",
                              strokeLinejoin: "round",
                              strokeWidth: 2,
                              d: "M5 13l4 4L19 7"
                            }
                          )
                        }
                      )
                    ]
                  },
                  user.id
                ))
              }
            ),
            value && !required && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("div", { className: "border-t border-slate-200 dark:border-slate-700" }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: cn(
                    "w-full px-3 py-2 text-left",
                    "text-sm text-slate-500",
                    "hover:bg-slate-100 dark:hover:bg-slate-700",
                    "focus:outline-none"
                  ),
                  onClick: handleClear,
                  children: "Unassign"
                }
              )
            ] })
          ]
        }
      )
    ] });
  }
);
function UserAvatar({ user, size = "md", showName = false, className }) {
  if (!user) return null;
  const sizeStyles2 = {
    sm: "w-5 h-5 text-[10px]",
    md: "w-6 h-6 text-xs",
    lg: "w-8 h-8 text-sm"
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    user.avatarUrl ? /* @__PURE__ */ jsx(
      "img",
      {
        src: user.avatarUrl,
        alt: getUserDisplayName(user),
        className: cn(sizeStyles2[size], "rounded-full object-cover")
      }
    ) : /* @__PURE__ */ jsx(
      "span",
      {
        className: cn(
          sizeStyles2[size],
          "rounded-full bg-slate-300 dark:bg-slate-600",
          "flex items-center justify-center",
          "font-medium text-slate-600 dark:text-slate-300"
        ),
        children: getUserInitials(user)
      }
    ),
    showName && /* @__PURE__ */ jsx("span", { className: "text-sm text-slate-700 dark:text-slate-200", children: getUserDisplayName(user) })
  ] });
}
var LABEL_COLORS = [
  "#ef4444",
  // red
  "#f97316",
  // orange
  "#eab308",
  // yellow
  "#22c55e",
  // green
  "#06b6d4",
  // cyan
  "#3b82f6",
  // blue
  "#8b5cf6",
  // violet
  "#ec4899",
  // pink
  "#94a3b8"
  // slate
];
function getRandomLabelColor() {
  return LABEL_COLORS[Math.floor(Math.random() * LABEL_COLORS.length)] || "#94a3b8";
}
var LabelPicker = forwardRef(
  function LabelPicker2({
    name,
    value: controlledValue,
    onChange,
    defaultValue = [],
    labels = [],
    allowCreate = true,
    isLoading = false,
    required,
    disabled,
    placeholder = "Add labels...",
    size = "md",
    max,
    onCreateLabel,
    className
  }, forwardedRef) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const searchInputRef = useRef(null);
    const buttonRef = useRef(null);
    const setButtonRef = useCallback(
      (node) => {
        buttonRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );
    const context = useCreateModalOptional();
    useEffect(() => {
      if (context && name) {
        context.registerField({
          id: name,
          name,
          value: controlledValue ?? defaultValue ?? [],
          required
        });
        return () => {
          context.unregisterField(name);
        };
      }
    }, [name, required, defaultValue]);
    useEffect(() => {
      if (context && name && controlledValue !== void 0) {
        const currentValue = context.getFieldValue(name);
        if (JSON.stringify(currentValue) !== JSON.stringify(controlledValue)) {
          context.setFieldValue(name, controlledValue);
        }
      }
    }, [name, controlledValue]);
    const value = controlledValue ?? (context?.getFieldValue(name) ?? defaultValue);
    const filteredLabels = useMemo(() => {
      const query = searchQuery.toLowerCase().trim();
      if (!query) return labels;
      return labels.filter(
        (label) => label.name.toLowerCase().includes(query)
      );
    }, [labels, searchQuery]);
    const canShowCreate = useMemo(() => {
      if (!allowCreate || !searchQuery.trim()) return false;
      const query = searchQuery.toLowerCase().trim();
      const exists = labels.some(
        (label) => label.name.toLowerCase() === query
      );
      return !exists;
    }, [allowCreate, searchQuery, labels]);
    const selectedLabels = useMemo(() => {
      return value.map((id) => labels.find((l) => l.id === id || l.name === id)).filter(Boolean);
    }, [value, labels]);
    const maxReached = max !== void 0 && value.length >= max;
    const handleToggle = useCallback(
      (labelId) => {
        const isSelected = value.includes(labelId);
        let newValue;
        if (isSelected) {
          newValue = value.filter((id) => id !== labelId);
        } else if (!maxReached) {
          newValue = [...value, labelId];
        } else {
          return;
        }
        onChange?.(newValue);
        context?.setFieldValue(name, newValue);
      },
      [value, maxReached, onChange, context, name]
    );
    const handleCreate = useCallback(async () => {
      if (!onCreateLabel || !searchQuery.trim() || isCreating) return;
      setIsCreating(true);
      try {
        const newLabel = await onCreateLabel(searchQuery.trim());
        if (newLabel) {
          const newValue = [...value, newLabel.id];
          onChange?.(newValue);
          context?.setFieldValue(name, newValue);
          setSearchQuery("");
        }
      } finally {
        setIsCreating(false);
      }
    }, [onCreateLabel, searchQuery, isCreating, value, onChange, context, name]);
    const handleRemove = useCallback(
      (labelId) => {
        const newValue = value.filter((id) => id !== labelId);
        onChange?.(newValue);
        context?.setFieldValue(name, newValue);
      },
      [value, onChange, context, name]
    );
    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
          setSearchQuery("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen]);
    const handleKeyDown = useCallback(
      (event) => {
        if (disabled) return;
        const totalItems = filteredLabels.length + (canShowCreate ? 1 : 0);
        switch (event.key) {
          case "Enter":
            event.preventDefault();
            if (isOpen) {
              if (canShowCreate && highlightedIndex === filteredLabels.length) {
                handleCreate();
              } else if (filteredLabels[highlightedIndex]) {
                handleToggle(filteredLabels[highlightedIndex].id);
              }
            } else {
              setIsOpen(true);
            }
            break;
          case " ":
            if (!isOpen) {
              event.preventDefault();
              setIsOpen(true);
            }
            break;
          case "Escape":
            event.preventDefault();
            setIsOpen(false);
            setSearchQuery("");
            buttonRef.current?.focus();
            break;
          case "ArrowDown":
            event.preventDefault();
            if (isOpen) {
              setHighlightedIndex(
                (prev) => Math.min(prev + 1, totalItems - 1)
              );
            } else {
              setIsOpen(true);
            }
            break;
          case "ArrowUp":
            event.preventDefault();
            if (isOpen) {
              setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            } else {
              setIsOpen(true);
            }
            break;
          case "Backspace":
            if (!searchQuery && value.length > 0) {
              const lastValue = value[value.length - 1];
              if (lastValue) {
                handleRemove(lastValue);
              }
            }
            break;
          case "Tab":
            if (isOpen) {
              setIsOpen(false);
              setSearchQuery("");
            }
            break;
        }
      },
      [disabled, isOpen, highlightedIndex, filteredLabels, canShowCreate, value, searchQuery, handleToggle, handleCreate, handleRemove]
    );
    useEffect(() => {
      if (isOpen && listRef.current) {
        const highlightedElement = listRef.current.children[highlightedIndex];
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ block: "nearest" });
        }
      }
    }, [isOpen, highlightedIndex]);
    useEffect(() => {
      setHighlightedIndex(0);
    }, [searchQuery]);
    const isDisabled = disabled || context?.isSubmitting;
    const sizeStyles2 = {
      sm: "px-2 py-1 text-xs gap-1",
      md: "px-2.5 py-1.5 text-sm gap-1.5"
    };
    return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative inline-block", className), children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          ref: setButtonRef,
          type: "button",
          disabled: isDisabled,
          onClick: () => !isDisabled && setIsOpen(!isOpen),
          onKeyDown: handleKeyDown,
          "aria-haspopup": "listbox",
          "aria-expanded": isOpen,
          "aria-labelledby": `${name}-label`,
          className: cn(
            "inline-flex items-center flex-wrap",
            "rounded-md font-medium",
            "transition-all duration-150",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            sizeStyles2[size],
            selectedLabels.length > 0 ? "bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700" : "bg-transparent border border-dashed border-slate-300 dark:border-slate-600 text-slate-500",
            isOpen && "ring-2 ring-blue-500 ring-offset-1"
          ),
          children: selectedLabels.length > 0 ? selectedLabels.map((label) => /* @__PURE__ */ jsxs(
            "span",
            {
              className: cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5",
                size === "sm" ? "text-xs" : "text-sm"
              ),
              style: {
                backgroundColor: label.color ? `${label.color}20` : "#94a3b820",
                color: label.color || "#94a3b8"
              },
              children: [
                label.name,
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    role: "button",
                    "aria-label": `Remove ${label.name}`,
                    onClick: (e) => {
                      e.stopPropagation();
                      handleRemove(label.id);
                    },
                    className: "hover:opacity-75 cursor-pointer",
                    children: /* @__PURE__ */ jsx(X, { className: "w-3 h-3" })
                  }
                )
              ]
            },
            label.id
          )) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Tag, { className: cn(size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4") }),
            /* @__PURE__ */ jsx("span", { children: placeholder })
          ] })
        }
      ),
      isOpen && /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "absolute z-50 mt-1 min-w-[220px]",
            "bg-white dark:bg-slate-800",
            "border border-slate-200 dark:border-slate-700",
            "rounded-lg shadow-lg",
            "animate-in fade-in-0 zoom-in-95 duration-100",
            "overflow-hidden"
          ),
          children: [
            /* @__PURE__ */ jsx("div", { className: "p-2 border-b border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx(
              "input",
              {
                ref: searchInputRef,
                type: "text",
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
                onKeyDown: handleKeyDown,
                placeholder: "Search or create labels...",
                className: cn(
                  "w-full px-2 py-1.5 text-sm",
                  "bg-slate-50 dark:bg-slate-900",
                  "border border-slate-200 dark:border-slate-600",
                  "rounded-md",
                  "focus:outline-none focus:ring-1 focus:ring-blue-500",
                  "placeholder:text-slate-400"
                )
              }
            ) }),
            /* @__PURE__ */ jsx(
              "div",
              {
                ref: listRef,
                role: "listbox",
                "aria-labelledby": `${name}-label`,
                "aria-multiselectable": "true",
                className: "max-h-[240px] overflow-y-auto py-1",
                children: isLoading ? /* @__PURE__ */ jsxs("div", { className: "px-3 py-4 text-center text-sm text-slate-500", children: [
                  /* @__PURE__ */ jsxs(
                    "svg",
                    {
                      className: "w-5 h-5 mx-auto mb-2 animate-spin",
                      fill: "none",
                      viewBox: "0 0 24 24",
                      children: [
                        /* @__PURE__ */ jsx(
                          "circle",
                          {
                            className: "opacity-25",
                            cx: "12",
                            cy: "12",
                            r: "10",
                            stroke: "currentColor",
                            strokeWidth: "4"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "path",
                          {
                            className: "opacity-75",
                            fill: "currentColor",
                            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          }
                        )
                      ]
                    }
                  ),
                  "Loading labels..."
                ] }) : filteredLabels.length === 0 && !canShowCreate ? /* @__PURE__ */ jsx("div", { className: "px-3 py-4 text-center text-sm text-slate-500", children: searchQuery ? "No labels found" : "No labels available" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                  filteredLabels.map((label, index) => {
                    const isSelected = value.includes(label.id);
                    return /* @__PURE__ */ jsxs(
                      "button",
                      {
                        type: "button",
                        role: "option",
                        "aria-selected": isSelected,
                        className: cn(
                          "w-full px-3 py-2 text-left",
                          "flex items-center gap-2",
                          "text-sm text-slate-700 dark:text-slate-200",
                          "hover:bg-slate-100 dark:hover:bg-slate-700",
                          "focus:outline-none",
                          index === highlightedIndex && "bg-slate-100 dark:bg-slate-700",
                          maxReached && !isSelected && "opacity-50 cursor-not-allowed"
                        ),
                        onClick: () => handleToggle(label.id),
                        onMouseEnter: () => setHighlightedIndex(index),
                        disabled: maxReached && !isSelected,
                        children: [
                          /* @__PURE__ */ jsx(
                            "span",
                            {
                              className: "w-3 h-3 rounded-sm flex-shrink-0",
                              style: { backgroundColor: label.color || "#94a3b8" }
                            }
                          ),
                          /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: label.name }),
                          isSelected && /* @__PURE__ */ jsx(
                            "svg",
                            {
                              className: "w-4 h-4 text-blue-500 flex-shrink-0",
                              fill: "none",
                              stroke: "currentColor",
                              viewBox: "0 0 24 24",
                              children: /* @__PURE__ */ jsx(
                                "path",
                                {
                                  strokeLinecap: "round",
                                  strokeLinejoin: "round",
                                  strokeWidth: 2,
                                  d: "M5 13l4 4L19 7"
                                }
                              )
                            }
                          )
                        ]
                      },
                      label.id
                    );
                  }),
                  canShowCreate && /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      className: cn(
                        "w-full px-3 py-2 text-left",
                        "flex items-center gap-2",
                        "text-sm text-slate-700 dark:text-slate-200",
                        "hover:bg-slate-100 dark:hover:bg-slate-700",
                        "focus:outline-none",
                        highlightedIndex === filteredLabels.length && "bg-slate-100 dark:bg-slate-700"
                      ),
                      onClick: handleCreate,
                      onMouseEnter: () => setHighlightedIndex(filteredLabels.length),
                      disabled: isCreating,
                      children: [
                        isCreating ? /* @__PURE__ */ jsxs(
                          "svg",
                          {
                            className: "w-4 h-4 animate-spin",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            children: [
                              /* @__PURE__ */ jsx(
                                "circle",
                                {
                                  className: "opacity-25",
                                  cx: "12",
                                  cy: "12",
                                  r: "10",
                                  stroke: "currentColor",
                                  strokeWidth: "4"
                                }
                              ),
                              /* @__PURE__ */ jsx(
                                "path",
                                {
                                  className: "opacity-75",
                                  fill: "currentColor",
                                  d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                }
                              )
                            ]
                          }
                        ) : /* @__PURE__ */ jsx(
                          "svg",
                          {
                            className: "w-4 h-4 text-blue-500",
                            fill: "none",
                            stroke: "currentColor",
                            viewBox: "0 0 24 24",
                            children: /* @__PURE__ */ jsx(
                              "path",
                              {
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeWidth: 2,
                                d: "M12 4v16m8-8H4"
                              }
                            )
                          }
                        ),
                        /* @__PURE__ */ jsxs("span", { children: [
                          'Create "',
                          /* @__PURE__ */ jsx("span", { className: "font-medium", children: searchQuery }),
                          '"'
                        ] })
                      ]
                    }
                  )
                ] })
              }
            ),
            max !== void 0 && /* @__PURE__ */ jsxs("div", { className: "px-3 py-2 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-400", children: [
              value.length,
              "/",
              max,
              " labels selected"
            ] })
          ]
        }
      )
    ] });
  }
);
function LabelBadge({ label, size = "md", onRemove, className }) {
  const sizeStyles2 = {
    sm: "px-1.5 py-0.5 text-xs",
    md: "px-2 py-1 text-sm"
  };
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1 rounded-md font-medium",
        sizeStyles2[size],
        className
      ),
      style: {
        backgroundColor: label.color ? `${label.color}20` : "#94a3b820",
        color: label.color || "#94a3b8"
      },
      children: [
        label.name,
        onRemove && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onRemove,
            className: "hover:opacity-75",
            children: /* @__PURE__ */ jsx(X, { className: cn(size === "sm" ? "w-3 h-3" : "w-4 h-4") })
          }
        )
      ]
    }
  );
}
function isMac() {
  return typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
}
function getModifierKey() {
  return isMac() ? "metaKey" : "ctrlKey";
}
function formatShortcut(shortcut) {
  const parts = [];
  const mac = isMac();
  if (shortcut.metaKey || shortcut.ctrlKey) {
    parts.push(mac ? "\u2318" : "Ctrl");
  }
  if (shortcut.shiftKey) {
    parts.push(mac ? "\u21E7" : "Shift");
  }
  if (shortcut.altKey) {
    parts.push(mac ? "\u2325" : "Alt");
  }
  const key = shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key;
  const formattedKey = key === "Enter" ? "\u21B5" : key === "Escape" ? "Esc" : key;
  parts.push(formattedKey);
  return parts.join(mac ? "" : "+");
}
function matchesShortcut(event, shortcut) {
  if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) {
    return false;
  }
  const modKey = getModifierKey();
  const wantsModifier = shortcut.metaKey || shortcut.ctrlKey;
  const hasModifier = event[modKey];
  if (wantsModifier !== hasModifier) {
    return false;
  }
  if ((shortcut.shiftKey ?? false) !== event.shiftKey) {
    return false;
  }
  if ((shortcut.altKey ?? false) !== event.altKey) {
    return false;
  }
  return true;
}
function useKeyboardShortcuts({
  enabled = true,
  shortcuts,
  container
}) {
  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;
      const target = event.target;
      const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
      for (const shortcut of shortcuts) {
        if (matchesShortcut(event, shortcut)) {
          const alwaysAllow = event.key === "Escape" || event.key === "Enter" && (event.metaKey || event.ctrlKey) || event.key === "k" && (event.metaKey || event.ctrlKey);
          if (isInput && !alwaysAllow) {
            continue;
          }
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }
          shortcut.action();
          return;
        }
      }
    },
    [enabled, shortcuts]
  );
  useEffect(() => {
    const target = container ?? document;
    target.addEventListener("keydown", handleKeyDown);
    return () => {
      target.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, container]);
}
function getCreateModalShortcuts(handlers) {
  const shortcuts = [];
  if (handlers.onClose) {
    shortcuts.push({
      key: "Escape",
      action: handlers.onClose,
      description: "Close"
    });
  }
  if (handlers.onSubmit) {
    shortcuts.push({
      key: "Enter",
      metaKey: true,
      action: handlers.onSubmit,
      description: "Submit"
    });
  }
  if (handlers.onOpenCommandPalette) {
    shortcuts.push({
      key: "k",
      metaKey: true,
      action: handlers.onOpenCommandPalette,
      description: "Command palette"
    });
  }
  return shortcuts;
}
function getKeyboardHints() {
  return [
    { shortcut: formatShortcut({ key: "k", metaKey: true }), label: "Commands" },
    { shortcut: formatShortcut({ key: "Enter", metaKey: true }), label: "Submit" },
    { shortcut: formatShortcut({ key: "Escape" }), label: "Close" }
  ];
}
var CreateModalHeader = forwardRef(
  function CreateModalHeader2({
    showCloseButton = true,
    children,
    titleProps,
    titleRequired = true,
    titlePlaceholder = "Issue title",
    className,
    ...props
  }, ref) {
    const { onClose } = useCreateModal();
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("relative", className),
        ...props,
        children: [
          showCloseButton && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: onClose,
              className: cn(
                "absolute -top-2 -right-2 p-1",
                "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300",
                "rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800",
                "transition-colors duration-150",
                "focus:outline-none focus:ring-2 focus:ring-blue-500"
              ),
              "aria-label": "Close",
              children: /* @__PURE__ */ jsx(X, { className: "w-5 h-5" })
            }
          ),
          children ?? /* @__PURE__ */ jsx(
            TextInput,
            {
              name: "title",
              variant: "title",
              placeholder: titlePlaceholder,
              required: titleRequired,
              autoFocus: true,
              ...titleProps
            }
          )
        ]
      }
    );
  }
);
var CreateModalBody = forwardRef(
  function CreateModalBody2({
    children,
    descriptionProps,
    showDescription = true,
    descriptionPlaceholder = "Add description...",
    className,
    ...props
  }, ref) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("mt-4", className),
        ...props,
        children: children ?? (showDescription && /* @__PURE__ */ jsx(
          TextInput,
          {
            name: "description",
            variant: "description",
            placeholder: descriptionPlaceholder,
            autoExpand: true,
            minRows: 3,
            maxRows: 10,
            ...descriptionProps
          }
        ))
      }
    );
  }
);
var CreateModalPropertyBar = forwardRef(
  function CreateModalPropertyBar2({ children, className, ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "mt-4 pt-4",
          "border-t border-slate-200 dark:border-slate-700",
          "flex flex-wrap gap-2",
          className
        ),
        ...props,
        children
      }
    );
  }
);
var CreateModalFooter = forwardRef(
  function CreateModalFooter2({
    submitLabel = "Create",
    cancelLabel = "Cancel",
    showKeyboardHints = true,
    submitDisabled,
    children,
    onCommandPalette,
    className,
    ...props
  }, ref) {
    const { isSubmitting, isValid, isDirty, onClose, submit } = useCreateModal();
    getKeyboardHints();
    const handleSubmit = async () => {
      await submit();
    };
    const isDisabled = submitDisabled ?? isSubmitting ?? !isValid;
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "mt-6 pt-4",
          "border-t border-slate-200 dark:border-slate-700",
          "flex items-center justify-between",
          className
        ),
        ...props,
        children: children ?? /* @__PURE__ */ jsxs(Fragment, { children: [
          showKeyboardHints ? /* @__PURE__ */ jsx("div", { className: "flex items-center gap-3", children: onCommandPalette && /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: onCommandPalette,
              className: "text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300",
              children: [
                /* @__PURE__ */ jsx("kbd", { className: "px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded mr-1", children: formatShortcut({ key: "k", metaKey: true }) }),
                "for commands"
              ]
            }
          ) }) : /* @__PURE__ */ jsx("div", {}),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: onClose,
                className: cn(
                  "px-4 py-2 text-sm font-medium",
                  "text-slate-600 dark:text-slate-400",
                  "hover:text-slate-900 dark:hover:text-slate-200",
                  "hover:bg-slate-100 dark:hover:bg-slate-800",
                  "rounded-lg",
                  "transition-colors duration-150",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500"
                ),
                children: cancelLabel
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: handleSubmit,
                disabled: isDisabled,
                className: cn(
                  "px-4 py-2 text-sm font-medium",
                  "text-white",
                  "bg-blue-600 hover:bg-blue-700",
                  "rounded-lg",
                  "transition-colors duration-150",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                ),
                children: isSubmitting ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsxs(
                    "svg",
                    {
                      className: "w-4 h-4 animate-spin",
                      fill: "none",
                      viewBox: "0 0 24 24",
                      children: [
                        /* @__PURE__ */ jsx(
                          "circle",
                          {
                            className: "opacity-25",
                            cx: "12",
                            cy: "12",
                            r: "10",
                            stroke: "currentColor",
                            strokeWidth: "4"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "path",
                          {
                            className: "opacity-75",
                            fill: "currentColor",
                            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          }
                        )
                      ]
                    }
                  ),
                  "Creating..."
                ] }) : submitLabel
              }
            )
          ] })
        ] })
      }
    );
  }
);
var FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  "[contenteditable]"
].join(",");
function getFocusableElements(container) {
  const elements = container.querySelectorAll(FOCUSABLE_SELECTOR);
  return Array.from(elements).filter((el) => {
    const style = window.getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
  });
}
function getFirstFocusable(container) {
  const elements = getFocusableElements(container);
  return elements[0] ?? null;
}
function getLastFocusable(container) {
  const elements = getFocusableElements(container);
  return elements[elements.length - 1] ?? null;
}
function isFocusable(element) {
  return element.matches(FOCUSABLE_SELECTOR);
}
function useFocusTrap(containerRef, options = {}) {
  const { enabled = true, initialFocusRef, returnFocusRef, onFocusEscape } = options;
  const previousActiveElement = useRef(null);
  const isRestoringFocus = useRef(false);
  useEffect(() => {
    if (enabled) {
      previousActiveElement.current = document.activeElement;
    }
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    const container = containerRef.current;
    const initialElement = initialFocusRef?.current ?? getFirstFocusable(container);
    if (initialElement) {
      requestAnimationFrame(() => {
        initialElement.focus();
      });
    }
    return () => {
      const returnElement = returnFocusRef?.current ?? previousActiveElement.current;
      if (returnElement && typeof returnElement.focus === "function") {
        returnElement.focus();
      }
    };
  }, [enabled, containerRef, initialFocusRef, returnFocusRef]);
  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    const container = containerRef.current;
    const handleKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };
    const handleFocusIn = (event) => {
      if (isRestoringFocus.current) return;
      if (!container.contains(event.target)) {
        onFocusEscape?.();
        const firstElement = getFirstFocusable(container);
        if (firstElement) {
          isRestoringFocus.current = true;
          firstElement.focus();
          requestAnimationFrame(() => {
            isRestoringFocus.current = false;
          });
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [enabled, containerRef, onFocusEscape]);
}
function useFieldNavigation({
  enabled = true,
  fieldRefs,
  fieldOrder,
  focusedFieldId,
  onFocusChange
}) {
  const focusField = useCallback(
    (fieldId) => {
      const element = fieldRefs.get(fieldId);
      if (element) {
        element.focus();
        onFocusChange?.(fieldId);
      }
    },
    [fieldRefs, onFocusChange]
  );
  const focusNext = useCallback(() => {
    const currentIndex = focusedFieldId ? fieldOrder.indexOf(focusedFieldId) : -1;
    const nextIndex = (currentIndex + 1) % fieldOrder.length;
    const nextField = fieldOrder[nextIndex];
    if (nextField) {
      focusField(nextField);
    }
  }, [focusedFieldId, fieldOrder, focusField]);
  const focusPrevious = useCallback(() => {
    const currentIndex = focusedFieldId ? fieldOrder.indexOf(focusedFieldId) : 0;
    const prevIndex = (currentIndex - 1 + fieldOrder.length) % fieldOrder.length;
    const prevField = fieldOrder[prevIndex];
    if (prevField) {
      focusField(prevField);
    }
  }, [focusedFieldId, fieldOrder, focusField]);
  const focusFirst = useCallback(() => {
    const firstField = fieldOrder[0];
    if (firstField) {
      focusField(firstField);
    }
  }, [fieldOrder, focusField]);
  const focusLast = useCallback(() => {
    const lastField = fieldOrder[fieldOrder.length - 1];
    if (lastField) {
      focusField(lastField);
    }
  }, [fieldOrder, focusField]);
  return {
    focusField,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast
  };
}
function useAutoFocus(ref, options = {}) {
  const { enabled = true, delay = 0 } = options;
  useEffect(() => {
    if (!enabled || !ref.current) return;
    const element = ref.current;
    if (delay > 0) {
      const timeoutId = setTimeout(() => {
        element.focus();
      }, delay);
      return () => clearTimeout(timeoutId);
    } else {
      const frameId = requestAnimationFrame(() => {
        element.focus();
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [enabled, ref, delay]);
}
var widthStyles = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-xl",
  xl: "max-w-2xl",
  full: "max-w-4xl"
};
function CreateModalRoot({
  children,
  isOpen,
  onClose,
  onSubmit,
  defaultValues,
  fieldOrder,
  className,
  backdropClassName,
  showBackdrop = true,
  width = "md"
}) {
  const modalRef = useRef(null);
  useFocusTrap(modalRef, { enabled: isOpen });
  const shortcuts = getCreateModalShortcuts({
    onClose,
    onSubmit: async () => {
    }
  });
  useKeyboardShortcuts({
    enabled: isOpen,
    shortcuts
  });
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);
  if (!isOpen) return null;
  return /* @__PURE__ */ jsxs(
    CreateModalProvider,
    {
      isOpen,
      onClose,
      onSubmit,
      defaultValues,
      fieldOrder,
      children: [
        showBackdrop && /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "fixed inset-0 z-40",
              "bg-black/50 backdrop-blur-sm",
              "animate-in fade-in-0 duration-200",
              backdropClassName
            ),
            onClick: onClose,
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4", children: /* @__PURE__ */ jsx(
          "div",
          {
            ref: modalRef,
            role: "dialog",
            "aria-modal": "true",
            className: cn(
              "w-full",
              widthStyles[width],
              "bg-white dark:bg-slate-900",
              "rounded-xl shadow-2xl",
              "p-6",
              "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-200",
              className
            ),
            onClick: (e) => e.stopPropagation(),
            children
          }
        ) })
      ]
    }
  );
}
var CreateModal = {
  Root: CreateModalRoot,
  Header: CreateModalHeader,
  Body: CreateModalBody,
  PropertyBar: CreateModalPropertyBar,
  Footer: CreateModalFooter,
  TextInput,
  PriorityPicker,
  UserPicker,
  LabelPicker
};
var DEFAULT_LABELS = [
  { id: "bug", name: "Bug", color: "#ef4444" },
  { id: "feature", name: "Feature", color: "#3b82f6" },
  { id: "enhancement", name: "Enhancement", color: "#8b5cf6" },
  { id: "documentation", name: "Documentation", color: "#06b6d4" },
  { id: "urgent", name: "Urgent", color: "#f97316" }
];
function CreateTicketModal({
  isOpen,
  onClose,
  onSuccess,
  onSubmit,
  users = [],
  usersLoading = false,
  labels: providedLabels,
  labelsLoading = false,
  allowCreateLabels = true,
  onCreateLabel: providedOnCreateLabel,
  initialValues,
  width = "lg",
  submitLabel = "Create Issue"
}) {
  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [priority, setPriority] = useState(initialValues?.priority ?? "P2");
  const [assigneeId, setAssigneeId] = useState(initialValues?.assigneeId ?? null);
  const [selectedLabels, setSelectedLabels] = useState(initialValues?.labels ?? []);
  const [localLabels, setLocalLabels] = useState(
    providedLabels ?? DEFAULT_LABELS
  );
  const [titleError, setTitleError] = useState(null);
  const labels = providedLabels ?? localLabels;
  const validateTitle = useCallback(() => {
    if (!title.trim()) {
      setTitleError("Title is required");
      return false;
    }
    setTitleError(null);
    return true;
  }, [title]);
  const handleTitleChange = useCallback((value) => {
    setTitle(value);
    if (titleError && value.trim()) {
      setTitleError(null);
    }
  }, [titleError]);
  const handleCreateLabel = useCallback(
    async (name) => {
      if (providedOnCreateLabel) {
        return providedOnCreateLabel(name);
      }
      const newLabel = {
        id: `label-${Date.now()}`,
        name,
        color: getRandomLabelColor()
      };
      setLocalLabels((prev) => [...prev, newLabel]);
      return newLabel;
    },
    [providedOnCreateLabel]
  );
  const handleSubmit = useCallback(async () => {
    if (!validateTitle()) {
      return;
    }
    const data = {
      title: title.trim(),
      description: description.trim(),
      priority,
      assigneeId,
      labels: selectedLabels
    };
    if (onSubmit) {
      await onSubmit(data);
    }
    onSuccess?.(data);
    setTitle("");
    setDescription("");
    setPriority("P2");
    setAssigneeId(null);
    setSelectedLabels([]);
    setTitleError(null);
  }, [
    title,
    description,
    priority,
    assigneeId,
    selectedLabels,
    validateTitle,
    onSubmit,
    onSuccess
  ]);
  const handleClose = useCallback(() => {
    setTitle(initialValues?.title ?? "");
    setDescription(initialValues?.description ?? "");
    setPriority(initialValues?.priority ?? "P2");
    setAssigneeId(initialValues?.assigneeId ?? null);
    setSelectedLabels(initialValues?.labels ?? []);
    setTitleError(null);
    onClose();
  }, [initialValues, onClose]);
  return /* @__PURE__ */ jsxs(
    CreateModal.Root,
    {
      isOpen,
      onClose: handleClose,
      onSubmit: handleSubmit,
      width,
      children: [
        /* @__PURE__ */ jsx(CreateModal.Header, { children: /* @__PURE__ */ jsx(
          CreateModal.TextInput,
          {
            name: "title",
            variant: "title",
            placeholder: "Issue title",
            value: title,
            onChange: handleTitleChange,
            onBlur: validateTitle,
            error: titleError,
            required: true
          }
        ) }),
        /* @__PURE__ */ jsx(CreateModal.Body, { children: /* @__PURE__ */ jsx(
          CreateModal.TextInput,
          {
            name: "description",
            variant: "description",
            placeholder: "Add description...",
            value: description,
            onChange: setDescription,
            autoExpand: true,
            minRows: 3,
            maxRows: 10
          }
        ) }),
        /* @__PURE__ */ jsxs(CreateModal.PropertyBar, { children: [
          /* @__PURE__ */ jsx(
            CreateModal.PriorityPicker,
            {
              name: "priority",
              value: priority,
              onChange: setPriority
            }
          ),
          /* @__PURE__ */ jsx(
            CreateModal.UserPicker,
            {
              name: "assigneeId",
              value: assigneeId,
              onChange: setAssigneeId,
              users,
              isLoading: usersLoading,
              placeholder: "Assignee"
            }
          ),
          /* @__PURE__ */ jsx(
            CreateModal.LabelPicker,
            {
              name: "labels",
              value: selectedLabels,
              onChange: setSelectedLabels,
              labels,
              isLoading: labelsLoading,
              allowCreate: allowCreateLabels,
              onCreateLabel: handleCreateLabel
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          CreateModal.Footer,
          {
            submitLabel,
            showKeyboardHints: true
          }
        )
      ]
    }
  );
}
var CONDITION_TYPE_OPTIONS = [
  {
    value: "ERROR_RATE_THRESHOLD",
    label: "Error Rate",
    description: "Alert when error rate exceeds threshold",
    color: "#ef4444",
    hasThreshold: true,
    thresholdLabel: "Error Rate (%)"
  },
  {
    value: "SUCCESS_RATE_DROP",
    label: "Success Drop",
    description: "Alert when success rate drops below threshold",
    color: "#f97316",
    hasThreshold: true,
    thresholdLabel: "Min Success Rate (%)"
  },
  {
    value: "NEW_ERROR_TYPE",
    label: "New Error",
    description: "Alert when a new error type is detected",
    color: "#3b82f6",
    hasThreshold: false
  },
  {
    value: "ERROR_RECURRENCE",
    label: "Recurrence",
    description: "Alert when a resolved error recurs",
    color: "#8b5cf6",
    hasThreshold: false
  },
  {
    value: "ERROR_SPIKE",
    label: "Error Spike",
    description: "Alert when error count spikes suddenly",
    color: "#ec4899",
    hasThreshold: true,
    thresholdLabel: "Spike Factor (x)"
  },
  {
    value: "AUTOMATION_FAILURE_STREAK",
    label: "Failure Streak",
    description: "Alert after consecutive automation failures",
    color: "#f43f5e",
    hasThreshold: true,
    thresholdLabel: "Consecutive Failures"
  },
  {
    value: "TEST_SUITE_FAILURE",
    label: "Test Failure",
    description: "Alert when a test suite fails",
    color: "#14b8a6",
    hasThreshold: false
  }
];
function CreateAlertRuleModal({
  isOpen,
  onClose,
  onSuccess,
  onSubmit,
  initialValues,
  width = "lg",
  submitLabel = "Create Alert Rule"
}) {
  const [name, setName] = useState(initialValues?.name ?? "");
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [conditionType, setConditionType] = useState(
    initialValues?.conditionType ?? "ERROR_RATE_THRESHOLD"
  );
  const [threshold, setThreshold] = useState(initialValues?.threshold ?? 10);
  const [cooldownMinutes, setCooldownMinutes] = useState(initialValues?.cooldownMinutes ?? 15);
  const [slackWebhookUrl, setSlackWebhookUrl] = useState(initialValues?.slackWebhookUrl ?? "");
  const [nameError, setNameError] = useState(null);
  const currentCondition = CONDITION_TYPE_OPTIONS.find((c) => c.value === conditionType);
  const validateName = useCallback(() => {
    if (!name.trim()) {
      setNameError("Name is required");
      return false;
    }
    setNameError(null);
    return true;
  }, [name]);
  const handleNameChange = useCallback(
    (value) => {
      setName(value);
      if (nameError && value.trim()) {
        setNameError(null);
      }
    },
    [nameError]
  );
  const handleSubmit = useCallback(async () => {
    if (!validateName()) {
      return;
    }
    const data = {
      name: name.trim(),
      description: description.trim(),
      conditionType,
      threshold,
      cooldownMinutes,
      slackWebhookUrl: slackWebhookUrl.trim()
    };
    if (onSubmit) {
      await onSubmit(data);
    }
    onSuccess?.(data);
    setName("");
    setDescription("");
    setConditionType("ERROR_RATE_THRESHOLD");
    setThreshold(10);
    setCooldownMinutes(15);
    setSlackWebhookUrl("");
    setNameError(null);
  }, [
    name,
    description,
    conditionType,
    threshold,
    cooldownMinutes,
    slackWebhookUrl,
    validateName,
    onSubmit,
    onSuccess
  ]);
  const handleClose = useCallback(() => {
    setName(initialValues?.name ?? "");
    setDescription(initialValues?.description ?? "");
    setConditionType(initialValues?.conditionType ?? "ERROR_RATE_THRESHOLD");
    setThreshold(initialValues?.threshold ?? 10);
    setCooldownMinutes(initialValues?.cooldownMinutes ?? 15);
    setSlackWebhookUrl(initialValues?.slackWebhookUrl ?? "");
    setNameError(null);
    onClose();
  }, [initialValues, onClose]);
  return /* @__PURE__ */ jsxs(
    CreateModal.Root,
    {
      isOpen,
      onClose: handleClose,
      onSubmit: handleSubmit,
      width,
      children: [
        /* @__PURE__ */ jsx(CreateModal.Header, { children: /* @__PURE__ */ jsx(
          CreateModal.TextInput,
          {
            name: "name",
            variant: "title",
            placeholder: "Alert rule name",
            value: name,
            onChange: handleNameChange,
            onBlur: validateName,
            error: nameError,
            required: true
          }
        ) }),
        /* @__PURE__ */ jsxs(CreateModal.Body, { children: [
          /* @__PURE__ */ jsx(
            CreateModal.TextInput,
            {
              name: "description",
              variant: "description",
              placeholder: "Add description...",
              value: description,
              onChange: setDescription,
              autoExpand: true,
              minRows: 2,
              maxRows: 5
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "mt-4", children: [
            /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2", children: "Condition Type" }),
            /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 gap-2", children: CONDITION_TYPE_OPTIONS.map((option) => /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => setConditionType(option.value),
                className: cn(
                  "p-3 rounded-lg border text-left transition-colors",
                  conditionType === option.value ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                ),
                children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        className: "w-2 h-2 rounded-full",
                        style: { backgroundColor: option.color }
                      }
                    ),
                    /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-slate-900 dark:text-white", children: option.label })
                  ] }),
                  /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-slate-500 dark:text-slate-400", children: option.description })
                ]
              },
              option.value
            )) })
          ] }),
          currentCondition?.hasThreshold && /* @__PURE__ */ jsxs("div", { className: "mt-4", children: [
            /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: currentCondition.thresholdLabel }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                value: threshold,
                onChange: (e) => setThreshold(parseInt(e.target.value) || 0),
                min: 1,
                max: 100,
                className: "w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-4", children: [
            /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Cooldown (minutes)" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                value: cooldownMinutes,
                onChange: (e) => setCooldownMinutes(parseInt(e.target.value) || 15),
                min: 1,
                className: "w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              }
            ),
            /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-slate-500", children: "Minimum time between alerts for this rule" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-4", children: [
            /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Slack Webhook URL (optional)" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "url",
                value: slackWebhookUrl,
                onChange: (e) => setSlackWebhookUrl(e.target.value),
                placeholder: "https://hooks.slack.com/services/...",
                className: "w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx(CreateModal.Footer, { submitLabel, showKeyboardHints: true })
      ]
    }
  );
}
var TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New York" },
  { value: "America/Los_Angeles", label: "America/Los Angeles" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai" },
  { value: "Australia/Sydney", label: "Australia/Sydney" }
];
var CRON_PRESETS = [
  { value: "0 * * * *", label: "Every hour" },
  { value: "0 0 * * *", label: "Daily at midnight" },
  { value: "0 9 * * *", label: "Daily at 9 AM" },
  { value: "0 0 * * 1", label: "Weekly on Monday" },
  { value: "0 0 1 * *", label: "Monthly on 1st" }
];
function ToggleSwitch({
  checked,
  onChange,
  label,
  description
}) {
  return /* @__PURE__ */ jsxs("label", { className: "flex items-start gap-3 cursor-pointer", children: [
    /* @__PURE__ */ jsxs("div", { className: "relative mt-0.5", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          checked,
          onChange: (e) => onChange(e.target.checked),
          className: "sr-only"
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "w-9 h-5 rounded-full transition-colors",
            checked ? "bg-blue-500" : "bg-slate-300 dark:bg-slate-600"
          )
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow",
            checked && "translate-x-4"
          )
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-slate-700 dark:text-slate-300", children: label }),
      description && /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: description })
    ] })
  ] });
}
function CreateTestSuiteModal({
  isOpen,
  onClose,
  onSuccess,
  onSubmit,
  initialValues,
  width = "lg",
  submitLabel = "Create Test Suite"
}) {
  const [name, setName] = useState(initialValues?.name ?? "");
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [scheduleEnabled, setScheduleEnabled] = useState(initialValues?.scheduleEnabled ?? false);
  const [scheduleCron, setScheduleCron] = useState(initialValues?.scheduleCron ?? "0 0 * * *");
  const [scheduleTimezone, setScheduleTimezone] = useState(initialValues?.scheduleTimezone ?? "UTC");
  const [parallelExecution, setParallelExecution] = useState(initialValues?.parallelExecution ?? false);
  const [stopOnFirstFailure, setStopOnFirstFailure] = useState(initialValues?.stopOnFirstFailure ?? false);
  const [alertOnFailure, setAlertOnFailure] = useState(initialValues?.alertOnFailure ?? true);
  const [nameError, setNameError] = useState(null);
  const validateName = useCallback(() => {
    if (!name.trim()) {
      setNameError("Name is required");
      return false;
    }
    setNameError(null);
    return true;
  }, [name]);
  const handleNameChange = useCallback(
    (value) => {
      setName(value);
      if (nameError && value.trim()) {
        setNameError(null);
      }
    },
    [nameError]
  );
  const handleSubmit = useCallback(async () => {
    if (!validateName()) {
      return;
    }
    const data = {
      name: name.trim(),
      description: description.trim(),
      scheduleEnabled,
      scheduleCron,
      scheduleTimezone,
      parallelExecution,
      stopOnFirstFailure,
      alertOnFailure
    };
    if (onSubmit) {
      await onSubmit(data);
    }
    onSuccess?.(data);
    setName("");
    setDescription("");
    setScheduleEnabled(false);
    setScheduleCron("0 0 * * *");
    setScheduleTimezone("UTC");
    setParallelExecution(false);
    setStopOnFirstFailure(false);
    setAlertOnFailure(true);
    setNameError(null);
  }, [
    name,
    description,
    scheduleEnabled,
    scheduleCron,
    scheduleTimezone,
    parallelExecution,
    stopOnFirstFailure,
    alertOnFailure,
    validateName,
    onSubmit,
    onSuccess
  ]);
  const handleClose = useCallback(() => {
    setName(initialValues?.name ?? "");
    setDescription(initialValues?.description ?? "");
    setScheduleEnabled(initialValues?.scheduleEnabled ?? false);
    setScheduleCron(initialValues?.scheduleCron ?? "0 0 * * *");
    setScheduleTimezone(initialValues?.scheduleTimezone ?? "UTC");
    setParallelExecution(initialValues?.parallelExecution ?? false);
    setStopOnFirstFailure(initialValues?.stopOnFirstFailure ?? false);
    setAlertOnFailure(initialValues?.alertOnFailure ?? true);
    setNameError(null);
    onClose();
  }, [initialValues, onClose]);
  return /* @__PURE__ */ jsxs(
    CreateModal.Root,
    {
      isOpen,
      onClose: handleClose,
      onSubmit: handleSubmit,
      width,
      children: [
        /* @__PURE__ */ jsx(CreateModal.Header, { children: /* @__PURE__ */ jsx(
          CreateModal.TextInput,
          {
            name: "name",
            variant: "title",
            placeholder: "Test suite name",
            value: name,
            onChange: handleNameChange,
            onBlur: validateName,
            error: nameError,
            required: true
          }
        ) }),
        /* @__PURE__ */ jsxs(CreateModal.Body, { children: [
          /* @__PURE__ */ jsx(
            CreateModal.TextInput,
            {
              name: "description",
              variant: "description",
              placeholder: "Add description...",
              value: description,
              onChange: setDescription,
              autoExpand: true,
              minRows: 2,
              maxRows: 5
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "mt-6 space-y-4", children: [
            /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold text-slate-900 dark:text-white", children: "Schedule" }),
            /* @__PURE__ */ jsx(
              ToggleSwitch,
              {
                checked: scheduleEnabled,
                onChange: setScheduleEnabled,
                label: "Enable scheduled runs",
                description: "Automatically run this test suite on a schedule"
              }
            ),
            scheduleEnabled && /* @__PURE__ */ jsxs("div", { className: "ml-12 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200", children: [
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2", children: "Schedule" }),
                /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 mb-2", children: CRON_PRESETS.map((preset) => /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: () => setScheduleCron(preset.value),
                    className: cn(
                      "px-2 py-1 text-xs rounded-md transition-colors",
                      scheduleCron === preset.value ? "bg-blue-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                    ),
                    children: preset.label
                  },
                  preset.value
                )) }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: scheduleCron,
                    onChange: (e) => setScheduleCron(e.target.value),
                    placeholder: "0 0 * * *",
                    className: "w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-mono bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1", children: "Timezone" }),
                /* @__PURE__ */ jsx(
                  "select",
                  {
                    value: scheduleTimezone,
                    onChange: (e) => setScheduleTimezone(e.target.value),
                    className: "w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500",
                    children: TIMEZONE_OPTIONS.map((tz) => /* @__PURE__ */ jsx("option", { value: tz.value, children: tz.label }, tz.value))
                  }
                )
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-6 space-y-4", children: [
            /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold text-slate-900 dark:text-white", children: "Execution Options" }),
            /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
              /* @__PURE__ */ jsx(
                ToggleSwitch,
                {
                  checked: parallelExecution,
                  onChange: setParallelExecution,
                  label: "Run tests in parallel",
                  description: "Execute multiple tests simultaneously for faster runs"
                }
              ),
              /* @__PURE__ */ jsx(
                ToggleSwitch,
                {
                  checked: stopOnFirstFailure,
                  onChange: setStopOnFirstFailure,
                  label: "Stop on first failure",
                  description: "Abort the test run when the first test fails"
                }
              ),
              /* @__PURE__ */ jsx(
                ToggleSwitch,
                {
                  checked: alertOnFailure,
                  onChange: setAlertOnFailure,
                  label: "Alert on failure",
                  description: "Send notifications when tests fail"
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx(CreateModal.Footer, { submitLabel, showKeyboardHints: true })
      ]
    }
  );
}
var RUN_COUNT_OPTIONS = [1, 3, 5, 10];
function CreateFromPromptModal({
  isOpen,
  onClose,
  onSuccess,
  onSubmit,
  width = "lg",
  submitLabel = "Generate & Run Tests",
  isSubmitting: externalIsSubmitting,
  wallets = []
}) {
  const [prompt, setPrompt] = useState("");
  const [runCount, setRunCount] = useState(5);
  const [selectedWalletId, setSelectedWalletId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promptError, setPromptError] = useState(null);
  const validatePrompt = useCallback(() => {
    if (!prompt.trim()) {
      setPromptError("Prompt is required");
      return false;
    }
    if (prompt.trim().length < 10) {
      setPromptError("Prompt should be at least 10 characters");
      return false;
    }
    setPromptError(null);
    return true;
  }, [prompt]);
  const handlePromptChange = useCallback(
    (value) => {
      setPrompt(value);
      if (promptError && value.trim().length >= 10) {
        setPromptError(null);
      }
    },
    [promptError]
  );
  const handleSubmit = useCallback(async () => {
    if (!validatePrompt()) {
      return;
    }
    const data = {
      prompt: prompt.trim(),
      runCount,
      walletId: selectedWalletId || void 0
    };
    setIsSubmitting(true);
    try {
      if (onSubmit) {
        const result = await onSubmit(data);
        onSuccess?.(result);
      }
      setPrompt("");
      setRunCount(5);
      setSelectedWalletId("");
      setPromptError(null);
      onClose();
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  }, [prompt, runCount, selectedWalletId, validatePrompt, onSubmit, onSuccess, onClose]);
  const handleClose = useCallback(() => {
    if (isSubmitting || externalIsSubmitting) return;
    setPrompt("");
    setRunCount(5);
    setSelectedWalletId("");
    setPromptError(null);
    onClose();
  }, [onClose, isSubmitting, externalIsSubmitting]);
  const submitting = isSubmitting || externalIsSubmitting;
  return /* @__PURE__ */ jsxs(
    CreateModal.Root,
    {
      isOpen,
      onClose: handleClose,
      onSubmit: handleSubmit,
      width,
      children: [
        /* @__PURE__ */ jsx(CreateModal.Header, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30", children: /* @__PURE__ */ jsx(SparklesIcon, { className: "w-5 h-5 text-purple-600 dark:text-purple-400" }) }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold text-slate-900 dark:text-white", children: "Create E2E Test from Prompt" }),
            /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: "AI will generate an automation and test it for reliability" })
          ] })
        ] }) }),
        /* @__PURE__ */ jsxs(CreateModal.Body, { children: [
          /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300", children: "Describe what the automation should do" }),
            /* @__PURE__ */ jsx(
              "textarea",
              {
                value: prompt,
                onChange: (e) => handlePromptChange(e.target.value),
                onBlur: validatePrompt,
                placeholder: "Example: Every 5 minutes, check the SOL balance of wallet ABC123 and if it exceeds 10 SOL, swap 5 SOL to USDC using Jupiter",
                rows: 4,
                disabled: submitting,
                className: cn(
                  "w-full px-4 py-3 border rounded-lg text-sm",
                  "bg-white dark:bg-slate-800 text-slate-900 dark:text-white",
                  "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                  "focus:outline-none focus:ring-2 focus:ring-purple-500",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  promptError ? "border-red-500 focus:ring-red-500" : "border-slate-200 dark:border-slate-700"
                )
              }
            ),
            promptError && /* @__PURE__ */ jsx("p", { className: "text-sm text-red-500", children: promptError }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: "Be specific about timing, actions, conditions, and any services to use." })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-6 space-y-3", children: [
            /* @__PURE__ */ jsxs("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300", children: [
              "Number of test runs: ",
              /* @__PURE__ */ jsx("span", { className: "font-bold text-purple-600", children: runCount })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "flex gap-2", children: RUN_COUNT_OPTIONS.map((count) => /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => setRunCount(count),
                disabled: submitting,
                className: cn(
                  "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  runCount === count ? "bg-purple-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                ),
                children: [
                  count,
                  "x"
                ]
              },
              count
            )) }),
            /* @__PURE__ */ jsxs("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: [
              "The automation will run ",
              runCount,
              " times to calculate error rate and reliability."
            ] })
          ] }),
          wallets.length > 0 && /* @__PURE__ */ jsxs("div", { className: "mt-6 space-y-3", children: [
            /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300", children: "Run with wallet (optional)" }),
            /* @__PURE__ */ jsxs(
              "select",
              {
                value: selectedWalletId,
                onChange: (e) => setSelectedWalletId(e.target.value),
                disabled: submitting,
                className: cn(
                  "w-full px-4 py-2.5 border rounded-lg text-sm appearance-none",
                  "bg-white dark:bg-slate-800 text-slate-900 dark:text-white",
                  "border-slate-200 dark:border-slate-700",
                  "focus:outline-none focus:ring-2 focus:ring-purple-500",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                ),
                children: [
                  /* @__PURE__ */ jsx("option", { value: "", children: "No wallet - dry run only" }),
                  wallets.map((wallet) => /* @__PURE__ */ jsxs("option", { value: wallet.id, children: [
                    wallet.chain,
                    ": ",
                    wallet.address.slice(0, 8),
                    "...",
                    wallet.address.slice(-4),
                    wallet.isDefault ? " (Default)" : ""
                  ] }, wallet.id))
                ]
              }
            ),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: "Select a wallet to run automation tests with real wallet context." })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700", children: [
            /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold text-slate-900 dark:text-white mb-2", children: "What happens next?" }),
            /* @__PURE__ */ jsxs("ol", { className: "text-xs text-slate-600 dark:text-slate-400 space-y-1 list-decimal list-inside", children: [
              /* @__PURE__ */ jsx("li", { children: "AI generates automation XML from your prompt" }),
              /* @__PURE__ */ jsx("li", { children: "Flow and automation are created in automation-core" }),
              /* @__PURE__ */ jsxs("li", { children: [
                "Automation runs ",
                runCount,
                " times to measure reliability"
              ] }),
              /* @__PURE__ */ jsx("li", { children: "Results are saved as a reusable test case" }),
              /* @__PURE__ */ jsx("li", { children: "Automation is deactivated after testing" })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          CreateModal.Footer,
          {
            submitLabel: submitting ? "Generating..." : submitLabel,
            showKeyboardHints: true,
            submitDisabled: submitting
          }
        )
      ]
    }
  );
}
function SparklesIcon({ className }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx("path", { d: "M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" }),
        /* @__PURE__ */ jsx("path", { d: "M5 3v4" }),
        /* @__PURE__ */ jsx("path", { d: "M3 5h4" }),
        /* @__PURE__ */ jsx("path", { d: "M19 17v4" }),
        /* @__PURE__ */ jsx("path", { d: "M17 19h4" })
      ]
    }
  );
}
function PromptStudioSection({
  title,
  icon,
  badge,
  defaultCollapsed = false,
  children,
  className
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  return /* @__PURE__ */ jsxs("div", { className: cn("border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden", className), children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => setIsCollapsed(!isCollapsed),
        className: "w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
            icon && /* @__PURE__ */ jsx("span", { className: "text-slate-500 dark:text-slate-400", children: icon }),
            /* @__PURE__ */ jsx("span", { className: "font-medium text-slate-900 dark:text-white", children: title }),
            badge !== void 0 && /* @__PURE__ */ jsx("span", { className: "px-2 py-0.5 text-xs font-medium rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300", children: badge })
          ] }),
          /* @__PURE__ */ jsx("span", { className: "text-slate-400 dark:text-slate-500", children: isCollapsed ? /* @__PURE__ */ jsx(ChevronRight, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx(ChevronDown, { className: "h-5 w-5" }) })
        ]
      }
    ),
    !isCollapsed && /* @__PURE__ */ jsx("div", { className: "p-4 bg-white dark:bg-slate-900", children })
  ] });
}
function PromptStudioToolCard({
  tool,
  onToggle,
  readOnly = false,
  defaultSchemaExpanded = false
}) {
  const [isSchemaExpanded, setIsSchemaExpanded] = useState(defaultSchemaExpanded);
  const [activeTab, setActiveTab] = useState("input");
  const hasInputSchema = tool.inputSchema && Object.keys(tool.inputSchema).length > 0;
  const hasOutputSchema = tool.outputSchema && Object.keys(tool.outputSchema).length > 0;
  const hasSchema = hasInputSchema || hasOutputSchema;
  const isBlockchain = tool.tags?.includes("blockchain");
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "border rounded-lg overflow-hidden transition-all",
        tool.enabled ? "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" : "border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/50 opacity-60"
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between p-3 gap-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 flex-1 min-w-0", children: [
            !readOnly && /* @__PURE__ */ jsx("div", { className: "pt-0.5", children: /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                checked: tool.enabled,
                onChange: (e) => onToggle?.(e.target.checked),
                className: "h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-purple-600 focus:ring-purple-500"
              }
            ) }),
            /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
                /* @__PURE__ */ jsx(Wrench, { className: "h-4 w-4 text-slate-400 flex-shrink-0" }),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: cn(
                      "font-medium text-slate-900 dark:text-white",
                      !tool.enabled && "line-through"
                    ),
                    children: tool.name
                  }
                ),
                isBlockchain && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400", children: [
                  /* @__PURE__ */ jsx(Zap, { className: "h-3 w-3" }),
                  "BLOCKCHAIN"
                ] }),
                tool.category && /* @__PURE__ */ jsx("span", { className: "px-1.5 py-0.5 text-xs font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400", children: tool.category })
              ] }),
              tool.description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2", children: tool.description }),
              /* @__PURE__ */ jsxs("div", { className: "mt-1.5 flex items-center gap-2 text-xs text-slate-400", children: [
                /* @__PURE__ */ jsx("code", { className: "font-mono", children: tool.id }),
                tool.service && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx("span", { children: "\u2022" }),
                  /* @__PURE__ */ jsx("span", { children: tool.service })
                ] })
              ] })
            ] })
          ] }),
          hasSchema && /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => setIsSchemaExpanded(!isSchemaExpanded),
              className: "flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors",
              children: [
                "Schema",
                isSchemaExpanded ? /* @__PURE__ */ jsx(ChevronDown, { className: "h-3 w-3" }) : /* @__PURE__ */ jsx(ChevronRight, { className: "h-3 w-3" })
              ]
            }
          )
        ] }),
        hasSchema && isSchemaExpanded && /* @__PURE__ */ jsx("div", { className: "border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50", children: hasInputSchema && hasOutputSchema ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs("div", { className: "flex border-b border-slate-200 dark:border-slate-700", children: [
            /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => setActiveTab("input"),
                className: cn(
                  "flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors",
                  activeTab === "input" ? "text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400 -mb-px bg-white dark:bg-slate-900" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                ),
                children: [
                  /* @__PURE__ */ jsx(ArrowRight, { className: "h-3 w-3" }),
                  "Input"
                ]
              }
            ),
            /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => setActiveTab("output"),
                className: cn(
                  "flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors",
                  activeTab === "output" ? "text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400 -mb-px bg-white dark:bg-slate-900" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                ),
                children: [
                  /* @__PURE__ */ jsx(ArrowLeft, { className: "h-3 w-3" }),
                  "Output"
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsx("div", { className: "p-3", children: /* @__PURE__ */ jsx(
            JsonViewer,
            {
              data: activeTab === "input" ? tool.inputSchema : tool.outputSchema,
              collapsed: false,
              collapsedDepth: 3,
              maxHeight: 300,
              size: "sm",
              variant: "minimal"
            }
          ) })
        ] }) : hasInputSchema ? /* @__PURE__ */ jsxs("div", { className: "p-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 mb-2 text-xs font-medium text-slate-500 dark:text-slate-400", children: [
            /* @__PURE__ */ jsx(ArrowRight, { className: "h-3 w-3" }),
            "Input Schema"
          ] }),
          /* @__PURE__ */ jsx(
            JsonViewer,
            {
              data: tool.inputSchema,
              collapsed: false,
              collapsedDepth: 3,
              maxHeight: 300,
              size: "sm",
              variant: "minimal"
            }
          )
        ] }) : hasOutputSchema ? /* @__PURE__ */ jsxs("div", { className: "p-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 mb-2 text-xs font-medium text-slate-500 dark:text-slate-400", children: [
            /* @__PURE__ */ jsx(ArrowLeft, { className: "h-3 w-3" }),
            "Output Schema"
          ] }),
          /* @__PURE__ */ jsx(
            JsonViewer,
            {
              data: tool.outputSchema,
              collapsed: false,
              collapsedDepth: 3,
              maxHeight: 300,
              size: "sm",
              variant: "minimal"
            }
          )
        ] }) : null })
      ]
    }
  );
}
function PromptStudioContextCard({
  context,
  onToggle,
  readOnly = false,
  defaultExpanded = false
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const hasData = context.data && Object.keys(context.data).length > 0;
  const dataPreview = hasData ? JSON.stringify(context.data).slice(0, 80) + (JSON.stringify(context.data).length > 80 ? "..." : "") : "No data";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "border rounded-lg overflow-hidden transition-all",
        context.enabled ? "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" : "border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/50 opacity-60"
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between p-3 gap-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 flex-1 min-w-0", children: [
            !readOnly && /* @__PURE__ */ jsx("div", { className: "pt-0.5", children: /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                checked: context.enabled,
                onChange: (e) => onToggle?.(e.target.checked),
                className: "h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-purple-600 focus:ring-purple-500"
              }
            ) }),
            /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
                /* @__PURE__ */ jsx(Database, { className: "h-4 w-4 text-slate-400 flex-shrink-0" }),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: cn(
                      "font-medium text-slate-900 dark:text-white",
                      !context.enabled && "line-through"
                    ),
                    children: context.serviceName
                  }
                ),
                context.contextName && context.contextName !== context.serviceName && /* @__PURE__ */ jsx("span", { className: "px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400", children: context.contextName })
              ] }),
              !isExpanded && hasData && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-slate-400 font-mono truncate", children: dataPreview }),
              context.serviceUrl && /* @__PURE__ */ jsx("div", { className: "mt-1 text-xs text-slate-400", children: /* @__PURE__ */ jsx("code", { className: "font-mono", children: context.id }) })
            ] })
          ] }),
          hasData && /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => setIsExpanded(!isExpanded),
              className: "flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors",
              children: [
                "Data",
                isExpanded ? /* @__PURE__ */ jsx(ChevronDown, { className: "h-3 w-3" }) : /* @__PURE__ */ jsx(ChevronRight, { className: "h-3 w-3" })
              ]
            }
          )
        ] }),
        hasData && isExpanded && /* @__PURE__ */ jsx("div", { className: "border-t border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/50", children: /* @__PURE__ */ jsx(
          JsonViewer,
          {
            data: context.data,
            collapsed: false,
            collapsedDepth: 2,
            maxHeight: 250,
            size: "sm",
            variant: "minimal"
          }
        ) })
      ]
    }
  );
}
function PromptStudio({
  userPrompt,
  systemPrompt,
  tools,
  contexts,
  onToolToggle,
  onContextToggle,
  readOnly = false,
  defaultCollapsed = false,
  className
}) {
  const [toolSearch, setToolSearch] = useState("");
  const [expandedServices, setExpandedServices] = useState(/* @__PURE__ */ new Set());
  const filteredTools = useMemo(() => {
    if (!toolSearch.trim()) return tools;
    const search = toolSearch.toLowerCase();
    return tools.filter(
      (tool) => tool.name.toLowerCase().includes(search) || tool.id.toLowerCase().includes(search) || tool.description?.toLowerCase().includes(search) || tool.service?.toLowerCase().includes(search)
    );
  }, [tools, toolSearch]);
  const toolStats = useMemo(() => {
    const enabled = tools.filter((t) => t.enabled).length;
    const total = tools.length;
    const hidden = total - enabled;
    const filtered = filteredTools.length;
    return { enabled, total, hidden, filtered };
  }, [tools, filteredTools]);
  const contextStats = useMemo(() => {
    const enabled = contexts.filter((c) => c.enabled).length;
    const total = contexts.length;
    const hidden = total - enabled;
    return { enabled, total, hidden };
  }, [contexts]);
  const toolsByService = useMemo(() => {
    const grouped = /* @__PURE__ */ new Map();
    for (const tool of filteredTools) {
      const service = tool.service || "Other";
      if (!grouped.has(service)) {
        grouped.set(service, []);
      }
      grouped.get(service).push(tool);
    }
    return new Map([...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }, [filteredTools]);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("h2", { className: "text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Eye, { className: "h-5 w-5 text-purple-500" }),
        "Prompt Studio"
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400", children: [
        /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(Wrench, { className: "h-4 w-4" }),
          toolStats.enabled,
          "/",
          toolStats.total,
          " tools"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(Database, { className: "h-4 w-4" }),
          contextStats.enabled,
          "/",
          contextStats.total,
          " contexts"
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      PromptStudioSection,
      {
        title: "User Prompt",
        icon: /* @__PURE__ */ jsx(MessageSquare, { className: "h-5 w-5" }),
        defaultCollapsed,
        children: userPrompt ? /* @__PURE__ */ jsx("div", { className: "bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700", children: /* @__PURE__ */ jsx("p", { className: "text-slate-700 dark:text-slate-300 whitespace-pre-wrap", children: userPrompt }) }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 dark:text-slate-500 italic", children: "No user prompt provided" })
      }
    ),
    /* @__PURE__ */ jsx(
      PromptStudioSection,
      {
        title: "Tools",
        icon: /* @__PURE__ */ jsx(Wrench, { className: "h-5 w-5" }),
        badge: toolStats.hidden > 0 ? `${toolStats.enabled} enabled, ${toolStats.hidden} hidden` : `${toolStats.total}`,
        defaultCollapsed,
        children: tools.length > 0 ? /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "text",
                value: toolSearch,
                onChange: (e) => setToolSearch(e.target.value),
                placeholder: "Search tools by name, ID, or description...",
                className: cn(
                  "w-full pl-9 pr-9 py-2 text-sm",
                  "bg-slate-50 dark:bg-slate-800",
                  "border border-slate-200 dark:border-slate-700",
                  "rounded-lg",
                  "text-slate-900 dark:text-white",
                  "placeholder-slate-400 dark:placeholder-slate-500",
                  "focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                )
              }
            ),
            toolSearch && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setToolSearch(""),
                className: "absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
              }
            )
          ] }),
          toolSearch && /* @__PURE__ */ jsxs("div", { className: "text-xs text-slate-500 dark:text-slate-400", children: [
            "Showing ",
            toolStats.filtered,
            " of ",
            toolStats.total,
            " tools",
            toolStats.filtered === 0 && /* @__PURE__ */ jsx("span", { className: "ml-1", children: "\u2014 try a different search term" })
          ] }),
          toolsByService.size > 0 ? Array.from(toolsByService.entries()).map(([service, serviceTools]) => {
            const enabledCount = serviceTools.filter((t) => t.enabled).length;
            const isCollapsed = !expandedServices.has(service);
            const toggleCollapse = () => {
              setExpandedServices((prev) => {
                const next = new Set(prev);
                if (next.has(service)) {
                  next.delete(service);
                } else {
                  next.add(service);
                }
                return next;
              });
            };
            return /* @__PURE__ */ jsxs("div", { className: "border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden", children: [
              /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  onClick: toggleCollapse,
                  className: "flex items-center justify-between w-full px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors",
                  children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                      isCollapsed ? /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4 text-slate-400" }) : /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 text-slate-400" }),
                      /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300", children: service })
                    ] }),
                    /* @__PURE__ */ jsxs("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: [
                      enabledCount,
                      "/",
                      serviceTools.length,
                      " enabled"
                    ] })
                  ]
                }
              ),
              !isCollapsed && /* @__PURE__ */ jsx("div", { className: "p-2 space-y-2", children: serviceTools.map((tool) => /* @__PURE__ */ jsx(
                PromptStudioToolCard,
                {
                  tool,
                  onToggle: (enabled) => onToolToggle?.(tool.id, enabled),
                  readOnly
                },
                tool.id
              )) })
            ] }, service);
          }) : toolSearch ? /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 dark:text-slate-500 italic text-center py-4", children: "No tools match your search" }) : null,
          toolStats.hidden > 0 && !toolSearch && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500 pt-2 border-t border-slate-200 dark:border-slate-700", children: [
            /* @__PURE__ */ jsx(EyeOff, { className: "h-4 w-4" }),
            toolStats.hidden,
            " tool",
            toolStats.hidden > 1 ? "s" : "",
            " hidden from prompt"
          ] })
        ] }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 dark:text-slate-500 italic", children: "No tools available. Enable services to add tools." })
      }
    ),
    /* @__PURE__ */ jsx(
      PromptStudioSection,
      {
        title: "Service Contexts",
        icon: /* @__PURE__ */ jsx(Database, { className: "h-5 w-5" }),
        badge: contextStats.hidden > 0 ? `${contextStats.enabled} enabled, ${contextStats.hidden} hidden` : `${contextStats.total}`,
        defaultCollapsed,
        children: contexts.length > 0 ? /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          contexts.map((context) => /* @__PURE__ */ jsx(
            PromptStudioContextCard,
            {
              context,
              onToggle: (enabled) => onContextToggle?.(context.id, enabled),
              readOnly
            },
            context.id
          )),
          contextStats.hidden > 0 && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500 pt-2 border-t border-slate-200 dark:border-slate-700", children: [
            /* @__PURE__ */ jsx(EyeOff, { className: "h-4 w-4" }),
            contextStats.hidden,
            " context",
            contextStats.hidden > 1 ? "s" : "",
            " hidden from prompt"
          ] })
        ] }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 dark:text-slate-500 italic", children: "No contexts available. Enable services to add context data." })
      }
    ),
    /* @__PURE__ */ jsx(
      PromptStudioSection,
      {
        title: "System Prompt (Full LLM Prompt)",
        icon: /* @__PURE__ */ jsx(FileText, { className: "h-5 w-5" }),
        badge: systemPrompt ? `${Math.ceil(systemPrompt.length / 4)} tokens` : void 0,
        defaultCollapsed: true,
        children: systemPrompt ? /* @__PURE__ */ jsx(
          CodeBlock,
          {
            code: systemPrompt,
            language: "markdown",
            showLineNumbers: false,
            maxHeight: 500,
            wrapLines: true,
            showCopyButton: true
          }
        ) : /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 dark:text-slate-500 italic", children: "Loading system prompt..." })
      }
    )
  ] });
}

export { AUTOMATION_STATUS_CONFIG, AUTOMATION_STATUS_OPTIONS, AUTOMATION_STATUS_PRESETS, ActiveFilters, Activity, ActivityFeed, ActivityFilters as ActivityFiltersComponent, ActivityHeader, ActivityItemSkeleton, ActivityList, ActivityPendingBadge, ActivityProvider, AddPropertyButton, AlertHistorySkeleton, AlertRuleSkeleton, Alerts, AlertsConditionTypeBadge, AlertsHeader, AlertsHistoryList, AlertsPagination, AlertsProvider, AlertsQuickActions, AlertsRulesList, AlertsSeverityBadge, AlertsStatusBadge, AreaChart, AutomationCardSkeleton, AutomationList, AutomationStatusBadge, AutomationUIProvider, Automations, AutomationsFilters as AutomationsFiltersComponent, AutomationsGrid, AutomationsHeader, AutomationsPagination, AutomationsProvider, AutomationsQuickActions, AutomationsStatusBadge, AutomationsTriggerTypeBadge, Badge, BarChart, Button, Card, CardContent, CardFooter, CardHeader, CodeBlock, AutomationsList as ComposableAutomationsList, RunsTable2 as ComposableRunsTable, TestSuitesList as ComposableTestSuitesList, TicketsList as ComposableTicketsList, TracesTable as ComposableTracesTable, ConnectionIndicator, ConnectionStatus, CoverageOverviewCard, CreateAlertRuleModal, CreateFromPromptModal, CreateModal, CreateModalContext, CreateModalProvider, CreateTestSuiteModal, CreateTicketModal, DEFAULT_PRIORITY_OPTIONS, DataGrid, DateRangeFilter, DonutChart, EVENT_TYPE_OPTIONS, EVENT_TYPE_PRESETS, EmptyBoxIcon, EmptyDocumentIcon, EmptyErrorIcon, EmptySearchIcon, EmptyState, ErrorCardSkeleton, ErrorSeverityBadge, ErrorStatusBadge, Errors, ErrorsFilters as ErrorsFiltersComponent, ErrorsHeader, ErrorsList, ErrorsPagination, ErrorsPanel, ErrorsProvider, ErrorsSeverityBadge, ErrorsStatusActions, ErrorsStatusBadge, EventCard, EventCardIcon, EventCardMeta, EventCardMetaItem, ExecutionResultView, FilterBar, FlakinessIndicator, FlakyTestsCard, FlowView, FlowViewWithExecution, HealthIndicator, InputMappingEditor, JsonViewer, LABEL_COLORS, LabelBadge, LabelPicker, LiveBadge, LiveBadgeContainer, MetricCard, Modal, MultiSelect, MultiSelectChips, NodeInputForm, PayloadEditor, PriorityBadge, PriorityPicker, PromptStudio, PromptStudioContextCard, PromptStudioSection, PromptStudioToolCard, PropertyPill, PropertyPillWithAvatar, PropertyPillWithDot, RUN_STATUS_CONFIG, RUN_STATUS_OPTIONS, RUN_STATUS_PRESETS, RunCardSkeleton, RunStatusBadge, Runs, RunsFilters as RunsFiltersComponent, RunsHeader, RunsList, RunsPagination, RunsProvider, RunsStatusBadge, RunsTable, SEVERITY_CONFIG, SEVERITY_OPTIONS, SEVERITY_PRESETS, STATUS_CONFIG, STATUS_OPTIONS, STATUS_PRESETS, SearchInput, Select, Skeleton, SkeletonCard, SkeletonTable, SkeletonText, StatusFilter, StepDetails, StepStatusBadge, TRACE_STATUS_CONFIG, TRACE_STATUS_OPTIONS, TRACE_STATUS_PRESETS, TRIGGER_TYPE_CONFIG, TRIGGER_TYPE_OPTIONS, TRIGGER_TYPE_PRESETS, Tab, TabList, TabPanel, Tabs, TestCaseCard, TestExecutionModal, TestRunnerPanel, TestSuiteCardSkeleton, TestSuites, TestSuitesHeader, TestSuitesPagination, TestSuitesProvider, TestSuitesRunStatusBadge, TextInput, TextInputWithHint, TicketCardSkeleton, Tickets, TicketsHeader, TicketsPagination, TicketsPriorityBadge, TicketsProvider, TicketsStatusBadge, TraceCardSkeleton, TraceTimeline, Traces, TracesAttemptNavigation, TracesDetail, TracesFilters as TracesFiltersComponent, TracesFlowView, TracesHeader, TracesList, TracesPagination, TracesProvider, TracesStatusBadge, TracesStepDetails, TracesTimelineView, TracesViewToggle, TriggerTypeBadge, UserAvatar, UserPicker, badgeVariants7 as badgeVariants, buttonVariants, cardVariants, cn, codeBlockVariants, deduplicateSteps, eventCardVariants, formatDuration3 as formatActivityDuration, formatTimeAgo2 as formatActivityTimeAgo, formatTimestamp3 as formatActivityTimestamp, formatDuration5 as formatAutomationDuration, formatTimeAgo5 as formatAutomationTimeAgo, formatTimestamp6 as formatAutomationTimestamp, formatCompact2 as formatCompact, formatDuration, formatDurationMs, formatTimeAgo3 as formatErrorTimeAgo, formatTimestamp4 as formatErrorTimestamp, formatPercentage, formatRelativeTime, formatDuration4 as formatRunDuration, formatTimeAgo4 as formatRunTimeAgo, formatTimestamp5 as formatRunTimestamp, formatShortcut, formatStatusLabel, formatSuccessRate, formatTimeAgo, formatTimestamp, formatTraceDuration, formatTraceTimeAgo, formatTraceTimestamp, getAutomationStatusColor, getAutomationStatusConfig, getAutomationStatusVariant, getCreateModalShortcuts, getErrorSeverityColor, getErrorSeverityVariant, getErrorStatusColor, getErrorStatusVariant, getErrorTypeLabel, getEventConfig, getEventDescription2 as getEventDescription, getFirstFocusable, getFocusableElements, getHealthStatus2 as getHealthStatus, getInlineChips, getKeyboardHints, getLastFocusable, getModifierKey, getPriorityColor, getPriorityLabel, getPriorityOption, getRandomLabelColor, getRunStatusColor, getRunStatusConfig, getRunStatusVariant, getSeverityBorderColor, getSeverityConfig, getStatusConfig, getStepStatusColor, getStepStatusVariant, getTraceStatusConfig, getTriggerTypeConfig, getUserDisplayName, getUserInitials, groupStepsByAttempt, isAutomationActive, isAutomationPaused, isAutomationRunnable, isFocusable, isMac, isRunActive, isRunFinished, isTraceActive, isTraceFailed, isTraceFinished, jsonViewerVariants, mapNodeType, mapRunStatus, mapStepStatus, mapToExecutionStatus, matchesShortcut, parseAutomationToFlow, parseFlowFromXmlContent, selectVariants, stepsToFlowDefinition, truncateMessage, truncateName, truncateRunId, truncateTraceId, useActivity, useActivityExpanded, useActivityFilters, useActivityList, useActivityLive, useAlertsActions, useAlertsContext, useAlertsFilters, useAlertsList, useAlertsPagination, useAlertsSelection, useAlertsStatsHook, useAutoFocus, useAutomationSelection, useAutomationUIStore, useAutomationsActions, useAutomationsFilters, useAutomationsHook, useAutomationsList, useAutomationsSelection, useAutomationsStats, useCoverageData, useCreateModal, useCreateModalOptional, useErrorSelection, useErrors3 as useErrors, useErrorsActions, useErrorsFilters, useErrorsList, useErrorsSelection, useErrorsStats, useFieldNavigation, useFlakyTestsData, useFocusTrap, useKeyboardShortcuts, useRunSelection, useRuns, useRunsFilters, useRunsList, useRunsSelection, useRunsStats, useTestCaseSelection, useTestSuitesActions, useTestSuitesContext, useTestSuitesFilters, useTestSuitesList, useTestSuitesPagination, useTestSuitesSelection, useTicketsActions, useTicketsContext, useTicketsFilters, useTicketsList, useTicketsPagination, useTicketsSelection, useTicketsStats, useTraceDetail, useTraceFlow, useTraceStep, useTraces4 as useTraces, useTracesFilters, useTracesList, useTracesSelection, useTracesStats };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map